// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * PROPOSAL: response body shape for the §12 probes is not pinned by the spec. `status` alone was the whole answer until the mute-refusals round (n0811/muti, 2026-08-09): a probe that says `degraded` and nothing else cannot be acted on — the reader cannot tell a database that is down from one that was never migrated, and neither can an orchestrator. `detail` carries the sentence that names the condition AND the gesture that fixes it; `schemaVersion` is the migration this build expects, the figure that makes a partial update visible in a bug report instead of arriving as a bare 500 on the first route touching a new column.
 *
 */
export const zHealth = z.object({
    status: z.enum(['ok', 'degraded']),
    detail: z.string().optional(),
    schemaVersion: z.string().optional()
});

/**
 * One reason the Home semaphore is not green — structured so the GUI can localize the message and link the affected page. `subject` names the offending object (node, storage or Ceph cluster name) when the reason is about one; `count` carries the aggregate when it is about many (failed tasks).
 *
 */
export const zOverviewHealthReason = z.object({
    code: z.enum([
        'node-down',
        'node-stale',
        'node-maintenance',
        'tasks-failed',
        'storage-degraded',
        'storage-error',
        'ceph-warning',
        'ceph-error',
        'store-unreachable',
        'license-absent',
        'license-expired',
        'license-exceeded',
        'failover-reserve-eroded',
        'failover-reserve-exhausted',
        'oci-cache-nearly-full',
        'oci-cache-full',
        'config-replica-empty',
        'config-replica-stalled'
    ]),
    severity: z.enum(['warning', 'critical']),
    subject: z.string().optional(),
    count: z.int().optional()
});

/**
 * One cluster node as the Home renders it — the registry row plus the heartbeat-observed inventory (round #108) and the Proximity liveness verdict. Observed fields are null until first reported.
 *
 */
export const zOverviewNode = z.object({
    id: z.uuid(),
    name: z.string(),
    phase: z.enum([
        'pending-join',
        'joining',
        'member'
    ]),
    maintenance: z.boolean(),
    attestedDown: z.boolean(),
    lastSeenAt: z.iso.datetime().nullish(),
    fresh: z.boolean(),
    cpuCount: z.int().nullish(),
    cpuTopology: z.object({
        physicalCores: z.int(),
        logicalCpus: z.int(),
        sockets: z.int().optional()
    }).nullish(),
    memoryTotalMib: z.int().nullish(),
    cpuUsagePercent: z.number().nullish(),
    memoryUsedMib: z.int().nullish(),
    activeVms: z.int().nullable()
});

/**
 * THE storage driver vocabulary — declared ONCE and referenced everywhere a driver is served or accepted (round «errori onesti», 2026-08-03). It was born inline on `Storage.driver` and hand-copied into the Home aggregate (`system.yaml`, `OverviewStorage.driver`), where the copy missed `nvme-tcp` when round #132 added it: the backend served a legitimate value, the client's generated zod refused it, and the whole dashboard died accusing the backend of being unreachable (bug hunt #157, finding U1). A vocabulary two schemas share is DERIVED from one place now, so every `$ref` follows the next driver by itself. Immutable on a Storage. `dir` is the v6.0 baseline; `nfs` and `cifs` (2026-07-23) are network filesystems MOUNTED at a TVirt-managed point (`<systemPath>/mounts/<storage-id>` — the same derivation on every node, §8.2 same-path by construction) with the dir layout riding on top; they are born `shared`. `iscsi` (design ratified 2026-07-23, wave A) is a BLOCK driver: one LUN, an LVM VG with a thin pool on top, one thin LV per disk — no managed mountpoint, no `dir` block, born `shared` like nfs/cifs. `nvme-tcp` (round #132, 2026-08-02) is the iscsi mechanic on the NVMe over Fabrics TCP transport: one subsystem namespace attached with the in-kernel initiator (`nvme-tcp`), the SAME LVM VG + thin pool + one-thin-LV-per-disk machinery on top, no managed mountpoint, born `shared` — the two form the SAN-LVM family and share every chain/lease/activation rule. `rbd` (the 6.1 ceph-rbd driver, ratified 2026-07-24 — wave A) is the CLUSTER block driver: one reachable Ceph pool (an external cluster, or a TVirt-managed CephCluster consumed through the Annex N.4 proposal), one RBD image per disk, consumed NATIVELY by QEMU's rbd blockdev — no node-side attachment state at all; shared by nature. `cephfs` (the MDS/CephFS round, ratified 2026-07-24) is a network filesystem of the nfs/cifs FAMILY: a CephFS filesystem kernel- mounted at the TVirt-managed point with the dir layout riding on top (qcow2 disks, ISO/images and templates like any dir storage), authenticated by a `kind: cephx` credential; born `shared`. `iscsi-lun` and `nvme-tcp-lun` (round #173, T173-P10 — the WHOLE-LUN family, born with the multi-attach disks) are the SAN carriers where the storage names the TARGET (portal + IQN, or subsystem NQN — the same `iscsi`/`nvme-tcp` configuration blocks WITHOUT `lun`/`nsid`) and every disk IS one whole LUN of it (`DiskSource.type: lun`): no LVM on top, no thin pool, no activation convention, no storage-wide lease — the device is the same on every node and is passed raw to the guest as a `host_device`, which is what lets two writers share it (`sharing: multiwriter`). Born `shared`, `reachableFrom` like the sisters, `disks` only; a SAN-LVM storage and a whole-LUN storage on the SAME target are the normal case (LUN 0 the pool, LUN 1..N the guest cluster's disks) — two whole-LUN storages on one target are `422`. TVirt never writes a signature on a whole LUN (T173-P12): delete unregisters, bytes intact. `zfs`/`lvm-thin` (node) arrive with their milestones (parent spec §6.1) and will extend this enum.
 *
 */
export const zStorageDriver = z.enum([
    'dir',
    'nfs',
    'cifs',
    'iscsi',
    'nvme-tcp',
    'rbd',
    'cephfs',
    'iscsi-lun',
    'nvme-tcp-lun'
]);

/**
 * One storage as the capacity zone renders it. Observed fields (phase, capacity/used) are null while unobserved or on the stale-replica fallback — the GUI then shows the config-derived `provisionedGiB` alone and never invents a percentage.
 *
 */
export const zOverviewStorage = z.object({
    id: z.uuid(),
    name: z.string(),
    driver: zStorageDriver,
    phase: z.string().nullish(),
    capacityGiB: z.number().nullish(),
    usedGiB: z.number().nullish(),
    provisionedGiB: z.number(),
    reserveGiB: z.int(),
    lostAt: z.iso.datetime().nullish()
});

/**
 * One managed Ceph cluster's health line (Annex N).
 */
export const zOverviewCephCluster = z.object({
    id: z.uuid(),
    name: z.string(),
    phase: z.enum([
        'not-deployed',
        'deploying',
        'healthy',
        'degraded',
        'error',
        'destroying',
        'destroyed'
    ]),
    healthStatus: z.string().nullish()
});

/**
 * A workload family grouped by phase: total plus a phase→count map (only phases with at least one member appear). The phase vocabulary is the family's own (Vm.phase, App.phase, NetworkService.phase). On the stale-replica fallback the map is empty whatever the total: phases are observed state, unknown while the store is unreachable.
 *
 */
export const zOverviewPhaseCounts = z.object({
    total: z.int(),
    phases: z.record(z.string(), z.int())
});

/**
 * The failover reserve's verdict (admission control N+1, task #133b — dictated 2026-07-31), served in two places: the Home's semaphore (`SystemOverview.failoverReserve`) and the node evacuation plan (`NodeEvacuationPlan.failoverReserve`, the reserve AFTER the drain). PRESENT only when the reserve is configured (cluster `failoverReserve.tolerateNodeFailures` > 0) AND the caller holds `cluster.read`: with the feature off it is simply ABSENT and the Home renders nothing (full backward compatibility). Omitted on the store-down fallback too — capacity is observed state, and an invented reserve is worse than none.
 *
 * The reserve counts the decider's own capacity currencies and nothing else: MiB of RAM, plus — ratified 2026-08-04 — huge pages PER SIZE in the `hugePages` rows (vCPU overcommit is routine and deliberately never gates). `capacityMib` is summed over the members that could RECEIVE a failover VM (fresh, not in maintenance, not attested down, memory observed); `committedMib` is charged for EVERY member, unavailable ones included, because a draining or unreachable node's VMs must land on the survivors. `reserveMib` is the capacity of the `worstCase` nodes — the LARGEST providers, since the survivors must absorb exactly what the lost nodes were holding — and `headroomMib` (`freeMib - reserveMib`) is what a new start may still take. `state` is the WORST verdict across the RAM figures and every huge-page size IN USE (committedPages > 0).
 *
 */
export const zFailoverReserveVerdict = z.object({
    state: z.enum([
        'unknown',
        'ok',
        'eroded',
        'exhausted'
    ]),
    tolerateNodeFailures: z.int(),
    providerNodes: z.int(),
    capacityMib: z.int(),
    committedMib: z.int(),
    expendableMib: z.int(),
    freeMib: z.int(),
    reserveMib: z.int(),
    headroomMib: z.int(),
    worstCase: z.array(z.object({
        node: z.string(),
        capacityMib: z.int()
    })),
    hugePages: z.array(z.object({
        size: z.enum(['2M', '1G']),
        capacityPages: z.int(),
        committedPages: z.int(),
        expendablePages: z.int(),
        freePages: z.int(),
        reservePages: z.int(),
        headroomPages: z.int(),
        worstCase: z.array(z.object({
            node: z.string(),
            capacityPages: z.int()
        }))
    })),
    unavailable: z.array(z.object({
        node: z.string(),
        reason: z.enum([
            'attestedDown',
            'maintenance',
            'unreachable',
            'capacityUnknown'
        ])
    }))
});

/**
 * What started a checkpoint: an operator ("Crea checkpoint"), the schedule, the automatic PARACHUTE a restore takes BEFORE touching any row (`pre-restore` — never pruned: it must outlive the automation), or — `pre-change` (BK6, PROPOSAL 17) — the automatic snapshot the first config write after a period of quiet triggers on every destination enrolled via `preChangeRetention`. A pre-change artefact freezes the state just BEFORE the modification (the snapshot is cut before the triggering write commits) and is pruned by its own count. Declared ONCE and referenced by every site that speaks it (the BackupSubjectKind idiom): the record, the upload's manifest and the dashboard overview must never drift apart — the enum census caught exactly that three-way drift the day `pre-change` was born.
 *
 */
export const zCheckpointOrigin = z.enum([
    'manual',
    'schedule',
    'pre-restore',
    'pre-change'
]);

/**
 * The most recent checkpoint record (by creation), any phase.
 */
export const zOverviewLastCheckpoint = z.object({
    id: z.uuid(),
    destination: z.string(),
    destinationName: z.string().nullish(),
    phase: z.enum([
        'running',
        'complete',
        'error'
    ]),
    origin: zCheckpointOrigin,
    createdAt: z.iso.datetime(),
    completedAt: z.iso.datetime().nullish(),
    error: z.string().nullish()
});

/**
 * The resource a task operates on.
 */
export const zTaskTarget = z.object({
    kind: z.string().optional(),
    id: z.uuid().optional(),
    name: z.string().optional()
});

/**
 * One terminal entry of the recent-activity zone — a TOP-LEVEL task, standalone or parent (parents — the former macrotasks — joined the zone with ratification 2026-07-28 appendix #42; one collection since round #128). A child task (one owned by a parent) never appears as its own entry: the parent represents its children, so a failed parent whose failed child is also failed is ONE logical entry — the parent wins.
 *
 */
export const zOverviewTask = z.object({
    id: z.string(),
    type: z.string(),
    phase: z.enum([
        'succeeded',
        'failed',
        'cancelled',
        'aborted'
    ]),
    message: z.string().nullish(),
    target: zTaskTarget.optional(),
    finishedAt: z.iso.datetime().nullish()
});

/**
 * Present (true) when the response was computed from this node's config replica because the cluster store was unreachable (§6 read path) — eventually consistent, at worst one generation behind. Absent on fresh store-served reads. The single-GET twin of the list envelopes' `stale` (same semantics, IpamView gap-G9 precedent): the render served it since the revision rounds, but on single GETs it was undeclared and strict clients stripped it — DECLARED with R6 fase 1 (P1 of R5, ratified 2026-08-24). Where this schema also serves as a list item or a write response, the field is simply absent there.
 *
 */
export const zStale = z.boolean().readonly();

/**
 * Present only alongside `stale` (the store-down fallback): WHEN this node's state replica last took the OBSERVED half of the fleet — phases, hosting nodes, agent reports, health, sizes, generations — from the cluster store. Observed state is a replication class of its own (round `classe stato`, 2026-08-10; B5 ratified with T324-P3, 2026-09-03): it descends asynchronously and best-effort, without generation or consensus, so an outage keeps it readable and keeps it OLD — this field says how old. Every envelope that carries `stale` carries it, the lists and the single GETs alike, whether or not the resource renders observed fields of its own: the age is a property of the node's replica, not of one resource, and a page that shows stale phases owes the same number to a page that shows none. `null` = the state replica has never reached the store: what you see are this node's own observations only. Absent on fresh store-served reads. Where this schema also serves as a list item or a write response, the field is simply absent there.
 *
 */
export const zStateAsOf = z.iso.datetime().readonly().nullable();

/**
 * The Home dashboard's single aggregate (ratified 2026-07-28): everything the landing page renders, computed server-side in one call, RESPECTING THE CALLER'S VISIBILITY. Every zone except `health` is OPTIONAL — ABSENT (never an error) when the caller holds the zone's read permission nowhere: `nodes` requires `cluster.read`, `storages.items` `storage.read`, `storages.cephClusters` `hci.cephCluster.read`, `workload.vms` `vm.read`, `workload.apps` `app.read`, `workload.services` `netsvc.read`, `checkpoints` `backupDestination.read` OR `checkpoint.read` (the zone reports two objects and each half answers to its own — task #281), `recentTasks` `task.read`; a zone whose sub-fields are all absent is omitted whole. Scoped rows (storages, workload, tasks, checkpoints) are filtered to the caller's RBAC selectors, so a tenant sees the overview of ITS world, not cluster totals. Only OBSERVED data: unobserved fields are null, never invented. `health` is always present and is computed over the zones the caller can see.
 *
 * STORE-DOWN FALLBACK (ratified 2026-07-28 appendix #43, supersedes the former 503): while the cluster store is unreachable the config zones — `nodes`, `storages`, `workload` — are served from this node's LOCAL config replica, the whole response is marked `stale: true` with `staleAsOf` carrying the replica's apply timestamp, and `health` is `critical` with reason `store-unreachable`. Observed values inside the stale zones are null/empty (state is store-born); the zones that REQUIRE the store — `recentTasks`, `checkpoints` (task and checkpoint records are store-only) — are OMITTED even when the caller holds their permission.
 *
 */
export const zSystemOverview = z.object({
    health: z.object({
        status: z.enum([
            'ok',
            'warning',
            'critical'
        ]),
        reasons: z.array(zOverviewHealthReason)
    }),
    nodes: z.array(zOverviewNode).optional(),
    storages: z.object({
        items: z.array(zOverviewStorage).optional(),
        cephClusters: z.array(zOverviewCephCluster).optional()
    }).optional(),
    workload: z.object({
        vms: zOverviewPhaseCounts.optional(),
        apps: zOverviewPhaseCounts.optional(),
        services: zOverviewPhaseCounts.optional()
    }).optional(),
    failoverReserve: zFailoverReserveVerdict.optional(),
    checkpoints: z.object({
        destinations: z.int(),
        lastCheckpoint: zOverviewLastCheckpoint.optional()
    }).optional(),
    recentTasks: z.object({
        items: z.array(zOverviewTask),
        failedLast24h: z.int()
    }).optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    staleAsOf: z.iso.datetime().readonly().nullish()
});

export const zFieldError = z.object({
    field: z.string(),
    message: z.string()
});

/**
 * RFC 9457 problem document with TVirt extensions (API spec §7).
 */
export const zProblem = z.object({
    type: z.string().optional(),
    title: z.string(),
    status: z.int(),
    detail: z.string().optional(),
    code: z.enum([
        'invalid_argument',
        'unauthenticated',
        'permission_denied',
        'sudo_required',
        'password_change_required',
        'not_found',
        'conflict',
        'in_use',
        'gone',
        'last_admin_protection',
        'node_membership_locked',
        'precondition_failed',
        'precondition_required',
        'template_frozen',
        'proxy_failed',
        'license_limited',
        'failover_reserve',
        'vm_is_replica',
        'schema_unsupported',
        'validation',
        'immutable_field',
        'rate_limited',
        'internal',
        'quorum_unavailable',
        'store_unavailable',
        'cluster_config_unavailable',
        'task_backlog',
        'purge_required',
        'release_required',
        'cordon_raised',
        'read_only_access_not_granted',
        'head_digest',
        'range_not_satisfiable',
        'step_failed',
        'mfa_required',
        'app_tokens_unavailable',
        'cbt_unavailable',
        'cbt_inconsistent',
        'cbt_chain_broken',
        'quiesce_unavailable',
        'not_implemented',
        'storage_not_writable',
        'storage_full'
    ]),
    retryable: z.boolean().optional(),
    errors: z.array(zFieldError).optional()
});

/**
 * One catalog as the Esplora tree renders it — a FOLDER forming a namespace inside its scope (`/tc/catalog1/iso/win11`, task #143): the GUI places every catalog file under its catalog's per-kind sub-folder, so the census names the catalogs separately from their files.
 *
 */
export const zExplorerCatalog = z.object({
    id: z.uuid(),
    name: z.string(),
    scope: z.string()
});

/**
 * WHAT the artefact is — immutable, and declared ONCE: the file itself (`CatalogFile.kind`) and the Explorer row that shows it (`ExplorerEntry.fileKind`, which says so in its own prose) speak the same five words. See the header comment for what each kind means. Derived 2026-08-11: the two faces were hand copies under two different names, and the enum census indexes twins by property NAME — the Explorer copy was invisible to it, and a kind added to one side alone would have rendered as an unlabelled row instead of failing a test.
 *
 */
export const zCatalogFileKind = z.enum([
    'iso',
    'ct-rootfs',
    'vm-template',
    'oci-image',
    'firmware-ovmf'
]);

/**
 * One named object of the Esplora census (task #151). `kind` is the section's own vocabulary — the api-js scoped families of round #143, with VM/CT templates already split out server-side (their lists split on the same `template` flag). `name` follows each family's display idiom: the username for users, the rendered reference for OCI images (they carry no name of their own), the plain `name` everywhere else. The per-kind extras exist so the GUI can PLACE and LINK the entry without a second call: `catalog`/`fileKind` put a catalog file in its catalog's per-kind sub-folder and address the canonical catalog tab, `attachedVm` points an attached disk at its owning VM (absent on an unused disk — the GUI then links the unused-disks tab).
 *
 */
export const zExplorerEntry = z.object({
    kind: z.enum([
        'vm',
        'vmTemplate',
        'ct',
        'ctTemplate',
        'app',
        'overlayNetwork',
        'networkService',
        'fabric',
        'subnet',
        'storage',
        'disk',
        'catalogFile',
        'ociRegistry',
        'ociImage',
        'cephCluster',
        'backupDestination',
        'user',
        'group',
        'role',
        'credential',
        'tag',
        'proximityRule'
    ]),
    id: z.uuid(),
    name: z.string(),
    scope: z.string(),
    catalog: z.uuid().optional(),
    fileKind: zCatalogFileKind.optional(),
    attachedVm: z.uuid().optional()
});

/**
 * The Esplora section's single aggregate (task #151, ratified — modeled on the overview #48): the whole scope-aware census in one call, in place of the section's former per-family listAll fan-out (~22 lists every refresh since #143). Every collection is computed RESPECTING THE CALLER'S VISIBILITY, with the SAME per-family read permission and RBAC scope selectors the family's own list applies (strict visibility): a family whose read permission is held nowhere simply contributes NO rows — never an error — and scoped rows are filtered to the caller's selectors, so a tenant reads the census of ITS branch, not the cluster's. `scopes` (the folder tree, `scope.read`) and `catalogs` (the namespace formers, `catalog.read`) are always-present arrays, empty under the same rule. Config truth only — no phases, no observed state: the section fetches the LIVE object on selection, as before.
 *
 */
export const zSystemExplorer = z.object({
    scopes: z.array(z.string()),
    catalogs: z.array(zExplorerCatalog),
    entries: z.array(zExplorerEntry)
});

/**
 * One finding of the diagnose run (#388/#392): what is wrong (or unknown), where, and — always — what to run next. Three grades, not two: `unknown` is the #228 convention brought into the report — «could not establish» is neither a fault found nor a clean machine, and it is the only honest answer when a read is denied. `headline`, `detail` and `next` are server-authored English prose, the SAME sentences on every surface (CLI, TUI, GUI): the parity between them is the point of #392, and a per-surface translation would be a second copy free to age.
 *
 */
export const zDiagnoseVerdict = z.object({
    severity: z.enum([
        'critical',
        'warning',
        'unknown'
    ]),
    area: z.string(),
    headline: z.string(),
    detail: z.string().optional(),
    next: z.string().optional()
});

/**
 * One diagnose run (#392, the GUI/TUI parity dettatura): the verdicts worst-first, plus the two lists WITHOUT which an empty verdict list would read as «all fine» instead of «nobody looked» — `inspected` (area names with the count of objects each one saw) and `unreachable` (areas that could not answer, with the reason: a permission held nowhere, a store that is down). `sinceHours` and `heartbeatLateSeconds` restate the thresholds the verdicts were computed against: an operator who disagrees with a threshold must be able to see it.
 *
 */
export const zDiagnoseRun = z.object({
    verdicts: z.array(zDiagnoseVerdict),
    inspected: z.array(z.string()),
    unreachable: z.array(z.string()),
    sinceHours: z.int(),
    heartbeatLateSeconds: z.int()
});

export const zSessionUser = z.object({
    username: z.string(),
    realm: z.string(),
    displayName: z.string()
});

/**
 * A session on the answering node (sessions are node-local; API spec §3). Scoped semantics (ratified 2026-07-28 #24/#25): a session inherits the SCOPE of its owner — the caller always reaches their own sessions, and reaches another principal's only with `user.sessions.revoke` granted on that principal's scope. An EXTERNAL owner (round #102) is homed at the cluster ROOT scope (round #181, confirmed 2026-08-03) — external identities are cluster-level, not scoped — so only a revoke grant covering the whole tree reaches their sessions.
 *
 */
export const zSession = z.object({
    id: z.uuid().readonly(),
    user: zSessionUser,
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    active: z.boolean().readonly(),
    mustChangePassword: z.boolean().optional(),
    external: z.boolean().readonly().optional(),
    externalUuid: z.string().readonly().optional()
});

export const zSessionList = z.object({
    items: z.array(zSession),
    nextCursor: z.string().optional()
});

export const zSessionCreated = zSession.and(z.object({
    token: z.string()
}));

export const zSudoGrant = z.object({
    sudoToken: z.string(),
    expiresInSeconds: z.int()
});

/**
 * One entry of the hardcoded permission catalog.
 */
export const zPermissionInfo = z.object({
    name: z.string(),
    description: z.string().optional()
});

export const zPermissionList = z.object({
    items: z.array(zPermissionInfo)
});

export const zIdentifyRequest = z.object({
    username: z.string().max(320)
});

/**
 * Step 1 of the identifier-first login (ratified 2026-07-31; **revised #315, 2026-08-09**). The answer is THE NEXT STEP and nothing else: type a password, or follow a redirect to an external identity provider.
 * It never names the realm the identifier resolved to. The realm is derived server-side — from the domain, or from the cluster's own name for a bare username — and it stays there: which realm answered *is* the federation map, and this route is unauthenticated. Until this revision the response carried `realm` outright and the mere PRESENCE of `realmKind` made the configured domains enumerable (a name that resolved to a realm got the field, one that did not omitted it), on a throttle keyed by the guessed string, so every guess had its own budget.
 * It says nothing about the ACCOUNT either, which was always the point of splitting the page in two: an identifier nobody has ever heard of is asked for a password exactly like a real one, so step 2 renders identically and only its submit can fail. Which realm the session ended up in is told by `GET /auth/sessions/current`, AFTER the login — that is where a UI needs it.
 *
 */
export const zIdentifyResult = z.object({
    method: z.enum(['password', 'redirect']),
    redirectUrl: z.string().optional()
});

/**
 * One second-factor method this deployment can verify — the extension point made visible. `id` is what a challenge names; `builtin` marks the implementations shipped with TVirt (v1: `totp` alone).
 *
 */
export const zMfaMethod = z.object({
    id: z.string(),
    displayName: z.string(),
    builtin: z.boolean(),
    enrollable: z.boolean(),
    description: z.string().optional()
});

export const zMfaMethodList = z.object({
    items: z.array(zMfaMethod)
});

/**
 * One subject's second factor, as an addressable resource (round #178 half B). It exists for MANAGED users and for EXTERNAL directory identities alike: it is NOT a shadow user — it carries the factor and nothing else, no profile, no grants, no scope of its own. External subjects live at the cluster ROOT (round #181), and so do their enrollments. Lifecycle is deliberately ASYMMETRIC (ratified 2026-08-04): a MANAGED user's enrollment goes with its user (delete cascade) — we delete internal users OURSELVES, so their absence is knowledge; an external identity that disappears from its directory is an INFERENCE, and an inference is MARKED (`orphaned`), never executed.
 *
 */
export const zMfaEnrollment = z.object({
    id: z.uuid().readonly(),
    subject: z.string().readonly(),
    realm: z.string().readonly(),
    external: z.boolean().readonly(),
    user: z.uuid().readonly().optional(),
    directoryUuid: z.string().readonly().optional(),
    methods: z.array(z.string()).readonly(),
    state: z.enum(['pending', 'active']).readonly(),
    enrolledAt: z.iso.datetime().readonly().optional(),
    recoveryCodesRemaining: z.int().readonly().optional(),
    lastSeenAt: z.iso.datetime().readonly().optional(),
    orphaned: z.boolean().readonly(),
    orphanedAt: z.iso.datetime().readonly().optional(),
    orphanedReason: z.enum(['directory-miss', 'realm-removed']).readonly().optional()
});

export const zMfaEnrollmentList = z.object({
    items: z.array(zMfaEnrollment),
    nextCursor: z.string().optional()
});

/**
 * The resource's OPAQUE state validator (R6 fase 1, ratified 2026-08-24 — the fabric's 2026-08-06 form generalized to every config resource with a single-object update): the same value the `ETag` response header carries, served in the body so a LIST already brings it and a client needs no second request to write safely. Send it back as `If-Match` on the update: absent keeps today's behavior, `*` is the deliberate opt-out, a stale value answers `412 precondition_failed` naming both validators. Never parse it: its only defined operation is equality, and its shape may change. WHAT IT COVERS: `id`, `name`, `scope` and exactly the fields the resource's update operation can write — at every depth. Deliberately NOT the observed state, NOT `updatedAt`, NOT the tag assignments (written through the tags endpoints): another node's observation or an unrelated write must not invalidate a pending edit, and a write that lands an IDENTICAL document does not move the validator.
 *
 */
export const zEntityTag = z.string().readonly();

/**
 * An external authentication realm. `name` is the DOMAIN written after the `@` in the login string and is IMMUTABLE after creation (it is the identity of every session and every audit line that ever named it). `kind` is the extension point: `ldap` is the only implementation of Round 1 — `oidc` and `saml` join it in rounds 2 and 3 without a new resource. The identities a realm authenticates are CLUSTER-level, homed at the cluster ROOT scope (round #181, confirmed 2026-08-03): a realm is cluster configuration and its users belong to no vdc — reaching their sessions takes `user.sessions.revoke` on the whole tree, while grants to them may target any scope, exactly as for native users.
 *
 */
export const zAuthRealm = z.object({
    etag: zEntityTag.optional(),
    id: z.uuid().readonly().optional(),
    name: z.string().min(1).max(253).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/),
    kind: z.enum(['ldap']).optional().default('ldap'),
    enabled: z.boolean().optional().default(true),
    urls: z.array(z.string().regex(/^ldaps?:\/\/[^\s\/]+$/)).min(1).max(8).optional(),
    startTls: z.boolean().optional().default(false),
    verifyCertificate: z.boolean().optional().default(true),
    caCertificate: z.string().max(65536).optional(),
    bindDn: z.string().max(1024).optional(),
    bindCredential: z.string().max(255).optional(),
    baseDn: z.string().max(1024).optional(),
    userFilter: z.string().max(1024).optional().default('(&(objectClass=person)(sAMAccountName=%u))'),
    memberOfAttribute: z.string().max(255).optional().default('memberOf'),
    uuidAttribute: z.string().max(255).optional().default('objectGUID'),
    displayNameAttribute: z.string().max(255).optional().default('displayName'),
    connectTimeoutSeconds: z.int().gte(1).lte(60).optional().default(5),
    notes: z.string().max(65536).optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    updatedAt: z.iso.datetime().readonly().optional()
});

export const zAuthRealmList = z.object({
    items: z.array(zAuthRealm),
    nextCursor: z.string().optional()
});

/**
 * The outcome of a live check against the realm's directory — the "does this configuration work" answer, never an authentication.
 *
 */
export const zAuthRealmTestResult = z.object({
    reachable: z.boolean(),
    url: z.string().optional(),
    detail: z.string().optional(),
    matchedUsers: z.int().optional(),
    checks: z.array(z.object({
        name: z.string(),
        passed: z.boolean(),
        detail: z.string().optional()
    }))
});

/**
 * One tag value attached to this resource. `tag` is the BARE name and `scope` the tag's scope: together they are the qualified form `/scope/name` that disambiguates homonyms living in different scopes (ratified: no uniqueness along the chain). `id` is the assignment handle — pass it to `DELETE /api/v6/tag-assignments/{assignment}` to detach without a lookup.
 *
 */
export const zResourceTag = z.object({
    id: z.uuid(),
    tag: z.string(),
    scope: z.string(),
    value: z.string(),
    tagId: z.uuid().optional(),
    valueId: z.uuid().optional()
});

/**
 * Fields shared by every flat resource (API spec §5, ADR-22). The kind's identifier field (`username` for users, `name` elsewhere) is declared per kind. Optimistic locking is NOT inherited from this schema — each kind that serves it declares its own `etag` field (the shared `EntityTag` component): since R6 fase 1 (ratified 2026-08-24) that is EVERY config resource with a single-object update operation, whose `If-Match` is honored as described on the `EntityTag` schema. Kinds without an update (and pure registries) carry no validator, and the recourse to a collision there stays the audit trail.
 *
 */
export const zResourceCommon = z.object({
    id: z.uuid().readonly().optional(),
    scope: z.string().max(255).regex(/^(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){0,7}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/).optional(),
    tags: z.array(zResourceTag).readonly().optional(),
    notes: z.string().max(65536).optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    updatedAt: z.iso.datetime().readonly().optional()
});

/**
 * A user account. `username` is the kind's identifier field (API spec §5).
 */
export const zUser = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    username: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    displayName: z.string().optional(),
    enabled: z.boolean().optional().default(true),
    mustChangePassword: z.boolean().optional().default(false),
    groups: z.array(z.string()).readonly().optional(),
    mfaRequired: z.boolean().optional().default(false),
    mfaEnrolled: z.boolean().readonly().optional(),
    mfaRequiredEffective: z.boolean().readonly().optional(),
    mfaRequiredVia: z.array(z.string()).readonly().optional(),
    hasPassword: z.boolean().readonly().optional(),
    lastLoginAt: z.iso.datetime().readonly().optional(),
    lastLoginFrom: z.string().readonly().optional(),
    failedAttempts: z.int().readonly().optional(),
    lockedUntil: z.iso.datetime().readonly().optional()
}));

export const zUserList = z.object({
    items: z.array(zUser),
    nextCursor: z.string().optional()
});

/**
 * The caller's own second-factor state, as the profile page shows it.
 */
export const zMfaStatus = z.object({
    required: z.boolean(),
    requiredVia: z.array(z.string()).readonly().optional(),
    enrolled: z.boolean(),
    methods: z.array(z.string()),
    recoveryCodesRemaining: z.int().optional(),
    enrolledAt: z.iso.datetime().optional()
});

/**
 * A STARTED, not yet confirmed, TOTP enrollment. The secret is shown exactly once, in both machine (`otpauthUri`, `qrSvg`) and human (`secret`) form; it becomes active only when a code computed from it is confirmed.
 *
 */
export const zTotpEnrollment = z.object({
    secret: z.string(),
    otpauthUri: z.string(),
    qrSvg: z.string(),
    digits: z.int(),
    periodSeconds: z.int(),
    algorithm: z.string()
});

/**
 * Single-use codes that stand in for the second factor when the authenticator is lost. Shown exactly ONCE (only their hashes are stored); regenerating invalidates every previous code.
 *
 */
export const zRecoveryCodes = z.object({
    codes: z.array(z.string())
});

/**
 * A long-lived credential for an automation. Two ways to use it, one identity: as a **bearer** on its own (`Authorization: Bearer tvt_…`, since 2026-09-05 — the node opens and keeps one session per token, see the `bearerToken` security scheme in `common.yaml`), or in place of the password at `POST /api/v6/auth/sessions` to obtain an interactive `tvts_` session. Either way the `tvt_` prefix routes verification and every act is audited to the token (`actor.appToken`). Inherits all of the owner's permissions and scope reach — narrowing is an open point. Metadata only: the secret appears exactly once, at creation. App tokens are immutable — changes are revoke-and-recreate; revoking one refuses its next bearer request and its next login, while the `tvts_` sessions it already logged in with follow the rules of sessions.
 * **The second factor does not apply to an app token** (ratified 2026-08-09, #315). A token is a NON-INTERACTIVE credential: it is minted on its own, revoked on its own, audited distinctly, and it is held by an automation that has no authenticator app to open. The exemption belongs to the TOKEN, never to the account — the same owner's password still owes the code, and revoking the token shuts the door again.
 * **Hence app tokens DO NOT EXIST for an identity under an MFA obligation** (ratified 2026-08-10). Since the token is a single-factor credential, leaving the mint open to an obliged subject would make the obligation optional: one POST with an ordinary session and the second factor is bypassed for good. So the mint is refused with `409 app_tokens_unavailable`, and the tokens such a subject already holds are REVOKED — no grace period, no list to review. The obligation is the one resolved in #178 half B (pure OR of the personal flag and the group flag), and the refusal's `detail` names its provenance so the way out is readable: an administrator lifting the obligation, not a different endpoint.
 *
 */
export const zAppToken = z.object({
    id: z.uuid().readonly().optional(),
    label: z.string().min(1).max(255),
    expiresAt: z.iso.datetime().nullish().default(null),
    createdAt: z.iso.datetime().readonly().optional(),
    lastUsedAt: z.iso.datetime().readonly().nullish()
});

export const zAppTokenList = z.object({
    items: z.array(zAppToken)
});

export const zAppTokenCreated = zAppToken.and(z.object({
    token: z.string()
}));

/**
 * Bulk delete (2026-07-23): the ids/usernames to remove. Partial success is normal — see `BatchDeleteResult`; the endpoint applies the SAME per-item guards as the single delete (protected admin, in-use overlay, running VM …).
 *
 */
export const zBatchDeleteRequest = z.object({
    ids: z.array(z.string()).min(1).max(500)
});

/**
 * Per-item outcome of a bulk delete; the endpoint returns `200` even on partial refusal.
 */
export const zBatchDeleteResult = z.object({
    deleted: z.array(z.string()),
    refused: z.array(z.object({
        id: z.string(),
        code: z.string(),
        detail: z.string()
    }))
});

/**
 * One realm's correspondence for a group.
 */
export const zGroupRealmMapping = z.object({
    realm: z.string().max(253),
    externalGroup: z.string().min(1).max(1024)
});

/**
 * A named, scoped, FLAT set of users (round #103, ratified 2026-07-31): members are users only — never other groups (directory nesting stays in the directory). Membership is managed through the member endpoints; `members` is read-only here, mirroring the tags idiom. Since round #102 the group also carries its own EXTERNAL-REALM MAPPINGS (`realmMappings`): the ratified home of the correspondence is the TVirt GROUP, not the realm — "what does this group correspond to in each external realm".
 *
 */
export const zGroup = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    members: z.array(z.string()).readonly().optional(),
    mfaRequired: z.boolean().optional().default(false),
    realmMappings: z.array(zGroupRealmMapping).max(64).optional()
}));

export const zGroupList = z.object({
    items: z.array(zGroup),
    nextCursor: z.string().optional()
});

/**
 * THE protection vocabulary for a DEGRADED cluster (task #204, design `priorita-e-sacrificabilita-design-notes.md`, ratified 2026-08-04) — declared once and `$ref`-erenced by `VirtualMachine.failoverProtection` and `Scope.failoverProtection` (the `StorageDriver` precedent: one list, never a hand copy). `protected` (the effective default): the failover reserve covers the holder and refuses new starts that would eat its room. `expendable`: it always starts — the reserve never blocks it — but in a degraded cluster it is sacrificed first: not counted in the reserve, stopped to make room for a protected failover, restarted best-effort after every protected VM. `null` = inherit: a VM from the nearest ancestor scope that declares, then `protected`; a Scope upward along its path — the cluster-wide root default is `protected` and is NOT a setting.
 *
 */
export const zFailoverProtection = z.enum(['protected', 'expendable']).nullable();

/**
 * A node of the namespace. Creating `{scope: "/roma-1", name: "vdc-web"}` makes `/roma-1/vdc-web` available for resources, RBAC bindings and delegation.
 *
 */
export const zScope = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    ociCacheStorage: z.string().nullish(),
    appEphemeralStorage: z.string().nullish(),
    networkWeight: z.int().gte(1).lte(10000).optional().default(100),
    networkCeilMbps: z.int().gte(1).lte(1000000).nullish(),
    failoverProtection: zFailoverProtection.optional()
}));

export const zScopeList = z.object({
    items: z.array(zScope),
    nextCursor: z.string().optional()
});

/**
 * A named, scoped aggregate of permissions and/or other roles (hierarchy): the effective permission set is the transitive union. At least one of `permissions` and `roles` MUST be present. Names with the reserved `tvirt-` prefix are system roles: present in every deployment, neither creatable nor modifiable nor deletable (mutations return `422 immutable_field`).
 *
 */
export const zRole = zResourceCommon.and(z.intersection(z.unknown(), z.object({
    etag: zEntityTag.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    permissions: z.array(z.string()).min(1).optional(),
    roles: z.array(z.string()).min(1).optional()
})));

export const zRoleList = z.object({
    items: z.array(zRole),
    nextCursor: z.string().optional()
});

export const zGrant = z.intersection(z.unknown(), z.object({
    id: z.uuid().readonly().optional(),
    user: z.string().regex(/^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?@[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/).optional(),
    group: z.string().optional(),
    role: z.string(),
    grantRight: z.boolean().optional().default(false),
    scope: z.string().max(257).regex(/^(\/\*|(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){1,8}(\/\*)?|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/\*)?)$/),
    createdAt: z.iso.datetime().readonly().optional(),
    createdBy: z.string().readonly().optional(),
    effective: z.boolean().readonly().optional()
}));

export const zGrantList = z.object({
    items: z.array(zGrant),
    nextCursor: z.string().optional()
});

export const zTagValue = z.object({
    id: z.uuid().readonly().optional(),
    value: z.string().min(1).max(253)
});

/**
 * NOT an allOf of ResourceCommon on purpose: a tag is not a taggable kind, so it carries no `tags` array of its own — it borrows only the common fields it genuinely has. A tag definition — the vocabulary entry. `name` is unique within its scope only; the same name may exist in sibling or ancestor scopes (ratified: no chain uniqueness). Names admit no `:` (the filter grammar's separator) and no `/` (the qualified-form separator). The `tvirt-` prefix is RESERVED for platform-created system tags, which are read-only for operators exactly like system roles.
 *
 */
export const zTag = z.object({
    etag: zEntityTag.optional(),
    id: z.uuid().readonly().optional(),
    scope: z.string().max(255).regex(/^(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){0,7}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/).optional(),
    notes: z.string().max(65536).optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    updatedAt: z.iso.datetime().readonly().optional(),
    name: z.string().min(1).max(63).regex(/^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?$/),
    cardinality: z.enum(['single', 'multiple']).optional().default('single'),
    autoCreateValues: z.boolean().optional().default(true),
    values: z.array(zTagValue).max(256).optional(),
    kinds: z.array(z.string()).max(64).optional(),
    system: z.boolean().readonly().optional()
});

export const zTagList = z.object({
    items: z.array(zTag),
    nextCursor: z.string().optional()
});

export const zTagValueWrite = z.object({
    value: z.string().min(1).max(253)
});

/**
 * One tag value attached to one resource. The `id` is the handle the detach operation takes; it is also carried by the resource's own `tags` array, so a client that has the resource never needs a lookup to detach.
 *
 */
export const zTagAssignment = z.object({
    id: z.uuid().readonly(),
    tag: z.string(),
    tagScope: z.string(),
    tagId: z.uuid().readonly().optional(),
    value: z.string(),
    valueId: z.uuid().readonly().optional(),
    kind: z.string(),
    resource: z.uuid(),
    createdAt: z.iso.datetime().readonly().optional()
});

export const zTagAssignmentList = z.object({
    items: z.array(zTagAssignment),
    nextCursor: z.string().optional()
});

/**
 * Attach a tag value to a resource. `tag` is a REFERENCE resolved server-side against the caller's visible set — that is where the bare-name rule lives, so the CLI and the web UI both get it for free.
 *
 */
export const zTagAssignmentRequest = z.object({
    kind: z.string(),
    resource: z.uuid(),
    tag: z.string(),
    value: z.string().min(1).max(253),
    overwrite: z.boolean().optional().default(false)
});

/**
 * WHOSE hand is behind a recorded fact — declared ONCE and referenced by every register that records one (`EventActor.kind` here, `AuditActor.kind` in audit.yaml). `user` is a person or a program acting AS one (the qualified identity travels beside it); `system` is the cluster acting on its own, and names the emitting component instead. The two registers were designed to read with the same grammar — audit.yaml says so in prose, and says it is a deliberate SUPERSET of `EventActor` — so the one field they share by construction is derived instead of hand-copied (the `StorageDriver` discipline, round «errori onesti»): the day a third kind is born, it is born on both surfaces or on neither.
 *
 */
export const zActorKind = z.enum(['user', 'system']);

/**
 * WHO did it. `kind: user` carries the qualified identity frozen at the act (design §3.2, the #113-ready shape): `username` plus the realm — `realm` is the realm NAME (always present; the cluster realm is implied by the store and has no UUID of its own), `realmId` the realm object's UUID where one exists (directory realms), `externalUuid` the directory identity for session-only external accounts. `kind: system` names the emitting `component` instead (`worker`, `ha`, `gc`, …). Entirely server-written.
 *
 */
export const zEventActor = z.object({
    kind: zActorKind,
    username: z.string().optional(),
    userId: z.uuid().optional(),
    realm: z.string().optional(),
    realmId: z.uuid().optional(),
    externalUuid: z.string().optional(),
    component: z.string().optional()
});

/**
 * The atom of execution — a single idempotent operation with an owner, an executor, a structured log, progress and a terminal outcome (parent spec §5.7). Flat, server-created and entirely read-only (API spec §9). A task may own CHILDREN (round #128 — the former macrotask): the children are its ordered steps, each gated on the success of the previous, and the parent itself executes no code — its phase derives from the children (running while one runs, failed when one fails, succeeded when all closed).
 *
 */
export const zTask = z.object({
    id: z.string(),
    scope: z.string().readonly().optional(),
    type: z.string(),
    target: zTaskTarget.optional(),
    actor: zEventActor.optional(),
    params: z.record(z.string(), z.unknown()).optional(),
    phase: z.enum([
        'queued',
        'running',
        'succeeded',
        'failed',
        'cancelled',
        'aborted'
    ]),
    parent: z.string().readonly().optional(),
    stepName: z.string().readonly().optional(),
    stepSeq: z.number().readonly().optional(),
    superseded: z.boolean().readonly().optional(),
    failedStep: z.string().readonly().optional(),
    progressPercent: z.number().gte(0).lte(100).optional(),
    message: z.string().optional(),
    startedAt: z.iso.datetime().optional(),
    finishedAt: z.iso.datetime().optional(),
    error: zProblem.optional(),
    result: z.record(z.string(), z.unknown()).readonly().optional(),
    node: z.string().optional()
});

/**
 * Il verdetto reso della licenza, dichiarato UNA volta e riferito da entrambe le facce della GET (piena e ridotta, #157 LC1). Precedente: `StorageDriver` — un enum ricopiato a mano e' un enum che un giorno diverge, e il censimento anti-copia lo pretende derivato.
 *
 */
export const zLicenseState = z.enum([
    'ok',
    'limitExceeded',
    'expired'
]);

/**
 * The cluster's subscription license — at most one per cluster. `basis` picks WHAT the license caps: `concurrentVms` (VMs concurrently powered on — running, paused or stopping, i.e. every VM whose QEMU is alive (bughunt #157 LC3); templates never count, they are always off) or `physicalCores` (sum of the member nodes' observed physical cores — `ClusterNode.cpuTopology.physicalCores`; a node whose topology was never observed makes the sum honest-but-partial, flagged in `usage`, never invented). Registration is the only writer: there is no PATCH — changing a license is remove-and-register (dev-stage, see the file header).
 *
 */
export const zLicense = z.object({
    productKey: z.string().min(1).max(255),
    basis: z.enum(['concurrentVms', 'physicalCores']),
    limit: z.int().gte(1),
    expiresAt: z.iso.datetime().nullish().default(null),
    registeredAt: z.iso.datetime().readonly().optional(),
    state: zLicenseState.readonly().optional(),
    usage: z.object({
        current: z.int().nullable(),
        complete: z.boolean(),
        unknownNodes: z.int().optional()
    }).readonly().optional()
});

/**
 * The REDUCED face of the license GET (#157 LC1): what `license.read` held on any scope reads when the caller's selectors do not cover the cluster root recursively. The verdict and the expiry — enough to understand why a VM start was refused (`license_limited`) — and nothing that describes the cluster: no product key, no basis, no limit, no usage. Distinguish the faces by the presence of `productKey`.
 *
 */
export const zLicenseStatus = z.object({
    state: zLicenseState.readonly(),
    expiresAt: z.iso.datetime().readonly().nullable()
});

/**
 * THE cluster lifecycle vocabulary — declared once and referenced by both faces of the cluster GET (full and reduced, the `LicenseState` precedent: #212 hand-copied this enum into `ClusterStatus` and the anti-copy census caught the twin). Install-born clusters are seeded `ready` (store up, root seeded, creating node registered); `forming` is a residual state the current flow never produces.
 *
 */
export const zClusterPhase = z.enum(['forming', 'ready']);

/**
 * THE posture vocabulary of the node firewall (#166, T166-P4) — declared ONCE and referenced by the cluster setting (`ClusterSettings. nodeFirewallMode`, the operator's knob) and by the node lens (`NodeFirewall.mode`, the posture a node last rendered against): the same three words with the same meaning on both sides, by construction. `disabled` = no table, no file, the node component never invoked; `permissive` = the table at policy accept, what enforced would drop counted and logged; `enforced` = policy drop, only the derived registry gets in.
 *
 */
export const zNodeFirewallMode = z.enum([
    'disabled',
    'permissive',
    'enforced'
]);

/**
 * A node reference BY TAG (round #123, ratified 2026-07-31: no new concept — node references accept the tags that already exist). The form is the #106 selector verbatim: a map of tag name → tag value (Annex T; nodes are taggable like every object), multiple entries ANDed — it selects the member nodes carrying EVERY entry. Wherever a single-node field admits this object in place of a node-name string, the JSON type disambiguates (a string is a name, an object is a selector — no magic prefixes). Admission is the #106/#146 discipline verbatim: every tag must exist and attach to nodes, every value must be in the governed list — ALWAYS strictly, `autoCreateValues` grants no leniency (a reference names declared values, it never coins them) — and an empty map is refused: each refusal is a speaking `422`. How the selected SET collapses onto the field is the field's own, documented semantics: an action target lets the placement decider pick among the matching nodes; a persistent single-node placement with no scheduler (netsvc, app) requires the selector to be UNIVOCAL at write time; a set-shaped field (storage reachability) takes the whole set, resolved dynamically.
 *
 */
export const zNodeSelector = z.record(z.string(), z.string());

/**
 * The node's cluster — a singleton, install-born (parent spec §3.4; revised 2026-07-15). Not an ordinary scoped resource: no `scope` field (it OWNS the single tree's root, named after it), never listed, never deletable at runtime. Cluster renders expose `revision`: it is the store generation clients watch for convergence (parent §5.10).
 *
 */
export const zCluster = z.object({
    etag: zEntityTag.optional(),
    id: z.uuid().readonly().optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    phase: zClusterPhase.readonly().optional(),
    ip: z.string().nullish(),
    roles: z.object({
        management: z.object({
            members: z.array(z.object({
                node: z.string(),
                ip: z.string()
            })).optional()
        }).optional(),
        liveMigration: z.object({
            members: z.array(z.object({
                node: z.string(),
                ip: z.string()
            })).optional()
        }).nullish()
    }).optional(),
    security: z.object({
        passwordPolicy: z.object({
            minLength: z.int().gte(8).lte(128).optional().default(12)
        }).optional(),
        sessionMaxIdleMinutes: z.int().gte(5).lte(43200).optional().default(480),
        lockout: z.object({
            threshold: z.int().gte(1).optional().default(5),
            durationMinutes: z.int().gte(1).optional().default(15)
        }).optional()
    }).optional(),
    taskRetention: z.object({
        maxAgeDays: z.int().gte(1).lte(3650).optional().default(7),
        maxCount: z.int().gte(100).lte(1000000).optional().default(5000)
    }).optional(),
    eventRetention: z.object({
        maxAgeDays: z.int().gte(1).lte(3650).optional().default(90),
        maxCount: z.int().gte(1000).lte(10000000).optional().default(100000)
    }).optional(),
    spaceThresholds: z.object({
        lowPct: z.int().gte(50).lte(99).optional().default(85),
        criticalPct: z.int().gte(50).lte(100).optional().default(95)
    }).optional(),
    auditRetention: z.object({
        maxAgeDays: z.int().gte(1).lte(3650).optional().default(365)
    }).optional(),
    debugMode: z.boolean().optional().default(false),
    nodeFirewallMode: zNodeFirewallMode.optional().default('disabled'),
    ociCache: z.object({
        storage: z.string().nullish()
    }).optional(),
    appVolumes: z.object({
        ephemeralStorage: z.string().nullish()
    }).optional(),
    failoverReserve: z.object({
        tolerateNodeFailures: z.int().gte(0).lte(16).optional().default(0)
    }).optional(),
    networkShaping: z.object({
        restWeight: z.int().gte(1).lte(10000).optional().default(300),
        overlayWeight: z.int().gte(1).lte(10000).optional().default(100),
        liveMigrationWeight: z.int().gte(1).lte(10000).optional().default(200)
    }).optional(),
    ups: z.object({
        policy: z.object({
            runtimeThresholdSeconds: z.int().gte(0).lte(86400).optional().default(300),
            resumeStableSeconds: z.int().gte(0).lte(86400).optional().default(60),
            resumeMinChargePercent: z.int().gte(0).lte(100).optional().default(25)
        }).optional(),
        units: z.array(z.object({
            name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
            mode: z.enum(['managed', 'external']),
            host: z.string().nullish().default(null),
            port: z.int().gte(1).lte(65535).optional().default(3493),
            upsname: z.string().min(1),
            attachedNode: z.string().nullish().default(null),
            driver: z.string().nullish().default(null),
            device: z.string().nullish().default(null),
            servedNodes: z.array(z.union([z.string(), zNodeSelector])).optional().default([]),
            policy: z.object({
                runtimeThresholdSeconds: z.int().gte(0).lte(86400).nullish().default(null),
                resumeStableSeconds: z.int().gte(0).lte(86400).nullish().default(null),
                resumeMinChargePercent: z.int().gte(0).lte(100).nullish().default(null)
            }).optional()
        })).optional()
    }).optional(),
    quorum: z.object({
        state: z.enum(['single']).optional(),
        storeMembers: z.array(z.string()).optional()
    }).readonly().optional(),
    configGeneration: z.int().readonly().nullish(),
    epoch: z.string().readonly().nullish(),
    minReplicatedGeneration: z.int().readonly().nullish(),
    overlayGeneration: z.int().readonly().nullish(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    notes: z.string().max(65536).optional(),
    revision: z.int().readonly().optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    updatedAt: z.iso.datetime().readonly().optional()
});

/**
 * The REDUCED face of the cluster GET (#157 censimento singleton, the `LicenseStatus` precedent): what `cluster.read` held on any scope reads when the caller's selectors do not cover the cluster root recursively. The cluster's existence, id, name and phase — the id is an opaque identity anchor (compat layers root their per-session inventory on it), the name is already the first segment of every scope path the caller lives in — and nothing that DESCRIBES the fleet: no member addresses, no quorum roster, no policy blocks, no generations. Distinguish the faces by the presence of `quorum`.
 *
 */
export const zClusterStatus = z.object({
    id: z.uuid().readonly().optional(),
    name: z.string().readonly(),
    phase: zClusterPhase.readonly()
});

/**
 * The per-node derivation state (O.3 engine, design #137 §7.1 — ratified 2026-08-24), COMPUTED at read time and never stored, and since round #405 (SN-P7-bis, 2026-09-03) judged PER BLOCK — the node's document carries one block per fabric it is cabled to (`fabrics: {<fabricId>: block}`), and each fabric's plan judges ITS block with the other blocks held fixed: `manual` — the node's network document carries no block of THIS fabric (nothing was ever derived by it, or the operator took everything back); `derived` — the block is present and the pure render, recomputed on the CURRENT inputs (fabric document, cabling, roles, references), structurally matches what the document carries; `diverged` — the block is present but the recomputed render differs: the fabric (or the cabling, or the roles, or a network reference) changed after the last apply, and the preview shows the pending diff. The computed state is what makes the badge honest: nobody has to remember to update it, and it can never lie about a torn manual edit.
 *
 */
export const zFabricDerivationState = z.enum([
    'manual',
    'derived',
    'diverged'
]);

/**
 * THE CPU architecture vocabulary — declared once and referenced by every face that names one (`VirtualMachine.arch`, `OCIImage.arch`, `OCIBranchResolution.arch`): the `StorageDriver`/`PassthroughUse` anti-copy discipline, applied to the list that the enum census caught copied by hand.
 * What a field MEANS at each face stays different — a VM's arch is what the serving node can boot, an image's is what its content was BUILT for — but the DOMAIN is one: the architectures TVirt knows. When riscv64 becomes runnable (#172) it becomes runnable for both, so a twin declared here would promise a divergence that is not coming.
 * There is no `unknown`: the OCI spec makes `architecture` MANDATORY in the config blob, so an image without one is not an image missing a field — it is not a valid image, and the system never has to represent that state. Content whose architecture cannot be read is NOT MATERIALIZED (a name without bytes), never an invented arch.
 * THE SCOPE OF THAT LAST CLAUSE, made explicit in R52 (2026-08-16), because it was being read as a rule of the whole product when its justification is specific: it holds where a SPEC makes the architecture mandatory — an OCI image, whose config blob must carry one. A CT's rootfs TARBALL has no such spec and no such place, so a rootfs whose ELF header cannot be read is an ORDINARY state, not an invalid artefact, and refusing it would make a perfectly good rootfs un-materializable. There the governing rule is R51 instead — absence is not a mismatch — with a WARNING that says the declaration went unconfirmed, so that nothing is silently assumed either. See `Ct.arch`.
 *
 */
export const zArchitecture = z.enum([
    'x86_64',
    'aarch64',
    'riscv64'
]);

/**
 * THE nested-virtualization diagnosis vocabulary (#259) — declared once and referenced by the node's observed block and by the Proximity panel (the `StorageDriver` anti-copy discipline): `enabled` = the kvm module reports nested on; `disabled` = loaded with nested off (the operator can flip the module option); `module-absent` = no kvm module exposes the parameter (KVM not loaded, or foreign silicon).
 *
 */
export const zNestedVirtDiagnosis = z.enum([
    'enabled',
    'disabled',
    'module-absent'
]);

/**
 * THE passthrough-destination vocabulary (round #330) — declared once and referenced by BOTH families of node registration, the GPUs and the USB devices (the `StorageDriver` anti-copy discipline: #330 was born with the list hand-copied into the two sites, and the enum census caught the twin the same night). It is not a policy knob and it is not per-family: it writes down WHO a marked device belongs to, and the answer is the same question for a card and for a dongle — so a third destination, the day one exists, must arrive in both places or in neither.
 *
 * `vm` is the default AND the meaning of every registration written before #330 (the field is absent from those rows and reads back `vm`, so nothing that exists changes behaviour): the device is claimable by a VM. For a GPU that means the WHOLE IOMMU group is bound to vfio-pci (per-device driver_override + drivers_probe) and eligibility requires an isolated group; for a USB device it means QEMU attaches it with `usb-host`, and nothing binds.
 *
 * `app` hands the device to containers instead. A GPU stays on its native driver (i915/amdgpu/nvidia) and TVirt passes its device nodes (`/dev/dri/…`) to the containers whose `devices[]` requires its class: no VFIO, no IOMMU-group requirement — which is the point, since an integrated GPU (the QuickSync/VAAPI transcoding case) essentially never has an isolated group, and a card VFIO would refuse is perfectly good to a container. A USB device gets its `/dev/bus/usb/<busnum>/<devnum>` node handed over the same way.
 *
 * The two are physically exclusive on the same silicon at the same time: a card bound to vfio-pci has no host device node left to give a container, and a card on i915/amdgpu/nvidia cannot be assigned to a guest. For USB nothing binds in either mode, which is exactly why the declaration is needed there too — without it the same dongle could be grabbed by a QEMU process and opened by a container at once, and the loser would find out at the kernel, at runtime, with no way back to a name. A device marked `app` is NEVER claimable by a VM. Changing `use` on a registration held by a running VM refuses `422` speaking; the App side needs no such guard (an App resolves its devices at every container creation).
 *
 */
export const zPassthroughUse = z.enum(['vm', 'app']).default('vm');

/**
 * The node's READ-ONLY service access (#233, T233-P2 ripuntata sul read-only — R4b, contro-dettatura 2026-09-03): the OS account `trantor` (journal + adm groups, no sudo — Q13) has NO permanent password. A user holding `cluster.node.readOnlyAccess` asks for one with `grantReadOnlyAccess`: the backend GENERATES it, hands it back ONCE in the 202 body and never persists it in clear; a task carries only the sha512crypt hash to the node, where the worker sets it on the LIVE system (`chpasswd -e`, never on the persist). It ceases at the TTL (the worker's reconcile re-locks the account against `expiresAt`), at an explicit `revokeReadOnlyAccess`, or at a reboot — by construction (the hash lives in the ephemeral overlay's shadow) and by assertion (`tvirt-apply-passwords` locks `trantor` at every boot). `state` `granted` means a live grant; `none` means nothing is open — and the LAST grant stays readable underneath (`grantedBy`, `grantedAt`, `reason`, `expiresAt`, `task`) together with how it ended (`endedAt`, `endedReason`), which is what the audit reads after the fact. A node that never had a grant reads `{state: none}` alone. The emergency account (`trantor-emergency`, sudo) has NO counterpart here: it is always enabled with the password generated at install (R4a) — nothing to grant, nothing to revoke.
 *
 */
export const zReadOnlyAccess = z.object({
    state: z.enum(['none', 'granted']),
    grantedBy: z.string().optional(),
    grantedAt: z.iso.datetime().optional(),
    expiresAt: z.iso.datetime().optional(),
    reason: z.string().optional(),
    task: z.string().optional(),
    appliedAt: z.iso.datetime().optional(),
    endedAt: z.iso.datetime().optional(),
    endedReason: z.enum([
        'revoked',
        'expired',
        'reboot',
        'failed'
    ]).optional()
}).readonly();

/**
 * The node's report on the EMERGENCY account (#233, T233-P13): the worker watches the journal for session openings of `trantor-emergency` — ssh, the console login, sudo — and reports the last one it saw. With R5 the emergency account has no unlock act to audit any more; the observable fact is the LOGIN itself, and without this report the only full-sudo access of the platform would be the only invisible one. Every login is also an event (`node.emergencyAccess.login`). Null until an access-aware worker heartbeats.
 *
 */
export const zEmergencyAccess = z.object({
    observedAt: z.iso.datetime(),
    lastLoginAt: z.iso.datetime().nullable(),
    lastLoginVia: z.enum([
        'ssh',
        'console',
        'sudo',
        'unknown'
    ]).nullable(),
    lastLoginFrom: z.string().nullable()
}).readonly().nullable();

/**
 * A cluster member or a pre-registered joiner (parent spec §3.4) — nodes are OBJECTS of this registry, not scopes (single tree, 2026-07-15). Born of addClusterNode in `pending-join` with a one-time join token; init-cluster registers the creating node directly as `member`. Exposes `revision` (parent §5.10).
 *
 */
export const zClusterNode = z.object({
    etag: zEntityTag.optional(),
    id: z.uuid().readonly().optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    ip: z.string().max(45),
    phase: z.enum([
        'pending-join',
        'joining',
        'member'
    ]).readonly().optional(),
    storeMember: z.boolean().readonly().optional(),
    ysqlPort: z.int().readonly().nullish(),
    attestedDownAt: z.iso.datetime().readonly().nullish(),
    maintenanceAt: z.iso.datetime().readonly().nullish(),
    upsCordonAt: z.iso.datetime().readonly().nullish(),
    networkConfig: z.record(z.string(), z.unknown()).readonly().nullish(),
    networkDerivation: z.array(z.object({
        fabricId: z.string(),
        fabricName: z.string().nullable(),
        derivedAt: z.iso.datetime(),
        fabricEtag: z.string(),
        state: zFabricDerivationState,
        retiring: z.array(z.object({
            address: z.string(),
            network: z.string(),
            replacedBy: z.string(),
            since: z.string()
        }))
    })).readonly().optional(),
    networkAppliedGeneration: z.int().readonly().nullish(),
    networkLastError: z.record(z.string(), z.unknown()).readonly().nullish(),
    networkInventory: z.object({
        observedAt: z.iso.datetime().optional(),
        devices: z.array(z.unknown()).optional()
    }).readonly().nullish(),
    blockDevices: z.object({
        observedAt: z.iso.datetime().optional(),
        cycleMs: z.int().optional(),
        devices: z.array(z.unknown()).optional()
    }).readonly().nullish(),
    overlaysAppliedGeneration: z.int().readonly().nullish(),
    overlaysLastError: z.record(z.string(), z.unknown()).readonly().nullish(),
    replicatedGeneration: z.int().readonly().nullish(),
    services: z.array(z.object({
        name: z.string().optional(),
        enabled: z.boolean().optional(),
        active: z.boolean().optional(),
        observedAt: z.iso.datetime().optional()
    })).readonly().nullish(),
    lastSeenAt: z.iso.datetime().readonly().nullish(),
    memoryTotalMib: z.int().readonly().nullish(),
    cpuCount: z.int().readonly().nullish(),
    arch: zArchitecture.nullish(),
    cpuTopology: z.object({
        physicalCores: z.int(),
        logicalCpus: z.int(),
        sockets: z.int().optional()
    }).readonly().nullish(),
    nestedVirtualization: z.object({
        available: z.boolean(),
        diagnosis: zNestedVirtDiagnosis,
        module: z.string().nullish()
    }).readonly().nullish(),
    hugePages: z.object({
        defaultSizeKib: z.int(),
        pools: z.array(z.object({
            sizeKib: z.int(),
            total: z.int(),
            free: z.int(),
            reserved: z.int()
        }))
    }).readonly().nullish(),
    ups: z.object({
        observedAt: z.iso.datetime(),
        unit: z.string(),
        status: z.string().nullable(),
        batteryChargePercent: z.number().nullable(),
        runtimeSeconds: z.number().nullable(),
        loadPercent: z.number().nullable(),
        onBatterySince: z.iso.datetime().nullable(),
        onLineSince: z.iso.datetime().nullable(),
        lastError: z.string().nullable(),
        lane: z.object({
            kind: z.enum(['evacuate', 'local']),
            by: z.enum(['runtime', 'lowBattery']),
            since: z.iso.datetime(),
            phase: z.enum([
                'draining',
                'stoppingGuests',
                'stoppingServices',
                'poweredOff'
            ]),
            drainTaskId: z.string().nullable(),
            stopped: z.array(z.object({
                kind: z.enum([
                    'VirtualMachine',
                    'Container',
                    'App'
                ]),
                id: z.string(),
                name: z.string(),
                scopePath: z.string()
            })),
            wave: z.int().gte(0)
        }).nullable()
    }).readonly().nullish(),
    cpuModel: z.string().readonly().nullish(),
    cpuMhzNominal: z.int().readonly().nullish(),
    stats: z.object({
        sampledAt: z.iso.datetime(),
        cpuUsagePercent: z.number().nullish(),
        memoryUsedMib: z.int()
    }).readonly().nullish(),
    guestTools: z.object({
        version: z.string().optional(),
        sha256: z.string().optional()
    }).readonly().nullish(),
    usbDevices: z.object({
        observedAt: z.iso.datetime().optional(),
        devices: z.array(z.unknown()).optional()
    }).readonly().nullish(),
    gpus: z.object({
        observedAt: z.iso.datetime().optional(),
        vfioReady: z.boolean().optional(),
        vfioDiagnosis: z.enum([
            'active',
            'missing-kernel-arg',
            'firmware-disabled',
            'unknown'
        ]).optional(),
        devices: z.array(z.unknown()).optional(),
        bindErrors: z.array(z.unknown()).optional()
    }).readonly().nullish(),
    versions: z.object({
        observedAt: z.iso.datetime().optional(),
        platform: z.string().optional(),
        qemu: z.string().nullish(),
        lxc: z.string().nullish(),
        kernel: z.string().nullish(),
        ovmfBundles: z.array(z.string()).optional()
    }).readonly().nullish(),
    baseCpuCores: z.int().gte(0).lte(1024).optional().default(1),
    baseMemoryMib: z.int().gte(0).lte(1048576).optional().default(2048),
    settings: z.object({
        console: z.object({
            keyboardLayout: z.string().nullish().default(null)
        }).optional(),
        logForwarding: z.object({
            syslog: z.object({
                endpoint: z.string().nullish().default(null),
                tls: z.boolean().optional().default(true)
            }).optional()
        }).optional(),
        tasks: z.object({
            maxQueued: z.int().gte(1).optional().default(100),
            concurrency: z.int().gte(1).lte(64).optional().default(2)
        }).optional(),
        passthrough: z.object({
            gpus: z.array(z.object({
                id: z.uuid().optional(),
                pciAddress: z.string().regex(/^[0-9a-f]{4}:[0-9a-f]{2}:[0-9a-f]{2}\.[0-7]$/),
                use: zPassthroughUse.optional(),
                vendorId: z.string().optional(),
                deviceId: z.string().optional(),
                vendorName: z.string().optional(),
                deviceName: z.string().optional(),
                groupAddresses: z.array(z.string()).optional(),
                notes: z.string().max(2000).optional(),
                orphaned: z.boolean().readonly().optional()
            })).optional(),
            usb: z.array(z.object({
                id: z.uuid().optional(),
                mode: z.enum(['device', 'port']),
                use: zPassthroughUse.optional(),
                vendorId: z.string().regex(/^[0-9a-f]{4}$/).optional(),
                productId: z.string().regex(/^[0-9a-f]{4}$/).optional(),
                manufacturer: z.string().optional(),
                product: z.string().optional(),
                port: z.string().regex(/^[0-9]+-[0-9]+(\.[0-9]+)*$/).optional(),
                notes: z.string().max(2000).optional(),
                orphaned: z.boolean().readonly().optional()
            })).optional()
        }).optional(),
        firewall: z.object({
            allowUnmanagedBridges: z.array(z.string()).optional()
        }).optional()
    }).optional(),
    dataDisks: z.array(z.object({
        name: z.string().min(1),
        serial: z.string().optional(),
        model: z.string().optional(),
        byPath: z.string().optional(),
        notes: z.string().max(2000).optional(),
        orphaned: z.boolean().readonly().optional()
    })).optional(),
    cabling: z.array(z.object({
        nic: z.string().min(1),
        fabric: z.string(),
        switch: z.string(),
        port: z.string()
    })).optional(),
    placement: z.object({
        chassis: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        rack: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        row: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        pdu: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        pod: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        room: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        datacenter: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        zone: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        region: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional()
    }).optional(),
    readOnlyAccess: zReadOnlyAccess.optional(),
    emergencyAccess: zEmergencyAccess.optional(),
    tags: z.array(zResourceTag).readonly().optional(),
    notes: z.string().max(65536).optional(),
    revision: z.int().readonly().optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    updatedAt: z.iso.datetime().readonly().optional()
});

export const zClusterNodeList = z.object({
    items: z.array(zClusterNode),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    nextCursor: z.string().optional()
});

export const zClusterNodeCreated = zClusterNode.and(z.object({
    joinToken: z.string().readonly().optional(),
    caFingerprint: z.string().readonly().nullish()
}));

/**
 * One structured reason behind an evacuation-plan verdict (round
 */
export const zNodeEvacuationReason = z.object({
    code: z.enum([
        'unsharedStorage',
        'usbClaim',
        'gpuClaim',
        'nestedVirt',
        'noEligibleNode',
        'expendableNoRoom',
        'nodeGap',
        'requireTag',
        'preferTag',
        'proximitySoft',
        'preferredNodeFallback',
        'sharedDisputed',
        'storageNotDeclared',
        'phantomTransition',
        'archMismatch',
        'coldMove'
    ]),
    message: z.string(),
    node: z.string().optional(),
    rule: z.string().optional()
});

/**
 * One live object's predicted drain outcome (round
 */
export const zNodeEvacuationItem = z.object({
    id: z.uuid(),
    name: z.string(),
    kind: z.enum([
        'vm',
        'netsvc',
        'app'
    ]),
    action: z.enum([
        'migrate',
        'migrateWarning',
        'stop',
        'blocked'
    ]),
    target: z.string().optional(),
    reasons: z.array(zNodeEvacuationReason)
});

/**
 * The what-if evacuation plan for a node (round
 */
export const zNodeEvacuationPlan = z.object({
    node: z.string(),
    strategy: z.enum([
        'leastVms',
        'leastUsedResources',
        'random'
    ]),
    items: z.array(zNodeEvacuationItem),
    failoverReserve: zFailoverReserveVerdict.optional()
});

/**
 * One entry of a node's DERIVED registry of platform ports (#166, T166-P1): a port (or an inclusive band) on a protocol, its class — which decides the source the rule accepts — and its ORIGIN, the fact of the replica it derives from. `public` (ssh, the api port, a wireguard endpoint): accepted from anywhere, no family restriction; `internode` (store, live migration, overlay VXLAN, Ceph): accepted from the MEMBERS' addresses only; `tenant` (a netsvc listener on a tenant network, T166-P14): accepted on the served network's bridge toward the claimed address.
 *
 */
export const zNodeFirewallEntry = z.object({
    class: z.enum([
        'public',
        'internode',
        'tenant'
    ]),
    proto: z.enum(['tcp', 'udp']),
    port: z.int().gte(1).lte(65535),
    portTo: z.int().nullable(),
    origin: z.string(),
    iface: z.string().nullable(),
    address: z.string().nullable()
});

/**
 * The state of the node component `tvirt-node-firewall` (T166-P7/P8), as its `state.json` reports it: the phase of the last apply cycle (`none` = never applied, `applied` = a candidate is in the kernel and the rollback timer is armed, `confirmed` = the worker confirmed within the deadline, `rolledBack` = the deadline expired or a probe failed and the previous confirmed ruleset — or no table — is back), the generation (the hash of the ruleset file) the phase refers to, the timestamps, and the LAST rollback with its reason.
 *
 */
export const zNodeFirewallComponent = z.object({
    phase: z.enum([
        'none',
        'applied',
        'confirmed',
        'rolledBack'
    ]),
    generation: z.string().nullable(),
    appliedAt: z.iso.datetime().nullable(),
    deadline: z.iso.datetime().nullable(),
    confirmedAt: z.iso.datetime().nullable(),
    lastRollback: z.object({
        at: z.iso.datetime(),
        reason: z.string(),
        generation: z.string().nullable()
    }).nullable()
});

/**
 * The node-firewall lens of one node (#166, T166-P6): read-only, as the node itself last reported it in its heartbeat (`node_status`). The posture it rendered against, the derived registry of platform ports (T166-P1/P2/P14), the generation the render produced, the component's state and the STANDING conditions: `nodeFirewallUnconfirmed` (the posture wants a ruleset and no confirmed one exists yet — the node is OPEN and says so, T166-P10), `nodeFirewallRolledBack` (the last apply rolled back: the worker does not retry the same generation blind — the registry must change or the operator re-flips the posture, T166-P9), `firewalldActive` (firewalld is running on the node: two default-deny engines on one node is the ambiguity #166 removes, so the worker REFUSES to apply a permissive/enforced ruleset there and says so, T166-P11). `observedAt` null = no firewall-aware worker has reported yet. A fetch of fleet material (#157 censimento singleton): below cluster-root authority the node is indistinguishable from absent — `404`.
 *
 */
export const zNodeFirewall = z.object({
    node: z.string(),
    mode: zNodeFirewallMode,
    observedAt: z.iso.datetime().nullable(),
    registry: z.array(zNodeFirewallEntry),
    desiredGeneration: z.string().nullable(),
    component: zNodeFirewallComponent.nullable(),
    conditions: z.array(z.enum([
        'nodeFirewallUnconfirmed',
        'nodeFirewallRolledBack',
        'firewalldActive'
    ])),
    firewalldActive: z.boolean(),
    lastError: z.string().nullable()
});

export const zStatsSeries = z.object({
    step: z.int(),
    resolution: z.int().nullable(),
    servedFrom: z.enum(['store', 'node']),
    series: z.array(z.object({
        metric: z.string(),
        unit: z.string(),
        labels: z.record(z.string(), z.unknown()).optional(),
        points: z.array(z.tuple([z.number(), z.number()]))
    }))
});

/**
 * The LACPDU transmission rate of an 802.3ad bond — declared ONCE and referenced by the fabric's LAGG (`FabricLagg.lacpRate`) and by the node network PATCH that actually builds the bond (`rate`, cluster.yaml). It is the same knob on the same kernel bond, named twice by history; deriving it is what keeps the two ends from ever offering different rates. Cross-name copy found 2026-08-11: the enum census indexes twins by property NAME, so `lacpRate` and `rate` had never met.
 *
 */
export const zLacpRate = z.enum(['slow', 'fast']);

/**
 * The 202 of `grantReadOnlyAccess` (the `AppDeleteResult` idiom — the task plus what only this response can say): the password is returned EXACTLY ONCE here and is never readable again (the `joinToken` precedent) — not in the task row (only the hash rides its params), not in the audit row, not in the HTTP log (the `tvro_` family is redacted by construction). It becomes usable when `task` is `succeeded`.
 *
 */
export const zReadOnlyAccessGranted = z.object({
    task: z.string(),
    username: z.string(),
    password: z.string(),
    expiresAt: z.iso.datetime(),
    readOnlyAccess: zReadOnlyAccess
});

/**
 * A measured quantity and WHERE IT CAME FROM (#326). No number in this subsystem exists without its provenance, because the three sources are not interchangeable: `declared` is read off configuration or the observed anagrafica and costs nothing but describes the ENDS, not the path; `observed` is derived from traffic that would have crossed the wire anyway (a live migration), so it is a FLOOR — what did get through, under whatever contention and throttling was in force — never a capacity; `probed` is traffic TVirt generated on purpose. A reader that treats the three as one number will draw a graph that lies quietly.
 *
 */
export const zLinkMeasure = z.object({
    value: z.number(),
    source: z.enum([
        'declared',
        'observed',
        'probed'
    ]),
    observedAt: z.iso.datetime().optional()
});

/**
 * The two ends' observed MTU on the device carrying the role's address, and the verdict of comparing them (#326 R16). Deliberately NOT a single number: the minimum of the two ends would be comfortable and would throw away the diagnosis, because 1500 on a leg whose ends declare 9000 and 1500 is the same figure as 1500 on a leg where both declare 1500 — and those are not the same fact. The first is a jumbo frame configured HALFWAY, which is the real case that breaks clusters; the second is a cluster without jumbo frames, which is fine. It costs nothing: `node_status.network_inventory` already carries `mtu` per device, so half the MTU question was in the house unused. What genuinely remains to be probed is the PATH MTU — the fragment lost in between — and that wants a DF packet, which is another probe with another doctrine: ratified explicitly OUT of this cut.
 *
 */
export const zLinkMtu = z.object({
    from: z.int().nullable(),
    to: z.int().nullable(),
    consistent: z.boolean().nullable()
});

/**
 * One LEG of inter-node connectivity: an ORDERED pair on a network ROLE (#326). Directional deliberately — bandwidth is asymmetric in cases that are not laboratory ones (a layer3+4 LACP hash, a queue saturated one way, two spines between two racks), and above all the OBSERVER is one: the measurement belongs to the node that made the gesture, and under an unordered key B's view of A would overwrite A's view of B. The worst of the two directions is a render-time minimum, computed where the reader knows it is a minimum — storing it in place of the data would erase the asymmetry exactly when it IS the diagnosis. The role is part of the key for the same reason: measuring the management network and presenting it as "the connectivity between A and B" would lie precisely about the cluster that has a dedicated migration network, which is the cluster where the number matters. When `roles.liveMigration` is null the two roles share one address and only the management leg is reported — the probe does not measure the same path twice, and the matrix does not invent a leg for a role this cluster does not distinguish.
 *
 */
export const zLink = z.object({
    from: z.string(),
    to: z.string(),
    role: z.enum(['management', 'liveMigration']),
    rtt: zLinkMeasure.nullable(),
    capacityMbps: zLinkMeasure.nullable(),
    mtu: zLinkMtu,
    topology: z.object({
        shared: z.array(z.string()),
        diverges: z.string().nullable()
    }),
    stale: z.boolean(),
    reason: z.string().optional()
});

/**
 * Which resolution the leg SERIES are keeping, and why (#326 R4 — ratified 2026-08-11: "past the threshold it degrades the RESOLUTION, not the coverage"). The matrix stays COMPLETE at any scale — every leg is here — and what disappears first is the fine history, which is exactly what costs. The reason is arithmetic: leg series are N·(N−1)·R, the #111 ladder keeps ~9720 rows per series in steady state, and past a few tens of nodes the matrix alone exhausts `statsMaxRows` — whose eviction bites `res = 60` for ALL subjects, so an O(N²) emitter does not fill its own space, it steals the VMs' recent detail. So the finest tier that FITS is chosen, where "fits" means a declared quarter of the row cap that already exists. Not a second cap and not a knob: `statsMaxRows` remains the single ceiling, and the threshold moves with it — raising the cap buys resolution, shortening the horizon costs it. Absent while no live witness has reported a plan yet. Honesty to declare: a coarse tier cannot be produced before its interval ends, so at `minRes: 86400` a leg's history appears the following day. That is the definition of daily resolution, not a lag. The dictation's question — "what connectivity is there between the two nodes hosting these VMs" — does not come through here: the per-pass snapshot answers it at ANY number of nodes. The history degrades; the NOW never does.
 *
 */
export const zLinkHistory = z.object({
    minRes: z.int(),
    degraded: z.boolean(),
    series: z.int(),
    budgetRows: z.int(),
    rowsProjected: z.int(),
    rowsAtFullScale: z.int()
});

/**
 * The inter-node connectivity matrix (#326): one row per ordered pair per role, name-ordered. Served from the cluster store, where every member's report already is — no synchronous fan-out over N nodes inside a user request, which at one node down would return a hole instead of an answer.
 *
 */
export const zLinkMatrix = z.object({
    links: z.array(zLink),
    history: zLinkHistory.optional()
});

/**
 * The Proximity control panel: cluster config plus the observed surface. `strategy` and `livenessTimeoutSeconds` are the writable half (PATCH); everything else is read-only observation composed at render time.
 *
 */
export const zProximityStatus = z.object({
    etag: zEntityTag.optional(),
    strategy: z.enum([
        'leastVms',
        'leastUsedResources',
        'random'
    ]).optional().default('leastUsedResources'),
    livenessTimeoutSeconds: z.int().gte(5).lte(3600).optional().default(75),
    decider: z.object({
        node: z.string().optional(),
        renewedAt: z.iso.datetime().optional(),
        expiresAt: z.iso.datetime().optional()
    }).readonly().nullish(),
    nodes: z.array(z.object({
        id: z.uuid().optional(),
        name: z.string().optional(),
        lastSeenAt: z.iso.datetime().nullish(),
        fresh: z.boolean().optional(),
        maintenance: z.boolean().optional(),
        attestedDown: z.boolean().optional(),
        memoryTotalMib: z.int().nullish(),
        baseMemoryMib: z.int().optional(),
        committedMemoryMib: z.int().optional(),
        usableMemoryMib: z.int().nullish(),
        cpuCount: z.int().nullish(),
        baseCpuCores: z.int().optional(),
        committedVcpus: z.int().optional(),
        liveVms: z.int().optional(),
        hugePages: z.object({
            defaultSizeKib: z.int().optional(),
            pools: z.array(z.object({
                sizeKib: z.int().optional(),
                total: z.int().optional(),
                free: z.int().optional(),
                reserved: z.int().optional()
            })).optional()
        }).nullish(),
        nestedVirtualization: z.object({
            available: z.boolean().optional(),
            diagnosis: zNestedVirtDiagnosis.optional(),
            module: z.string().nullish()
        }).nullish(),
        committedHugePages: z.object({
            '2M': z.int().optional(),
            '1G': z.int().optional()
        }).optional()
    })).readonly().optional()
});

/**
 * An affinity/anti-affinity rule over a set of VMs (≥ 2). v1 semantics are NODE-scoped (ratified 2026-07-25): `affinity` wants every member VM on the SAME node, `antiAffinity` on DIFFERENT nodes; the physical-topology generalization (rack/room, #100) is a future round. `hard` rules gate admission — VM start placement, deploy, live-migration targets and the HA decider's failover all refuse a violating node with a named `422` (or exclude it from the candidate set); `soft` rules order the candidate preference — a violated soft rule never blocks, it is reported (task message, rule render). Canonical example: «VM1 and VM2 should PREFERABLY run on different servers» = antiAffinity soft.
 *
 */
export const zProximityRule = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    type: z.enum(['affinity', 'antiAffinity']).optional().default('antiAffinity'),
    mode: z.enum(['hard', 'soft']).optional().default('soft'),
    vms: z.array(z.uuid()).min(2),
    satisfied: z.boolean().readonly().optional(),
    violations: z.array(z.string()).readonly().optional()
}));

export const zProximityRuleList = z.object({
    items: z.array(zProximityRule),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * THE virtual-disk format vocabulary — declared ONCE and referenced by every face that serves or accepts one: the disk itself, the two inline-creation bodies (`VmDisk.format` and the `createDisk` request), the storage default (`Storage.defaults.diskFormat`) and the naked description a backup reads (`VmDescription.disks[].format`). Five faces, and until 2026-08-11 they were five hand copies — four of them found by the enum census, the fifth (`diskFormat`) invisible to it because that census indexes twins by the NAME of the property carrying them, and a copy under another name is a copy nobody is watching. `qcow2` is the file-storage default; a block storage (iscsi/nvme-tcp/rbd) only accepts `raw`, and that rule lives at the sites that enforce it, not here — this schema is the vocabulary, not the policy.
 *
 */
export const zDiskFormat = z.enum(['qcow2', 'raw']);

export const zVmDisk = z.intersection(z.unknown(), z.object({
    index: z.int().gte(1).lte(4),
    disk: z.string().optional(),
    storage: z.string().optional(),
    sizeGiB: z.int().gte(1).optional(),
    format: zDiskFormat.optional(),
    encrypted: z.boolean().optional().default(false)
}));

export const zVmCdrom = z.object({
    index: z.int().gte(1).lte(3),
    image: z.string().nullish(),
    guestTools: z.object({
        version: z.string().optional(),
        node: z.string().optional()
    }).readonly().nullish()
});

export const zVmNic = z.object({
    index: z.int().gte(1).lte(6),
    network: z.string().nullish(),
    networkName: z.string().readonly().nullish(),
    bridge: z.string().nullish(),
    untagged: z.string().nullish(),
    tagged: z.record(z.string(), z.string()).optional(),
    mac: z.string().regex(/^([0-9a-fA-F]{2}:){5}[0-9a-fA-F]{2}$/).optional(),
    connected: z.boolean().optional().default(true),
    firewallGroups: z.array(z.string()).optional(),
    firewallEnabled: z.boolean().optional().default(false),
    firewallAntiSpoof: z.boolean().optional().default(false)
});

/**
 * A USB passthrough requirement by CLASS (round U2 #72, ratified 2026-07-30 — usb-gpu-passthrough-design.md v2): the entry names a tag of kind `usb-device` and one of its values (e.g. `usb_class=token`), NEVER a concrete device — the exact device stays expressible as a one-member class. The decider resolves the claim at START: it picks a node holding a FREE registration of the class (observed, not orphaned, not claimed by another placed VM), claims it and the worker attaches the concrete device (`device` mode: vendor:product wherever plugged; `port` mode: the physical port, whatever sits in it). Claims release at stop and failover; HA restarts the VM on any node with a free device of the class. Several entries of the same class require that many free devices.
 *
 */
export const zVmUsb = z.object({
    index: z.int().gte(1).lte(4),
    tag: z.string(),
    value: z.string().min(1).max(253),
    tagId: z.uuid().readonly().optional(),
    valueId: z.uuid().readonly().optional()
});

/**
 * A GPU passthrough requirement by CLASS (round G2 #73, ratified 2026-07-30 — usb-gpu-passthrough-design.md v2, the VmUsb model verbatim): the entry names a tag of kind `gpu-device` and one of its values (e.g. `gpu_class=compute`), NEVER a concrete card — the exact card stays expressible as a one-member class. The decider resolves the claim at START: it picks a node holding a FREE registration of the class (observed, not orphaned, not claimed by another placed VM), claims it, and the worker passes the registration's WHOLE IOMMU group through vfio-pci — VGA, audio and USB-C functions together (the group was bound at marking; functions of a group never split). NO PCI hotplug in v1, but the edit lane is the ordinary power-cycle one (ratified #146, 2026-08-01, superseding G2's strictly-cold `412`): a PATCH touching `gpus[]` on a running/paused VM is ACCEPTED and surfaces as the derived `PendingRestart` condition — the guest keeps what it holds (the read-only `gpuClaims` says so honestly) and the change applies at the next power cycle, when the claim machinery runs. `?live=true` on a gpus[] change still answers `412` (nothing hotplugs). Claims release at stop and failover; HA restarts the VM on any node with a free device of the class. Live migration with a GPU requirement refuses speaking (the silicon is physical to the node and VFIO state never migrates), and so does a MEMORY snapshot on a running VM — the device state cannot ride the migration stream the memory snapshot uses.
 *
 */
export const zVmGpu = z.object({
    index: z.int().gte(1).lte(4),
    tag: z.string(),
    value: z.string().min(1).max(253),
    tagId: z.uuid().readonly().optional(),
    valueId: z.uuid().readonly().optional()
});

/**
 * The console wire protocol — VNC only in 6.0 (SPICE removed 2026-07-10). Declared ONCE and referenced by what the VM OFFERS (`VirtualMachine.console.protocol`) and by what a viewer ticket GRANTS (`ViewerAccess.proto`): one member today, and exactly for that reason the two must move together — the day a second protocol comes back it comes back on both faces or the ticket promises something the VM does not serve.
 *
 */
export const zConsoleProtocol = z.enum(['vnc']);

/**
 * The guest family a QuickDeploy-compatible template DECLARES (round #110, T110-P1): `linux` asserts the golden image carries cloud-init, `windows` asserts cloudbase-init plus a sysprep-generalized image (manual, chapter Template VM). Declared ONCE and referenced by the template's declaration (`VirtualMachine.quickDeploy.os`) and by the deploy action's optional assertion (`quickDeploy` body `os`) — the two faces of one vocabulary. It is NOT `hardwareProfile`: the profile says the virtual bus (and `legacy` is where imported Windows lands), this says which first-boot engine the guest runs.
 *
 */
export const zQuickDeployOs = z.enum(['linux', 'windows']);

/**
 * Un capo del legame, nominato PER INTERO e non per riferimento (vedi la testa di questo file): il giorno del DR il capo lontano e' irraggiungibile per definizione, e l'operatore deve comunque poter leggere di che VM si trattava e dove viveva.
 *
 */
export const zVmReplicaEnd = z.object({
    cluster: z.string().nullish(),
    vm: z.uuid(),
    name: z.string(),
    scope: z.string()
});

/**
 * Il legame di replica fra due VM. Lo stesso documento si rende su entrambi i capi — `VirtualMachine.replica` (io sono il bersaglio) e `VirtualMachine.replicas[]` (io sono la sorgente) — perche' un lettore che possiede un capo solo deve poter vedere l'altro senza andarlo a cercare in un cluster che magari e' spento.
 *
 */
export const zVmReplicaLink = z.object({
    id: z.uuid(),
    source: zVmReplicaEnd,
    target: zVmReplicaEnd,
    transport: z.enum(['node-to-node']),
    state: z.enum([
        'seeding',
        'synced',
        'behind',
        'promoted',
        'detached'
    ]),
    intervalSeconds: z.int().gte(60).default(900),
    baseEpoch: z.string().nullish(),
    baseTakenAt: z.iso.datetime().nullish(),
    lastAttemptAt: z.iso.datetime().nullish(),
    lastSuccessAt: z.iso.datetime().nullish(),
    lastError: z.string().nullish(),
    consecutiveFailures: z.int().optional(),
    promotedAt: z.iso.datetime().nullish(),
    detachedAt: z.iso.datetime().nullish()
}).readonly();

/**
 * Status condition. PROPOSAL: shape (Kubernetes-style) not pinned by the spec, which only names conditions like `Ready`, `PendingRestart`, `CustomizationPending`.
 *
 */
export const zCondition = z.object({
    type: z.string(),
    status: z.enum([
        'True',
        'False',
        'Unknown'
    ]),
    reason: z.string().optional(),
    message: z.string().optional(),
    lastTransitionTime: z.iso.datetime().optional()
});

/**
 * A virtual machine (flat resource; Annex F). `firmware`, `arch` and `schema` are immutable for the life of the VM — through templates and backups too (parent §4.2, §4.5). `hardwareProfile` left the immutable fence 2026-08-03 (round #179) and since the 2026-08-04 ratifica rides the ordinary power-cycle lane like `gpus[]` (#146, superseding the round's stopped-only 412): the change saves in any phase, a running/paused VM carries the derived `PendingRestart` condition until it power-cycles, and the next start builds the machine on the new profile — the imported-VM road to virtio (boot on `legacy`, install the drivers in the guest, switch profile, power-cycle). `hugePages` (round #135) stays stopped-only. The UEFI firmware VERSION is `firmwareFile`, a pointer to a `firmware-ovmf` catalog file INDEPENDENT of `schema` (round #125): stopped-only like the profile, and never moved automatically by a platform upgrade. Change applicability for the other fields is the Annex F table (live hotplug, next power cycle with a `PendingRestart` condition, or stopped-only).
 *
 */
export const zVirtualMachine = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    hardwareProfile: z.enum([
        'windows',
        'linux',
        'legacy'
    ]),
    firmware: z.enum([
        'bios',
        'uefi',
        'uefi-secure'
    ]).optional(),
    firmwareFile: z.string().nullish(),
    arch: zArchitecture.optional(),
    schema: z.string().optional(),
    cpu: z.object({
        sockets: z.int().gte(1).lte(32767).optional().default(1),
        cores: z.int().gte(1).lte(32767).optional().default(1),
        threads: z.int().gte(1).lte(32767).optional().default(1),
        model: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/).optional().default('host'),
        limit: z.number().gte(0).nullish(),
        weight: z.int().gte(1).lte(10000).optional().default(100),
        nestedVirtualization: z.boolean().optional().default(false)
    }).optional(),
    memory: z.object({
        sizeMiB: z.int().gte(512).lte(2147483647),
        balloon: z.boolean().optional().default(true)
    }),
    hugePages: z.enum([
        'none',
        '2M',
        '1G'
    ]).optional().default('none'),
    bootOrder: z.array(z.string().regex(/^(disk|cdrom|nic):[0-9]+$/)).optional().default(['disk:1', 'cdrom:1']),
    disks: z.array(zVmDisk).max(4).optional(),
    cdroms: z.array(zVmCdrom).max(3).optional(),
    nics: z.array(zVmNic).max(6).optional(),
    usb: z.array(zVmUsb).max(4).optional(),
    usbClaims: z.array(z.object({
        index: z.int().optional(),
        registrationId: z.uuid().optional(),
        node: z.string().optional(),
        mode: z.string().optional(),
        vendorId: z.string().optional(),
        productId: z.string().optional(),
        port: z.string().optional(),
        product: z.string().optional()
    })).readonly().optional(),
    gpus: z.array(zVmGpu).max(4).optional(),
    gpuClaims: z.array(z.object({
        index: z.int().optional(),
        registrationId: z.uuid().optional(),
        node: z.string().optional(),
        pciAddress: z.string().optional(),
        groupAddresses: z.array(z.string()).optional(),
        vendorName: z.string().optional(),
        deviceName: z.string().optional()
    })).readonly().optional(),
    console: z.object({
        protocol: zConsoleProtocol.optional().default('vnc')
    }).optional(),
    agent: z.object({
        enabled: z.boolean().optional().default(true)
    }).optional(),
    cloudInit: z.object({
        userData: z.string().nullish(),
        networkConfig: z.string().nullish()
    }).optional(),
    secretFields: z.array(z.string().max(160)).max(8).optional(),
    restartPolicy: z.enum([
        'always',
        'onFailure',
        'never'
    ]).optional().default('never'),
    treatGuestShutdownAsVoluntary: z.boolean().optional().default(false),
    ha: z.boolean().optional().default(false),
    failoverProtection: zFailoverProtection.optional(),
    failoverProtectionEffective: z.enum(['protected', 'expendable']).readonly().optional(),
    startup: z.object({
        onBoot: z.boolean().optional().default(false),
        order: z.int().optional().default(100),
        delaySeconds: z.int().optional().default(0)
    }).optional(),
    protected: z.boolean().optional().default(false),
    template: z.boolean().readonly().optional().default(false),
    quickDeploy: z.object({
        os: zQuickDeployOs
    }).nullish().default(null),
    linkedClones: z.int().readonly().optional(),
    linkedCloneVms: z.array(z.object({
        id: z.uuid().optional(),
        name: z.string().optional()
    })).readonly().optional(),
    replica: zVmReplicaLink.nullish(),
    replicas: z.array(zVmReplicaLink).readonly().optional(),
    metadataStorage: z.string(),
    preferredNode: z.string().nullish(),
    preferNodes: z.record(z.string(), z.string()).optional(),
    requireNodes: z.record(z.string(), z.string()).optional(),
    phase: z.enum([
        'defined',
        'starting',
        'running',
        'paused',
        'stopping',
        'stopped',
        'failed',
        'deleting'
    ]).readonly().optional(),
    node: z.string().readonly().optional(),
    authorityNode: z.string().readonly().nullish(),
    haRestarts: z.int().readonly().optional(),
    haGaveUp: z.boolean().readonly().optional(),
    haBackoffUntil: z.iso.datetime().readonly().nullish(),
    sacrificedAt: z.iso.datetime().readonly().nullish(),
    startedAt: z.iso.datetime().readonly().nullish(),
    agentReportedAt: z.iso.datetime().readonly().nullish(),
    migrating: z.boolean().readonly().optional(),
    agentConnected: z.boolean().readonly().optional(),
    agentVersion: z.string().readonly().nullish(),
    ipAddresses: z.array(z.string()).readonly().optional(),
    conditions: z.array(zCondition).readonly().optional(),
    appliedConfig: z.record(z.string(), z.unknown()).readonly().optional(),
    snapshots: z.array(z.object({
        name: z.string().optional(),
        createdAt: z.iso.datetime().optional(),
        memory: z.boolean().optional(),
        parent: z.string().optional(),
        current: z.boolean().optional(),
        quiesced: z.boolean().optional(),
        varstore: z.boolean().readonly().optional(),
        vtpm: z.boolean().readonly().optional(),
        firmware: z.string().readonly().optional(),
        firmwareVersion: z.string().readonly().optional(),
        configIncluded: z.boolean().readonly().optional()
    })).readonly().optional(),
    lastStopReason: z.object({
        voluntary: z.boolean().optional(),
        cause: z.enum([
            'api',
            'guest-shutdown',
            'crash',
            'qemu-exit',
            'host-reboot',
            'migrated',
            'fenced'
        ]).optional(),
        at: z.iso.datetime().optional()
    }).readonly().optional()
}));

export const zVirtualMachineList = z.object({
    items: z.array(zVirtualMachine),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * Bulk delete (2026-07-24) — ids plus the single-delete option: `keepDisks` applies to every id, exactly like `?keepDisks=true` on the single delete.
 *
 */
export const zBatchVmDeleteRequest = z.object({
    ids: z.array(z.string()).min(1).max(500),
    keepDisks: z.boolean().optional().default(false)
});

/**
 * Bulk action (2026-07-23): the resource ids to act on. Same per-item guards as the single action; partial acceptance is normal (`BatchActionResult`).
 *
 */
export const zBatchActionRequest = z.object({
    ids: z.array(z.string()).min(1).max(500)
});

/**
 * Per-item outcome of a bulk action; `200` even on partial refusal.
 */
export const zBatchActionResult = z.object({
    accepted: z.array(z.string()),
    refused: z.array(z.object({
        id: z.string(),
        code: z.string(),
        detail: z.string()
    }))
});

/**
 * Bulk stop (2026-07-23) — ids plus the single-stop options.
 */
export const zBatchVmStopRequest = z.object({
    ids: z.array(z.string()).min(1).max(500),
    mode: z.enum(['acpi', 'hard']).optional().default('acpi'),
    timeoutSeconds: z.int().gte(1).optional().default(60)
});

/**
 * One member's start verdict for the VM (task
 */
export const zVmPlacementNode = z.object({
    node: z.string(),
    eligible: z.boolean(),
    reason: z.string().optional(),
    softViolations: z.array(z.object({
        rule: z.string(),
        detail: z.string()
    })),
    preferMisses: z.array(z.object({
        tag: z.string(),
        value: z.string()
    })).optional()
});

/**
 * The start placement census for a VM (task
 */
export const zVmPlacement = z.object({
    strategy: z.enum([
        'leastVms',
        'leastUsedResources',
        'random'
    ]),
    recommended: z.string().optional(),
    nodes: z.array(zVmPlacementNode)
});

/**
 * The independence «by construction» (task #344 structural round, ratified 2026-08-19): where the VM COULD run ignoring the transient state of the moment — a node that is down, in maintenance, unreachable, in a shared-probe dispute, or merely FULL right now (the other VMs can move). The structural gates stay: architecture, nested virt, node tags, storage reachability, hard rules, and the node's PHYSICAL RAM. This is the designer's view; `alternatives`/`spof` above are the operator's emergency view of NOW. When the two diverge, the gap is exactly the transient trouble the operator can wait out.
 *
 */
export const zVmSpofStructural = z.object({
    alternatives: z.int(),
    spof: z.boolean(),
    bindingConstraint: z.string().nullish()
});

/**
 * The admission lens (task #344 — ratified by Luca 2026-08-19): whether the VM may LEAVE its host RIGHT NOW. `alternatives` says where it could land; this says whether a live migration would be ADMITTED today, given the tasks in flight — answered by the SAME guards that would decide a `vm.migrate` birth (the declarative admission model, rounds of 2026-08-19), never a second list. The gap between having alternatives and being movable is an in-flight task the operator can usually wait out; `blockedBy` names it with the exact words the migrate's 409 would use.
 *
 */
export const zVmSpofAdmission = z.object({
    movableNow: z.boolean(),
    blockedBy: z.string().nullable()
});

/**
 * The independence report of a VM (task #344, first cut — ratified by Luca 2026-08-19, all four decisions): how exposed the VM is to the failure of its host, answered by the SAME engine the auto-placer uses, never a second list. The report is a snapshot of NOW (`asOf`): capacity, maintenance and probe verdicts move, so a stored copy is a memory, not a fact.
 *
 */
export const zVmSpof = z.object({
    domain: z.enum(['node']),
    asOf: z.iso.datetime(),
    node: z.string().nullable(),
    alternatives: z.int(),
    spof: z.boolean(),
    bindingConstraint: z.string().nullish(),
    structural: zVmSpofStructural.optional(),
    admission: zVmSpofAdmission,
    candidates: z.array(zVmPlacementNode)
});

/**
 * The structured QuickDeploy grammar (round #110, T110-P3 — the dettatura of 2026-08-01 raised to contract): the platform GENERATES the cloud-init / cloudbase-init documents from these fields, once, in the engine (T110-P2); the GUI form and `tvirt quickdeploy-vm` are thin clients of this body. The raw documents (`userData`, `networkConfig`) are the explicit FALLBACK that supersedes the generators — a structured block next to a raw document is refused (`422`), never silently ignored. Fields of the OS the template does not declare are refused in both directions. The cross-field rules live in ONE place (`common/src/quickdeploy/spec.ts`) and every refusal names its field.
 *
 */
export const zQuickDeploySpec = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    scope: z.string().optional(),
    storage: z.string().optional(),
    linked: z.boolean().optional().default(true),
    start: z.boolean().optional().default(true),
    count: z.int().gte(1).lte(50).optional().default(1),
    os: zQuickDeployOs.optional(),
    hostname: z.string().max(253).optional(),
    ip4: z.string().max(18).optional(),
    gw4: z.string().max(15).optional(),
    dns: z.array(z.string().max(15)).max(3).optional(),
    linux: z.object({
        user: z.string().max(32).optional(),
        password: z.string().max(256).optional(),
        sshKeys: z.array(z.string().max(4096)).max(16).optional()
    }).optional(),
    windows: z.object({
        user: z.string().max(20).optional(),
        password: z.string().max(256).optional(),
        productKey: z.string().max(29).optional(),
        kmsServer: z.string().max(260).optional(),
        joinAd: z.object({
            domain: z.string().max(253),
            user: z.string().max(256),
            password: z.string().max(256),
            ou: z.string().max(1024).optional()
        }).optional()
    }).optional(),
    userData: z.string().nullish(),
    networkConfig: z.string().nullish(),
    secretFields: z.array(z.string().max(160)).max(8).optional()
});

export const zViewerAccess = z.object({
    viewerWebSocketUrl: z.string(),
    proto: zConsoleProtocol
});

/**
 * The vocabulary of a ticket's fate — declared ONCE and referenced by the VM viewer fate and the CT console fate (`CtConsoleFate`, T157-P8 d), so the two surfaces cannot drift apart by one word (the contract enum census's rule).
 *
 */
export const zViewerFateKind = z.enum([
    'active',
    'superseded',
    'expired',
    'revoked',
    'unknown'
]);

/**
 * Why a viewer ticket stopped answering (task #32, in-contract since round #148): the stale session's way to tell a TAKEOVER from a network blip.
 *
 */
export const zViewerFate = z.object({
    fate: zViewerFateKind,
    takenBy: z.string().optional()
});

/**
 * Immutable after creation.
 */
export const zDiskSource = z.object({
    type: z.enum([
        'blank',
        'clone',
        'image',
        'lun'
    ]),
    lun: z.object({
        number: z.int().gte(0)
    }).optional(),
    clone: z.object({
        disk: z.string(),
        snapshot: z.string().optional(),
        linked: z.boolean().optional().default(false)
    }).optional(),
    image: z.object({
        image: z.string()
    }).optional()
});

/**
 * THE sharing vocabulary of a disk (round #173, T173-P1 ratified 2026-09-03): a PROPERTY OF THE DISK, one mode for every attach, changeable only while no VM attaches it. `none` (the default) is the ordinary single-attach disk: a second VM naming it is `409 in_use`. `multiwriter` is SHARED WRITE for guest clusters (GFS2/OCFS2 on DLM, RHEL HA with a quorum device or an sbd watchdog, shared-disk databases) — the COORDINATION OF WRITES IS THE GUEST'S, TVirt does not arbitrate: it delivers the same raw device to N QEMU processes with `share-rw=on` and direct cache (no host page cache in between) and nothing else. The perimeter is physical, not policy (T173-P2-bis): `format: raw` is mandatory (qcow2 metadata has a single writer by format — "qcow2 in shared write cannot be done"), and the storage must be one where two writers are sane by construction: `rbd` (the image is born WITHOUT exclusive-lock, object-map and fast-diff), `nfs`/`cifs`/`cephfs` (a raw file on a kernel mount, T173-P15) and the WHOLE-LUN family `iscsi-lun`/ `nvme-tcp-lun` (the SAN LUN passed raw to the guest, §3.4). The SAN-LVM thin family (`iscsi`/`nvme-tcp`) is OUT — a thin pool has ONE activator per node and two would corrupt its metadata; `dir` is not shared. `readonly` is SHARED READ for common assets: N readers, every blockdev `read-only`, any format (qcow2 included — nothing writes metadata) on every born-shared family EXCEPT SAN-LVM thin (T173-P17: N readers on N nodes would activate the pool N times — the same physics). Refusals are `422` at creation, at PATCH and at attach, each naming the driver and the alternative. WHAT IS NOT PROMISED (T173-P4-bis, T173-P14, declared by name): no SCSI-3 Persistent Reservations in v1 — the `scsi-hd` device does not emulate PERSISTENT RESERVE IN/OUT and `qemu-pr-helper` is a passthrough that only a SCSI whole LUN (`scsi-block`) can carry. A Windows Server Failover Cluster with a classic shared disk REQUIRES PR and is NOT validated on this release; the PR round lands on the SAME whole-LUN carrier, on its SCSI leg only (`iscsi-lun`, FC when #138 arrives) — never on NVMe, whose reservations are another protocol with no passthrough in QEMU. THE FORBIDDEN, by name (T173-P6): disk snapshot, VM snapshot (a `422` that NAMES the disk — never a silent exclusion), linked clone from or to a shared disk, `encrypted`, VM backup (#28) of a `multiwriter` disk (no single journal from which to derive consistency; `readonly` disks stay backuppable); clone, move and resize require EVERY attached VM stopped. With the store down the emergency lane does NOT start a VM holding a `multiwriter` disk (T173-P7, fail-closed: the QEMU locks are relaxed by declaration and the lane has no other net). Anti-affinity between the members of a guest cluster is SUGGESTED (a warning where two attaches run on one node without a `ProximityRule antiAffinity`), never imposed (T173-P9).
 *
 */
export const zDiskSharing = z.enum([
    'none',
    'multiwriter',
    'readonly'
]);

/**
 * A virtual disk. `storage`, `format` and `source` are immutable; `sizeGiB` is writable but grow-only (`422` on shrink), with live growth propagated to a running VM via QMP `block_resize`.
 *
 */
export const zDisk = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    storage: z.string(),
    sizeGiB: z.int().gte(1).optional(),
    format: zDiskFormat.optional(),
    source: zDiskSource.optional(),
    sharing: zDiskSharing.optional(),
    lun: z.object({
        wwn: z.string().optional(),
        serial: z.string().optional()
    }).readonly().optional(),
    phase: z.enum([
        'creating',
        'ready',
        'in-use',
        'deleting',
        'error'
    ]).readonly().optional(),
    observedAt: z.iso.datetime().readonly().optional(),
    virtualSizeGiB: z.number().readonly().optional(),
    allocatedGiB: z.number().readonly().optional(),
    usedByVMs: z.int().readonly().optional(),
    attachedTo: z.array(z.object({
        vm: z.uuid(),
        index: z.int()
    })).readonly().optional(),
    snapshots: z.array(z.object({
        name: z.string().optional(),
        createdAt: z.iso.datetime().optional()
    })).readonly().optional()
}));

/**
 * The VM's hardware description in the `.tvtpl` manifest vocabulary (#64, docs/tvtpl-format.md) served NAKED — no tar, no payload, no checksums: the metadata a backup product archives next to the disk bytes so the VM can be recreated at restore (the import road of a later round consumes exactly this JSON plus the restored disks). Unlike `exportVmToCatalog` this never copies a disk.
 *
 */
export const zVmDescription = z.object({
    tvtpl: z.int().readonly(),
    generator: z.object({
        product: z.string(),
        version: z.string()
    }).readonly(),
    createdAt: z.iso.datetime().readonly(),
    template: z.record(z.string(), z.unknown()).readonly(),
    firmwareVersion: z.string().readonly().nullish(),
    disks: z.array(z.object({
        index: z.int(),
        disk: z.uuid().optional(),
        format: zDiskFormat,
        sizeGiB: z.int(),
        encrypted: z.boolean().optional()
    })).readonly()
});

/**
 * One change-tracking bitmap of one VM disk, as last observed by the serving node (QMP `query-named-block-nodes` on a live VM, `qemu-img info` on a stopped one) — the copy on the disk's HEAD. Since the epoch rotation (round D, T235-P1) a bitmap name is a LINEAGE of epochs: every snapshot disables the recording copy inside the file it freezes (that copy is an epoch — the writes since the previous snapshot, readable from that one file through a read-only export) and arms an empty recording twin of the same name on the fresh overlay. So the head's copy records the granules written SINCE THE LAST SNAPSHOT (or since the last reset, whichever is later); the older epochs sit in the frozen files, listed in `lineages`. The external software reads an epoch's dirty extents through the NBD export of the snapshot that hosts it (`qemu:dirty-bitmap:<name>` meta-context) and composes a delta across several snapshots by opening one export per intermediate snapshot.
 *
 */
export const zVmCbtBitmap = z.object({
    name: z.string(),
    changeId: z.string().readonly().optional(),
    granularityBytes: z.int().optional(),
    recording: z.boolean(),
    persistent: z.boolean(),
    inconsistent: z.boolean(),
    dirtyBytes: z.int().optional()
});

/**
 * The tracking chain of this disk was SEVERED by an event the platform controls, and the epoch rotation could not cover. A REVERT branches the disk onto a fresh overlay off an older snapshot: the timeline diverges, nothing on the new head records, and every bitmap that was recording is severed. A SNAPSHOT no longer severs what it rotates (round D, T235-P1: every persistent recording bitmap is disabled in the frozen file and re-armed on the new overlay inside the same transaction — the chain continues as a new epoch); it severs only what the rotation cannot carry — a bitmap whose cold-road rotation failed, an inconsistent survivor, a non-persistent bitmap. Re-enabling one of the named bitmaps would produce a chain that only LOOKS continuous — an incremental built on it would be silently holed. So the platform declares "I do not know" and forces a full, exactly as `disable` does. Enabling one of these names answers `cbt_chain_broken` until it is disabled first; disabling it is the acknowledgement and drops it from the list. Names NOT in the list are untouched: a bitmap never seen before this break has no continuity to fake. The merge-delete of a snapshot is NOT a break: the epochs its file hosted become `lost` in `lineages` (a changeId crossing them answers `cbt_unavailable`), while the bitmap on the head keeps recording.
 *
 */
export const zVmCbtChainBreak = z.object({
    at: z.iso.datetime().readonly(),
    reason: z.enum(['snapshot', 'revert']).readonly(),
    bitmaps: z.array(z.string()).readonly()
});

/**
 * One epoch of a bitmap lineage (round D, T235-P1/P2). An epoch is born `recording` on the disk's head (at enable, at every snapshot, after a reset) and ends in one of two ways: `frozen` — a snapshot disabled it inside the file it froze, and that file (named by `snapshot`) is where its dirty extents are read from, through a read-only export with this bitmap; or `lost` — its extents are gone or unreadable, for the `cause` given: `reset` (the caller's new baseline), `revert` (the timeline diverged past it), `merge` (the snapshot hosting it was deleted and its file merged forward: the bitmaps inside died with it — until the epochs-fusion PROPOSAL of the dossier is ratified this is the third event, and it does not sever the head's chain), `unobserved` (the bitmap stopped being seen on the head — a storage move copies no bitmap), `rotation` (the cold-road rotation failed at that snapshot).
 *
 */
export const zVmCbtEpoch = z.object({
    changeId: z.string().readonly(),
    state: z.enum([
        'recording',
        'frozen',
        'lost'
    ]).readonly(),
    openedAt: z.iso.datetime().readonly(),
    closedAt: z.iso.datetime().readonly().optional(),
    snapshot: z.string().readonly().optional(),
    cause: z.enum([
        'reset',
        'revert',
        'merge',
        'unobserved',
        'rotation'
    ]).readonly().optional()
});

/**
 * The epoch history of one bitmap name on one disk, oldest first: the frozen epochs (one per snapshot taken while the bitmap recorded), the lost ones, and at most one recording (the last). Two changeIds `from` and `to` of the same lineage name a COMPOSABLE delta when every epoch from `from` (inclusive) to `to` (exclusive) is `frozen`: the delta is their union, read from the exports of their snapshots. A `lost` epoch in between means `cbt_unavailable` — the deliberate degrade to a full (T235-P2). The engine's own `tvbk-` series bitmaps are hidden here as everywhere in this read.
 *
 */
export const zVmCbtLineage = z.object({
    bitmap: z.string().readonly(),
    changeId: z.string().readonly().optional(),
    epochs: z.array(zVmCbtEpoch).readonly()
});

/**
 * On a `milestone: snapshot` disk (rbd, SAN thin) there is no bitmap: the epoch IS the platform snapshot, and its changeId is `tvt/<diskId>/<snapshotName>` (round D, decision of the step). This lists the snapshots that freeze this disk, oldest first, with the changeId to quote — the two ends of a `queryChangedAreas` question.
 *
 */
export const zVmCbtMilestone = z.object({
    snapshot: z.string().readonly(),
    changeId: z.string().readonly(),
    createdAt: z.iso.datetime().readonly().optional()
});

/**
 * The change-tracking state of one VM disk slot. Entirely read-only: the observation the serving node recorded at the last change-tracking operation (each enable/disable/reset refreshes it) and at the last snapshot or revert of the VM.
 *
 */
export const zVmDiskChangeTracking = z.object({
    disk: z.int().readonly(),
    supported: z.boolean().readonly(),
    mechanic: z.enum([
        'qcow2-bitmap',
        'rbd-snapdiff',
        'lvm-thin-delta',
        'none'
    ]).readonly().optional(),
    milestone: z.enum([
        'bitmap',
        'snapshot',
        'none'
    ]).readonly().optional(),
    granularity: z.enum([
        'chosen',
        'object',
        'poolChunk',
        'none'
    ]).readonly().optional(),
    bitmaps: z.array(zVmCbtBitmap).readonly(),
    observedAt: z.iso.datetime().readonly().optional(),
    chainBroken: zVmCbtChainBreak.readonly().optional(),
    lineages: z.array(zVmCbtLineage).readonly().optional(),
    milestones: z.array(zVmCbtMilestone).readonly().optional()
});

/**
 * Creates a PERSISTENT recording bitmap on the disk, or resumes recording on an existing bitmap of the same name that was left disabled. Names starting with `tvirt-` are reserved for the platform (`422`).
 *
 */
export const zEnableChangeTrackingRequest = z.object({
    name: z.string().min(1).max(120).regex(/^[A-Za-z0-9._-]+$/),
    granularityBytes: z.int().gte(4096).lte(67108864).optional()
});

/**
 * Names the bitmap a change-tracking action operates on. Names starting with `tvirt-` are reserved for the platform (`422`).
 *
 */
export const zChangeTrackingRequest = z.object({
    name: z.string().min(1).max(120).regex(/^[A-Za-z0-9._-]+$/)
});

/**
 * The two ends of a delta on a `milestone: snapshot` disk (rbd, SAN thin): changeIds of the form `tvt/<diskId>/<snapshotName>` — the platform snapshot IS the epoch there (see `VmCbtMilestone`; the change-tracking read lists them). `from` must be older than `to`; the answer is paginated by offset: repeat with `startOffset` = `nextOffset` until it is absent.
 *
 */
export const zQueryChangedAreasRequest = z.object({
    from: z.string().min(1).max(300),
    to: z.string().min(1).max(300),
    startOffset: z.int().gte(0).optional(),
    limit: z.int().gte(1).lte(65536).optional()
});

/**
 * What to export: one disk slot, frozen at one snapshot. `bitmap` names a change-tracking bitmap to publish on the export as its dirty-extent meta-context: the EPOCH of that bitmap frozen inside the snapshot's file — the writes between the previous snapshot and this one. It is non-recording there by construction since the epoch rotation (round D, T235-P1: the snapshot disabled it in the same transaction that froze the file), so the export is legal with the VM running. A bitmap still recording inside the frozen file is an epoch that was never rotated (a snapshot older than the rotation, a failed cold-road rotation): the task refuses with `cbt_chain_broken` — nothing is disabled on the fly. A bitmap enabled AFTER the snapshot has no epoch there and refuses too.
 *
 */
export const zCreateBackupExportRequest = z.object({
    disk: z.int().gte(1).lte(4),
    snapshot: z.string().min(1).max(120).regex(/^[A-Za-z0-9._-]+$/),
    bitmap: z.string().min(1).max(120).regex(/^[A-Za-z0-9._-]+$/).optional(),
    ttlMinutes: z.int().gte(1).lte(1440).optional()
});

/**
 * Which disk slot to open for WRITING (round D4, T235-P6). No `snapshot` and no `bitmap`: a write session serves the LIVE HEAD of a disk that has never been written — the restore fills virgin disks, it never repairs lived ones. The expected road: `createVmFromDescription` mints the VM with empty disks of the right geometry, then one write session per slot fills it.
 *
 */
export const zCreateRestoreExportRequest = z.object({
    disk: z.int().gte(1).lte(4),
    ttlMinutes: z.int().gte(1).lte(1440).optional()
});

/**
 * T235-P7: the naked `VmDescription` served by `getVmDescription` comes BACK and mints a STOPPED VM with EMPTY disks of the described geometry — ready for `createRestoreExport` to fill, slot by slot. One vocabulary both ways (the manifest's own), so a backup product stores the description and hands it back verbatim; translating it into a `createVm` body on the caller's side would be a second vocabulary that diverges at every new field. The `description` is validated by the platform's own manifest validator: every refusal names the field.
 *
 */
export const zCreateVmFromDescriptionRequest = z.object({
    description: z.record(z.string(), z.unknown()),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    scope: z.string().optional(),
    storage: z.string(),
    metadataStorage: z.string().optional(),
    bridge: z.string().optional(),
    network: z.string().optional()
});

/**
 * Where to write the backup, plus the ONE consistency knob the hot path earned (PROPOSAL 7, ratified 2026-08-24). Nothing else is a knob on purpose: the shape of the artefact is not an operator choice (a backup that can be configured into uselessness is the template trap with a checkbox), and the consistency MECHANISM stays the snapshot layer's ratified automatism — the knob only decides what happens when the automatism cannot deliver what was promised.
 *
 */
export const zCreateVmBackupRequest = z.object({
    destination: z.string(),
    mode: z.enum(['full', 'incremental']).optional().default('full'),
    requireQuiesce: z.boolean().optional().default(false)
});

/**
 * An ephemeral NBD export session (#235 primitive 3), in ONE of two modes. `read` (born by `createBackupExport`): the data path serving the bytes of ONE disk as they were at ONE snapshot, plus — when a bitmap was named — the dirty-extent map as the NBD meta-context `qemu:dirty-bitmap:<name>` on the same connection (dossier §1.2: one connection, first the map, then reads of the dirty extents only). `write` (born by `createRestoreExport`, round D4 — T235-P6): the restore's data path, a WRITABLE `qemu-nbd` on the live head of a VIRGIN disk of a stopped VM, which the client (`nbdcopy`, `qemu-img convert -n`) fills with the image it recomposed on its side. The two modes are ONE resource on purpose — same session row, same TTL, same ticket, same TLS, same list/show/close/sweep; only the door and the permission differ. Dies by `closeBackupExport` or by TTL. The export name is the TICKET (#148 idiom applied to a non-HTTP protocol): unguessable, single-purpose, dead with the session — whoever holds it holds at most that one read (or that one write) until `expiresAt`, never a session, never a token. While a `write` session is `preparing` or `ready` its disk is FENCED at the origin: starting the VM (single or batch), snapshotting, reverting or cloning it, and deleting, moving, cloning, resizing or detach-deleting the disk answer `409` naming the session. Deleting the VM is not fenced: the row dies with the VM (FK cascade) and the sweep kills the server.
 *
 */
export const zBackupExport = z.object({
    id: z.uuid().readonly(),
    vm: z.uuid().readonly(),
    scope: z.string().readonly().optional(),
    disk: z.int().readonly(),
    mode: z.enum(['read', 'write']).readonly(),
    snapshot: z.string().readonly().optional(),
    bitmap: z.string().readonly().optional(),
    phase: z.enum([
        'preparing',
        'ready',
        'closed',
        'error'
    ]).readonly(),
    node: z.string().readonly().optional(),
    endpoint: z.object({
        host: z.string().optional(),
        port: z.int().optional(),
        exportName: z.string().optional(),
        caCertificate: z.string().optional()
    }).readonly().optional(),
    error: zProblem.optional(),
    createdAt: z.iso.datetime().readonly(),
    expiresAt: z.iso.datetime().readonly()
});

export const zBackupExportList = z.object({
    items: z.array(zBackupExport),
    nextCursor: z.string().optional()
});

/**
 * One fabric named from a logical network's membership (round
 */
export const zFabricNetworkOwnerRef = z.object({
    fabricId: z.uuid(),
    fabricName: z.string()
});

/**
 * How the logical network reaches the wire — a deliberately extensible discriminated shape (Annex O.1). `vlan` is the v1 kind; `overlay` (wrapping an existing VXLAN overlay — same concept, two backings) is the Annex O.6 PROPOSAL and arrives only if ratified. The server applies `kind: vlan` when the whole group is omitted at creation. A logical network carries NO VLAN id of its own (ratified 2026-07-27 — the former fabric-wide `vlanId` is DEAD): VLANs exist ONLY at the per-port / per-LAGG mapping level — the same network may ride tag 101 on one port and 201 on another (the canonical "Rete IPMI" example). A `vlanId` still present in stored rows is ignored on read (lenient — no data migration).
 *
 */
export const zFabricNetworkBacking = z.object({
    kind: z.enum(['vlan'])
});

/**
 * The addressing half of a logical network — MANDATORY since v2 (Annex O.9: the pure L2 segment without addressing died) and distinct from every other subnet of the fabric. Subnets must not overlap — within the fabric, across fabrics, against the overlay networks (`409 in_use` naming the holder; adjacent is fine, overlapping never). The write that lands it DERIVES the registry Subnet (revised Annex O.4 — the IPAM is derived-only since the 2026-07-28 dictation): created/updated in the same transaction, id stable across updates, removed when the logical network goes away. Reserved ranges live HERE since the revision (the "Range riservati a TVirt©" dialog tab) and are mirrored onto the derived Subnet — the pools the IPAM allocator draws automatic addresses from.
 *
 */
export const zFabricSubnet = z.object({
    cidr: z.string(),
    gateway: z.string().nullish(),
    reservedRanges: z.array(z.object({
        from: z.string(),
        to: z.string()
    })).optional()
});

/**
 * One static route of the logical network: traffic for `destination` goes through `via`. Part of the single-source network identity (dictation 2026-07-28): consumers render it, never duplicate it.
 *
 */
export const zFabricRoute = z.object({
    destination: z.string(),
    via: z.string()
});

/**
 * A logical network of the fabric (Annex O.1/O.9; revised by fabric-ipam-revision-notes.md; ONE ROW with its own identity since round #405 — SN-P1-bis variant 2, SN-P9, SN-P11-bis, ratified 2026-09-03). A network MAY be delivered heterogeneously — untagged on one port, tagged on another: it is the SAME network (the canonical "Rete IPMI" example) — and, since #405, by SEVERAL fabrics: it is born in ONE fabric (the OWNER, the only one that writes its identity) and any other fabric may IMPORT it (`POST …/imports`), after which the same row — same `id`, same `stem` — appears in the importer's `importedNetworks` and is mapped on the importer's ports/LAGGs exactly like an own network. Since v2 the subnet is mandatory and the network carries a mandatory `mtu` (server default 1500). Gateway, DNS servers and static routes declared here are the SINGLE SOURCE of the network identity (ratified 2026-07-28): server NICs, Apps and network services draw from here — config derived, never duplicated. Dual-stack (IPv6) is the O.10 round: networks are born IPv4 with the structure ready to grow. The NAME is a pure label: every interface the derivation makes for this network is named after the `stem` (`tvf-<stem>`, `ab-<stem>`), so renaming a network renames no interface on any node.
 *
 */
export const zFabricLogicalNetwork = z.object({
    id: z.uuid().optional(),
    stem: z.string().regex(/^[0-9a-f]{7}$/).readonly().optional(),
    bridge: z.string().readonly().optional(),
    importedBy: z.array(zFabricNetworkOwnerRef).readonly().optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    mtu: z.int().gte(68).lte(65535).optional().default(1500),
    backing: zFabricNetworkBacking.optional(),
    subnet: zFabricSubnet,
    dnsServers: z.array(z.string()).optional(),
    routes: z.array(zFabricRoute).optional(),
    nodeAddressing: z.enum(['none', 'pool']).optional().default('none'),
    notes: z.string().max(2000).optional()
});

/**
 * A logical network IMPORTED into this fabric (round #405): the owner's row, read-only here — identity, `id` and `stem` are the owner's, the importer declares nothing about it and can only map it on its own ports/LAGGs (by bare name, exactly like an own network) and un-import it. `owner` names the fabric that holds the identity.
 *
 */
export const zFabricImportedNetwork = zFabricLogicalNetwork.and(z.object({
    owner: zFabricNetworkOwnerRef
}));

/**
 * The switch's optional PHYSICAL position (Annex O.9): a level→name map over the SAME native CRUSH bucket vocabulary as the node placement (cluster.yaml `ClusterNode.placement`, round #93) — ONE physical- position vocabulary for nodes and switches, a second one explicitly refused. Purely declarative here: names are DNS-safe but the crushmap uniqueness rule does NOT apply (no CRUSH bucket is ever born of a switch). Partial declarations are legal; `{}` = not declared.
 *
 */
export const zFabricSwitchPosition = z.object({
    chassis: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    rack: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    row: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    pdu: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    pod: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    room: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    datacenter: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    zone: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    region: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional()
});

export const zFabricTaggedNetwork = z.object({
    tag: z.int().gte(1).lte(4094),
    network: z.string()
});

/**
 * One switch port and its declared configuration state (Annex O.9, three states): UNUSED — no mappings, its role in the fabric unknown to TVirt (the default); NON-LAGG — at most one untagged (native/access) network plus per-tag mappings, every `tag` unique on the port, every referenced network existing in the same fabric (`422` speaking otherwise); LAGG-MEMBER — reached ONLY from the LAGG side (a port inside a LAGG carries NO mapping config of its own, `422` otherwise; removal from the LAGG returns it to unused). The v1 per-port `cabledTo` is DEAD: the cabling declaration lives on the NODE (ClusterNode.cabling).
 *
 */
export const zFabricSwitchPort = z.object({
    name: z.string().min(1).max(63),
    untaggedNetwork: z.string().nullish(),
    taggedNetworks: z.array(zFabricTaggedNetwork).optional()
});

/**
 * The manual switch registry entry (Annex O.1/O.9): a named object, its optional physical position and its ports. Vendor facts, management addressing and LLDP-fed discovery arrive with the observation rounds (O.7).
 *
 */
export const zFabricSwitch = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    position: zFabricSwitchPosition.optional(),
    notes: z.string().max(2000).optional(),
    ports: z.array(zFabricSwitchPort).optional()
});

/**
 * A member port, referenced as `(switch, port)` by name.
 */
export const zFabricLaggPort = z.object({
    switch: z.string(),
    port: z.string()
});

/**
 * A LAGG (Annex O.9 — FIRM): 2+ ports in LACP, an object at FABRIC level referencing `(switch, port)` pairs — deliberately NOT a child of a switch, because ports of DIFFERENT switches may sit in one LAGG (MLAG is the switch stack's implementation detail, transparent to TVirt and its clients). A port belongs to AT MOST one LAGG, and a member port carries no mapping config of its own: the LAGG is the mapping surface, exactly like a non-LAGG port. Membership is edited HERE (the LAGG side): adding an UNUSED port makes it a member, removing it returns it to unused. The 802.3ad bond the derivation builds for it is named `bo-<stem>` (SN-P17, ratified 2026-09-04): the stem is coined at the LAGG's creation and kept by NAME across edits — a LAGG that disappears from the array loses it, a new name is a new bond.
 *
 */
export const zFabricLagg = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    stem: z.string().regex(/^[0-9a-f]{7}$/).readonly().optional(),
    bond: z.string().readonly().optional(),
    ports: z.array(zFabricLaggPort).min(2),
    lacpRate: zLacpRate,
    untaggedNetwork: z.string().nullish(),
    taggedNetworks: z.array(zFabricTaggedNetwork).optional()
});

/**
 * One declared cable, seen from the fabric side (round R4, read-only).
 *
 */
export const zFabricCable = z.object({
    switch: z.string(),
    port: z.string(),
    node: z.string(),
    nodeId: z.string().optional(),
    nic: z.string(),
    lagg: z.string().nullish()
});

/**
 * One EXTERNAL physical L2 domain (Annex O.9; revised by fabric-ipam-revision-notes.md): the physical registry — logical networks, switches with ports, fabric-level LAGGs. The scope is FIXED to the cluster root `/cluster1` (dictation 2026-07-28: physical infrastructure is by definition cluster-wide) — a create naming any other scope refuses `422` speaking; the rendered document always carries the root scope. Pure config — `201` with no task: nothing converges; the derivation engine that renders per-node VLAN/bond/bridge configuration from the node-declared cabling is its own future round (O.3), as is LLDP observation (O.7). The addressing half lives in the IPAM as DERIVED rows (revised O.4): every logical network's subnet is derived into the registry by the same write and dies with the network.
 *
 */
export const zFabric = zResourceCommon.and(z.object({
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    etag: z.string().readonly().optional(),
    logicalNetworks: z.array(zFabricLogicalNetwork).optional(),
    importedNetworks: z.array(zFabricImportedNetwork).readonly().optional(),
    switches: z.array(zFabricSwitch).optional(),
    laggs: z.array(zFabricLagg).optional(),
    cabling: z.array(zFabricCable).readonly().optional()
}));

export const zFabricList = z.object({
    items: z.array(zFabric),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * The IMPORT act (round #405, SN-P1-bis: «aderire = importare, POI mappare»): this fabric adheres to a logical network another fabric owns. Importing ANTICIPATES the same assertion mapping makes (SN-P6): whoever imports declares that the ports/LAGGs they will map it on reach the SAME L2 domain as the owner's delivery — TVirt trusts it, places and live-migrates on it, and never verifies it.
 *
 */
export const zFabricNetworkImport = z.object({
    network: z.string().min(3)
});

/**
 * One line of the derived-vs-current diff, deterministic in order (kind, then device). `before`/`after` carry the affected declaration halves verbatim (interface block, vlan entry, bridge spec, nic registration, or the assignment address) — absent on the side that does not exist.
 *
 */
export const zFabricNodeConfigChange = z.object({
    action: z.enum([
        'add',
        'remove',
        'replace'
    ]),
    kind: z.enum([
        'nic',
        'interface',
        'vlan',
        'bond',
        'bridge',
        'assignment'
    ]),
    device: z.string(),
    before: z.unknown().optional(),
    after: z.unknown().optional()
});

/**
 * One TOPOLOGY declaration of the derivation plan (A5/A6/A7 as refined 2026-08-24): on double-way surfaces the render CHOOSES the shape and the preview DECLARES it — the operator never reverse-engineers the form from the diff. One entry per derived bond, plus one per unprotected single-port network of a multi-way node. `perNetworkBond` — twin TAGGED surfaces: one active-backup bond per network ABOVE the VLAN children (`ab-<stem>` of the network, SN-P11-bis), primaries ALTERNATED by the deterministic hash of the network name over the link ordinals — per-network failover plus static load spreading over the cables, identical on twin nodes. `singleBond` — the twin surface carries an UNTAGGED network: an enslaved eth cannot carry its own VLAN children, so the pair falls back to ONE bond above the eths (`ab-<stem>` of the untagged network); VLAN children and bridges ride the bond. `laggBond` — a declared LAGG: 802.3ad bond above the PHYSICAL eths (`bo-<stem>` of the LAGG, SN-P17) with the VLAN children on top — LACPDUs travel untagged on the wire, LACP NEVER rides a VLAN. `singlePort` — the network stays on its one port (the `networkUnbalanced` / `autoBondHeld` warnings speak alongside).
 *
 */
export const zFabricNodeConfigTopology = z.object({
    form: z.enum([
        'perNetworkBond',
        'singleBond',
        'laggBond',
        'singlePort'
    ]),
    device: z.string().optional(),
    networks: z.array(z.string()),
    members: z.array(z.string()).optional(),
    primary: z.string().optional(),
    held: z.boolean().optional(),
    message: z.string()
});

/**
 * One dual-stack IP flip of the derivation plan (F4 — A9 CONTRO-DETTATURA + A9-bis + A10, ratified 2026-08-24/25). A derived IP change never replaces: step 1 (the make) ADDS the new address beside the old on the same attachment — existing connections keep the old, new ones are born on the new, no VM is migrated; step 2 is the worker's drain probe observing the old address (established connections, age of the last activity, «empty for N minutes»); step 3 — the DECLARED point of no return — is the explicit removal (the `retire` entry of the apply), subordinated to the drain and NEVER automatic, however long the probe reports the address empty. Before step 3 the way back is re-declaring the old address (the engine flips the retirement symmetrically); after it, the way back is the INVERSE flip — the same engine re-adds the old as new — never a blind restore. `phase` is `planned` while THIS plan's apply would introduce the dual-stack, `draining` once the stored document carries it. Ceph is OUT of the flip (`cephBoundAddress`).
 *
 */
export const zFabricNodeConfigFlip = z.object({
    network: z.string(),
    point: z.string(),
    address: z.string(),
    replacedBy: z.string(),
    phase: z.enum(['planned', 'draining']),
    since: z.string().nullable(),
    probe: z.object({
        connections: z.int().nullish(),
        lastActiveAt: z.string().nullish(),
        emptySince: z.string().nullish(),
        flows: z.int().nullish(),
        observedAt: z.string().optional()
    }).nullable(),
    message: z.string()
});

/**
 * One structured refusal or warning of the derivation preview/apply — the NodeEvacuationReason idiom (#133a): a STABLE code clients may branch on, a speaking message, and optional context fields riding the code. Refusals block the node's apply; warnings inform and never block (the operator decides).
 *
 */
export const zFabricDerivationReason = z.object({
    code: z.enum([
        'networkDeliveredTwice',
        'carrierEnslavedByBond',
        'subifNameTooLong',
        'inventoryMissing',
        'nicUnobserved',
        'manualConflict',
        'otherFabricDerived',
        'deviceHeldByFabric',
        'addressPoolMissing',
        'addressPoolExhausted',
        'managementLost',
        'roleAddressLost',
        'documentMoved',
        'storeMemberInFlight',
        'storageSubnetTouched',
        'netsvcInterfaceTouched',
        'carrierMtuUnset',
        'networkUnbalanced',
        'autoBondHeld',
        'cephBoundAddress',
        'oldAddressActive',
        'drainUnobserved'
    ]),
    message: z.string(),
    network: z.string().optional(),
    device: z.string().optional(),
    address: z.string().optional()
});

/**
 * One node of the derivation plan. `document` is the WOULD-BE result — an ORDINARY host-network document (the architectural point of the design: the converger applies it unchanged) — null when refusals block the node. `generation` is the node document's CURRENT generation: the apply sends it back as its half of the P10 anchor. `previousGeneration` is the generation of the one-deep pre-apply snapshot the node carries in THIS fabric's block (`fabrics[<fabricId>].previous`, H13) — what a …/node-configs/restore would bring back — or null when there is nothing to restore (no block of this fabric, or a chain closed by a restore to a document without it); RP-P1 (ratified 2026-08-29/31): the client surface of the restore reads it here, no second call. `topology` declares the dual-way shape the render chose (F2, A5/A6 refined) — empty when nothing is bonded and no way is unbalanced, and on refused nodes (the refusals speak there).
 *
 */
export const zFabricNodeConfigItem = z.object({
    node: z.string(),
    nodeId: z.string(),
    state: zFabricDerivationState,
    generation: z.int(),
    previousGeneration: z.int().nullable(),
    previousManual: z.boolean(),
    document: z.record(z.string(), z.unknown()).nullish(),
    diff: z.array(zFabricNodeConfigChange),
    topology: z.array(zFabricNodeConfigTopology),
    flips: z.array(zFabricNodeConfigFlip),
    refusals: z.array(zFabricDerivationReason),
    warnings: z.array(zFabricDerivationReason)
});

/**
 * The derivation plan of one fabric — read-only, what-if pure (the #133a idiom: nothing is enqueued, nothing is written). `fabricEtags` is the COMPOSITE anchor (round #405 §5.3 — SN-P4 extended by SN-P7-bis): the opaque validators AT PLAN TIME of this fabric, of the OWNERS of the networks it imports (their identity lives there) and of every OTHER fabric the plan's nodes are cabled to (their blocks compose the picture the operator approved). The apply carries the map back whole, and a fabric that moved in between answers `409` speaking, naming it (the P10 anchor over the R6 validator — plan and execution coincide by construction, never an apply on a stale preview).
 *
 */
export const zFabricNodeConfigPlan = z.object({
    fabricId: z.string(),
    fabricEtags: z.record(z.string(), z.string()),
    items: z.array(zFabricNodeConfigItem)
});

/**
 * The explicit apply (H1, ratified: the ONLY way derived configuration reaches a node — never an auto-apply). Nodes are applied one at a time, each in its own store transaction, outcomes reported per item; the wave (order, waiting on watermarks, stopping at the first red) is the CLIENT's (H12 — client-driven, the server enforces only the store-member serialization). On a node cabled to several fabrics (SN-P7-bis) the apply rewrites ONLY this fabric's block and keeps the other blocks and their devices fixed — the node composes the union.
 *
 */
export const zFabricNodeConfigApply = z.object({
    nodes: z.array(z.string()).min(1),
    fabricEtags: z.record(z.string(), z.string()),
    expectedGenerations: z.record(z.string(), z.int()),
    retire: z.array(z.object({
        node: z.string(),
        address: z.string(),
        severActive: z.boolean().optional().default(false)
    })).optional()
});

export const zFabricNodeConfigOutcome = z.object({
    node: z.string(),
    nodeId: z.string(),
    outcome: z.enum(['applied', 'refused']),
    generation: z.int().nullish(),
    refusals: z.array(zFabricDerivationReason),
    warnings: z.array(zFabricDerivationReason)
});

export const zFabricNodeConfigApplyResult = z.object({
    items: z.array(zFabricNodeConfigOutcome)
});

/**
 * The H13 restore act (F1-P3): one node, one snapshot, anchored to the generation the operator looked at. On a node cabled to several fabrics the one-deep undo is a STACK (SN-P12, ratified 2026-09-03): the snapshot is the WHOLE pre-apply document, so restoring this fabric while another fabric applied on the node AFTER it answers `409` speaking, naming the fabric to restore first — LIFO, never a silent undo of someone else's apply.
 *
 */
export const zFabricNodeConfigRestore = z.object({
    node: z.string(),
    expectedGeneration: z.int()
});

/**
 * The restored document's coordinates.
 */
export const zFabricNodeConfigRestoreResult = z.object({
    node: z.string(),
    nodeId: z.uuid(),
    generation: z.int()
});

/**
 * The source object this subnet DERIVES from — every registry row has one since the 2026-07-28 revision (manual rows died with it).
 *
 */
export const zSubnetSource = z.object({
    kind: z.enum(['fabric', 'overlay']),
    id: z.string(),
    name: z.string(),
    network: z.string().nullish()
}).readonly();

/**
 * One DERIVED IPAM subnet — the addressing half of a fabric logical network or of an overlay network, materialized into the registry by the source's own write transaction (fabric-ipam-revision-notes.md). Read-only through this surface: cidr, gateway and reserved ranges are edited on the SOURCE dialog and mirrored here; the id is STABLE across source updates, so referrers (dhcp/dns instances, auto-assigned Apps) keep working. The row dies with its source — `409 in_use` naming the referrer when something still references it. Subnets never overlap fleet-wide (`409 in_use` naming the holder at the source write); adjacent is fine.
 *
 */
export const zSubnet = zResourceCommon.and(z.object({
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    cidr: z.string(),
    gateway: z.string().nullish(),
    reservedRanges: z.array(z.object({
        from: z.string(),
        to: z.string()
    })).optional(),
    mtu: z.int().nullish(),
    dnsServers: z.array(z.string()).optional(),
    routes: z.array(z.object({
        destination: z.string(),
        via: z.string()
    })).optional(),
    source: zSubnetSource.optional()
}));

export const zSubnetList = z.object({
    items: z.array(zSubnet),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * One assigned address and WHO HOLDS IT — provenance is part of the datum.
 */
export const zIpamAddress = z.object({
    address: z.string(),
    origin: z.enum(['declared', 'observed']),
    owner: z.object({
        kind: z.string().optional(),
        id: z.string().optional(),
        name: z.string().optional(),
        detail: z.string().optional()
    }).optional()
});

/**
 * The registry view (fabric-ipam-revision-notes.md): EVERY derived subnet — all fabric logical networks and all subnet-carrying overlays — with the addresses it contains (provenance-labeled: which App/VM/service/node holds each one) and its reserved ranges. The discovery census (candidates, overlap warnings) died with the derived-only model; `unhomed` survives it in a narrower form — see the field.
 *
 */
export const zIpamView = z.object({
    subnets: z.array(z.object({
        subnet: zSubnet,
        addresses: z.array(zIpamAddress)
    })),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    unhomed: z.array(zIpamAddress).readonly().optional()
});

/**
 * THE A11 vocabulary (ratified 2026-08-24) — declared ONCE and referenced by every address-bearing netsvc surface (listener, lb-rp, gateway, vpn): who chooses the listening address. `static` = the operator dictates `address`; `auto` = the ONE IPAM allocator draws it at admission and persists it into `address`. Deliberately NOT shared with `apps.ipAssignment` (same two words today, different futures — see the enum census).
 *
 */
export const zAddressAssignment = z.enum(['static', 'auto']);

/**
 * One listening endpoint (#239, dictated 2026-08-06: every network service is SELF-CONTAINED — it listens on a precise network and a precise address, and the wildcard left the vocabulary). Exactly ONE listening attachment — network XOR overlay XOR interface (`422` speaking, the gateway idiom verbatim) — plus the address, which must sit inside the attachment's subnet where the registry knows one (a raw interface resolves nothing: the address is taken on trust, the fabric-is-optional principle). The dns and ntp kinds carry a LIST of these (the ratified exception: they may listen on several networks and several addresses of those networks); gateway and vpn keep the same members inline, singular by principle. Since A11 (ratified 2026-08-24) the address is required only with `static` assignment — see `addressAssignment`.
 *
 */
export const zNetsvcListener = z.object({
    network: z.string().nullish(),
    overlay: z.string().nullish(),
    interface: z.string().nullish(),
    addressAssignment: zAddressAssignment.optional(),
    address: z.string().optional()
});

/**
 * One side of a NAT rule match (the ratified source/destination shape): an optional address or subnet (an address is a /32 or /128 subnet), an optional port range where the protocol carries ports (`422` speaking otherwise — a semantic rule, not an engine limit), and the invert-match switch. Whole side optional — null/absent constrains nothing (the render says null, the codebase's nullable-object idiom).
 *
 */
export const zGatewayRuleMatch = z.object({
    invert: z.boolean().optional().default(false),
    address: z.string().nullish(),
    ports: z.string().regex(/^[0-9]{1,5}(-[0-9]{1,5})?$/).nullish()
}).nullable();

/**
 * One DNAT rule (ratified form): traffic matching source/destination is redirected to a new destination address (+ optionally a new port). The simple L4 forms (tcp/udp/sctp with a destination port range and no source ports) land as firewalld rich `forward-port` rules; every OTHER ratified form — any/non-L4 protocols, source-port matches, a port-less DNAT — lands in the worker-owned nftables table (ratified 2026-08-03, round-gateway-notes.md). The whole dictated shape is SUPPORTED; the only `422`s left are semantic (ports only where the protocol carries ports).
 *
 */
export const zGatewayDnatRule = z.object({
    family: z.enum(['ipv4', 'ipv6']),
    protocol: z.enum([
        'any',
        'tcp',
        'udp',
        'tcp-udp',
        'icmp',
        'esp',
        'ah',
        'gre',
        'ipv6',
        'igmp',
        'pim',
        'ospf',
        'sctp'
    ]),
    source: zGatewayRuleMatch.optional(),
    destination: zGatewayRuleMatch.optional(),
    toAddress: z.string(),
    toPort: z.string().regex(/^[0-9]{1,5}(-[0-9]{1,5})?$/).nullish()
});

/**
 * One 1:1 NAT rule (ratified form): every packet for `external` maps to `internal` and the internal host egresses as `external`. SUPPORTED since the 2026-08-03 ratification of the worker-owned nftables table (all-protocol DNAT in prerouting + fixed-source SNAT in postrouting, both in `inet tvirt-gateway-*` — beyond firewalld's rich language, which is why this family used to refuse `422`).
 *
 */
export const zGatewayOneToOneRule = z.object({
    family: z.enum(['ipv4', 'ipv6']),
    external: z.string(),
    internal: z.string()
});

/**
 * One SNAT rule (ratified form): traffic matching source/destination leaves with a rewritten source. `toAddress` ABSENT = masquerade on the egress interface's address (the plain form — a firewalld rich `masquerade` rule when the rule carries address matches only). An EXPLICIT new source (address, optional port) and any protocol or port match land in the worker-owned nftables table (ratified 2026-08-03 — these used to refuse `422` as firewalld limits). The only `422`s left are semantic: ports only where the protocol carries ports, and a source-port rewrite needs a port protocol.
 *
 */
export const zGatewaySnatRule = z.object({
    family: z.enum([
        'ipv4',
        'ipv6',
        'both'
    ]),
    protocol: z.enum([
        'any',
        'tcp',
        'udp',
        'tcp-udp',
        'icmp',
        'esp',
        'ah',
        'gre',
        'ipv6',
        'igmp',
        'pim',
        'ospf',
        'sctp'
    ]),
    source: zGatewayRuleMatch.optional(),
    destination: zGatewayRuleMatch.optional(),
    toAddress: z.string().nullish(),
    toPort: z.string().regex(/^[0-9]{1,5}(-[0-9]{1,5})?$/).nullish()
});

/**
 * One static route the gateway node installs (ratified 2026-08-03: v1 routing is KERNEL PURE — ip_forward on the serving node plus the routes declared here; dynamic routing / FRR is explicitly a LATER round with its own dependency ratification). Runtime-only like the rest of the service: routes die with the node and the worker's reconvergence is the persistence.
 *
 */
export const zGatewayRoute = z.object({
    destination: z.string(),
    gateway: z.string(),
    metric: z.int().gte(0).lte(4294967295).nullish()
});

/**
 * One REMOTE-ACCESS WireGuard peer — a roaming client (ratified 2026-08-02; narrowed to the remote-access form by R7-c, 2026-08-24: the site-to-site form is `VpnSitePeer` under `wireguard.sitePeers`). The public key is either GIVEN (the peer generated its own pair) or MINTED BY TVirt when absent — the private half then lives in the service's keystore entry and leaves only through the profile export, never through a render. The peer's allowed-ips is its assigned `/32` inside the tunnel subnet — derived, never declared.
 *
 */
export const zVpnPeer = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    publicKey: z.string().regex(/^[A-Za-z0-9+\/]{42}[AEIMQUYcgkosw048]=$/).nullish(),
    address: z.string().nullish(),
    presharedKey: z.boolean().optional(),
    persistentKeepalive: z.int().gte(1).lte(65535).nullish()
});

/**
 * One SITE-TO-SITE WireGuard peer in the canonical wg form (R7-c, ratified 2026-08-24): `{publicKey, endpoint?, allowedIps[], persistentKeepalive?}` — nothing else, validated severely. The far side brings its OWN pair (TVirt mints nothing here — the minted-pair machinery belongs to the remote-access peers); the public key IS the peer's identity, in the declaration and in the observed telemetry alike. A stored peer in the pre-R7-c shape (name/address/ presharedKey) stops validating and the instance is re-declared — speaking errors are the migration (alpha).
 *
 */
export const zVpnSitePeer = z.object({
    publicKey: z.string().regex(/^[A-Za-z0-9+\/]{42}[AEIMQUYcgkosw048]=$/),
    endpoint: z.string().max(259).nullish(),
    allowedIps: z.array(z.string()).min(1).max(64),
    persistentKeepalive: z.int().gte(1).lte(65535).nullish()
});

/**
 * One OpenVPN remote-access client (ratified 2026-08-02). Auth v1 is the CERTIFICATE: adding an entry ISSUES a client certificate from the instance's own CA (round #159 `issueCertificate` machinery) and records it in `certificate`; removing the entry REVOKES it — the CA's CRL is regenerated in the same transaction, so a removed client is locked out at the next handshake. Password/OTP arrive with #102.
 *
 */
export const zVpnClient = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    certificate: z.uuid().readonly().optional()
});

/**
 * One managed network-service instance (Annex P): a worker-supervised bare-metal daemon on the node the authority registry assigns, its config rendered each reconcile pass from the replica (instance block + the IPAM subnets it references) behind a hash gate — restarts only on real changes. Phase is OBSERVED (the worker's service report): stale reads degrade honest.
 *
 */
export const zNetworkService = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum([
        'dhcp',
        'dns',
        'floatip',
        'gateway',
        'lb-rp',
        'ntp',
        'vpn'
    ]),
    node: z.string().readonly().optional(),
    authorityNode: z.string().readonly().nullish(),
    preferredNode: z.union([
        z.string(),
        zNodeSelector
    ]).nullish(),
    enabled: z.boolean().optional(),
    preferNodes: z.record(z.string(), z.string()).optional(),
    requireNodes: z.record(z.string(), z.string()).optional(),
    dhcp: z.object({
        subnets: z.array(z.string()).min(1),
        interface: z.string(),
        dnsServers: z.array(z.string()).optional(),
        domain: z.string().nullish()
    }).optional(),
    dns: z.object({
        zones: z.array(z.object({
            name: z.string().regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/),
            ns: z.string().regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/).nullish(),
            subnets: z.array(z.string()).optional(),
            reverse: z.boolean().optional(),
            ttl: z.int().gte(30).lte(604800).optional(),
            records: z.array(z.object({
                name: z.string(),
                type: z.enum([
                    'A',
                    'CNAME',
                    'MX',
                    'TXT'
                ]),
                value: z.string(),
                ttl: z.int().gte(30).lte(604800).nullish()
            })).optional()
        })).min(1),
        listeners: z.array(zNetsvcListener).min(1).max(16)
    }).optional(),
    lbRp: z.object({
        network: z.string().nullish(),
        overlay: z.string().nullish(),
        interface: z.string().nullish(),
        addressAssignment: zAddressAssignment.optional(),
        address: z.string().optional(),
        listeners: z.array(z.object({
            name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
            port: z.int().gte(1).lte(65535),
            mode: z.enum(['tcp', 'http']),
            pool: z.string()
        })).min(1),
        pools: z.array(z.object({
            name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
            backend: z.object({
                pod: z.string(),
                port: z.int().gte(1).lte(65535)
            }).nullish(),
            algorithm: z.enum(['round_robin', 'least_request']).optional(),
            members: z.array(z.object({
                address: z.string(),
                port: z.int().gte(1).lte(65535),
                weight: z.int().gte(1).lte(128).optional()
            })).min(1).optional(),
            healthCheck: z.object({
                type: z.enum(['tcp', 'http']).optional(),
                path: z.string().nullish(),
                intervalSeconds: z.int().gte(1).lte(300).optional(),
                rise: z.int().gte(1).lte(10).optional(),
                fall: z.int().gte(1).lte(10).optional()
            }).nullish()
        })).min(1)
    }).optional(),
    gateway: z.object({
        network: z.string().nullish(),
        overlay: z.string().nullish(),
        interface: z.string().nullish(),
        addressAssignment: zAddressAssignment.optional(),
        address: z.string().optional(),
        dnat: z.array(zGatewayDnatRule).optional(),
        oneToOne: z.array(zGatewayOneToOneRule).optional(),
        snat: z.array(zGatewaySnatRule).optional(),
        routes: z.array(zGatewayRoute).optional()
    }).optional(),
    ntp: z.object({
        mode: z.enum(['node-clock', 'relay']),
        servers: z.array(z.string()).max(8).optional(),
        listeners: z.array(zNetsvcListener).min(1).max(8),
        port: z.int().gte(1).lte(65535).optional()
    }).optional(),
    vpn: z.object({
        engine: z.enum(['wireguard', 'openvpn']),
        mode: z.enum(['remote-access', 'site-to-site']),
        network: z.string().nullish(),
        overlay: z.string().nullish(),
        interface: z.string().nullish(),
        addressAssignment: zAddressAssignment.optional(),
        address: z.string().optional(),
        port: z.int().gte(1).lte(65535).optional(),
        protocol: z.enum(['udp', 'tcp']).optional(),
        tunnelSubnet: z.string(),
        dnsServers: z.array(z.string()).max(8).optional(),
        routes: z.array(z.string()).max(64).optional(),
        keyMaterial: z.uuid().readonly().nullish(),
        wireguard: z.object({
            serverPublicKey: z.string().readonly().optional(),
            peers: z.array(zVpnPeer).max(512).optional(),
            sitePeers: z.array(zVpnSitePeer).max(512).optional()
        }).nullish(),
        openvpn: z.object({
            ca: z.uuid().readonly().nullish(),
            serverCertificate: z.uuid().readonly().optional(),
            auth: z.enum(['tls']).optional(),
            fingerprint: z.string().readonly().nullish(),
            peerFingerprint: z.string().regex(/^[0-9A-Fa-f]{2}(:[0-9A-Fa-f]{2}){31}$/).nullish(),
            remote: z.string().nullish(),
            clients: z.array(zVpnClient).max(512).optional(),
            peerRoutes: z.array(z.string()).max(64).optional()
        }).nullish()
    }).optional(),
    floatip: z.object({
        network: z.string().nullish(),
        overlay: z.string().nullish(),
        interface: z.string().nullish(),
        addressAssignment: zAddressAssignment.optional(),
        address: z.string(),
        targets: z.array(z.object({
            app: z.string().nullish(),
            pod: z.string().nullish(),
            vm: z.string().nullish(),
            healthCheck: z.object({
                type: z.enum([
                    'running',
                    'tcp',
                    'http',
                    'ping'
                ]).optional(),
                port: z.int().gte(1).lte(65535).nullish(),
                path: z.string().nullish()
            }).optional()
        })).min(1).max(64),
        preempt: z.boolean().optional()
    }).optional(),
    floatipStatus: z.object({
        holder: z.object({
            target: z.int().optional(),
            app: z.uuid().optional(),
            name: z.string().optional(),
            node: z.string().nullish(),
            since: z.iso.datetime().optional()
        }).nullish(),
        targets: z.array(z.object({
            target: z.int().optional(),
            app: z.uuid().optional(),
            name: z.string().optional(),
            healthy: z.boolean().optional(),
            reason: z.string().nullish()
        })).optional(),
        observedAt: z.iso.datetime().nullish()
    }).readonly().nullish(),
    phase: z.enum([
        'configured',
        'running',
        'error'
    ]).readonly().optional(),
    lastError: z.string().readonly().nullish(),
    reportObservedAt: z.iso.datetime().readonly().nullish(),
    vpnPeers: z.array(z.object({
        name: z.string(),
        connected: z.boolean().optional(),
        endpoint: z.string().nullish(),
        lastHandshake: z.iso.datetime().nullish(),
        rxBytes: z.int().nullish(),
        txBytes: z.int().nullish()
    })).readonly().optional()
}));

export const zNetworkServiceList = z.object({
    items: z.array(zNetworkService),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * One DERIVED admission fact of an instance (#343, dictated 2026-08-13): "this listening attachment can only be served where the wire arrives". It is NOT a constraint the operator wrote and NOT one they can remove — it is observed off the registry TVirt already holds (the attachment of the instance plus the fabric's cabling and port/LAGG mappings), exactly the family of the storages' declared reachability (#141): the placement chooses among the ADMISSIBLE members, the others are not candidates. A GUI must render these apart from `preferNodes`/`requireNodes` — one is a fact of the cluster, the other a policy a human typed, and mixing them produces the worst question an operator can ask ("why does it not start, when I set no constraint at all?").
 *
 */
export const zNetsvcReachability = z.object({
    field: z.string(),
    kind: z.enum([
        'interface',
        'overlay',
        'network'
    ]),
    ref: z.string(),
    detail: z.string(),
    nodes: z.array(z.string()),
    unknownNodes: z.array(z.string())
});

/**
 * One member's verdict for this instance (#343).
 */
export const zNetsvcPlacementNode = z.object({
    node: z.string(),
    eligible: z.boolean(),
    excluded: z.boolean().optional(),
    blockedBy: z.enum([
        'excluded',
        'reachability',
        'requireTag'
    ]).optional(),
    reason: z.string().optional(),
    reachabilityGaps: z.array(z.object({
        field: z.string(),
        detail: z.string()
    })),
    requireMisses: z.array(z.object({
        tag: z.string(),
        value: z.string()
    })),
    preferMisses: z.array(z.object({
        tag: z.string(),
        value: z.string()
    })),
    unknowns: z.array(z.object({
        field: z.string(),
        detail: z.string()
    }))
});

/**
 * The placement census of ONE network service (#343). Answers three questions with one computation: where may this instance live, WHY not elsewhere, and — the diagnosis the product did not have before — on how FEW members it can live at all.
 *
 */
export const zNetsvcPlacement = z.object({
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    service: z.uuid(),
    name: z.string(),
    kind: z.string(),
    node: z.string().optional(),
    reachability: z.array(zNetsvcReachability),
    nodes: z.array(zNetsvcPlacementNode),
    eligibleNodes: z.array(z.string()),
    memberNodes: z.int(),
    recommended: z.string().optional(),
    emptyBecause: z.enum(['reachability', 'requireTag']).optional(),
    singlePointOfFailure: z.boolean().optional(),
    survivesExclusion: z.boolean().optional(),
    wireConflicts: z.array(z.string()).readonly().optional()
});

export const zNetsvcPlacementList = z.object({
    items: z.array(zNetsvcPlacement),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

export const zOverlayHost = z.object({
    underlay: z.string(),
    address: z.string().nullish()
});

export const zNetworkFirewall = z.object({
    defaultPolicy: z.enum(['allow', 'deny']).optional().default('allow')
});

/**
 * PROPOSAL: shape not pinned by the spec.
 */
export const zAttachedNic = z.object({
    vm: z.uuid().optional(),
    nic: z.string().optional()
}).readonly();

/**
 * A node-spanning L2 overlay: VXLAN unicast with a static FDB toward the declared underlay endpoints (§8, ratified 2026-07-20). `hosts` maps each PARTICIPATING node to its underlay endpoint — participation is the placement constraint for VMs trunking into the overlay. VM NICs attach as TRUNKS: untagged traffic to one overlay, tagged VLANs mapped to others (see VmNic).
 *
 */
export const zOverlayNetwork = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    vni: z.int().gte(1).lte(16777215).nullish(),
    subnet: z.string().nullish(),
    reservedRanges: z.array(z.object({
        from: z.string(),
        to: z.string()
    })).optional(),
    hosts: z.record(z.string(), zOverlayHost).optional(),
    firewall: zNetworkFirewall.optional(),
    mtu: z.int().gte(576).lte(9216).optional().default(1450),
    weight: z.int().gte(1).lte(10000).optional().default(100),
    ceilMbps: z.int().gte(1).lte(1000000).nullish(),
    observedAt: z.iso.datetime().readonly().optional(),
    operState: z.enum([
        'up',
        'down',
        'unknown'
    ]).readonly().optional(),
    contention: z.boolean().readonly().optional(),
    usedByVMs: z.int().readonly().optional(),
    attachedNics: z.array(zAttachedNic).readonly().optional()
}));

export const zOverlayNetworkList = z.object({
    items: z.array(zOverlayNetwork),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * One rule of a group — the ratified v1 shape (#168 §5/§9.3), flat and minimal on purpose: direction, verdict, protocol, two address sides, ports. Stateful semantics are IMPLICIT and engine-owned — established/ related traffic is accepted and invalid traffic dropped by rules the engine injects, so a group full of `in` rules never needs a mirror `out` rule for the replies, and the user cannot forget them. Deliberately NOT here (ratified out of v1): named service macros, custom ipsets, per-rule logging, IPv6 addresses (#118 brings the family).
 *
 */
export const zFirewallRule = z.object({
    direction: z.enum(['in', 'out']),
    action: z.enum(['accept', 'drop']),
    protocol: z.enum([
        'any',
        'tcp',
        'udp',
        'icmp'
    ]).optional().default('any'),
    source: z.string().nullish(),
    dest: z.string().nullish(),
    ports: z.string().regex(/^[0-9]{1,5}(-[0-9]{1,5})?(,[0-9]{1,5}(-[0-9]{1,5})?)*$/).nullish(),
    comment: z.string().max(255).nullish()
});

/**
 * NOT an allOf of ResourceCommon on purpose: like a tag, a firewall group is a vocabulary entry, not a taggable kind — it borrows only the common fields it genuinely has. `name` is unique within its scope only (the tag rule: sibling and ancestor scopes may hold homonyms, disambiguated by the qualified reference form); ancestor groups are usable downward.
 *
 */
export const zFirewallGroup = z.object({
    etag: zEntityTag.optional(),
    id: z.uuid().readonly().optional(),
    scope: z.string().max(255).regex(/^(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){0,7}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/).optional(),
    notes: z.string().max(65536).optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    updatedAt: z.iso.datetime().readonly().optional(),
    name: z.string().min(1).max(63).regex(/^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?$/),
    description: z.string().max(255).nullish(),
    enforced: z.boolean().optional().default(false),
    rules: z.array(zFirewallRule).max(256).optional()
});

export const zFirewallGroupList = z.object({
    items: z.array(zFirewallGroup),
    nextCursor: z.string().optional()
});

/**
 * A configured storage backend holding inventory (disks, catalog files, backups, app volumes — parent spec §6.1). `driver` and its configuration block are immutable after creation (`422 immutable_field`): a storage is where data lives, not a setting to repoint.
 *
 */
export const zStorage = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    driver: zStorageDriver,
    dir: z.object({
        path: z.string()
    }).optional(),
    nfs: z.object({
        server: z.string(),
        export: z.string(),
        options: z.string().optional()
    }).optional(),
    cifs: z.object({
        server: z.string(),
        share: z.string(),
        credential: z.string().optional(),
        username: z.string().optional(),
        options: z.string().optional()
    }).optional(),
    iscsi: z.object({
        portal: z.string(),
        target: z.string(),
        credential: z.string().optional(),
        lun: z.int().gte(0).optional()
    }).optional(),
    'nvme-tcp': z.object({
        address: z.string(),
        port: z.int().gte(1).lte(65535).optional(),
        targetNqn: z.string(),
        nsid: z.int().gte(1).optional(),
        credential: z.string().optional()
    }).optional(),
    rbd: z.object({
        monitors: z.array(z.string()).min(1),
        pool: z.string(),
        credential: z.string(),
        cluster: z.string().optional()
    }).optional(),
    cephfs: z.object({
        monitors: z.array(z.string()).min(1),
        fs: z.string(),
        path: z.string().optional(),
        credential: z.string(),
        cluster: z.string().optional()
    }).optional(),
    supportedContent: z.array(z.enum([
        'disks',
        'catalogFiles',
        'backups',
        'appVolumes'
    ])).readonly().optional(),
    defaults: z.object({
        diskFormat: zDiskFormat.optional().default('qcow2')
    }).optional(),
    allocationPolicy: z.object({
        reserveGiB: z.int().gte(0).optional().default(5)
    }).optional(),
    shared: z.boolean().optional(),
    reachableFrom: z.object({
        nodes: z.array(z.string()).optional(),
        nodeTags: zNodeSelector.optional(),
        resolved: z.array(z.string()).readonly().optional()
    }).optional(),
    sharedProbe: z.object({
        verified: z.enum([
            'verified',
            'disputed',
            'unknown'
        ]).optional(),
        checkedAt: z.iso.datetime().optional(),
        nodes: z.array(z.string()).optional(),
        declaredNotSeen: z.array(z.string()).optional(),
        seenNotDeclared: z.array(z.string()).optional(),
        mismatches: z.array(z.object({
            observer: z.string().optional(),
            writer: z.string().optional(),
            reason: z.enum(['probe-missing', 'probe-stale']).optional(),
            lastSeenWriteAt: z.iso.datetime().optional()
        })).optional()
    }).readonly().optional(),
    observedAt: z.iso.datetime().readonly().optional(),
    lostAt: z.iso.datetime().readonly().optional(),
    phase: z.enum([
        'ready',
        'degraded',
        'error'
    ]).readonly().optional(),
    capacityGiB: z.number().readonly().optional(),
    usedGiB: z.number().readonly().optional(),
    availableGiB: z.number().readonly().optional(),
    provisionedGiB: z.number().readonly().optional(),
    diskCount: z.int().readonly().optional(),
    filesystem: z.object({
        type: z.string().optional(),
        mount: z.string().optional()
    }).readonly().optional()
}));

export const zStorageList = z.object({
    items: z.array(zStorage),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * One row of the read-only per-storage census (round K1, inventory v2b #2): a TVirt object living on this storage, whatever its domain. Derived from the CONFIGURATION rows referencing the storage — never an on-disk walk (that is `scan`) — and strictly read-only: every object is managed from its own section ("Gestisci in …"), the census only tells you it is here.
 *
 */
export const zStorageObject = z.object({
    kind: z.enum([
        'disk',
        'catalogFile',
        'ociBlob',
        'checkpoint',
        'vmMetadata',
        'appRoot',
        'appVolume'
    ]),
    id: z.string(),
    name: z.string(),
    scope: z.string().optional(),
    sizeBytes: z.int().nullish(),
    detail: z.string().optional()
});

export const zStorageFinding = z.object({
    id: z.uuid(),
    kind: z.enum([
        'disk',
        'catalogFile',
        'vmMetadata',
        'namespace',
        'foreign',
        'volumeGroup'
    ]),
    foundId: z.string(),
    conflict: z.boolean(),
    details: z.record(z.string(), z.unknown()).optional(),
    dismissed: z.boolean(),
    scannedAt: z.iso.datetime()
});

/**
 * A named keystore entry. `kind` and `scope` are immutable after creation; `name` is writable (unique per scope). `secret` is REQUIRED at creation, WRITE-ONLY forever, optional on PATCH — present means rotation.
 *
 */
export const zCredential = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum([
        'usernamePassword',
        'iscsiChap',
        'nvmeDhchap',
        'cephx',
        'ca',
        'certificate',
        'vpnKey',
        'luksKey',
        'webhookSecret'
    ]).optional().default('usernamePassword'),
    username: z.string().optional(),
    x509: z.object({
        subject: z.string(),
        issuer: z.string(),
        serial: z.string(),
        notBefore: z.iso.datetime(),
        notAfter: z.iso.datetime(),
        san: z.array(z.string()).optional(),
        fingerprint: z.string(),
        keyType: z.string(),
        ca: z.uuid().optional(),
        revokedAt: z.iso.datetime().optional()
    }).readonly().optional()
}));

export const zCredentialList = z.object({
    items: z.array(zCredential),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

export const zDiskList = z.object({
    items: z.array(zDisk),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * Per-kind census of the catalog's files, for the list surface.
 */
export const zCatalogFileCounts = z.object({
    total: z.int(),
    iso: z.int(),
    ctRootfs: z.int(),
    vmTemplate: z.int(),
    ociImage: z.int(),
    firmwareOvmf: z.int()
}).readonly();

/**
 * A named, scoped container of catalog files. No folders inside (for now); tags go on the files. The contents live at `/api/v6/catalogFiles` (`?filter=catalog:eq:"<id>"`); the render carries only the counts — a catalog can hold many large artefacts.
 *
 */
export const zCatalog = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    name: z.string().min(1).max(63).regex(/^(TVirt-[A-Za-z0-9][A-Za-z0-9-]{0,55}|[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)$/),
    files: zCatalogFileCounts.optional()
}));

export const zCatalogList = z.object({
    items: z.array(zCatalog),
    nextCursor: z.string().optional()
});

/**
 * Immutable after creation.
 */
export const zCatalogFileSource = z.object({
    type: z.enum([
        'upload',
        'url',
        'import',
        'embedded'
    ]),
    url: z.url().optional(),
    checksum: z.object({
        sha256: z.string().regex(/^[0-9a-f]{64}$/).optional()
    }).optional()
});

/**
 * An inert artefact on a storage, registered inside a catalog. The resource is a LINK to an underlying storage OBJECT (round #93): `name`, `catalog`, `notes` and `tags` belong to the link; `storage`, `kind`, `format`, `source`, `phase`, `sizeBytes` and `sha256` describe the shared object — identical across every link born of a copy. Content is immutable once ready: replacing means a new file. Uploads: create with `source.type: upload` (phase `awaiting-upload`), then deliver the bytes with `PUT …/content`. `source.type: import` is reserved to the flows that adopt bytes already on the storage (the storage scan&import, the unused-disk conversion of round #107) — it is never accepted from a direct create (`422`).
 *
 */
export const zCatalogFile = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^(TVirt-[A-Za-z0-9][A-Za-z0-9-]{0,55}|[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)$/),
    catalog: z.string(),
    kind: zCatalogFileKind,
    format: z.enum([
        'iso',
        'tar',
        'tar.gz',
        'tar.xz',
        'tar.zst',
        'tar.bz2',
        'tvtpl',
        'ova',
        'ovf',
        'qcow2'
    ]).optional(),
    storage: z.string().nullable(),
    source: zCatalogFileSource,
    object: z.uuid().readonly().optional(),
    objectLinks: z.int().readonly().optional(),
    phase: z.enum([
        'awaiting-upload',
        'downloading',
        'verifying',
        'ready',
        'deleting',
        'error'
    ]).readonly().optional(),
    sizeBytes: z.int().readonly().optional(),
    virtualSizeBytes: z.int().readonly().optional(),
    sha256: z.string().readonly().optional(),
    transferredBytes: z.int().readonly().nullish(),
    totalBytes: z.int().readonly().nullish(),
    usedByVMs: z.int().readonly().optional(),
    usedBy: z.object({
        vms: z.array(z.string()).optional(),
        disks: z.array(z.string()).optional()
    }).readonly().optional()
}));

export const zCatalogFileList = z.object({
    items: z.array(zCatalogFile),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * The wanted-state vocabulary of the container family — the Pod's DEFAULT and each instance's own (PD-P19), one list derived by both.
 *
 */
export const zAppDesiredState = z.enum(['running', 'stopped']);

/**
 * A volume DECLARED on the Pod (the k8s split: the Pod declares, the container mounts — `PodContainer.volumeMounts`). Data lives PER INSTANCE at `<storage>/apps/<app id>/volumes/<name>/` (PD-P13, no volume shared between instances in v1).
 *
 */
export const zPodVolume = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum(['persistent', 'ephemeral']),
    storage: z.string().nullish()
});

export const zPodVolumeMount = z.object({
    volume: z.string(),
    mountPath: z.string().min(1).max(4096).regex(/^\/.*$/),
    readOnly: z.boolean().optional().default(false)
});

/**
 * A host-device requirement by CLASS (round #330 — the `VmUsb`/`VmGpu` model, deliberately NOT its claim): the entry names a tag of kind `gpu-device` OR `usb-device` and one of its values (e.g. `gpu_class=transcode`), never a concrete card or port. The exact device stays expressible as a one-member class, exactly as for a VM.
 *
 * ONE array for both families, where a VM has two. That is not an oversight: for a VM the two are different hardware paths with different lifecycles (a VFIO PCI function passed at cold boot versus a `usb-host` device on an xHCI controller that hotplugs). For a container there is ONE mechanism — a path under `/dev` handed to `podman --device` — and two arrays would be two names for one thing. The tag's kind already says which family an entry belongs to, so nothing is lost.
 *
 * What an entry RESOLVES to is every registration of that class marked `use: app` on the instance's node — see `deviceResolution`. A container does not CLAIM: the kernel device node it opens is shareable by construction, and TVirt has no way to enforce an exclusivity it would have invented. So the resolution is a deterministic function of the node's registrations, recomputed at every container creation, and there is no claim table, no release path and nothing to leak.
 *
 * Consequences, all deliberate: since the Pod round the class is a PLACEMENT gate — a node that does not carry it is not a candidate for the instance, and the refusal names the nodes that do; a MOVE re-validates the same way. Adding, removing or re-pointing an entry is a real change to the container: the render hash moves and the container is recreated at the next converge, like the image digest.
 *
 */
export const zAppDevice = z.object({
    index: z.int().gte(1).lte(4),
    tag: z.string(),
    value: z.string().min(1).max(253),
    tagId: z.uuid().readonly().optional(),
    valueId: z.uuid().readonly().optional()
});

/**
 * cgroup LIMITS only (the cgroup is the truth; `requests` are deliberately absent — without a resource scheduler a request would be a lie). The memory limit is ALSO the placement memory of the instance (PD-P18: the instance's Proximity memory is the sum of its containers' memory limits, 0 when none is declared — Apps stay outside the host reservation).
 *
 */
export const zPodContainerResources = z.object({
    limits: z.object({
        cpu: z.number().gte(0.1).lte(1024).nullish(),
        memory: z.int().gte(16).lte(4194304).nullish()
    }).optional()
});

/**
 * An exec liveness probe (PD-P12): `podman healthcheck run` driven by the worker's supervision pass every tick (`--health-interval disable`: podman's own timer would be a systemd transient unit, the «no systemd, no quadlet» doctrine forbids it — measured on the box, P0). `ready` = running ∧ healthy (or no probe); `retries` consecutive failures make the pass restart the container like a dead one.
 *
 */
export const zPodContainerHealth = z.object({
    cmd: z.array(z.string().max(4096)).min(1).max(64),
    timeoutSeconds: z.int().gte(1).lte(300).optional().default(10),
    retries: z.int().gte(1).lte(10).optional().default(3)
});

/**
 * One container of the template. `initContainers` run to completion IN ORDER before the `containers` start (each must exit 0; a failure lands the instance in `failed`/`error` with the exit code observed); the `containers` are started together and supervised per `restartPolicy`. Every container of an instance shares the pod's network (one IP, `localhost` between them), IPC and UTS namespaces — a container that asks for its own network is refused by construction.
 *
 */
export const zPodContainer = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    image: z.string().min(1).max(512),
    imagePolicy: z.enum(['pinned', 'tracking']).optional().default('pinned'),
    imageRepository: z.string().readonly().optional(),
    imageBranch: z.string().readonly().nullish(),
    imageDigest: z.string().readonly().optional(),
    command: z.array(z.string().max(4096)).max(256).nullish(),
    args: z.array(z.string().max(4096)).max(256).nullish(),
    env: z.record(z.string(), z.string().max(4096)).optional(),
    secretFields: z.array(z.string().max(200)).max(64).optional(),
    volumeMounts: z.array(zPodVolumeMount).max(32).optional(),
    devices: z.array(zAppDevice).max(4).optional(),
    resources: zPodContainerResources.optional(),
    health: zPodContainerHealth.nullish()
});

/**
 * The instance census, derived on read from the App rows and their observed state.
 */
export const zPodInstances = z.object({
    desired: z.int().optional(),
    ready: z.int().optional(),
    running: z.int().optional(),
    error: z.int().optional(),
    updated: z.int().optional()
}).readonly();

/**
 * The template with replicas (≈ k8s Deployment + PodTemplate). Everything here is the TEMPLATE the instances derive from; the instances are Apps (`/api/v6/apps`, filter `pod:eq:<id>`), named `<pod>-<index>` with a STABLE ordinal (scale-down removes from the top, scale-up fills the lowest gaps — the StatefulSet idiom). `desiredState` is the DEFAULT of the instances (PD-P19): the Pod's actions write ALL the instances, the per-instance actions write their own — an instance diverging from the Pod is shown as such. Editing the template bumps `templateRevision` and every instance converges at its own tick behind its own hash (no sequencer, no `strategy` field until a rolling update exists, PD-P9); the Pod's `restart` IS a rolling restart (one child task per instance, gated in sequence).
 *
 */
export const zPod = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(58).regex(/^[a-z0-9]([a-z0-9-]{0,56}[a-z0-9])?$/),
    replicas: z.int().gte(0).lte(64).optional().default(1),
    desiredState: zAppDesiredState.optional().default('stopped'),
    restartPolicy: z.enum([
        'always',
        'onFailure',
        'never'
    ]).optional().default('always'),
    terminationGracePeriodSeconds: z.int().gte(1).lte(600).optional().default(30),
    hostname: z.string().max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).nullish(),
    shareProcessNamespace: z.boolean().optional().default(false),
    network: z.string().nullish(),
    overlay: z.string().nullish(),
    bridge: z.string().nullish(),
    networkName: z.string().readonly().nullish(),
    ipAssignment: z.string().optional().default('static'),
    ips: z.array(z.string().max(18)).max(64).optional(),
    gateway: z.string().nullish(),
    dnsServers: z.array(z.string()).optional(),
    firewallGroups: z.array(z.string()).optional(),
    firewallEnabled: z.boolean().optional().default(false),
    firewallAntiSpoof: z.boolean().optional().default(false),
    rootKind: z.enum(['ephemeral', 'persistent']).optional(),
    rootStorage: z.string().nullish(),
    ephemeralStorage: z.string().nullish(),
    volumes: z.array(zPodVolume).max(32).optional(),
    archMovePolicy: z.enum(['refuse', 'reresolve']).optional().default('refuse'),
    preferNodes: z.record(z.string(), z.string()).optional(),
    requireNodes: z.record(z.string(), z.string()).optional(),
    spread: z.enum([
        'preferred',
        'required',
        'none'
    ]).optional().default('preferred'),
    ha: z.boolean().optional().default(false),
    initContainers: z.array(zPodContainer).max(16).optional(),
    containers: z.array(zPodContainer).min(1).max(16),
    templateRevision: z.int().readonly().optional(),
    instances: zPodInstances.optional(),
    deletingAt: z.iso.datetime().readonly().nullish()
}));

export const zPodList = z.object({
    items: z.array(zPod),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

export const zAppKeptData = z.object({
    app: z.string().optional(),
    name: z.string(),
    storage: z.string(),
    path: z.string()
});

export const zPodDeleteResult = z.object({
    task: zTask,
    keptData: z.array(zAppKeptData)
});

/**
 * One container of the instance, as observed by the serving node's worker (the k8s `containerStatuses` shape, filled ONLY with what was observed): the template says what it should be, this says what it is.
 *
 */
export const zAppContainerStatus = z.object({
    name: z.string().optional(),
    init: z.boolean().optional(),
    imageDigest: z.string().optional(),
    runningDigest: z.string().nullish(),
    state: z.enum([
        'waiting',
        'running',
        'terminated'
    ]).nullish(),
    exitCode: z.int().nullish(),
    startedAt: z.iso.datetime().nullish(),
    finishedAt: z.iso.datetime().nullish(),
    restartCount: z.int().optional(),
    lastRestartAt: z.iso.datetime().readonly().nullish(),
    nextRestartAt: z.iso.datetime().readonly().nullish(),
    ready: z.boolean().optional(),
    health: z.enum(['healthy', 'unhealthy']).nullish(),
    containerId: z.string().nullish(),
    lastError: z.string().nullish()
}).readonly();

/**
 * ONE instance of a Pod: a `podman pod` on the node its authority names, with one IP plumbed by TVirt and the template's containers inside. The template part is READ from the Pod (`pod`); this object carries the per-instance facts and the observed state, findings-class — stale reads degrade honest (`phase: unknown`, `lastError` preserved).
 *
 */
export const zApp = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().readonly().optional(),
    pod: z.uuid().readonly().optional(),
    podName: z.string().readonly().optional(),
    replicaIndex: z.int().readonly().optional(),
    node: z.string().readonly().nullish(),
    ha: z.boolean().readonly().optional(),
    desiredState: zAppDesiredState.readonly().optional(),
    ip: z.string().readonly().nullish(),
    hostname: z.string().readonly().optional(),
    templateRevision: z.int().readonly().optional(),
    templateRevisionApplied: z.int().readonly().nullish(),
    deletingAt: z.iso.datetime().readonly().nullish(),
    network: z.string().readonly().nullish(),
    overlay: z.string().readonly().nullish(),
    bridge: z.string().readonly().nullish(),
    networkName: z.string().readonly().nullish(),
    containers: z.array(zAppContainerStatus).readonly().optional(),
    deviceResolution: z.array(z.object({
        container: z.string().optional(),
        index: z.int().optional(),
        registrationId: z.uuid().optional(),
        kind: z.enum(['gpu-device', 'usb-device']).optional(),
        node: z.string().optional(),
        name: z.string().optional(),
        paths: z.array(z.string()).optional()
    })).readonly().optional(),
    phase: z.enum([
        'pending',
        'running',
        'degraded',
        'succeeded',
        'failed',
        'stopped',
        'error',
        'unknown'
    ]).readonly().optional(),
    infraPid: z.int().readonly().nullish(),
    startTime: z.iso.datetime().readonly().nullish(),
    lastError: z.string().readonly().nullish(),
    reportObservedAt: z.iso.datetime().readonly().nullish()
}));

export const zAppList = z.object({
    items: z.array(zApp),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * The optional body of the restart action: without it the WHOLE pod bounces; with `container` ONE member is restarted alone (stop, and for an ephemeral root a fresh container, then start) — the infra keeps its pid and namespace, the sisters never notice (§4.4). A manual restart resets the container's backoff and does not count in `restartCount`.
 *
 */
export const zAppRestartRequest = z.object({
    container: z.string().max(63).optional()
});

export const zAppMoveRequest = z.object({
    node: z.union([
        z.string(),
        zNodeSelector
    ])
});

/**
 * The two shapes of an exec session (round #98, T98-P2) — ONE mechanism, two modes, because a shell IS the exec of a shell with a terminal and two surfaces would pretend two powers. `tty`: the command gets a pty (the interactive shell, `top`, `vim`) — one byte stream both ways, the terminal geometry on the control channel. `pipe`: the one-shot — no pty, stdout and stderr stay two distinct streams, stdin is a pipe the client may close, the exit code is the last word.
 *
 */
export const zAppExecMode = z.enum(['tty', 'pipe']);

/**
 * One dimension of a terminal, in character cells — declared ONCE here and referenced by everything that carries a geometry: the CT console (round #356, where it was born as `CtConsoleGeometryAxis`) and the App exec session (round #98, T98-P2, which promoted it to common because two surfaces saying the same bound in two places are two chances to disagree). The number is checked in three places (the mint, the bridge's control channel, and again by the worker, which does not trust a socket that is 0666 in the run dir). Bounded on BOTH ends because the value reaches a `TIOCSWINSZ` on a pty: `0` is the un-set state this exists to leave behind — measured, a fresh pty answers `0 0` — and a figure large enough to make a curses program allocate a grid the size of a disk is a denial of service dressed as a window. The bounds `1..1000` are T98-P8 (proposed to ratification: no real terminal exceeds a thousand cells per axis; the ceiling is the belt against the 0666 socket, not a feature).
 *
 */
export const zTerminalGeometryAxis = z.int().gte(1).lte(1000);

/**
 * What the mint of an exec session declares (round #98). `argv` is the command as an ARRAY — never a shell string: the worker hands it to `podman exec` element by element, so no quoting of the caller's is ever interpreted on the node. REQUIRED in `pipe` mode; optional in `tty` mode, where it defaults to `/bin/sh`. The geometry is the `tty` half only: both axes or neither. `container` picks the container of the instance (default: the first non-init container of the template — the `kubectl.kubernetes.io/default-container` idiom).
 *
 */
export const zAppExecRequest = z.object({
    mode: zAppExecMode,
    container: z.string().max(63).optional(),
    argv: z.array(z.string().max(4096)).min(1).max(256).optional(),
    rows: zTerminalGeometryAxis.optional(),
    cols: zTerminalGeometryAxis.optional()
});

/**
 * What a freshly minted exec ticket grants (round #98) — the CT console `CtConsoleAccess` idiom, one runtime over: a URL whose path segment IS the credential.
 *
 */
export const zAppExecAccess = z.object({
    execWebSocketUrl: z.string(),
    mode: zAppExecMode
});

/**
 * Where to write the backup. Nothing else is a knob (the shared rule): the artefact's shape is not an operator choice — persistent travels, ephemeral is excluded per ratifica — and the consistency mechanism is decided by the App's own state.
 *
 */
export const zCreateAppBackupRequest = z.object({
    destination: z.string()
});

/**
 * One external OCI registry in the anagraphics (Annex Q.2): a named reference to Docker Hub, GHCR, ECR, Quay or a generic self-hosted registry (Harbor, plain registry:2, …). Credentials never ride the resource: `credential` references the cluster keystore and workers resolve it from their local replica at use time — no per-node `auth.json`, ever. The verify observed group (`verifiedAt` / `verifyOk` / `verifyMessage`) is written by the verify task: an honest login/list probe with skopeo, never a stored claim.
 *
 */
export const zOciRegistry = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?$/),
    provider: z.enum([
        'dockerhub',
        'ghcr',
        'ecr',
        'quay',
        'generic'
    ]),
    url: z.url().max(512).optional(),
    credential: z.string().nullish(),
    verifiedAt: z.iso.datetime().readonly().optional(),
    verifyOk: z.boolean().readonly().optional(),
    verifyMessage: z.string().readonly().nullish()
}));

export const zOciRegistryList = z.object({
    items: z.array(zOciRegistry),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * One repository on a registry, with the branches (OCI tags) the cluster OBSERVES — the remote-tracking-branch of the model (Annex Q.3). Registering never downloads anything: it promotes implicit tracking to explicit configuration. Auto-create is implicit and documented: any reference to a branch not yet listed (a pull, a refresh naming it) mints its `OCIBranch` as observed. An observed branch IS an OCIBranch (R37): `branches` is the derived list of their names and the per-branch resolution/reflog lives on the branch, where it belongs. Refresh is still the repository's "git fetch": every observed branch is resolved via `skopeo inspect`, the resolution is stamped on its branch and the previous one is kept in the per-branch reflog (last 10). Repositories of provenance `catalog` (ratifica 2026-07-30 sera — the air-gapped import of a catalog `oci-image` archive) have NO registry: their synthetic reference is `catalog/<catalogName>/<fileName>` (branch = file name, digest = commit — the tag-as-branch model verbatim) and they are minted by the import action only, never POSTed here.
 *
 */
export const zOciRepository = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    registry: z.string().nullable(),
    registryName: z.string().readonly().optional(),
    provenance: z.enum(['registry', 'catalog']).readonly().optional(),
    path: z.string().min(1).max(255).regex(/^[a-z0-9]+(?:(?:[._]|__|-+)[a-z0-9]+)*(?:\/[a-z0-9]+(?:(?:[._]|__|-+)[a-z0-9]+)*)*$/),
    branches: z.array(z.string().min(1).max(128).regex(/^[a-zA-Z0-9_][a-zA-Z0-9._-]{0,127}$/)).readonly().optional(),
    imageCount: z.int().readonly().optional(),
    refreshedAt: z.iso.datetime().readonly().optional(),
    refreshError: z.string().readonly().nullish()
}));

export const zOciRepositoryList = z.object({
    items: z.array(zOciRepository),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * One architecture's content inside a branch's resolution (R51). The pair is the whole point: on a mixed cluster «the digest of `latest`» is not a question with one answer, and pretending otherwise is how a list ended up wearing the arch of the worker that resolved it.
 *
 */
export const zOciArchDigest = z.object({
    arch: zArchitecture,
    digest: z.string().regex(/^sha256:[a-f0-9]{64}$/)
});

/**
 * One past resolution of a branch (the reflog entry — Annex Q.3). The history is resolution history, not cache history: it survives the eviction of the digests it names. R51 — an entry records the WHOLE set resolved at that refresh, one digest per architecture, because that is what the branch pointed at. A reflog of single digests could only answer "what was latest, for the arch of the node that asked", which is not a history — it is N histories interleaved.
 *
 */
export const zOciReflogEntry = z.object({
    digests: z.array(zOciArchDigest),
    resolvedAt: z.iso.datetime(),
    listDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/).nullish()
});

/**
 * THE BRANCH — the third of the four OCI levels (R37/R39, ratifica Luca 2026-08-13): `OCIRegistry` is `docker.io`, `OCIRepository` is `docker.io/nginx`, an **OCIBranch** is `docker.io/nginx:latest`, and an `OCIImage` is `docker.io/nginx:latest@sha256:…`. This resource was called `OCIImage` between the collector round (2026-08-05) and R37: it is the same object, renamed to the name that belongs to it, so that the word "image" means the same thing in the GUI, the API, the CLI and the manual. An OCIBranch IS one observed branch of a repository: identity is the pair `(repository, branch)`, unique per repository (`409 conflict`), and the name one pronounces is the reference the Apps already write — `<registry>/<path>:<branch>`. It is a GOVERNED object (notes, tags, `POST`/`PATCH`/`DELETE`). Creating one observes a branch; deleting it stops observing it. R40 — THE BRANCH IS WHERE THE DIGESTS LIVE, as the chronology of the refreshes from upstream: `currentDigest` is the head and `reflog` is the history, and that history is the load-bearing structure of the object, not an accessory. Which images a branch has named is therefore a JOIN over that chronology — calculated at render time, never stored (the same discipline the wave-1 `branches` array already followed). The branch does NOT carry a digest register any more: the digests are `OCIImage` resources of their own, listable with `GET /oci/images?branch=…`. R44.2/R48 — a branch referenced by an App cannot be deleted, pinned or tracking alike (`409 speaking`): the App needs the NAME, because the name is the address one returns to in order to learn whether a new version came out.
 *
 */
export const zOciBranch = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    repository: z.string(),
    registry: z.string().readonly().nullish(),
    registryName: z.string().readonly().optional(),
    branch: z.string().min(1).max(128).regex(/^[a-zA-Z0-9_][a-zA-Z0-9._-]{0,127}$/),
    reference: z.string().readonly(),
    currentDigests: z.array(zOciArchDigest).readonly().optional(),
    currentListDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/).readonly().nullish(),
    resolvedAt: z.iso.datetime().readonly().optional(),
    reflog: z.array(zOciReflogEntry).readonly().optional(),
    imageCount: z.int().readonly().optional()
}));

export const zOciBranchList = z.object({
    items: z.array(zOciBranch),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * One NAME that reaches an image (R39/R40) — derived, never stored. The four levels are all here, which is why the App lockfile `<registry>/<path>:<branch>@<digest>` needed no change of grammar: it was always a name plus a content.
 *
 */
export const zOciImageName = z.object({
    repository: z.string(),
    registry: z.string().nullish(),
    registryName: z.string().optional(),
    branch: z.string().nullish(),
    branchName: z.string().nullish(),
    reference: z.string(),
    head: z.boolean(),
    listDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/).nullish()
});

/**
 * One consumer of a digest, named so the refusal can name it.
 */
export const zOciImageUsage = z.object({
    kind: z.enum(['app']),
    name: z.string(),
    role: z.enum(['lockfile', 'running'])
});

/**
 * THE IMAGE — the fourth OCI level and the CONTENT itself (R39, ratifica Luca 2026-08-13). An image is identified by its DIGEST and carries NO name: no registry, no repository, no branch. The disk had already said so before the model did — the pull and the catalog import both compose their destination as `path.join(cacheDir, digest)`, one directory per digest — while the register said N rows for one content and bridged the two truths with a lookup before every delete. That lookup is now the model: one row per digest, and the names live among the names. R41 — images are unique per CLUSTER; the REFERENCES to them are per SCOPE (`views`). "Se un dipartimento elimina un'immagine usata da un altro, ha eliminato solo la sua view": `DELETE` removes the caller's view, never another tenant's content. R46 — but the last view that goes takes the bytes with it, because the existence of an image on TVirt IS the set of its references. It is `docker image rm`: dropping the last tag makes the image go away. This is NOT the automatic retention excluded on 2026-08-05 (a GC by time or pressure, deleting what nobody asked to delete) — it is the deterministic consequence of an act. R42 — the image STAYS WHERE IT IS: `storage` is where it landed, and a scope whose cache pool differs neither duplicates nor moves it. The scope cache decides where NEW images land, not where the existing ones must live. R45 draws the consequence at placement time. R47 — a manifest list is a NAME, not an image: only the per-architecture digests exist as images, each with its `arch` (R43), and the list digest is a way of ARRIVING at one of them — it lives among the names, in `names[].listDigest`.
 *
 */
export const zOciImage = zResourceCommon.and(z.object({
    digest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    arch: zArchitecture.readonly(),
    sizeBytes: z.int().readonly().nullish(),
    layerCount: z.int().readonly().nullish(),
    mediaType: z.string().readonly().nullish(),
    pulledAt: z.iso.datetime().readonly().optional(),
    storage: z.string().readonly().nullish(),
    nodes: z.array(z.string()).readonly().optional(),
    names: z.array(zOciImageName).readonly().optional(),
    usedBy: z.array(zOciImageUsage).readonly().optional(),
    views: z.int().readonly().optional(),
    lastView: z.boolean().readonly().optional()
}));

export const zOciImageList = z.object({
    items: z.array(zOciImage),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * IL RIFERIMENTO SOPRAVVISSUTO AL CONTENUTO. A claim is what a scope's view becomes when the content stops being materialized: the register row is gone, so the image does not exist (R46 — existence IS the set of references), but "quale scope nominava questo digest" survives. It is the ONE thing a re-adoption could never rebuild: no byte in a blob directory knows which department it belonged to. That is why claims are written by the 0206 sweep and by `storage.delete?releaseImages=true`, and why `adoptObservedBlobs` turns them back into views the moment the content is observed again. #370 — AND THAT IS WHY THEY DO NOT EXPIRE. A claim is cheap to keep (one row per digest and scope) and irreplaceable once lost, so no timer deletes them: what bounds them is an operator who can SEE them and drop the ones that will never come back (`DELETE /api/v6/oci/claims/{digest}`). Before this list they were invisible — a released pool that never came back left rows nobody could name.
 *
 */
export const zOciViewClaim = z.object({
    digest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    scope: z.uuid(),
    scopePath: z.string(),
    createdAt: z.iso.datetime().readonly()
});

export const zOciViewClaimList = z.object({
    items: z.array(zOciViewClaim),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * Provenance, immutable after creation — exactly ONE of the two sides. A reference, never a dependency (materializing = copying, v2g #1): the referenced object may die later (the render then carries null) and the CT does not care — the bytes were copied.
 *
 */
export const zCtSource = z.object({
    file: z.string().nullish(),
    template: z.string().nullish()
});

/**
 * The VM trunk idiom verbatim (R4, host-network-design.md §9): exactly one attachment per NIC — a fabric logical network (the RECOMMENDED way, A1), an NM host bridge BY NAME, or an overlay trunk (untagged and/or tagged). The firewall trio (`firewallGroups`, `firewallEnabled`, `firewallAntiSpoof`) is the VmNic surface verbatim (#168, ratified: VM + CT + App together — the enforcement is one chain keyed on the interface name, tap or veth alike).
 *
 */
export const zCtNic = z.object({
    index: z.int().gte(1).lte(6),
    network: z.string().nullish(),
    networkName: z.string().readonly().nullish(),
    bridge: z.string().nullish(),
    untagged: z.string().nullish(),
    tagged: z.record(z.string(), z.string()).optional(),
    mac: z.string().regex(/^([0-9a-fA-F]{2}:){5}[0-9a-fA-F]{2}$/).optional(),
    connected: z.boolean().optional().default(true),
    firewallGroups: z.array(z.string()).optional(),
    firewallEnabled: z.boolean().optional().default(false),
    firewallAntiSpoof: z.boolean().optional().default(false)
});

/**
 * A container, config-side (round K2 + the #61 config-side slice — the liblxc runtime is #61 proper). A TEMPLATE births from a catalog file (`source.file`, kind `ct-rootfs`) and rests `ready`; a plain CT births from a template (`source.template`), inherits its metadata as defaults and rests `defined`. Creation answers `201` + `X-TVirt-Task` while the worker builds the rootfs onto `storage`.
 *
 */
export const zCt = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    template: z.boolean().optional().default(false),
    source: zCtSource.optional(),
    storage: z.string().optional(),
    arch: zArchitecture.nullish(),
    cpu: z.object({
        cores: z.int().gte(1).optional().default(1),
        limit: z.number().gte(0).nullish(),
        weight: z.int().gte(1).lte(10000).optional().default(100)
    }).optional(),
    memory: z.object({
        sizeMiB: z.int().gte(32)
    }).optional(),
    nics: z.array(zCtNic).max(6).optional(),
    hostname: z.string().max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).nullish(),
    dns: z.object({
        nameservers: z.array(z.string()).max(3).optional(),
        searchDomains: z.array(z.string()).max(6).optional()
    }).optional(),
    phase: z.enum([
        'materializing',
        'defined',
        'ready',
        'starting',
        'running',
        'stopping',
        'stopped',
        'deleting',
        'error'
    ]).readonly().optional(),
    sizeBytes: z.int().readonly().nullish(),
    node: z.string().readonly().nullish(),
    startedAt: z.iso.datetime().readonly().nullish(),
    lastStopReason: z.object({
        voluntary: z.boolean().optional(),
        cause: z.enum([
            'api',
            'ct-exit',
            'host-reboot',
            'start-failed'
        ]).optional(),
        at: z.iso.datetime().optional()
    }).readonly().optional()
}));

export const zCtList = z.object({
    items: z.array(zCt),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * Where to write the backup. Nothing else is a knob on purpose (the CreateVmBackupRequest rule): the artefact's shape is not an operator choice, and the consistency mechanism is decided by the CT's own power state — stopped copies still files, running is frozen for the pack.
 *
 */
export const zCreateCtBackupRequest = z.object({
    destination: z.string()
});

/**
 * The console wire protocol of a CT — a TTY byte stream, and only that (round #347). Declared as an enum of one for the reason the VM's `ConsoleProtocol` is: the day a second one exists, the ticket must not be able to promise what the runtime does not serve. It is deliberately NOT the VM enum: a VM console is a framed display protocol (RFB) and a CT console is a character device — the two never take each other's values, so sharing the enum would only make a wrong value expressible.
 *
 */
export const zCtConsoleProtocol = z.enum(['tty']);

/**
 * What a freshly minted CT console ticket grants (round #347) — the VM `ViewerAccess` idiom, one runtime over.
 *
 */
export const zCtConsoleAccess = z.object({
    consoleWebSocketUrl: z.string(),
    proto: zCtConsoleProtocol
});

/**
 * The OBSERVED state of the CT's console endpoint on its serving node (T157-P8 d, rig 2026-09-06): the worker records, every time it exposes (or fails to expose) the console socket, whether the socket is there and — when it is not — WHY, with the numbers. Before this the cause lived in the worker's stdout alone, and the operator met a WebSocket `1011 endpoint unavailable` forever (a 113-byte socket path against the kernel's 107). Carried by the fate endpoint so a viewer whose socket closed `1011` can read the cause and STOP retrying, and quoted (capped) in the `1011` close reason itself.
 *
 */
export const zCtConsoleEndpoint = z.object({
    available: z.boolean(),
    error: z.string().nullable(),
    at: z.iso.datetime().optional()
});

/**
 * The CT console ticket's fate — the VM `ViewerFate` vocabulary verbatim (see `vms.yaml`), plus the console ENDPOINT's observed state (T157-P8 d): a ticket can be perfectly `active` while the socket behind it cannot be opened, and that is exactly the case the extra field exists for.
 *
 */
export const zCtConsoleFate = z.object({
    fate: zViewerFateKind,
    takenBy: z.string().optional(),
    endpoint: zCtConsoleEndpoint.optional()
});

/**
 * What the mint of a CT exec session declares (T157-P8 f-ii) — the App's `AppExecRequest` one runtime over, minus `container`: a CT is one container. `argv` is the command as an ARRAY, handed to `lxc-attach` element by element (never a shell string); REQUIRED in `pipe` mode, optional in `tty` mode where it defaults to `/bin/sh`. The geometry is the `tty` half only: both axes or neither.
 *
 */
export const zCtExecRequest = z.object({
    mode: zAppExecMode,
    argv: z.array(z.string().max(4096)).min(1).max(256).optional(),
    rows: zTerminalGeometryAxis.optional(),
    cols: zTerminalGeometryAxis.optional()
});

/**
 * What a freshly minted CT exec ticket grants (T157-P8 f-ii) — the App's `AppExecAccess` idiom, one runtime over: a URL whose path segment IS the credential.
 *
 */
export const zCtExecAccess = z.object({
    execWebSocketUrl: z.string(),
    mode: zAppExecMode
});

export const zCephClusterNetworkMember = z.object({
    node: z.string(),
    address: z.string()
});

/**
 * THE cluster-wide OSD flag vocabulary — declared ONCE and referenced both where the flags are OBSERVED (`CephCluster.osdFlags.active[]`) and where one is SET (`setOsdFlag`). The pair had to be derived: a flag that can be observed but not set is a flag the GUI shows lit and cannot turn off, and the two lists were hand copies under two different names (`active` and `flag`), which is precisely the shape the enum census could not see until 2026-08-11. `CEPH_OSD_FLAGS` in common/ceph.ts and in the webui are pinned to the observed face by the census.
 *
 */
export const zCephOsdFlag = z.enum([
    'noout',
    'norebalance',
    'nobackfill',
    'norecover',
    'pause',
    'noin',
    'nodown'
]);

/**
 * A managed Ceph cluster (Annex N; birth lifecycle ratified 2026-07-24: a CephCluster is NEVER empty or undeployed as a stable state). `POST` births the fabric: `201` + the AUTO bootstrap parent Task (`X-TVirt-Task`, the storage-initDir precedent) — mon + mgr on the declared `controlNode`, `phase: deploying` from the first breath. `not-deployed` survives ONLY as the parking state of a FAILED creation (batch 2026-07-26, item 6 — the destroy path left it: a successful destroy parks `destroyed` and the periodic backend sweeper deletes the row): retry with the `deploy` action, or `DELETE` the configuration there — nowhere else. Additional mon membership and the OSD device registry are written through actions, never by field writes — a device registered on the live cluster is prepared automatically (the incremental OSD choice (a), RATIFIED 2026-07-24: Ceph is imperative — the backend validates admission and the device row plus its prepare task are one atomic, visible claim). `osdEncryptionMode` governs how NEW OSDs are prepared (round #90, ratified 2026-07-24 — SUPERSEDES the immutable `encryptionAtRest` boolean of the same day: the mixed-fleet worry is answered by the per-row observed state, not by immutability; changing an existing OSD's state means remove + re-create, 2026-07-27).
 *
 */
export const zCephCluster = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    osdEncryptionMode: z.enum([
        'alwaysOn',
        'alwaysOff',
        'perOsd'
    ]).optional(),
    advanced: z.object({
        splitMonMgr: z.boolean().optional(),
        rawCrushEditor: z.boolean().optional()
    }).optional(),
    network: z.object({
        publicCidr: z.string().optional(),
        clusterCidr: z.string().optional(),
        publicMembers: z.array(zCephClusterNetworkMember).nullish(),
        clusterMembers: z.array(zCephClusterNetworkMember).nullish()
    }).optional(),
    mons: z.array(z.object({
        node: z.string()
    })).readonly().optional(),
    mgrs: z.array(z.object({
        node: z.string(),
        address: z.string().optional()
    })).readonly().optional(),
    mds: z.array(z.object({
        node: z.string(),
        address: z.string().optional()
    })).readonly().optional(),
    osdDevices: z.array(z.object({
        node: z.string(),
        name: z.string(),
        serial: z.string(),
        phase: z.enum(['registered', 'active']),
        osdId: z.int().optional(),
        encrypted: z.boolean().optional(),
        encryption: z.enum(['encrypted', 'plain']).optional(),
        address: z.string().optional(),
        clusterAddress: z.string().optional()
    })).readonly().optional(),
    phase: z.enum([
        'not-deployed',
        'deploying',
        'healthy',
        'degraded',
        'error',
        'destroying',
        'destroyed'
    ]).readonly().optional(),
    health: z.object({
        status: z.enum([
            'HEALTH_OK',
            'HEALTH_WARN',
            'HEALTH_ERR'
        ]).optional(),
        checks: z.array(z.object({
            code: z.string(),
            severity: z.string(),
            message: z.string(),
            count: z.int().optional(),
            muted: z.boolean().optional(),
            detail: z.array(z.string()).optional()
        })).optional(),
        observedAt: z.iso.datetime().optional()
    }).readonly().optional(),
    osds: z.array(z.object({
        id: z.int().optional(),
        node: z.string().optional(),
        up: z.boolean().optional(),
        in: z.boolean().optional(),
        encrypted: z.boolean().optional()
    })).readonly().optional(),
    osdFlags: z.object({
        active: z.array(zCephOsdFlag).optional(),
        observedAt: z.iso.datetime().optional()
    }).readonly().optional(),
    pools: z.object({
        items: z.array(z.object({
            name: z.string(),
            size: z.int().optional(),
            minSize: z.int().optional(),
            pgNum: z.int().optional(),
            pgAutoscaleMode: z.enum([
                'on',
                'off',
                'warn'
            ]).optional(),
            application: z.string().optional(),
            usedGiB: z.number().optional(),
            objectCount: z.int().optional(),
            crushRule: z.string().optional()
        })).optional(),
        observedAt: z.iso.datetime().optional()
    }).readonly().optional(),
    versions: z.record(z.string(), z.unknown()).readonly().optional(),
    daemons: z.record(z.string(), z.unknown()).readonly().optional(),
    pgs: z.record(z.string(), z.unknown()).readonly().optional(),
    capacity: z.record(z.string(), z.unknown()).readonly().optional(),
    io: z.record(z.string(), z.unknown()).readonly().optional(),
    osdDetails: z.array(z.record(z.string(), z.unknown())).readonly().optional(),
    crush: z.object({
        rules: z.array(z.object({
            name: z.string(),
            ruleId: z.int(),
            type: z.enum(['replicated', 'erasure']).optional(),
            failureDomain: z.string().nullish()
        })).optional(),
        bucketTypes: z.array(z.string()).optional(),
        tree: z.array(z.object({
            name: z.string(),
            type: z.string().optional(),
            children: z.array(z.string()).optional()
        })).optional(),
        observedAt: z.iso.datetime().optional()
    }).readonly().optional(),
    filesystems: z.object({
        items: z.array(z.object({
            name: z.string(),
            metadataPool: z.string().optional(),
            dataPools: z.array(z.string()).optional()
        })).optional(),
        observedAt: z.iso.datetime().optional()
    }).readonly().optional(),
    mdsStatus: z.object({
        daemons: z.array(z.object({
            name: z.string(),
            state: z.string(),
            filesystem: z.string().nullish()
        })).optional(),
        observedAt: z.iso.datetime().optional()
    }).readonly().optional(),
    cephVersion: z.string().readonly().optional(),
    monPorts: z.object({
        msgr2: z.int().optional(),
        msgr1: z.int().optional()
    }).readonly().optional(),
    fsid: z.string().readonly().optional()
}));

export const zCephClusterList = z.object({
    items: z.array(zCephCluster),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * THE cadence vocabulary of the destination's unattended runs — declared ONCE and referenced by every schedule that speaks it (the `StorageDriver` cure, round «errori onesti»). Born inline on the checkpoint `schedule` and, for exactly one round, hand-copied into the data-backup schedule of #28 step 4: the enum census caught the copy the same day it was written, which is the whole reason that census exists. Sharing it is also the DESIGN and not merely hygiene. The data scheduler was ratified as a RECAST of the checkpoint one, not a second engine — the two read the same `scheduleBoundary`/`scheduleDue` code — so the day a round adds a fourth cadence, adding it to one face and not the other would be a bug in every case. A vocabulary two schemas share is derived from one place, and every `$ref` follows the next cadence by itself.
 *
 */
export const zScheduleInterval = z.enum([
    'hourly',
    'daily',
    'weekly'
]);

/**
 * THE KIND AXIS of a data-backup policy (BK5, PROPOSAL 2+3): which of the three sibling object families a policy backs up — VmBackup, CtBackup and AppBackup are three objects by dettatura #278, and their knobs differ (mode/requireQuiesce are VM-only). Declared ONCE and referenced by every site that speaks it (the `ScheduleInterval` idiom, for the same reason): the selector's axis and the per-policy status must never drift apart.
 *
 */
export const zBackupSubjectKind = z.enum([
    'vm',
    'ct',
    'app'
]);

/**
 * One backup destination: a dir-like TVirt storage (dir/nfs/cifs/cephfs — NFS/CIFS on a NAS is off-cluster by nature, the scenario-3 requirement) plus an optional simple schedule, a count-based retention and the declaration of WHAT may land in it (`contents`). Artefacts land under the platform namespace: `<storage>/trantor-tvirt-v6/backups/<destinationId>/`. No destination-level encryption in v1 — the secrets are protected by the per-checkpoint masterpass; whole-artefact encryption is FUTURE.
 *
 */
export const zBackupDestination = zResourceCommon.and(z.object({
    etag: zEntityTag.optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?$/),
    storage: z.string(),
    contents: z.array(z.enum(['checkpoints', 'backups'])).min(1).optional(),
    schedule: z.object({
        interval: zScheduleInterval,
        hour: z.int().gte(0).lte(23).optional(),
        weekday: z.int().gte(0).lte(6).optional()
    }).nullish(),
    retention: z.int().gte(1).lte(1000).nullish(),
    preChangeRetention: z.int().gte(1).lte(1000).nullish(),
    paused: z.boolean().optional().default(false),
    vmBackups: z.object({
        policies: z.array(z.object({
            id: z.uuid().optional(),
            name: z.string().min(1).max(64).regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/),
            selector: z.object({
                kind: zBackupSubjectKind,
                tags: z.record(z.string(), z.string()).optional(),
                scopes: z.array(z.string()).optional()
            }),
            schedule: z.object({
                interval: zScheduleInterval,
                hour: z.int().gte(0).lte(23).optional(),
                weekday: z.int().gte(0).lte(6).optional()
            }).nullish(),
            window: z.object({
                start: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/),
                end: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/)
            }).nullish(),
            mode: z.enum(['full', 'incremental']).optional().default('full'),
            requireQuiesce: z.boolean().optional().default(false),
            paused: z.boolean().optional().default(false),
            retention: z.union([
                z.int().gte(1).lte(1000),
                z.object({
                    keepLast: z.int().gte(1).lte(1000).optional(),
                    daily: z.int().gte(1).lte(1000).optional(),
                    weekly: z.int().gte(1).lte(1000).optional(),
                    monthly: z.int().gte(1).lte(1000).optional(),
                    yearly: z.int().gte(1).lte(1000).optional()
                })
            ]).nullish()
        })).min(1).max(32)
    }).nullish(),
    orphanRuns: z.int().readonly().optional(),
    policyStatus: z.array(z.object({
        id: z.uuid().optional(),
        name: z.string().optional(),
        kind: zBackupSubjectKind.optional(),
        lastRunAt: z.iso.datetime().nullish(),
        lastOutcome: z.string().nullish()
    })).readonly().optional(),
    lastRunAt: z.iso.datetime().readonly().optional()
}));

export const zBackupDestinationList = z.object({
    items: z.array(zBackupDestination),
    nextCursor: z.string().optional(),
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional()
});

/**
 * One checkpoint record — append-only observed rows created by the create-checkpoint task (the findings/oci_*_status precedent), never POSTed or PATCHed. The artefact is ONE downloadable file `<name>-<timestamp>.tvbk`; `manifest` is its cleartext index, recorded verbatim. Delete removes the file from the destination and the row (a task — `202`).
 *
 */
export const zCheckpoint = z.object({
    id: z.uuid().readonly(),
    destination: z.string().readonly(),
    origin: zCheckpointOrigin.readonly().optional(),
    phase: z.enum([
        'running',
        'complete',
        'error'
    ]).readonly().optional(),
    error: z.string().readonly().nullish(),
    families: z.array(z.string()).readonly().optional(),
    secrets: z.enum(['masterpass', 'placeholders']).readonly().optional(),
    fileName: z.string().readonly().nullish(),
    sizeBytes: z.int().readonly().nullish(),
    checksum: z.string().readonly().nullish(),
    generation: z.int().readonly().nullish(),
    manifest: z.record(z.string(), z.unknown()).readonly().optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    completedAt: z.iso.datetime().readonly().nullish()
});

export const zCheckpointList = z.object({
    items: z.array(zCheckpoint),
    nextCursor: z.string().optional()
});

/**
 * The manifest fields the GUI reasons about, structured (the raw manifest stays inside the artefact): where and when the checkpoint was taken, and whether its secrets are real (masterpass) or placeholders.
 *
 */
export const zConfigRestoreManifestInfo = z.object({
    formatVersion: z.int().optional(),
    backupId: z.string().optional(),
    backupCreatedAt: z.iso.datetime().optional(),
    origin: zCheckpointOrigin.optional(),
    clusterId: z.string().optional(),
    clusterName: z.string().optional(),
    generation: z.int().optional(),
    tvirtVersion: z.string().optional(),
    secrets: z.enum(['masterpass', 'placeholders']).optional(),
    sameCluster: z.boolean().optional()
});

export const zConfigRestoreExplorer = z.object({
    families: z.array(z.object({
        name: z.string(),
        tables: z.array(z.object({
            table: z.string(),
            rows: z.int(),
            objects: z.array(z.object({
                key: z.string(),
                name: z.string(),
                encrypted: z.boolean().optional()
            }))
        }))
    }))
});

/**
 * One staged restore artefact (design §Task — UPLOAD-first): born `awaiting-upload` at the POST, filled by chunked PUTs to `/content` (the images/uploadContent precedent — chunks sized under the proxy threshold), opened and indexed by the backend at the final chunk. `ready` serves the explorer (families → named objects, encrypted flags); the restore action then applies a selection. Uploads are ephemeral staging: discarded by DELETE, by a completed restore, or by the sweeper after 24 hours.
 *
 */
export const zConfigRestoreUpload = z.object({
    id: z.uuid().readonly(),
    phase: z.enum([
        'awaiting-upload',
        'ready',
        'error'
    ]).readonly().optional(),
    error: z.string().readonly().nullish(),
    transferredBytes: z.int().readonly().nullish(),
    totalBytes: z.int().readonly().nullish(),
    manifestInfo: zConfigRestoreManifestInfo.readonly().optional(),
    explorer: zConfigRestoreExplorer.readonly().optional(),
    createdAt: z.iso.datetime().readonly().optional()
});

/**
 * The restore PREVIEW (B15/PROPOSAL 15, ratified 2026-08-24): the selection compared per object against the LIVE state, read through the very traversal the pack uses — so the preview and the artefact cannot drift. A pure read: the diff INFORMS, the restore admission DECIDES (#294 — nothing here is consulted by the apply, whose guards re-check everything regardless of what this preview showed).
 *
 */
export const zConfigRestoreDiff = z.object({
    upload: z.string().readonly(),
    artefactGeneration: z.int().readonly().optional(),
    currentGeneration: z.int().readonly(),
    families: z.array(z.object({
        family: z.string(),
        fullFamily: z.boolean(),
        jsonbRefs: z.boolean().optional(),
        objects: z.array(z.object({
            table: z.string(),
            key: z.string(),
            name: z.string(),
            status: z.enum([
                'equal',
                'changed',
                'missing',
                'extra'
            ]),
            changedFields: z.array(z.object({
                field: z.string(),
                current: z.unknown().optional(),
                checkpoint: z.unknown().optional()
            })).optional(),
            secretFields: z.array(z.string()).optional()
        }))
    })).readonly()
});

export const zConfigRestoreSelection = z.object({
    family: z.string(),
    objects: z.array(z.object({
        table: z.string(),
        key: z.string()
    })).optional()
});

export const zConfigRestoreRequest = z.object({
    mode: z.enum(['merge', 'overwrite']),
    selection: z.array(zConfigRestoreSelection).min(1),
    masterpass: z.string().min(1).optional(),
    parachuteDestination: z.string().optional()
});

/**
 * BK5-P3-bis (design session, tornata 5 — 2026-09-03) — WHAT A RECORD SAYS ABOUT THE POLICY THAT SCHEDULED IT. Every policy carries a stable UUIDv7 `id`; the record references that id and keeps the name the policy had when the run was coined. Served resolved against the destination's LIVE policies: while the policy lives, `name` is its CURRENT label (a rename shows through — renaming is free) and `removed` is false; when it no longer lives, `removed` is true and `name` is the coined name — «policy rimossa: <nome>», never a null that loses the provenance. `id` is null only on the rows the upgrade migration could not resolve by name (pre-id orphans), which read as removed too. The whole object is null on a MANUAL run: it never had a policy. Declared once, referenced by VmBackup, CtBackup and AppBackup.
 *
 */
export const zBackupPolicyRef = z.object({
    id: z.uuid().nullable(),
    name: z.string(),
    removed: z.boolean()
}).nullable();

/**
 * One VM data backup — an append-only observed record created by the `vmBackup.create` task, never POSTed or PATCHed (the Checkpoint and findings precedent). The artefact is a DIRECTORY under the destination's tree, indexed by `manifest`, verifiable artefact by artefact through the sha256 the manifest records. Delete removes the directory and the row (a task — `202`).
 *
 */
export const zVmBackup = z.object({
    id: z.uuid().readonly(),
    vm: z.uuid().readonly(),
    vmName: z.string().readonly().optional(),
    scope: z.string().readonly().optional(),
    destination: z.uuid().readonly(),
    mode: z.enum(['full', 'incremental']).readonly().optional(),
    baseBackup: z.uuid().readonly().nullish(),
    origin: z.enum(['manual', 'schedule']).readonly().optional(),
    policy: zBackupPolicyRef.readonly().optional(),
    phase: z.enum([
        'running',
        'complete',
        'error'
    ]).readonly().optional(),
    error: z.string().readonly().nullish(),
    vmState: z.enum(['stopped', 'running']).readonly().optional(),
    contains: z.array(z.enum([
        'disks',
        'varstore',
        'vtpm',
        'snapshots'
    ])).readonly().optional(),
    subjectToRetention: z.boolean().readonly().optional(),
    secretsExposed: z.boolean().readonly().optional(),
    path: z.string().readonly().nullish(),
    sizeBytes: z.int().readonly().nullish(),
    checksum: z.string().readonly().nullish(),
    pointInTime: z.iso.datetime().readonly().nullish(),
    node: z.string().readonly().nullish(),
    manifest: z.record(z.string(), z.unknown()).readonly().optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    completedAt: z.iso.datetime().readonly().nullish()
});

export const zVmBackupList = z.object({
    items: z.array(zVmBackup),
    nextCursor: z.string().optional()
});

/**
 * THE FIRST GESTURE — restore into a machine that does not exist yet. Nothing of the cluster is destroyed by it, which is why it is the one an operator reaches for to CHECK a backup.
 *
 */
export const zRestoreVmBackupToNewVmRequest = z.object({
    name: z.string(),
    scope: z.string().optional(),
    storage: z.string(),
    metadataStorage: z.string().optional(),
    bridge: z.string().optional(),
    network: z.string().optional()
});

/**
 * THE SECOND GESTURE — put the data back into the machine it came from. The destructive one, and cold by construction: the VM must be stopped.
 *
 */
export const zRestoreVmBackupOverRequest = z.object({
    vm: z.uuid()
});

/**
 * One CT data backup — an append-only observed record created by the `ctBackup.create` task, never POSTed or PATCHed (the VmBackup precedent). The artefact is a DIRECTORY under the destination's tree, indexed by `manifest`, verifiable artefact by artefact through the sha256 the manifest records. Delete removes the directory and the row (a task — `202`).
 *
 */
export const zCtBackup = z.object({
    id: z.uuid().readonly(),
    ct: z.uuid().readonly(),
    ctName: z.string().readonly().optional(),
    scope: z.string().readonly().optional(),
    destination: z.uuid().readonly(),
    origin: z.enum(['manual', 'schedule']).readonly().optional(),
    policy: zBackupPolicyRef.readonly().optional(),
    phase: z.enum([
        'running',
        'complete',
        'error'
    ]).readonly().optional(),
    error: z.string().readonly().nullish(),
    ctState: z.enum(['stopped', 'running']).readonly().optional(),
    contains: z.array(z.enum(['rootfs'])).readonly().optional(),
    subjectToRetention: z.boolean().readonly().optional(),
    secretsExposed: z.boolean().readonly().optional(),
    path: z.string().readonly().nullish(),
    sizeBytes: z.int().readonly().nullish(),
    checksum: z.string().readonly().nullish(),
    pointInTime: z.iso.datetime().readonly().nullish(),
    node: z.string().readonly().nullish(),
    manifest: z.record(z.string(), z.unknown()).readonly().optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    completedAt: z.iso.datetime().readonly().nullish()
});

export const zCtBackupList = z.object({
    items: z.array(zCtBackup),
    nextCursor: z.string().optional()
});

/**
 * THE FIRST GESTURE — restore into a container that does not exist yet. Nothing of the cluster is destroyed by it, which is why it is the one an operator reaches for to CHECK a backup.
 *
 */
export const zRestoreCtBackupToNewCtRequest = z.object({
    name: z.string(),
    scope: z.string().optional(),
    storage: z.string(),
    bridge: z.string().optional(),
    network: z.string().optional()
});

/**
 * THE SECOND GESTURE — put the data back into the container it came from. The destructive one, and cold by construction: the CT must be stopped. Its whole rootfs TREE is replaced by the artefact's (B12 — «il restore RIMPIAZZA COMPLETAMENTE l'oggetto»: extract beside, swap over, reap the retired tree); its row, NICs, MACs and configuration are NOT touched — the operator putting a container back expects to find the container.
 *
 */
export const zRestoreCtBackupOverRequest = z.object({
    ct: z.uuid()
});

/**
 * One App data backup — an append-only observed record created by the `appBackup.create` task, never POSTed or PATCHed. The artefact is a DIRECTORY under the destination's tree, indexed by `manifest`, verifiable artefact by artefact through the sha256 the manifest records. Delete removes the directory and the row (a task — `202`).
 *
 */
export const zAppBackup = z.object({
    id: z.uuid().readonly(),
    app: z.uuid().readonly(),
    appName: z.string().readonly().optional(),
    scope: z.string().readonly().optional(),
    destination: z.uuid().readonly(),
    origin: z.enum(['manual', 'schedule']).readonly().optional(),
    policy: zBackupPolicyRef.readonly().optional(),
    phase: z.enum([
        'running',
        'complete',
        'error'
    ]).readonly().optional(),
    error: z.string().readonly().nullish(),
    appState: z.enum(['stopped', 'running']).readonly().optional(),
    contains: z.array(z.enum([
        'config',
        'volumes',
        'root'
    ])).readonly().optional(),
    subjectToRetention: z.boolean().readonly().optional(),
    secretsExposed: z.boolean().readonly().optional(),
    path: z.string().readonly().nullish(),
    sizeBytes: z.int().readonly().nullish(),
    checksum: z.string().readonly().nullish(),
    pointInTime: z.iso.datetime().readonly().nullish(),
    node: z.string().readonly().nullish(),
    manifest: z.record(z.string(), z.unknown()).readonly().optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    completedAt: z.iso.datetime().readonly().nullish()
});

export const zAppBackupList = z.object({
    items: z.array(zAppBackup),
    nextCursor: z.string().optional()
});

/**
 * THE FIRST GESTURE — restore into an App that does not exist yet: a new row minted from the manifest's declared config, its persistent volumes materialized from the artefact. Nothing of the cluster is destroyed by it.
 *
 */
export const zRestoreAppBackupToNewAppRequest = z.object({
    name: z.string(),
    scope: z.string().optional(),
    node: z.string().nullish(),
    storage: z.string().optional()
});

/**
 * THE SECOND GESTURE — put the data back into the App it came from. The destructive one, and cold by construction: the App must be stopped. Every persistent volume's CONTENTS (and the persistent root's) are replaced by the artefact's (B12 — extract beside, swap over, reap the retired tree, per volume); the row, its configuration, its attachment and its ephemeral declarations are NOT touched. The volume sets must match by NAME (`422` listing every mismatch): a volume the backup carries and the App lacks has nowhere to land, and a persistent volume the App holds and the backup does not carry would be left holding data from another point in time.
 *
 */
export const zRestoreAppBackupOverRequest = z.object({
    app: z.uuid()
});

/**
 * The COARSE cut, for GUI filters and #115 routing — a property of the type in the canonical catalog. The fine grain lives in `type` and `subject.kind`. Declared ONCE and referenced by `Event.category` and by the set a notification rule subscribes with (`NotificationRule.categories[]`): the consumer of a journal cannot be allowed to know a coarser or a finer cut than the journal itself.
 *
 */
export const zEventCategory = z.enum([
    'lifecycle',
    'config',
    'availability',
    'security'
]);

/**
 * THE gravity scale of the journal, declared ONCE — a TOTAL ORDER (`info < warning < error < critical`) and a property of the event TYPE in the canonical catalog, never a choice of the emission site: `vm.started` is always `info`, `vm.start.failed` always `error`. Where the outcome changes the gravity, the TYPE changes — never the severity at runtime. Referenced by `Event.severity`, by the event echo a notification delivery carries (`NotificationDelivery.event.severity`) and by the THRESHOLD a rule subscribes with (`NotificationRule.minSeverity`). It is derived rather than copied because a consumer that does not know a severity the journal already emits cannot route it: `#115` reads this vocabulary, it does not own a second one. (`OverviewHealthReason.severity` is a deliberate SUBSET — the Home semaphore speaks only of degradations — and is declared as such in the enum census, not derived from here.)
 *
 */
export const zEventSeverity = z.enum([
    'info',
    'warning',
    'error',
    'critical'
]);

/**
 * The resource the fact happened to — the TaskTarget mirror (same shape, same familiarity). The `name` is FROZEN at the act: if the resource is renamed later the event keeps telling the name it had — this is history, and the rename itself is a fact of its own.
 *
 */
export const zEventSubject = z.object({
    kind: z.string().optional(),
    id: z.uuid().optional(),
    name: z.string().optional()
});

/**
 * One accomplished fact of the cluster (design §1) — entirely read-only by construction: the resource has no writable field and no write operation exists. `scope` is the subject's scope FROZEN at the act (history is never rewritten; a later move of the subject does not re-anchor its past). `task` is the plain correlation to the act's task, deliberately WITHOUT referential guarantee: task retention prunes much earlier than the journal, so the id may no longer resolve (`404`, exactly like any pruned task) — the id itself remains a fact of the event. `data` is the per-type structured payload, censused by the canonical catalog (never a free bag); `message` is the English one-liner fallback — clients localize from `type` + `data`.
 *
 */
export const zEvent = z.object({
    id: z.string(),
    at: z.iso.datetime(),
    category: zEventCategory,
    severity: zEventSeverity,
    type: z.enum([
        'vm.created',
        'vm.create.failed',
        'vm.create.cancelled',
        'vm.create.aborted',
        'vm.deleted',
        'vm.delete.failed',
        'vm.delete.cancelled',
        'vm.delete.aborted',
        'vm.started',
        'vm.start.failed',
        'vm.start.cancelled',
        'vm.start.aborted',
        'vm.stopped',
        'vm.stop.failed',
        'vm.stop.cancelled',
        'vm.stop.aborted',
        'vm.suspended',
        'vm.pause.failed',
        'vm.pause.cancelled',
        'vm.pause.aborted',
        'vm.resumed',
        'vm.resume.failed',
        'vm.resume.cancelled',
        'vm.resume.aborted',
        'vm.restarted',
        'vm.reset.failed',
        'vm.reset.cancelled',
        'vm.reset.aborted',
        'vm.migrated',
        'vm.migrate.failed',
        'vm.migrate.cancelled',
        'vm.migrate.aborted',
        'vm.storageMoved',
        'vm.moveStorage.failed',
        'vm.moveStorage.cancelled',
        'vm.moveStorage.aborted',
        'vm.cloned',
        'vm.clone.failed',
        'vm.clone.cancelled',
        'vm.clone.aborted',
        'vm.quickDeployed',
        'vm.quickDeploy.failed',
        'vm.quickDeploy.cancelled',
        'vm.quickDeploy.aborted',
        'vm.scopeMoved',
        'ct.created',
        'ct.materialize.failed',
        'ct.materialize.cancelled',
        'ct.materialize.aborted',
        'ct.started',
        'ct.start.failed',
        'ct.start.cancelled',
        'ct.start.aborted',
        'ct.stopped',
        'ct.stop.failed',
        'ct.stop.cancelled',
        'ct.stop.aborted',
        'ct.restarted',
        'ct.restart.failed',
        'ct.restart.cancelled',
        'ct.restart.aborted',
        'ct.deleted',
        'ct.delete.failed',
        'ct.delete.cancelled',
        'ct.delete.aborted',
        'app.created',
        'app.create.failed',
        'app.create.cancelled',
        'app.create.aborted',
        'app.started',
        'app.start.failed',
        'app.start.cancelled',
        'app.start.aborted',
        'app.stopped',
        'app.stop.failed',
        'app.stop.cancelled',
        'app.stop.aborted',
        'app.restarted',
        'app.restart.failed',
        'app.restart.cancelled',
        'app.restart.aborted',
        'app.deleted',
        'app.delete.failed',
        'app.delete.cancelled',
        'app.delete.aborted',
        'ha.restarted',
        'ha.gaveUp',
        'ha.nodeAttested',
        'ha.failedOver',
        'ha.noCandidate',
        'ha.storageLocalSkip',
        'ha.expendableSkip',
        'ha.expendableStart',
        'ha.sacrificed',
        'ha.failoverDeferred',
        'ha.sacrificeReturned',
        'ha.zombieFenced',
        'ha.appFailedOver',
        'ha.appNoCandidate',
        'ha.appStorageLocalSkip',
        'ha.appReaped',
        'ha.netsvcFailedOver',
        'ha.netsvcNoCandidate',
        'ha.netsvcReaped',
        'netsvc.floatipMoved',
        'netsvc.floatipNoHolder',
        'vmBackup.skipped',
        'vmBackup.degraded',
        'ctBackup.skipped',
        'appBackup.skipped',
        'vmBackup.windowOverrun',
        'ctBackup.windowOverrun',
        'appBackup.windowOverrun',
        'backupDestination.paused',
        'checkpoint.preChange.skipped',
        'backupDestination.rescanned',
        'backupDestination.rescan.failed',
        'backupDestination.rescan.cancelled',
        'backupDestination.rescan.aborted',
        'networkService.leasesNotPublished',
        'storage.spaceLow',
        'storage.spaceCritical',
        'storage.spaceRecovered',
        'license.expiring',
        'license.expired',
        'ups.onBattery',
        'ups.lowBattery',
        'ups.powerRestored',
        'ups.unreachable',
        'ups.nodeCondemned',
        'ups.nodeResumed',
        'node.firewall.applied',
        'node.firewall.rolledBack',
        'node.firewall.unconfirmed',
        'node.firewall.firewalldActive',
        'node.firewall.restored',
        'node.readOnlyAccess.granted',
        'node.readOnlyAccess.revoked',
        'node.readOnlyAccess.expired',
        'node.emergencyAccess.login',
        'vmBackup.created',
        'vmBackup.create.failed',
        'vmBackup.create.cancelled',
        'vmBackup.create.aborted',
        'vmBackup.restored',
        'vmBackup.restore.failed',
        'vmBackup.restore.cancelled',
        'vmBackup.restore.aborted',
        'vmBackup.deleted',
        'vmBackup.delete.failed',
        'vmBackup.delete.cancelled',
        'vmBackup.delete.aborted',
        'ctBackup.created',
        'ctBackup.create.failed',
        'ctBackup.create.cancelled',
        'ctBackup.create.aborted',
        'ctBackup.restored',
        'ctBackup.restore.failed',
        'ctBackup.restore.cancelled',
        'ctBackup.restore.aborted',
        'ctBackup.deleted',
        'ctBackup.delete.failed',
        'ctBackup.delete.cancelled',
        'ctBackup.delete.aborted',
        'appBackup.created',
        'appBackup.create.failed',
        'appBackup.create.cancelled',
        'appBackup.create.aborted',
        'appBackup.restored',
        'appBackup.restore.failed',
        'appBackup.restore.cancelled',
        'appBackup.restore.aborted',
        'appBackup.deleted',
        'appBackup.delete.failed',
        'appBackup.delete.cancelled',
        'appBackup.delete.aborted',
        'checkpoint.created',
        'checkpoint.create.failed',
        'checkpoint.create.cancelled',
        'checkpoint.create.aborted',
        'checkpoint.restored',
        'checkpoint.restore.failed',
        'checkpoint.restore.cancelled',
        'checkpoint.restore.aborted',
        'checkpoint.deleted',
        'checkpoint.delete.failed',
        'checkpoint.delete.cancelled',
        'checkpoint.delete.aborted'
    ]),
    scope: z.string(),
    subject: zEventSubject.optional(),
    actor: zEventActor.optional(),
    task: z.string().optional(),
    node: z.string().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
    message: z.string().optional()
});

export const zEventList = z.object({
    items: z.array(zEvent),
    nextCursor: z.string().optional()
});

/**
 * CHI ha chiesto l'atto — l'attore VERO, mai l'effetto. Sovrainsieme deliberato di `EventActor` (events.yaml): stessi nomi per i campi comuni, cosi' le due superfici si leggono con la stessa grammatica, piu' i tre campi che solo un audit richiede. `kind: user` porta l'identita' qualificata congelata all'atto — `username` piu' `realm` (il NOME del realm, sempre presente; il realm di cluster e' implicito nello store e non ha UUID proprio), `realmId` dove il realm e' un oggetto (realm directory), `externalUuid` per le identita' di sola sessione. `kind: system` nomina il `component` (`worker`, `ha`, `gc`, `netsvc`). Interamente scritto dal server: nessun campo arriva dal client. Il `kind` non e' ricopiato: e' DERIVATO da `ActorKind` (events.yaml), l'unico posto dove quel vocabolario vive — due registri che si leggono con la stessa grammatica non possono permettersi due liste che divergono (disciplina `StorageDriver`).
 *
 */
export const zAuditActor = z.object({
    kind: zActorKind,
    username: z.string().optional(),
    userId: z.uuid().optional(),
    realm: z.string().optional(),
    realmId: z.uuid().optional(),
    externalUuid: z.string().optional(),
    component: z.string().optional(),
    appToken: z.string().optional(),
    sudo: z.boolean().optional(),
    session: z.string().optional()
});

/**
 * Il PERCHE' di un atto non riuscito, nella forma RFC 9457 che tutta la piattaforma parla: `code` e' il problem code stabile (l'identificatore machine-matchable della tabella canonica), `title` la riga, `detail` il testo intero. La stessa scelta che H3 ha fatto sul giornale il 2026-08-10, e per la stessa ragione: il perche' non puo' vivere solo su un oggetto che una ritenzione piu' corta ha gia' potato. Campi assenti restano assenti — un problema senza `detail` non ne porta uno vuoto.
 *
 */
export const zAuditProblem = z.object({
    code: z.string().optional(),
    title: z.string().optional(),
    detail: z.string().optional()
});

/**
 * L'oggetto su cui l'atto e' stato tentato — specchio di `TaskTarget` e `EventSubject`, stessa forma e stessa familiarita'. Il `name` e' CONGELATO all'atto. Assente quando l'atto non ha un oggetto risolto: e' il caso normale dei rifiuti, dove la risorsa non e' mai stata caricata proprio perche' il chiamante non aveva titolo a vederla.
 *
 */
export const zAuditTarget = z.object({
    kind: z.string().optional(),
    id: z.string().optional(),
    name: z.string().optional(),
    detail: z.string().optional()
});

/**
 * SU PROPOSTA DI CHI — la macchina che ha suggerito l'atto che un umano ha poi approvato (ratifica R8 del questionario del 2026-08-11). Non sostituisce `actor` e non ne divide la responsabilita': `actor` resta CHI ha approvato e risponde dell'atto, questo campo aggiunge da dove veniva l'idea. Un atto pensato da un umano e un atto suggerito da una macchina e approvato da un umano non sono lo stesso atto, e il giorno in cui si indaga una decisione sbagliata la differenza e' tutto.
 * GENERICO PER COSTRUZIONE, e non «il campo dell'assistente»: vale per TVirt Maestro, per gli operatori stile k8s (#99), per il rebalance automatico (#117) e per qualunque automazione che nessuno ha ancora scritto. Il cardine e' `component`, un vocabolario APERTO: un'automazione futura si nomina e basta, senza migrazione e senza un enum da allargare — la stessa disciplina che `action` ha su questa stessa superficie.
 * ASSENTE significa NESSUNA PROPOSTA: l'atto e' nato dalla testa di chi l'ha chiesto. E' vero perche' il campo arriva ADESSO, mentre il registro e' giovane e nessuna automazione e' ancora viva: il registro e' append-only, e un campo aggiunto dopo avrebbe lasciato cieche per sempre tutte le righe gia' scritte, senza modo di ricostruirle.
 *
 */
export const zAuditSuggester = z.object({
    kind: zActorKind,
    component: z.string(),
    version: z.string().optional(),
    ref: z.string().optional(),
    attested: z.boolean()
});

/**
 * Un atto tentato sul cluster: chi, quando, cosa, su quale oggetto, e com'e' andata. Interamente di sola lettura per costruzione — nessun campo scrivibile, nessuna operazione di scrittura, nessuna di cancellazione. Un atto ASINCRONO lascia qui una riga sola, `accepted`, col `task` che ne porta avanti l'esito: la riuscita o il fallimento sono materia del giornale eventi, che li scrive alla chiusura del task. Cercare qui il «vm.start e' andato bene» e' cercare nel registro sbagliato — e i due si uniscono sull'id del task.
 *
 */
export const zAuditRecord = z.object({
    id: z.string(),
    at: z.iso.datetime(),
    actor: zAuditActor,
    action: z.string(),
    method: z.string(),
    path: z.string(),
    outcome: z.enum([
        'succeeded',
        'accepted',
        'denied',
        'rejected',
        'failed'
    ]),
    status: z.int(),
    problem: zAuditProblem.optional(),
    scope: z.string(),
    target: zAuditTarget.optional(),
    task: z.string().optional(),
    node: z.string().optional(),
    clientIp: z.string().optional(),
    suggestedBy: zAuditSuggester.optional()
});

export const zAuditRecordList = z.object({
    items: z.array(zAuditRecord),
    nextCursor: z.string().optional()
});

export const zTaskList = z.object({
    items: z.array(zTask),
    nextCursor: z.string().optional()
});

/**
 * Outcome of a parent-task abort — declared undo actions of completed steps run in reverse where they exist; honest where they do not (parent spec §5.7).
 *
 */
export const zAbortResult = z.object({
    undone: z.array(z.string()).optional(),
    reported: z.array(z.string()).optional()
});

/**
 * COME si consegna — dichiarato UNA volta e riferito da ogni faccia che lo serve (`NotificationTarget.kind` e l'eco congelata sulla consegna, `NotificationDelivery.target.kind`). Derivato e non ricopiato per la disciplina `StorageDriver` (round «errori onesti»): il giorno in cui una terza via di consegna nasce, la lista che la GUI legge sulla destinazione e quella che legge sulla riga di consegna si muovono insieme o non si muovono affatto — e una copia rimasta indietro fa esplodere lo zod del client generato su un valore che il backend serve legittimamente.
 *
 */
export const zNotificationTargetKind = z.enum(['smtp', 'webhook']);

/**
 * Una destinazione di consegna. `kind` e `scope` sono IMMUTABILI dopo la creazione (`422 immutable_field`): cambiare tipo a una destinazione viva significa riconfigurarla da capo, ed e' una destinazione NUOVA — mai una PATCH. `name` e' scrivibile e unico per scope.
 *
 */
export const zNotificationTarget = z.object({
    id: z.uuid().readonly().optional(),
    scope: z.string().max(255).optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: zNotificationTargetKind.optional(),
    enabled: z.boolean().optional().default(true),
    smtp: z.object({
        host: z.string(),
        port: z.int().gte(1).lte(65535).optional().default(25),
        security: z.enum([
            'none',
            'starttls',
            'tls'
        ]).optional().default('starttls'),
        credential: z.string().optional(),
        from: z.string(),
        to: z.array(z.string()).min(1)
    }).optional(),
    webhook: z.object({
        url: z.string(),
        credential: z.string().optional(),
        payload: z.enum(['full', 'minimal']).optional().default('full')
    }).optional(),
    notes: z.string().max(65536).optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    updatedAt: z.iso.datetime().readonly().optional()
});

export const zNotificationTargetList = z.object({
    items: z.array(zNotificationTarget),
    nextCursor: z.string().optional()
});

/**
 * L'esito di una consegna di PROVA, sincrona e fuori dalla coda. Risponde `200` anche quando la prova fallisce, e l'ha deciso la domanda che l'operatore sta facendo: «questo server risponde?». Un `502` direbbe che la richiesta e' andata storta, mentre e' andata benissimo — ed e' la risposta ad avere una cattiva notizia dentro, col problem che la spiega.
 *
 */
export const zNotificationTestResult = z.object({
    ok: z.boolean(),
    error: z.object({
        code: z.string().optional(),
        title: z.string().optional(),
        detail: z.string().optional()
    }).optional()
});

/**
 * La regola di sottoscrizione: quali fatti del giornale escono, e verso quali destinazioni. Le quattro dimensioni sono in AND fra loro e in OR dentro ogni insieme; un insieme VUOTO non filtra (`categories: []` = tutte le categorie). `scope` e' immutabile: e' il recinto della regola, e spostarlo cambierebbe silenziosamente cosa un destinatario riceve.
 *
 */
export const zNotificationRule = z.object({
    id: z.uuid().readonly().optional(),
    scope: z.string().max(255).optional(),
    recursive: z.boolean().optional().default(true),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    enabled: z.boolean().optional().default(true),
    minSeverity: zEventSeverity.optional().default('warning'),
    categories: z.array(zEventCategory).optional().default([]),
    types: z.array(z.string()).optional().default([]),
    targets: z.array(z.string()).min(1),
    repeatWindowS: z.int().gte(0).optional().default(3600),
    notes: z.string().max(65536).optional(),
    createdAt: z.iso.datetime().readonly().optional(),
    updatedAt: z.iso.datetime().readonly().optional()
});

export const zNotificationRuleList = z.object({
    items: z.array(zNotificationRule),
    nextCursor: z.string().optional()
});

/**
 * Una consegna e il suo esito — interamente scritta dal sistema. Nome della regola e della destinazione sono CONGELATI qui come il nome del soggetto sull'evento: una regola rinominata domani non riscrive cio' che e' stato spedito ieri. Per la stessa ragione la consegna sopravvive alla regola che l'ha generata (nessun vincolo referenziale), esattamente come l'evento sopravvive al suo task.
 *
 */
export const zNotificationDelivery = z.object({
    id: z.uuid().readonly(),
    at: z.iso.datetime().readonly(),
    state: z.enum([
        'pending',
        'sending',
        'sent',
        'failed'
    ]).readonly(),
    rule: z.object({
        id: z.uuid().optional(),
        name: z.string().optional()
    }).readonly().optional(),
    target: z.object({
        id: z.uuid().optional(),
        name: z.string().optional(),
        kind: zNotificationTargetKind.optional()
    }).readonly().optional(),
    event: z.object({
        id: z.string().optional(),
        type: z.string().optional(),
        severity: zEventSeverity.optional(),
        at: z.iso.datetime().optional(),
        message: z.string().optional()
    }).readonly().optional(),
    scope: z.string().readonly().optional(),
    attempts: z.int().readonly().optional(),
    nextAttemptAt: z.iso.datetime().readonly().optional(),
    sentAt: z.iso.datetime().readonly().optional(),
    groupedCount: z.int().readonly().optional(),
    lastGroupedAt: z.iso.datetime().readonly().optional(),
    error: z.object({
        code: z.string().optional(),
        title: z.string().optional(),
        detail: z.string().optional()
    }).readonly().optional()
});

export const zNotificationDeliveryList = z.object({
    items: z.array(zNotificationDelivery),
    nextCursor: z.string().optional()
});

/**
 * The Home dashboard's single aggregate (ratified 2026-07-28): everything the landing page renders, computed server-side in one call, RESPECTING THE CALLER'S VISIBILITY. Every zone except `health` is OPTIONAL — ABSENT (never an error) when the caller holds the zone's read permission nowhere: `nodes` requires `cluster.read`, `storages.items` `storage.read`, `storages.cephClusters` `hci.cephCluster.read`, `workload.vms` `vm.read`, `workload.apps` `app.read`, `workload.services` `netsvc.read`, `checkpoints` `backupDestination.read` OR `checkpoint.read` (the zone reports two objects and each half answers to its own — task #281), `recentTasks` `task.read`; a zone whose sub-fields are all absent is omitted whole. Scoped rows (storages, workload, tasks, checkpoints) are filtered to the caller's RBAC selectors, so a tenant sees the overview of ITS world, not cluster totals. Only OBSERVED data: unobserved fields are null, never invented. `health` is always present and is computed over the zones the caller can see.
 *
 * STORE-DOWN FALLBACK (ratified 2026-07-28 appendix #43, supersedes the former 503): while the cluster store is unreachable the config zones — `nodes`, `storages`, `workload` — are served from this node's LOCAL config replica, the whole response is marked `stale: true` with `staleAsOf` carrying the replica's apply timestamp, and `health` is `critical` with reason `store-unreachable`. Observed values inside the stale zones are null/empty (state is store-born); the zones that REQUIRE the store — `recentTasks`, `checkpoints` (task and checkpoint records are store-only) — are OMITTED even when the caller holds their permission.
 *
 */
export const zSystemOverviewWritable = z.object({
    health: z.object({
        status: z.enum([
            'ok',
            'warning',
            'critical'
        ]),
        reasons: z.array(zOverviewHealthReason)
    }),
    nodes: z.array(zOverviewNode).optional(),
    storages: z.object({
        items: z.array(zOverviewStorage).optional(),
        cephClusters: z.array(zOverviewCephCluster).optional()
    }).optional(),
    workload: z.object({
        vms: zOverviewPhaseCounts.optional(),
        apps: zOverviewPhaseCounts.optional(),
        services: zOverviewPhaseCounts.optional()
    }).optional(),
    failoverReserve: zFailoverReserveVerdict.optional(),
    checkpoints: z.object({
        destinations: z.int(),
        lastCheckpoint: zOverviewLastCheckpoint.optional()
    }).optional(),
    recentTasks: z.object({
        items: z.array(zOverviewTask),
        failedLast24h: z.int()
    }).optional()
});

/**
 * A session on the answering node (sessions are node-local; API spec §3). Scoped semantics (ratified 2026-07-28 #24/#25): a session inherits the SCOPE of its owner — the caller always reaches their own sessions, and reaches another principal's only with `user.sessions.revoke` granted on that principal's scope. An EXTERNAL owner (round #102) is homed at the cluster ROOT scope (round #181, confirmed 2026-08-03) — external identities are cluster-level, not scoped — so only a revoke grant covering the whole tree reaches their sessions.
 *
 */
export const zSessionWritable = z.object({
    user: zSessionUser,
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    mustChangePassword: z.boolean().optional()
});

export const zSessionListWritable = z.object({
    items: z.array(zSessionWritable),
    nextCursor: z.string().optional()
});

export const zSessionCreatedWritable = zSessionWritable.and(z.object({
    token: z.string()
}));

export const zMfaEnrollmentListWritable = z.object({
    items: z.array(z.unknown()),
    nextCursor: z.string().optional()
});

/**
 * An external authentication realm. `name` is the DOMAIN written after the `@` in the login string and is IMMUTABLE after creation (it is the identity of every session and every audit line that ever named it). `kind` is the extension point: `ldap` is the only implementation of Round 1 — `oidc` and `saml` join it in rounds 2 and 3 without a new resource. The identities a realm authenticates are CLUSTER-level, homed at the cluster ROOT scope (round #181, confirmed 2026-08-03): a realm is cluster configuration and its users belong to no vdc — reaching their sessions takes `user.sessions.revoke` on the whole tree, while grants to them may target any scope, exactly as for native users.
 *
 */
export const zAuthRealmWritable = z.object({
    name: z.string().min(1).max(253).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/),
    kind: z.enum(['ldap']).optional().default('ldap'),
    enabled: z.boolean().optional().default(true),
    urls: z.array(z.string().regex(/^ldaps?:\/\/[^\s\/]+$/)).min(1).max(8).optional(),
    startTls: z.boolean().optional().default(false),
    verifyCertificate: z.boolean().optional().default(true),
    caCertificate: z.string().max(65536).optional(),
    bindDn: z.string().max(1024).optional(),
    bindCredential: z.string().max(255).optional(),
    baseDn: z.string().max(1024).optional(),
    userFilter: z.string().max(1024).optional().default('(&(objectClass=person)(sAMAccountName=%u))'),
    memberOfAttribute: z.string().max(255).optional().default('memberOf'),
    uuidAttribute: z.string().max(255).optional().default('objectGUID'),
    displayNameAttribute: z.string().max(255).optional().default('displayName'),
    connectTimeoutSeconds: z.int().gte(1).lte(60).optional().default(5),
    notes: z.string().max(65536).optional()
});

export const zAuthRealmListWritable = z.object({
    items: z.array(zAuthRealmWritable),
    nextCursor: z.string().optional()
});

/**
 * Fields shared by every flat resource (API spec §5, ADR-22). The kind's identifier field (`username` for users, `name` elsewhere) is declared per kind. Optimistic locking is NOT inherited from this schema — each kind that serves it declares its own `etag` field (the shared `EntityTag` component): since R6 fase 1 (ratified 2026-08-24) that is EVERY config resource with a single-object update operation, whose `If-Match` is honored as described on the `EntityTag` schema. Kinds without an update (and pure registries) carry no validator, and the recourse to a collision there stays the audit trail.
 *
 */
export const zResourceCommonWritable = z.object({
    scope: z.string().max(255).regex(/^(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){0,7}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/).optional(),
    notes: z.string().max(65536).optional()
});

/**
 * A user account. `username` is the kind's identifier field (API spec §5).
 */
export const zUserWritable = zResourceCommonWritable.and(z.object({
    username: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    displayName: z.string().optional(),
    enabled: z.boolean().optional().default(true),
    mustChangePassword: z.boolean().optional().default(false),
    mfaRequired: z.boolean().optional().default(false),
    initialPassword: z.string().optional()
}));

export const zUserListWritable = z.object({
    items: z.array(zUserWritable),
    nextCursor: z.string().optional()
});

/**
 * The caller's own second-factor state, as the profile page shows it.
 */
export const zMfaStatusWritable = z.object({
    required: z.boolean(),
    enrolled: z.boolean(),
    methods: z.array(z.string()),
    recoveryCodesRemaining: z.int().optional(),
    enrolledAt: z.iso.datetime().optional()
});

/**
 * A long-lived credential for an automation. Two ways to use it, one identity: as a **bearer** on its own (`Authorization: Bearer tvt_…`, since 2026-09-05 — the node opens and keeps one session per token, see the `bearerToken` security scheme in `common.yaml`), or in place of the password at `POST /api/v6/auth/sessions` to obtain an interactive `tvts_` session. Either way the `tvt_` prefix routes verification and every act is audited to the token (`actor.appToken`). Inherits all of the owner's permissions and scope reach — narrowing is an open point. Metadata only: the secret appears exactly once, at creation. App tokens are immutable — changes are revoke-and-recreate; revoking one refuses its next bearer request and its next login, while the `tvts_` sessions it already logged in with follow the rules of sessions.
 * **The second factor does not apply to an app token** (ratified 2026-08-09, #315). A token is a NON-INTERACTIVE credential: it is minted on its own, revoked on its own, audited distinctly, and it is held by an automation that has no authenticator app to open. The exemption belongs to the TOKEN, never to the account — the same owner's password still owes the code, and revoking the token shuts the door again.
 * **Hence app tokens DO NOT EXIST for an identity under an MFA obligation** (ratified 2026-08-10). Since the token is a single-factor credential, leaving the mint open to an obliged subject would make the obligation optional: one POST with an ordinary session and the second factor is bypassed for good. So the mint is refused with `409 app_tokens_unavailable`, and the tokens such a subject already holds are REVOKED — no grace period, no list to review. The obligation is the one resolved in #178 half B (pure OR of the personal flag and the group flag), and the refusal's `detail` names its provenance so the way out is readable: an administrator lifting the obligation, not a different endpoint.
 *
 */
export const zAppTokenWritable = z.object({
    label: z.string().min(1).max(255),
    expiresAt: z.iso.datetime().nullish().default(null)
});

export const zAppTokenListWritable = z.object({
    items: z.array(zAppTokenWritable)
});

export const zAppTokenCreatedWritable = zAppTokenWritable.and(z.object({
    token: z.string()
}));

/**
 * A named, scoped, FLAT set of users (round #103, ratified 2026-07-31): members are users only — never other groups (directory nesting stays in the directory). Membership is managed through the member endpoints; `members` is read-only here, mirroring the tags idiom. Since round #102 the group also carries its own EXTERNAL-REALM MAPPINGS (`realmMappings`): the ratified home of the correspondence is the TVirt GROUP, not the realm — "what does this group correspond to in each external realm".
 *
 */
export const zGroupWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    mfaRequired: z.boolean().optional().default(false),
    realmMappings: z.array(zGroupRealmMapping).max(64).optional()
}));

export const zGroupListWritable = z.object({
    items: z.array(zGroupWritable),
    nextCursor: z.string().optional()
});

/**
 * A node of the namespace. Creating `{scope: "/roma-1", name: "vdc-web"}` makes `/roma-1/vdc-web` available for resources, RBAC bindings and delegation.
 *
 */
export const zScopeWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    ociCacheStorage: z.string().nullish(),
    appEphemeralStorage: z.string().nullish(),
    networkWeight: z.int().gte(1).lte(10000).optional().default(100),
    networkCeilMbps: z.int().gte(1).lte(1000000).nullish(),
    failoverProtection: zFailoverProtection.optional()
}));

export const zScopeListWritable = z.object({
    items: z.array(zScopeWritable),
    nextCursor: z.string().optional()
});

/**
 * A named, scoped aggregate of permissions and/or other roles (hierarchy): the effective permission set is the transitive union. At least one of `permissions` and `roles` MUST be present. Names with the reserved `tvirt-` prefix are system roles: present in every deployment, neither creatable nor modifiable nor deletable (mutations return `422 immutable_field`).
 *
 */
export const zRoleWritable = zResourceCommonWritable.and(z.intersection(z.unknown(), z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    permissions: z.array(z.string()).min(1).optional(),
    roles: z.array(z.string()).min(1).optional()
})));

export const zRoleListWritable = z.object({
    items: z.array(zRoleWritable),
    nextCursor: z.string().optional()
});

export const zGrantWritable = z.intersection(z.unknown(), z.object({
    user: z.string().regex(/^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?@[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/).optional(),
    group: z.string().optional(),
    role: z.string(),
    grantRight: z.boolean().optional().default(false),
    scope: z.string().max(257).regex(/^(\/\*|(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){1,8}(\/\*)?|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/\*)?)$/)
}));

export const zGrantListWritable = z.object({
    items: z.array(zGrantWritable),
    nextCursor: z.string().optional()
});

export const zTagValueWritable = z.object({
    value: z.string().min(1).max(253)
});

/**
 * NOT an allOf of ResourceCommon on purpose: a tag is not a taggable kind, so it carries no `tags` array of its own — it borrows only the common fields it genuinely has. A tag definition — the vocabulary entry. `name` is unique within its scope only; the same name may exist in sibling or ancestor scopes (ratified: no chain uniqueness). Names admit no `:` (the filter grammar's separator) and no `/` (the qualified-form separator). The `tvirt-` prefix is RESERVED for platform-created system tags, which are read-only for operators exactly like system roles.
 *
 */
export const zTagWritable = z.object({
    scope: z.string().max(255).regex(/^(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){0,7}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/).optional(),
    notes: z.string().max(65536).optional(),
    name: z.string().min(1).max(63).regex(/^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?$/),
    cardinality: z.enum(['single', 'multiple']).optional().default('single'),
    autoCreateValues: z.boolean().optional().default(true),
    values: z.array(zTagValueWritable).max(256).optional(),
    kinds: z.array(z.string()).max(64).optional()
});

export const zTagListWritable = z.object({
    items: z.array(zTagWritable),
    nextCursor: z.string().optional()
});

/**
 * One tag value attached to one resource. The `id` is the handle the detach operation takes; it is also carried by the resource's own `tags` array, so a client that has the resource never needs a lookup to detach.
 *
 */
export const zTagAssignmentWritable = z.object({
    tag: z.string(),
    tagScope: z.string(),
    value: z.string(),
    kind: z.string(),
    resource: z.uuid()
});

export const zTagAssignmentListWritable = z.object({
    items: z.array(zTagAssignmentWritable),
    nextCursor: z.string().optional()
});

/**
 * The atom of execution — a single idempotent operation with an owner, an executor, a structured log, progress and a terminal outcome (parent spec §5.7). Flat, server-created and entirely read-only (API spec §9). A task may own CHILDREN (round #128 — the former macrotask): the children are its ordered steps, each gated on the success of the previous, and the parent itself executes no code — its phase derives from the children (running while one runs, failed when one fails, succeeded when all closed).
 *
 */
export const zTaskWritable = z.object({
    id: z.string(),
    type: z.string(),
    target: zTaskTarget.optional(),
    actor: zEventActor.optional(),
    params: z.record(z.string(), z.unknown()).optional(),
    phase: z.enum([
        'queued',
        'running',
        'succeeded',
        'failed',
        'cancelled',
        'aborted'
    ]),
    progressPercent: z.number().gte(0).lte(100).optional(),
    message: z.string().optional(),
    startedAt: z.iso.datetime().optional(),
    finishedAt: z.iso.datetime().optional(),
    error: zProblem.optional(),
    node: z.string().optional()
});

/**
 * The cluster's subscription license — at most one per cluster. `basis` picks WHAT the license caps: `concurrentVms` (VMs concurrently powered on — running, paused or stopping, i.e. every VM whose QEMU is alive (bughunt #157 LC3); templates never count, they are always off) or `physicalCores` (sum of the member nodes' observed physical cores — `ClusterNode.cpuTopology.physicalCores`; a node whose topology was never observed makes the sum honest-but-partial, flagged in `usage`, never invented). Registration is the only writer: there is no PATCH — changing a license is remove-and-register (dev-stage, see the file header).
 *
 */
export const zLicenseWritable = z.object({
    productKey: z.string().min(1).max(255),
    basis: z.enum(['concurrentVms', 'physicalCores']),
    limit: z.int().gte(1),
    expiresAt: z.iso.datetime().nullish().default(null)
});

/**
 * The node's cluster — a singleton, install-born (parent spec §3.4; revised 2026-07-15). Not an ordinary scoped resource: no `scope` field (it OWNS the single tree's root, named after it), never listed, never deletable at runtime. Cluster renders expose `revision`: it is the store generation clients watch for convergence (parent §5.10).
 *
 */
export const zClusterWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    adminPassword: z.string().optional(),
    ip: z.string().nullish(),
    roles: z.object({
        management: z.object({
            members: z.array(z.object({
                node: z.string(),
                ip: z.string()
            })).optional()
        }).optional(),
        liveMigration: z.object({
            members: z.array(z.object({
                node: z.string(),
                ip: z.string()
            })).optional()
        }).nullish()
    }).optional(),
    security: z.object({
        passwordPolicy: z.object({
            minLength: z.int().gte(8).lte(128).optional().default(12)
        }).optional(),
        sessionMaxIdleMinutes: z.int().gte(5).lte(43200).optional().default(480),
        lockout: z.object({
            threshold: z.int().gte(1).optional().default(5),
            durationMinutes: z.int().gte(1).optional().default(15)
        }).optional()
    }).optional(),
    taskRetention: z.object({
        maxAgeDays: z.int().gte(1).lte(3650).optional().default(7),
        maxCount: z.int().gte(100).lte(1000000).optional().default(5000)
    }).optional(),
    eventRetention: z.object({
        maxAgeDays: z.int().gte(1).lte(3650).optional().default(90),
        maxCount: z.int().gte(1000).lte(10000000).optional().default(100000)
    }).optional(),
    spaceThresholds: z.object({
        lowPct: z.int().gte(50).lte(99).optional().default(85),
        criticalPct: z.int().gte(50).lte(100).optional().default(95)
    }).optional(),
    auditRetention: z.object({
        maxAgeDays: z.int().gte(1).lte(3650).optional().default(365)
    }).optional(),
    debugMode: z.boolean().optional().default(false),
    nodeFirewallMode: zNodeFirewallMode.optional().default('disabled'),
    ociCache: z.object({
        storage: z.string().nullish()
    }).optional(),
    appVolumes: z.object({
        ephemeralStorage: z.string().nullish()
    }).optional(),
    failoverReserve: z.object({
        tolerateNodeFailures: z.int().gte(0).lte(16).optional().default(0)
    }).optional(),
    networkShaping: z.object({
        restWeight: z.int().gte(1).lte(10000).optional().default(300),
        overlayWeight: z.int().gte(1).lte(10000).optional().default(100),
        liveMigrationWeight: z.int().gte(1).lte(10000).optional().default(200)
    }).optional(),
    ups: z.object({
        policy: z.object({
            runtimeThresholdSeconds: z.int().gte(0).lte(86400).optional().default(300),
            resumeStableSeconds: z.int().gte(0).lte(86400).optional().default(60),
            resumeMinChargePercent: z.int().gte(0).lte(100).optional().default(25)
        }).optional(),
        units: z.array(z.object({
            name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
            mode: z.enum(['managed', 'external']),
            host: z.string().nullish().default(null),
            port: z.int().gte(1).lte(65535).optional().default(3493),
            upsname: z.string().min(1),
            attachedNode: z.string().nullish().default(null),
            driver: z.string().nullish().default(null),
            device: z.string().nullish().default(null),
            servedNodes: z.array(z.union([z.string(), zNodeSelector])).optional().default([]),
            policy: z.object({
                runtimeThresholdSeconds: z.int().gte(0).lte(86400).nullish().default(null),
                resumeStableSeconds: z.int().gte(0).lte(86400).nullish().default(null),
                resumeMinChargePercent: z.int().gte(0).lte(100).nullish().default(null)
            }).optional()
        })).optional()
    }).optional(),
    notes: z.string().max(65536).optional()
});

/**
 * A cluster member or a pre-registered joiner (parent spec §3.4) — nodes are OBJECTS of this registry, not scopes (single tree, 2026-07-15). Born of addClusterNode in `pending-join` with a one-time join token; init-cluster registers the creating node directly as `member`. Exposes `revision` (parent §5.10).
 *
 */
export const zClusterNodeWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    ip: z.string().max(45),
    baseCpuCores: z.int().gte(0).lte(1024).optional().default(1),
    baseMemoryMib: z.int().gte(0).lte(1048576).optional().default(2048),
    settings: z.object({
        console: z.object({
            keyboardLayout: z.string().nullish().default(null)
        }).optional(),
        logForwarding: z.object({
            syslog: z.object({
                endpoint: z.string().nullish().default(null),
                tls: z.boolean().optional().default(true)
            }).optional()
        }).optional(),
        tasks: z.object({
            maxQueued: z.int().gte(1).optional().default(100),
            concurrency: z.int().gte(1).lte(64).optional().default(2)
        }).optional(),
        passthrough: z.object({
            gpus: z.array(z.object({
                id: z.uuid().optional(),
                pciAddress: z.string().regex(/^[0-9a-f]{4}:[0-9a-f]{2}:[0-9a-f]{2}\.[0-7]$/),
                use: zPassthroughUse.optional(),
                vendorId: z.string().optional(),
                deviceId: z.string().optional(),
                vendorName: z.string().optional(),
                deviceName: z.string().optional(),
                groupAddresses: z.array(z.string()).optional(),
                notes: z.string().max(2000).optional()
            })).optional(),
            usb: z.array(z.object({
                id: z.uuid().optional(),
                mode: z.enum(['device', 'port']),
                use: zPassthroughUse.optional(),
                vendorId: z.string().regex(/^[0-9a-f]{4}$/).optional(),
                productId: z.string().regex(/^[0-9a-f]{4}$/).optional(),
                manufacturer: z.string().optional(),
                product: z.string().optional(),
                port: z.string().regex(/^[0-9]+-[0-9]+(\.[0-9]+)*$/).optional(),
                notes: z.string().max(2000).optional()
            })).optional()
        }).optional(),
        firewall: z.object({
            allowUnmanagedBridges: z.array(z.string()).optional()
        }).optional()
    }).optional(),
    dataDisks: z.array(z.object({
        name: z.string().min(1),
        serial: z.string().optional(),
        model: z.string().optional(),
        byPath: z.string().optional(),
        notes: z.string().max(2000).optional()
    })).optional(),
    cabling: z.array(z.object({
        nic: z.string().min(1),
        fabric: z.string(),
        switch: z.string(),
        port: z.string()
    })).optional(),
    placement: z.object({
        chassis: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        rack: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        row: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        pdu: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        pod: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        room: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        datacenter: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        zone: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
        region: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional()
    }).optional(),
    readOnlyAccess: zReadOnlyAccess.optional(),
    emergencyAccess: zEmergencyAccess.optional(),
    notes: z.string().max(65536).optional()
});

export const zClusterNodeListWritable = z.object({
    items: z.array(zClusterNodeWritable),
    nextCursor: z.string().optional()
});

export const zClusterNodeCreatedWritable = zClusterNodeWritable;

/**
 * The 202 of `grantReadOnlyAccess` (the `AppDeleteResult` idiom — the task plus what only this response can say): the password is returned EXACTLY ONCE here and is never readable again (the `joinToken` precedent) — not in the task row (only the hash rides its params), not in the audit row, not in the HTTP log (the `tvro_` family is redacted by construction). It becomes usable when `task` is `succeeded`.
 *
 */
export const zReadOnlyAccessGrantedWritable = z.object({
    task: z.string(),
    username: z.string(),
    password: z.string(),
    expiresAt: z.iso.datetime(),
    readOnlyAccess: zReadOnlyAccess
});

/**
 * The Proximity control panel: cluster config plus the observed surface. `strategy` and `livenessTimeoutSeconds` are the writable half (PATCH); everything else is read-only observation composed at render time.
 *
 */
export const zProximityStatusWritable = z.object({
    strategy: z.enum([
        'leastVms',
        'leastUsedResources',
        'random'
    ]).optional().default('leastUsedResources'),
    livenessTimeoutSeconds: z.int().gte(5).lte(3600).optional().default(75)
});

/**
 * An affinity/anti-affinity rule over a set of VMs (≥ 2). v1 semantics are NODE-scoped (ratified 2026-07-25): `affinity` wants every member VM on the SAME node, `antiAffinity` on DIFFERENT nodes; the physical-topology generalization (rack/room, #100) is a future round. `hard` rules gate admission — VM start placement, deploy, live-migration targets and the HA decider's failover all refuse a violating node with a named `422` (or exclude it from the candidate set); `soft` rules order the candidate preference — a violated soft rule never blocks, it is reported (task message, rule render). Canonical example: «VM1 and VM2 should PREFERABLY run on different servers» = antiAffinity soft.
 *
 */
export const zProximityRuleWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    type: z.enum(['affinity', 'antiAffinity']).optional().default('antiAffinity'),
    mode: z.enum(['hard', 'soft']).optional().default('soft'),
    vms: z.array(z.uuid()).min(2)
}));

export const zProximityRuleListWritable = z.object({
    items: z.array(zProximityRuleWritable),
    nextCursor: z.string().optional()
});

export const zVmCdromWritable = z.object({
    index: z.int().gte(1).lte(3),
    image: z.string().nullish()
});

export const zVmNicWritable = z.object({
    index: z.int().gte(1).lte(6),
    network: z.string().nullish(),
    bridge: z.string().nullish(),
    untagged: z.string().nullish(),
    tagged: z.record(z.string(), z.string()).optional(),
    mac: z.string().regex(/^([0-9a-fA-F]{2}:){5}[0-9a-fA-F]{2}$/).optional(),
    connected: z.boolean().optional().default(true),
    firewallGroups: z.array(z.string()).optional(),
    firewallEnabled: z.boolean().optional().default(false),
    firewallAntiSpoof: z.boolean().optional().default(false)
});

/**
 * A USB passthrough requirement by CLASS (round U2 #72, ratified 2026-07-30 — usb-gpu-passthrough-design.md v2): the entry names a tag of kind `usb-device` and one of its values (e.g. `usb_class=token`), NEVER a concrete device — the exact device stays expressible as a one-member class. The decider resolves the claim at START: it picks a node holding a FREE registration of the class (observed, not orphaned, not claimed by another placed VM), claims it and the worker attaches the concrete device (`device` mode: vendor:product wherever plugged; `port` mode: the physical port, whatever sits in it). Claims release at stop and failover; HA restarts the VM on any node with a free device of the class. Several entries of the same class require that many free devices.
 *
 */
export const zVmUsbWritable = z.object({
    index: z.int().gte(1).lte(4),
    tag: z.string(),
    value: z.string().min(1).max(253)
});

/**
 * A GPU passthrough requirement by CLASS (round G2 #73, ratified 2026-07-30 — usb-gpu-passthrough-design.md v2, the VmUsb model verbatim): the entry names a tag of kind `gpu-device` and one of its values (e.g. `gpu_class=compute`), NEVER a concrete card — the exact card stays expressible as a one-member class. The decider resolves the claim at START: it picks a node holding a FREE registration of the class (observed, not orphaned, not claimed by another placed VM), claims it, and the worker passes the registration's WHOLE IOMMU group through vfio-pci — VGA, audio and USB-C functions together (the group was bound at marking; functions of a group never split). NO PCI hotplug in v1, but the edit lane is the ordinary power-cycle one (ratified #146, 2026-08-01, superseding G2's strictly-cold `412`): a PATCH touching `gpus[]` on a running/paused VM is ACCEPTED and surfaces as the derived `PendingRestart` condition — the guest keeps what it holds (the read-only `gpuClaims` says so honestly) and the change applies at the next power cycle, when the claim machinery runs. `?live=true` on a gpus[] change still answers `412` (nothing hotplugs). Claims release at stop and failover; HA restarts the VM on any node with a free device of the class. Live migration with a GPU requirement refuses speaking (the silicon is physical to the node and VFIO state never migrates), and so does a MEMORY snapshot on a running VM — the device state cannot ride the migration stream the memory snapshot uses.
 *
 */
export const zVmGpuWritable = z.object({
    index: z.int().gte(1).lte(4),
    tag: z.string(),
    value: z.string().min(1).max(253)
});

/**
 * A virtual machine (flat resource; Annex F). `firmware`, `arch` and `schema` are immutable for the life of the VM — through templates and backups too (parent §4.2, §4.5). `hardwareProfile` left the immutable fence 2026-08-03 (round #179) and since the 2026-08-04 ratifica rides the ordinary power-cycle lane like `gpus[]` (#146, superseding the round's stopped-only 412): the change saves in any phase, a running/paused VM carries the derived `PendingRestart` condition until it power-cycles, and the next start builds the machine on the new profile — the imported-VM road to virtio (boot on `legacy`, install the drivers in the guest, switch profile, power-cycle). `hugePages` (round #135) stays stopped-only. The UEFI firmware VERSION is `firmwareFile`, a pointer to a `firmware-ovmf` catalog file INDEPENDENT of `schema` (round #125): stopped-only like the profile, and never moved automatically by a platform upgrade. Change applicability for the other fields is the Annex F table (live hotplug, next power cycle with a `PendingRestart` condition, or stopped-only).
 *
 */
export const zVirtualMachineWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    hardwareProfile: z.enum([
        'windows',
        'linux',
        'legacy'
    ]),
    firmware: z.enum([
        'bios',
        'uefi',
        'uefi-secure'
    ]).optional(),
    firmwareFile: z.string().nullish(),
    arch: zArchitecture.optional(),
    schema: z.string().optional(),
    cpu: z.object({
        sockets: z.int().gte(1).lte(32767).optional().default(1),
        cores: z.int().gte(1).lte(32767).optional().default(1),
        threads: z.int().gte(1).lte(32767).optional().default(1),
        model: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/).optional().default('host'),
        limit: z.number().gte(0).nullish(),
        weight: z.int().gte(1).lte(10000).optional().default(100),
        nestedVirtualization: z.boolean().optional().default(false)
    }).optional(),
    memory: z.object({
        sizeMiB: z.int().gte(512).lte(2147483647),
        balloon: z.boolean().optional().default(true)
    }),
    hugePages: z.enum([
        'none',
        '2M',
        '1G'
    ]).optional().default('none'),
    bootOrder: z.array(z.string().regex(/^(disk|cdrom|nic):[0-9]+$/)).optional().default(['disk:1', 'cdrom:1']),
    disks: z.array(zVmDisk).max(4).optional(),
    cdroms: z.array(zVmCdromWritable).max(3).optional(),
    nics: z.array(zVmNicWritable).max(6).optional(),
    usb: z.array(zVmUsbWritable).max(4).optional(),
    gpus: z.array(zVmGpuWritable).max(4).optional(),
    console: z.object({
        protocol: zConsoleProtocol.optional().default('vnc')
    }).optional(),
    agent: z.object({
        enabled: z.boolean().optional().default(true)
    }).optional(),
    cloudInit: z.object({
        userData: z.string().nullish(),
        networkConfig: z.string().nullish()
    }).optional(),
    secretFields: z.array(z.string().max(160)).max(8).optional(),
    restartPolicy: z.enum([
        'always',
        'onFailure',
        'never'
    ]).optional().default('never'),
    treatGuestShutdownAsVoluntary: z.boolean().optional().default(false),
    ha: z.boolean().optional().default(false),
    failoverProtection: zFailoverProtection.optional(),
    startup: z.object({
        onBoot: z.boolean().optional().default(false),
        order: z.int().optional().default(100),
        delaySeconds: z.int().optional().default(0)
    }).optional(),
    protected: z.boolean().optional().default(false),
    quickDeploy: z.object({
        os: zQuickDeployOs
    }).nullish().default(null),
    metadataStorage: z.string(),
    preferredNode: z.string().nullish(),
    preferNodes: z.record(z.string(), z.string()).optional(),
    requireNodes: z.record(z.string(), z.string()).optional()
}));

export const zVirtualMachineListWritable = z.object({
    items: z.array(zVirtualMachineWritable),
    nextCursor: z.string().optional()
});

/**
 * A virtual disk. `storage`, `format` and `source` are immutable; `sizeGiB` is writable but grow-only (`422` on shrink), with live growth propagated to a running VM via QMP `block_resize`.
 *
 */
export const zDiskWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    storage: z.string(),
    sizeGiB: z.int().gte(1).optional(),
    format: zDiskFormat.optional(),
    source: zDiskSource.optional(),
    sharing: zDiskSharing.optional()
}));

/**
 * The VM's hardware description in the `.tvtpl` manifest vocabulary (#64, docs/tvtpl-format.md) served NAKED — no tar, no payload, no checksums: the metadata a backup product archives next to the disk bytes so the VM can be recreated at restore (the import road of a later round consumes exactly this JSON plus the restored disks). Unlike `exportVmToCatalog` this never copies a disk.
 *
 */
export const zVmDescriptionWritable = z.record(z.string(), z.unknown());

/**
 * The change-tracking state of one VM disk slot. Entirely read-only: the observation the serving node recorded at the last change-tracking operation (each enable/disable/reset refreshes it) and at the last snapshot or revert of the VM.
 *
 */
export const zVmDiskChangeTrackingWritable = z.record(z.string(), z.unknown());

/**
 * One change-tracking bitmap of one VM disk, as last observed by the serving node (QMP `query-named-block-nodes` on a live VM, `qemu-img info` on a stopped one) — the copy on the disk's HEAD. Since the epoch rotation (round D, T235-P1) a bitmap name is a LINEAGE of epochs: every snapshot disables the recording copy inside the file it freezes (that copy is an epoch — the writes since the previous snapshot, readable from that one file through a read-only export) and arms an empty recording twin of the same name on the fresh overlay. So the head's copy records the granules written SINCE THE LAST SNAPSHOT (or since the last reset, whichever is later); the older epochs sit in the frozen files, listed in `lineages`. The external software reads an epoch's dirty extents through the NBD export of the snapshot that hosts it (`qemu:dirty-bitmap:<name>` meta-context) and composes a delta across several snapshots by opening one export per intermediate snapshot.
 *
 */
export const zVmCbtBitmapWritable = z.object({
    name: z.string(),
    granularityBytes: z.int().optional(),
    recording: z.boolean(),
    persistent: z.boolean(),
    inconsistent: z.boolean(),
    dirtyBytes: z.int().optional()
});

/**
 * An ephemeral NBD export session (#235 primitive 3), in ONE of two modes. `read` (born by `createBackupExport`): the data path serving the bytes of ONE disk as they were at ONE snapshot, plus — when a bitmap was named — the dirty-extent map as the NBD meta-context `qemu:dirty-bitmap:<name>` on the same connection (dossier §1.2: one connection, first the map, then reads of the dirty extents only). `write` (born by `createRestoreExport`, round D4 — T235-P6): the restore's data path, a WRITABLE `qemu-nbd` on the live head of a VIRGIN disk of a stopped VM, which the client (`nbdcopy`, `qemu-img convert -n`) fills with the image it recomposed on its side. The two modes are ONE resource on purpose — same session row, same TTL, same ticket, same TLS, same list/show/close/sweep; only the door and the permission differ. Dies by `closeBackupExport` or by TTL. The export name is the TICKET (#148 idiom applied to a non-HTTP protocol): unguessable, single-purpose, dead with the session — whoever holds it holds at most that one read (or that one write) until `expiresAt`, never a session, never a token. While a `write` session is `preparing` or `ready` its disk is FENCED at the origin: starting the VM (single or batch), snapshotting, reverting or cloning it, and deleting, moving, cloning, resizing or detach-deleting the disk answer `409` naming the session. Deleting the VM is not fenced: the row dies with the VM (FK cascade) and the sweep kills the server.
 *
 */
export const zBackupExportWritable = z.object({
    error: zProblem.optional()
});

export const zBackupExportListWritable = z.object({
    items: z.array(zBackupExportWritable),
    nextCursor: z.string().optional()
});

/**
 * A logical network of the fabric (Annex O.1/O.9; revised by fabric-ipam-revision-notes.md; ONE ROW with its own identity since round #405 — SN-P1-bis variant 2, SN-P9, SN-P11-bis, ratified 2026-09-03). A network MAY be delivered heterogeneously — untagged on one port, tagged on another: it is the SAME network (the canonical "Rete IPMI" example) — and, since #405, by SEVERAL fabrics: it is born in ONE fabric (the OWNER, the only one that writes its identity) and any other fabric may IMPORT it (`POST …/imports`), after which the same row — same `id`, same `stem` — appears in the importer's `importedNetworks` and is mapped on the importer's ports/LAGGs exactly like an own network. Since v2 the subnet is mandatory and the network carries a mandatory `mtu` (server default 1500). Gateway, DNS servers and static routes declared here are the SINGLE SOURCE of the network identity (ratified 2026-07-28): server NICs, Apps and network services draw from here — config derived, never duplicated. Dual-stack (IPv6) is the O.10 round: networks are born IPv4 with the structure ready to grow. The NAME is a pure label: every interface the derivation makes for this network is named after the `stem` (`tvf-<stem>`, `ab-<stem>`), so renaming a network renames no interface on any node.
 *
 */
export const zFabricLogicalNetworkWritable = z.object({
    id: z.uuid().optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    mtu: z.int().gte(68).lte(65535).optional().default(1500),
    backing: zFabricNetworkBacking.optional(),
    subnet: zFabricSubnet,
    dnsServers: z.array(z.string()).optional(),
    routes: z.array(zFabricRoute).optional(),
    nodeAddressing: z.enum(['none', 'pool']).optional().default('none'),
    notes: z.string().max(2000).optional()
});

/**
 * A logical network IMPORTED into this fabric (round #405): the owner's row, read-only here — identity, `id` and `stem` are the owner's, the importer declares nothing about it and can only map it on its own ports/LAGGs (by bare name, exactly like an own network) and un-import it. `owner` names the fabric that holds the identity.
 *
 */
export const zFabricImportedNetworkWritable = zFabricLogicalNetworkWritable.and(z.object({
    owner: zFabricNetworkOwnerRef
}));

/**
 * A LAGG (Annex O.9 — FIRM): 2+ ports in LACP, an object at FABRIC level referencing `(switch, port)` pairs — deliberately NOT a child of a switch, because ports of DIFFERENT switches may sit in one LAGG (MLAG is the switch stack's implementation detail, transparent to TVirt and its clients). A port belongs to AT MOST one LAGG, and a member port carries no mapping config of its own: the LAGG is the mapping surface, exactly like a non-LAGG port. Membership is edited HERE (the LAGG side): adding an UNUSED port makes it a member, removing it returns it to unused. The 802.3ad bond the derivation builds for it is named `bo-<stem>` (SN-P17, ratified 2026-09-04): the stem is coined at the LAGG's creation and kept by NAME across edits — a LAGG that disappears from the array loses it, a new name is a new bond.
 *
 */
export const zFabricLaggWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    ports: z.array(zFabricLaggPort).min(2),
    lacpRate: zLacpRate,
    untaggedNetwork: z.string().nullish(),
    taggedNetworks: z.array(zFabricTaggedNetwork).optional()
});

/**
 * One EXTERNAL physical L2 domain (Annex O.9; revised by fabric-ipam-revision-notes.md): the physical registry — logical networks, switches with ports, fabric-level LAGGs. The scope is FIXED to the cluster root `/cluster1` (dictation 2026-07-28: physical infrastructure is by definition cluster-wide) — a create naming any other scope refuses `422` speaking; the rendered document always carries the root scope. Pure config — `201` with no task: nothing converges; the derivation engine that renders per-node VLAN/bond/bridge configuration from the node-declared cabling is its own future round (O.3), as is LLDP observation (O.7). The addressing half lives in the IPAM as DERIVED rows (revised O.4): every logical network's subnet is derived into the registry by the same write and dies with the network.
 *
 */
export const zFabricWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    logicalNetworks: z.array(zFabricLogicalNetworkWritable).optional(),
    switches: z.array(zFabricSwitch).optional(),
    laggs: z.array(zFabricLaggWritable).optional()
}));

export const zFabricListWritable = z.object({
    items: z.array(zFabricWritable),
    nextCursor: z.string().optional()
});

/**
 * One DERIVED IPAM subnet — the addressing half of a fabric logical network or of an overlay network, materialized into the registry by the source's own write transaction (fabric-ipam-revision-notes.md). Read-only through this surface: cidr, gateway and reserved ranges are edited on the SOURCE dialog and mirrored here; the id is STABLE across source updates, so referrers (dhcp/dns instances, auto-assigned Apps) keep working. The row dies with its source — `409 in_use` naming the referrer when something still references it. Subnets never overlap fleet-wide (`409 in_use` naming the holder at the source write); adjacent is fine.
 *
 */
export const zSubnetWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    cidr: z.string(),
    gateway: z.string().nullish(),
    reservedRanges: z.array(z.object({
        from: z.string(),
        to: z.string()
    })).optional(),
    mtu: z.int().nullish(),
    dnsServers: z.array(z.string()).optional(),
    routes: z.array(z.object({
        destination: z.string(),
        via: z.string()
    })).optional(),
    source: zSubnetSource.optional()
}));

export const zSubnetListWritable = z.object({
    items: z.array(zSubnetWritable),
    nextCursor: z.string().optional()
});

/**
 * The registry view (fabric-ipam-revision-notes.md): EVERY derived subnet — all fabric logical networks and all subnet-carrying overlays — with the addresses it contains (provenance-labeled: which App/VM/service/node holds each one) and its reserved ranges. The discovery census (candidates, overlap warnings) died with the derived-only model; `unhomed` survives it in a narrower form — see the field.
 *
 */
export const zIpamViewWritable = z.object({
    subnets: z.array(z.object({
        subnet: zSubnetWritable,
        addresses: z.array(zIpamAddress)
    }))
});

/**
 * One OpenVPN remote-access client (ratified 2026-08-02). Auth v1 is the CERTIFICATE: adding an entry ISSUES a client certificate from the instance's own CA (round #159 `issueCertificate` machinery) and records it in `certificate`; removing the entry REVOKES it — the CA's CRL is regenerated in the same transaction, so a removed client is locked out at the next handshake. Password/OTP arrive with #102.
 *
 */
export const zVpnClientWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)
});

/**
 * One managed network-service instance (Annex P): a worker-supervised bare-metal daemon on the node the authority registry assigns, its config rendered each reconcile pass from the replica (instance block + the IPAM subnets it references) behind a hash gate — restarts only on real changes. Phase is OBSERVED (the worker's service report): stale reads degrade honest.
 *
 */
export const zNetworkServiceWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum([
        'dhcp',
        'dns',
        'floatip',
        'gateway',
        'lb-rp',
        'ntp',
        'vpn'
    ]),
    preferredNode: z.union([
        z.string(),
        zNodeSelector
    ]).nullish(),
    enabled: z.boolean().optional(),
    preferNodes: z.record(z.string(), z.string()).optional(),
    requireNodes: z.record(z.string(), z.string()).optional(),
    dhcp: z.object({
        subnets: z.array(z.string()).min(1),
        interface: z.string(),
        dnsServers: z.array(z.string()).optional(),
        domain: z.string().nullish()
    }).optional(),
    dns: z.object({
        zones: z.array(z.object({
            name: z.string().regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/),
            ns: z.string().regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/).nullish(),
            subnets: z.array(z.string()).optional(),
            reverse: z.boolean().optional(),
            ttl: z.int().gte(30).lte(604800).optional(),
            records: z.array(z.object({
                name: z.string(),
                type: z.enum([
                    'A',
                    'CNAME',
                    'MX',
                    'TXT'
                ]),
                value: z.string(),
                ttl: z.int().gte(30).lte(604800).nullish()
            })).optional()
        })).min(1),
        listeners: z.array(zNetsvcListener).min(1).max(16)
    }).optional(),
    lbRp: z.object({
        network: z.string().nullish(),
        overlay: z.string().nullish(),
        interface: z.string().nullish(),
        addressAssignment: zAddressAssignment.optional(),
        address: z.string().optional(),
        listeners: z.array(z.object({
            name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
            port: z.int().gte(1).lte(65535),
            mode: z.enum(['tcp', 'http']),
            pool: z.string()
        })).min(1),
        pools: z.array(z.object({
            name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
            backend: z.object({
                pod: z.string(),
                port: z.int().gte(1).lte(65535)
            }).nullish(),
            algorithm: z.enum(['round_robin', 'least_request']).optional(),
            members: z.array(z.object({
                address: z.string(),
                port: z.int().gte(1).lte(65535),
                weight: z.int().gte(1).lte(128).optional()
            })).min(1).optional(),
            healthCheck: z.object({
                type: z.enum(['tcp', 'http']).optional(),
                path: z.string().nullish(),
                intervalSeconds: z.int().gte(1).lte(300).optional(),
                rise: z.int().gte(1).lte(10).optional(),
                fall: z.int().gte(1).lte(10).optional()
            }).nullish()
        })).min(1)
    }).optional(),
    gateway: z.object({
        network: z.string().nullish(),
        overlay: z.string().nullish(),
        interface: z.string().nullish(),
        addressAssignment: zAddressAssignment.optional(),
        address: z.string().optional(),
        dnat: z.array(zGatewayDnatRule).optional(),
        oneToOne: z.array(zGatewayOneToOneRule).optional(),
        snat: z.array(zGatewaySnatRule).optional(),
        routes: z.array(zGatewayRoute).optional()
    }).optional(),
    ntp: z.object({
        mode: z.enum(['node-clock', 'relay']),
        servers: z.array(z.string()).max(8).optional(),
        listeners: z.array(zNetsvcListener).min(1).max(8),
        port: z.int().gte(1).lte(65535).optional()
    }).optional(),
    vpn: z.object({
        engine: z.enum(['wireguard', 'openvpn']),
        mode: z.enum(['remote-access', 'site-to-site']),
        network: z.string().nullish(),
        overlay: z.string().nullish(),
        interface: z.string().nullish(),
        addressAssignment: zAddressAssignment.optional(),
        address: z.string().optional(),
        port: z.int().gte(1).lte(65535).optional(),
        protocol: z.enum(['udp', 'tcp']).optional(),
        tunnelSubnet: z.string(),
        dnsServers: z.array(z.string()).max(8).optional(),
        routes: z.array(z.string()).max(64).optional(),
        wireguard: z.object({
            peers: z.array(zVpnPeer).max(512).optional(),
            sitePeers: z.array(zVpnSitePeer).max(512).optional()
        }).nullish(),
        openvpn: z.object({
            auth: z.enum(['tls']).optional(),
            peerFingerprint: z.string().regex(/^[0-9A-Fa-f]{2}(:[0-9A-Fa-f]{2}){31}$/).nullish(),
            remote: z.string().nullish(),
            clients: z.array(zVpnClientWritable).max(512).optional(),
            peerRoutes: z.array(z.string()).max(64).optional()
        }).nullish()
    }).optional(),
    floatip: z.object({
        network: z.string().nullish(),
        overlay: z.string().nullish(),
        interface: z.string().nullish(),
        addressAssignment: zAddressAssignment.optional(),
        address: z.string(),
        targets: z.array(z.object({
            app: z.string().nullish(),
            pod: z.string().nullish(),
            vm: z.string().nullish(),
            healthCheck: z.object({
                type: z.enum([
                    'running',
                    'tcp',
                    'http',
                    'ping'
                ]).optional(),
                port: z.int().gte(1).lte(65535).nullish(),
                path: z.string().nullish()
            }).optional()
        })).min(1).max(64),
        preempt: z.boolean().optional()
    }).optional()
}));

export const zNetworkServiceListWritable = z.object({
    items: z.array(zNetworkServiceWritable),
    nextCursor: z.string().optional()
});

/**
 * The placement census of ONE network service (#343). Answers three questions with one computation: where may this instance live, WHY not elsewhere, and — the diagnosis the product did not have before — on how FEW members it can live at all.
 *
 */
export const zNetsvcPlacementWritable = z.object({
    service: z.uuid(),
    name: z.string(),
    kind: z.string(),
    node: z.string().optional(),
    reachability: z.array(zNetsvcReachability),
    nodes: z.array(zNetsvcPlacementNode),
    eligibleNodes: z.array(z.string()),
    memberNodes: z.int(),
    recommended: z.string().optional(),
    emptyBecause: z.enum(['reachability', 'requireTag']).optional(),
    singlePointOfFailure: z.boolean().optional(),
    survivesExclusion: z.boolean().optional()
});

export const zNetsvcPlacementListWritable = z.object({
    items: z.array(zNetsvcPlacementWritable)
});

/**
 * A node-spanning L2 overlay: VXLAN unicast with a static FDB toward the declared underlay endpoints (§8, ratified 2026-07-20). `hosts` maps each PARTICIPATING node to its underlay endpoint — participation is the placement constraint for VMs trunking into the overlay. VM NICs attach as TRUNKS: untagged traffic to one overlay, tagged VLANs mapped to others (see VmNic).
 *
 */
export const zOverlayNetworkWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    vni: z.int().gte(1).lte(16777215).nullish(),
    subnet: z.string().nullish(),
    reservedRanges: z.array(z.object({
        from: z.string(),
        to: z.string()
    })).optional(),
    hosts: z.record(z.string(), zOverlayHost).optional(),
    firewall: zNetworkFirewall.optional(),
    mtu: z.int().gte(576).lte(9216).optional().default(1450),
    weight: z.int().gte(1).lte(10000).optional().default(100),
    ceilMbps: z.int().gte(1).lte(1000000).nullish()
}));

export const zOverlayNetworkListWritable = z.object({
    items: z.array(zOverlayNetworkWritable),
    nextCursor: z.string().optional()
});

/**
 * NOT an allOf of ResourceCommon on purpose: like a tag, a firewall group is a vocabulary entry, not a taggable kind — it borrows only the common fields it genuinely has. `name` is unique within its scope only (the tag rule: sibling and ancestor scopes may hold homonyms, disambiguated by the qualified reference form); ancestor groups are usable downward.
 *
 */
export const zFirewallGroupWritable = z.object({
    scope: z.string().max(255).regex(/^(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){0,7}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/).optional(),
    notes: z.string().max(65536).optional(),
    name: z.string().min(1).max(63).regex(/^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?$/),
    description: z.string().max(255).nullish(),
    enforced: z.boolean().optional().default(false),
    rules: z.array(zFirewallRule).max(256).optional()
});

export const zFirewallGroupListWritable = z.object({
    items: z.array(zFirewallGroupWritable),
    nextCursor: z.string().optional()
});

/**
 * A configured storage backend holding inventory (disks, catalog files, backups, app volumes — parent spec §6.1). `driver` and its configuration block are immutable after creation (`422 immutable_field`): a storage is where data lives, not a setting to repoint.
 *
 */
export const zStorageWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    driver: zStorageDriver,
    dir: z.object({
        path: z.string()
    }).optional(),
    nfs: z.object({
        server: z.string(),
        export: z.string(),
        options: z.string().optional()
    }).optional(),
    cifs: z.object({
        server: z.string(),
        share: z.string(),
        credential: z.string().optional(),
        username: z.string().optional(),
        password: z.string().optional(),
        options: z.string().optional()
    }).optional(),
    iscsi: z.object({
        portal: z.string(),
        target: z.string(),
        credential: z.string().optional(),
        lun: z.int().gte(0).optional()
    }).optional(),
    'nvme-tcp': z.object({
        address: z.string(),
        port: z.int().gte(1).lte(65535).optional(),
        targetNqn: z.string(),
        nsid: z.int().gte(1).optional(),
        credential: z.string().optional()
    }).optional(),
    rbd: z.object({
        monitors: z.array(z.string()).min(1),
        pool: z.string(),
        credential: z.string(),
        cluster: z.string().optional()
    }).optional(),
    cephfs: z.object({
        monitors: z.array(z.string()).min(1),
        fs: z.string(),
        path: z.string().optional(),
        credential: z.string(),
        cluster: z.string().optional()
    }).optional(),
    defaults: z.object({
        diskFormat: zDiskFormat.optional().default('qcow2')
    }).optional(),
    allocationPolicy: z.object({
        reserveGiB: z.int().gte(0).optional().default(5)
    }).optional(),
    shared: z.boolean().optional(),
    reachableFrom: z.object({
        nodes: z.array(z.string()).optional(),
        nodeTags: zNodeSelector.optional()
    }).optional()
}));

export const zStorageListWritable = z.object({
    items: z.array(zStorageWritable),
    nextCursor: z.string().optional()
});

/**
 * A named keystore entry. `kind` and `scope` are immutable after creation; `name` is writable (unique per scope). `secret` is REQUIRED at creation, WRITE-ONLY forever, optional on PATCH — present means rotation.
 *
 */
export const zCredentialWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum([
        'usernamePassword',
        'iscsiChap',
        'nvmeDhchap',
        'cephx',
        'ca',
        'certificate',
        'vpnKey',
        'luksKey',
        'webhookSecret'
    ]).optional().default('usernamePassword'),
    username: z.string().optional(),
    secret: z.string().optional(),
    certificate: z.string().max(131072).optional()
}));

export const zCredentialListWritable = z.object({
    items: z.array(zCredentialWritable),
    nextCursor: z.string().optional()
});

export const zDiskListWritable = z.object({
    items: z.array(zDiskWritable),
    nextCursor: z.string().optional()
});

/**
 * A named, scoped container of catalog files. No folders inside (for now); tags go on the files. The contents live at `/api/v6/catalogFiles` (`?filter=catalog:eq:"<id>"`); the render carries only the counts — a catalog can hold many large artefacts.
 *
 */
export const zCatalogWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^(TVirt-[A-Za-z0-9][A-Za-z0-9-]{0,55}|[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)$/),
    files: zCatalogFileCounts.optional()
}));

export const zCatalogListWritable = z.object({
    items: z.array(zCatalogWritable),
    nextCursor: z.string().optional()
});

/**
 * An inert artefact on a storage, registered inside a catalog. The resource is a LINK to an underlying storage OBJECT (round #93): `name`, `catalog`, `notes` and `tags` belong to the link; `storage`, `kind`, `format`, `source`, `phase`, `sizeBytes` and `sha256` describe the shared object — identical across every link born of a copy. Content is immutable once ready: replacing means a new file. Uploads: create with `source.type: upload` (phase `awaiting-upload`), then deliver the bytes with `PUT …/content`. `source.type: import` is reserved to the flows that adopt bytes already on the storage (the storage scan&import, the unused-disk conversion of round #107) — it is never accepted from a direct create (`422`).
 *
 */
export const zCatalogFileWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^(TVirt-[A-Za-z0-9][A-Za-z0-9-]{0,55}|[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)$/),
    catalog: z.string(),
    kind: zCatalogFileKind,
    format: z.enum([
        'iso',
        'tar',
        'tar.gz',
        'tar.xz',
        'tar.zst',
        'tar.bz2',
        'tvtpl',
        'ova',
        'ovf',
        'qcow2'
    ]).optional(),
    storage: z.string().nullable(),
    source: zCatalogFileSource
}));

export const zCatalogFileListWritable = z.object({
    items: z.array(zCatalogFileWritable),
    nextCursor: z.string().optional()
});

/**
 * A host-device requirement by CLASS (round #330 — the `VmUsb`/`VmGpu` model, deliberately NOT its claim): the entry names a tag of kind `gpu-device` OR `usb-device` and one of its values (e.g. `gpu_class=transcode`), never a concrete card or port. The exact device stays expressible as a one-member class, exactly as for a VM.
 *
 * ONE array for both families, where a VM has two. That is not an oversight: for a VM the two are different hardware paths with different lifecycles (a VFIO PCI function passed at cold boot versus a `usb-host` device on an xHCI controller that hotplugs). For a container there is ONE mechanism — a path under `/dev` handed to `podman --device` — and two arrays would be two names for one thing. The tag's kind already says which family an entry belongs to, so nothing is lost.
 *
 * What an entry RESOLVES to is every registration of that class marked `use: app` on the instance's node — see `deviceResolution`. A container does not CLAIM: the kernel device node it opens is shareable by construction, and TVirt has no way to enforce an exclusivity it would have invented. So the resolution is a deterministic function of the node's registrations, recomputed at every container creation, and there is no claim table, no release path and nothing to leak.
 *
 * Consequences, all deliberate: since the Pod round the class is a PLACEMENT gate — a node that does not carry it is not a candidate for the instance, and the refusal names the nodes that do; a MOVE re-validates the same way. Adding, removing or re-pointing an entry is a real change to the container: the render hash moves and the container is recreated at the next converge, like the image digest.
 *
 */
export const zAppDeviceWritable = z.object({
    index: z.int().gte(1).lte(4),
    tag: z.string(),
    value: z.string().min(1).max(253)
});

/**
 * One container of the template. `initContainers` run to completion IN ORDER before the `containers` start (each must exit 0; a failure lands the instance in `failed`/`error` with the exit code observed); the `containers` are started together and supervised per `restartPolicy`. Every container of an instance shares the pod's network (one IP, `localhost` between them), IPC and UTS namespaces — a container that asks for its own network is refused by construction.
 *
 */
export const zPodContainerWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    image: z.string().min(1).max(512),
    imagePolicy: z.enum(['pinned', 'tracking']).optional().default('pinned'),
    command: z.array(z.string().max(4096)).max(256).nullish(),
    args: z.array(z.string().max(4096)).max(256).nullish(),
    env: z.record(z.string(), z.string().max(4096)).optional(),
    secretFields: z.array(z.string().max(200)).max(64).optional(),
    volumeMounts: z.array(zPodVolumeMount).max(32).optional(),
    devices: z.array(zAppDeviceWritable).max(4).optional(),
    resources: zPodContainerResources.optional(),
    health: zPodContainerHealth.nullish()
});

/**
 * The template with replicas (≈ k8s Deployment + PodTemplate). Everything here is the TEMPLATE the instances derive from; the instances are Apps (`/api/v6/apps`, filter `pod:eq:<id>`), named `<pod>-<index>` with a STABLE ordinal (scale-down removes from the top, scale-up fills the lowest gaps — the StatefulSet idiom). `desiredState` is the DEFAULT of the instances (PD-P19): the Pod's actions write ALL the instances, the per-instance actions write their own — an instance diverging from the Pod is shown as such. Editing the template bumps `templateRevision` and every instance converges at its own tick behind its own hash (no sequencer, no `strategy` field until a rolling update exists, PD-P9); the Pod's `restart` IS a rolling restart (one child task per instance, gated in sequence).
 *
 */
export const zPodWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(58).regex(/^[a-z0-9]([a-z0-9-]{0,56}[a-z0-9])?$/),
    replicas: z.int().gte(0).lte(64).optional().default(1),
    desiredState: zAppDesiredState.optional().default('stopped'),
    restartPolicy: z.enum([
        'always',
        'onFailure',
        'never'
    ]).optional().default('always'),
    terminationGracePeriodSeconds: z.int().gte(1).lte(600).optional().default(30),
    hostname: z.string().max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).nullish(),
    shareProcessNamespace: z.boolean().optional().default(false),
    network: z.string().nullish(),
    overlay: z.string().nullish(),
    bridge: z.string().nullish(),
    ipAssignment: z.string().optional().default('static'),
    ips: z.array(z.string().max(18)).max(64).optional(),
    gateway: z.string().nullish(),
    dnsServers: z.array(z.string()).optional(),
    firewallGroups: z.array(z.string()).optional(),
    firewallEnabled: z.boolean().optional().default(false),
    firewallAntiSpoof: z.boolean().optional().default(false),
    rootKind: z.enum(['ephemeral', 'persistent']).optional(),
    rootStorage: z.string().nullish(),
    ephemeralStorage: z.string().nullish(),
    volumes: z.array(zPodVolume).max(32).optional(),
    archMovePolicy: z.enum(['refuse', 'reresolve']).optional().default('refuse'),
    preferNodes: z.record(z.string(), z.string()).optional(),
    requireNodes: z.record(z.string(), z.string()).optional(),
    spread: z.enum([
        'preferred',
        'required',
        'none'
    ]).optional().default('preferred'),
    ha: z.boolean().optional().default(false),
    initContainers: z.array(zPodContainerWritable).max(16).optional(),
    containers: z.array(zPodContainerWritable).min(1).max(16),
    instances: zPodInstances.optional()
}));

export const zPodListWritable = z.object({
    items: z.array(zPodWritable),
    nextCursor: z.string().optional()
});

export const zPodDeleteResultWritable = z.object({
    task: zTaskWritable,
    keptData: z.array(zAppKeptData)
});

/**
 * ONE instance of a Pod: a `podman pod` on the node its authority names, with one IP plumbed by TVirt and the template's containers inside. The template part is READ from the Pod (`pod`); this object carries the per-instance facts and the observed state, findings-class — stale reads degrade honest (`phase: unknown`, `lastError` preserved).
 *
 */
export const zAppWritable = zResourceCommonWritable;

export const zAppListWritable = z.object({
    items: z.array(zAppWritable),
    nextCursor: z.string().optional()
});

/**
 * One container of the instance, as observed by the serving node's worker (the k8s `containerStatuses` shape, filled ONLY with what was observed): the template says what it should be, this says what it is.
 *
 */
export const zAppContainerStatusWritable = z.object({
    name: z.string().optional(),
    init: z.boolean().optional(),
    imageDigest: z.string().optional(),
    runningDigest: z.string().nullish(),
    state: z.enum([
        'waiting',
        'running',
        'terminated'
    ]).nullish(),
    exitCode: z.int().nullish(),
    startedAt: z.iso.datetime().nullish(),
    finishedAt: z.iso.datetime().nullish(),
    restartCount: z.int().optional(),
    ready: z.boolean().optional(),
    health: z.enum(['healthy', 'unhealthy']).nullish(),
    containerId: z.string().nullish(),
    lastError: z.string().nullish()
}).readonly();

/**
 * One external OCI registry in the anagraphics (Annex Q.2): a named reference to Docker Hub, GHCR, ECR, Quay or a generic self-hosted registry (Harbor, plain registry:2, …). Credentials never ride the resource: `credential` references the cluster keystore and workers resolve it from their local replica at use time — no per-node `auth.json`, ever. The verify observed group (`verifiedAt` / `verifyOk` / `verifyMessage`) is written by the verify task: an honest login/list probe with skopeo, never a stored claim.
 *
 */
export const zOciRegistryWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?$/),
    provider: z.enum([
        'dockerhub',
        'ghcr',
        'ecr',
        'quay',
        'generic'
    ]),
    url: z.url().max(512).optional(),
    credential: z.string().nullish()
}));

export const zOciRegistryListWritable = z.object({
    items: z.array(zOciRegistryWritable),
    nextCursor: z.string().optional()
});

/**
 * One repository on a registry, with the branches (OCI tags) the cluster OBSERVES — the remote-tracking-branch of the model (Annex Q.3). Registering never downloads anything: it promotes implicit tracking to explicit configuration. Auto-create is implicit and documented: any reference to a branch not yet listed (a pull, a refresh naming it) mints its `OCIBranch` as observed. An observed branch IS an OCIBranch (R37): `branches` is the derived list of their names and the per-branch resolution/reflog lives on the branch, where it belongs. Refresh is still the repository's "git fetch": every observed branch is resolved via `skopeo inspect`, the resolution is stamped on its branch and the previous one is kept in the per-branch reflog (last 10). Repositories of provenance `catalog` (ratifica 2026-07-30 sera — the air-gapped import of a catalog `oci-image` archive) have NO registry: their synthetic reference is `catalog/<catalogName>/<fileName>` (branch = file name, digest = commit — the tag-as-branch model verbatim) and they are minted by the import action only, never POSTed here.
 *
 */
export const zOciRepositoryWritable = zResourceCommonWritable.and(z.object({
    registry: z.string().nullable(),
    path: z.string().min(1).max(255).regex(/^[a-z0-9]+(?:(?:[._]|__|-+)[a-z0-9]+)*(?:\/[a-z0-9]+(?:(?:[._]|__|-+)[a-z0-9]+)*)*$/)
}));

export const zOciRepositoryListWritable = z.object({
    items: z.array(zOciRepositoryWritable),
    nextCursor: z.string().optional()
});

/**
 * THE BRANCH — the third of the four OCI levels (R37/R39, ratifica Luca 2026-08-13): `OCIRegistry` is `docker.io`, `OCIRepository` is `docker.io/nginx`, an **OCIBranch** is `docker.io/nginx:latest`, and an `OCIImage` is `docker.io/nginx:latest@sha256:…`. This resource was called `OCIImage` between the collector round (2026-08-05) and R37: it is the same object, renamed to the name that belongs to it, so that the word "image" means the same thing in the GUI, the API, the CLI and the manual. An OCIBranch IS one observed branch of a repository: identity is the pair `(repository, branch)`, unique per repository (`409 conflict`), and the name one pronounces is the reference the Apps already write — `<registry>/<path>:<branch>`. It is a GOVERNED object (notes, tags, `POST`/`PATCH`/`DELETE`). Creating one observes a branch; deleting it stops observing it. R40 — THE BRANCH IS WHERE THE DIGESTS LIVE, as the chronology of the refreshes from upstream: `currentDigest` is the head and `reflog` is the history, and that history is the load-bearing structure of the object, not an accessory. Which images a branch has named is therefore a JOIN over that chronology — calculated at render time, never stored (the same discipline the wave-1 `branches` array already followed). The branch does NOT carry a digest register any more: the digests are `OCIImage` resources of their own, listable with `GET /oci/images?branch=…`. R44.2/R48 — a branch referenced by an App cannot be deleted, pinned or tracking alike (`409 speaking`): the App needs the NAME, because the name is the address one returns to in order to learn whether a new version came out.
 *
 */
export const zOciBranchWritable = zResourceCommonWritable.and(z.object({
    repository: z.string(),
    branch: z.string().min(1).max(128).regex(/^[a-zA-Z0-9_][a-zA-Z0-9._-]{0,127}$/)
}));

export const zOciBranchListWritable = z.object({
    items: z.array(zOciBranchWritable),
    nextCursor: z.string().optional()
});

/**
 * THE IMAGE — the fourth OCI level and the CONTENT itself (R39, ratifica Luca 2026-08-13). An image is identified by its DIGEST and carries NO name: no registry, no repository, no branch. The disk had already said so before the model did — the pull and the catalog import both compose their destination as `path.join(cacheDir, digest)`, one directory per digest — while the register said N rows for one content and bridged the two truths with a lookup before every delete. That lookup is now the model: one row per digest, and the names live among the names. R41 — images are unique per CLUSTER; the REFERENCES to them are per SCOPE (`views`). "Se un dipartimento elimina un'immagine usata da un altro, ha eliminato solo la sua view": `DELETE` removes the caller's view, never another tenant's content. R46 — but the last view that goes takes the bytes with it, because the existence of an image on TVirt IS the set of its references. It is `docker image rm`: dropping the last tag makes the image go away. This is NOT the automatic retention excluded on 2026-08-05 (a GC by time or pressure, deleting what nobody asked to delete) — it is the deterministic consequence of an act. R42 — the image STAYS WHERE IT IS: `storage` is where it landed, and a scope whose cache pool differs neither duplicates nor moves it. The scope cache decides where NEW images land, not where the existing ones must live. R45 draws the consequence at placement time. R47 — a manifest list is a NAME, not an image: only the per-architecture digests exist as images, each with its `arch` (R43), and the list digest is a way of ARRIVING at one of them — it lives among the names, in `names[].listDigest`.
 *
 */
export const zOciImageWritable = zResourceCommonWritable.and(z.object({
    digest: z.string().regex(/^sha256:[a-f0-9]{64}$/)
}));

export const zOciImageListWritable = z.object({
    items: z.array(zOciImageWritable),
    nextCursor: z.string().optional()
});

/**
 * IL RIFERIMENTO SOPRAVVISSUTO AL CONTENUTO. A claim is what a scope's view becomes when the content stops being materialized: the register row is gone, so the image does not exist (R46 — existence IS the set of references), but "quale scope nominava questo digest" survives. It is the ONE thing a re-adoption could never rebuild: no byte in a blob directory knows which department it belonged to. That is why claims are written by the 0206 sweep and by `storage.delete?releaseImages=true`, and why `adoptObservedBlobs` turns them back into views the moment the content is observed again. #370 — AND THAT IS WHY THEY DO NOT EXPIRE. A claim is cheap to keep (one row per digest and scope) and irreplaceable once lost, so no timer deletes them: what bounds them is an operator who can SEE them and drop the ones that will never come back (`DELETE /api/v6/oci/claims/{digest}`). Before this list they were invisible — a released pool that never came back left rows nobody could name.
 *
 */
export const zOciViewClaimWritable = z.object({
    digest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    scope: z.uuid(),
    scopePath: z.string()
});

export const zOciViewClaimListWritable = z.object({
    items: z.array(zOciViewClaimWritable),
    nextCursor: z.string().optional()
});

/**
 * The VM trunk idiom verbatim (R4, host-network-design.md §9): exactly one attachment per NIC — a fabric logical network (the RECOMMENDED way, A1), an NM host bridge BY NAME, or an overlay trunk (untagged and/or tagged). The firewall trio (`firewallGroups`, `firewallEnabled`, `firewallAntiSpoof`) is the VmNic surface verbatim (#168, ratified: VM + CT + App together — the enforcement is one chain keyed on the interface name, tap or veth alike).
 *
 */
export const zCtNicWritable = z.object({
    index: z.int().gte(1).lte(6),
    network: z.string().nullish(),
    bridge: z.string().nullish(),
    untagged: z.string().nullish(),
    tagged: z.record(z.string(), z.string()).optional(),
    mac: z.string().regex(/^([0-9a-fA-F]{2}:){5}[0-9a-fA-F]{2}$/).optional(),
    connected: z.boolean().optional().default(true),
    firewallGroups: z.array(z.string()).optional(),
    firewallEnabled: z.boolean().optional().default(false),
    firewallAntiSpoof: z.boolean().optional().default(false)
});

/**
 * A container, config-side (round K2 + the #61 config-side slice — the liblxc runtime is #61 proper). A TEMPLATE births from a catalog file (`source.file`, kind `ct-rootfs`) and rests `ready`; a plain CT births from a template (`source.template`), inherits its metadata as defaults and rests `defined`. Creation answers `201` + `X-TVirt-Task` while the worker builds the rootfs onto `storage`.
 *
 */
export const zCtWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    template: z.boolean().optional().default(false),
    source: zCtSource.optional(),
    storage: z.string().optional(),
    arch: zArchitecture.nullish(),
    cpu: z.object({
        cores: z.int().gte(1).optional().default(1),
        limit: z.number().gte(0).nullish(),
        weight: z.int().gte(1).lte(10000).optional().default(100)
    }).optional(),
    memory: z.object({
        sizeMiB: z.int().gte(32)
    }).optional(),
    nics: z.array(zCtNicWritable).max(6).optional(),
    hostname: z.string().max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).nullish(),
    dns: z.object({
        nameservers: z.array(z.string()).max(3).optional(),
        searchDomains: z.array(z.string()).max(6).optional()
    }).optional(),
    rootPassword: z.string().min(1).max(512).optional(),
    sshAuthorizedKeys: z.array(z.string().min(1).max(4096).regex(/^[^\r\n]+$/)).max(16).optional()
}));

export const zCtListWritable = z.object({
    items: z.array(zCtWritable),
    nextCursor: z.string().optional()
});

/**
 * A managed Ceph cluster (Annex N; birth lifecycle ratified 2026-07-24: a CephCluster is NEVER empty or undeployed as a stable state). `POST` births the fabric: `201` + the AUTO bootstrap parent Task (`X-TVirt-Task`, the storage-initDir precedent) — mon + mgr on the declared `controlNode`, `phase: deploying` from the first breath. `not-deployed` survives ONLY as the parking state of a FAILED creation (batch 2026-07-26, item 6 — the destroy path left it: a successful destroy parks `destroyed` and the periodic backend sweeper deletes the row): retry with the `deploy` action, or `DELETE` the configuration there — nowhere else. Additional mon membership and the OSD device registry are written through actions, never by field writes — a device registered on the live cluster is prepared automatically (the incremental OSD choice (a), RATIFIED 2026-07-24: Ceph is imperative — the backend validates admission and the device row plus its prepare task are one atomic, visible claim). `osdEncryptionMode` governs how NEW OSDs are prepared (round #90, ratified 2026-07-24 — SUPERSEDES the immutable `encryptionAtRest` boolean of the same day: the mixed-fleet worry is answered by the per-row observed state, not by immutability; changing an existing OSD's state means remove + re-create, 2026-07-27).
 *
 */
export const zCephClusterWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    osdEncryptionMode: z.enum([
        'alwaysOn',
        'alwaysOff',
        'perOsd'
    ]).optional(),
    advanced: z.object({
        splitMonMgr: z.boolean().optional(),
        rawCrushEditor: z.boolean().optional()
    }).optional(),
    network: z.object({
        publicCidr: z.string().optional(),
        clusterCidr: z.string().optional(),
        controlAddress: z.string().optional(),
        controlClusterAddress: z.string().optional(),
        publicMembers: z.array(zCephClusterNetworkMember).nullish(),
        clusterMembers: z.array(zCephClusterNetworkMember).nullish()
    }).optional(),
    controlNode: z.string().optional()
}));

export const zCephClusterListWritable = z.object({
    items: z.array(zCephClusterWritable),
    nextCursor: z.string().optional()
});

/**
 * One backup destination: a dir-like TVirt storage (dir/nfs/cifs/cephfs — NFS/CIFS on a NAS is off-cluster by nature, the scenario-3 requirement) plus an optional simple schedule, a count-based retention and the declaration of WHAT may land in it (`contents`). Artefacts land under the platform namespace: `<storage>/trantor-tvirt-v6/backups/<destinationId>/`. No destination-level encryption in v1 — the secrets are protected by the per-checkpoint masterpass; whole-artefact encryption is FUTURE.
 *
 */
export const zBackupDestinationWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?$/),
    storage: z.string(),
    contents: z.array(z.enum(['checkpoints', 'backups'])).min(1).optional(),
    schedule: z.object({
        interval: zScheduleInterval,
        hour: z.int().gte(0).lte(23).optional(),
        weekday: z.int().gte(0).lte(6).optional()
    }).nullish(),
    retention: z.int().gte(1).lte(1000).nullish(),
    preChangeRetention: z.int().gte(1).lte(1000).nullish(),
    paused: z.boolean().optional().default(false),
    vmBackups: z.object({
        policies: z.array(z.object({
            id: z.uuid().optional(),
            name: z.string().min(1).max(64).regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/),
            selector: z.object({
                kind: zBackupSubjectKind,
                tags: z.record(z.string(), z.string()).optional(),
                scopes: z.array(z.string()).optional()
            }),
            schedule: z.object({
                interval: zScheduleInterval,
                hour: z.int().gte(0).lte(23).optional(),
                weekday: z.int().gte(0).lte(6).optional()
            }).nullish(),
            window: z.object({
                start: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/),
                end: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/)
            }).nullish(),
            mode: z.enum(['full', 'incremental']).optional().default('full'),
            requireQuiesce: z.boolean().optional().default(false),
            paused: z.boolean().optional().default(false),
            retention: z.union([
                z.int().gte(1).lte(1000),
                z.object({
                    keepLast: z.int().gte(1).lte(1000).optional(),
                    daily: z.int().gte(1).lte(1000).optional(),
                    weekly: z.int().gte(1).lte(1000).optional(),
                    monthly: z.int().gte(1).lte(1000).optional(),
                    yearly: z.int().gte(1).lte(1000).optional()
                })
            ]).nullish()
        })).min(1).max(32)
    }).nullish()
}));

export const zBackupDestinationListWritable = z.object({
    items: z.array(zBackupDestinationWritable),
    nextCursor: z.string().optional()
});

export const zCheckpointListWritable = z.object({
    items: z.array(z.unknown()),
    nextCursor: z.string().optional()
});

/**
 * One staged restore artefact (design §Task — UPLOAD-first): born `awaiting-upload` at the POST, filled by chunked PUTs to `/content` (the images/uploadContent precedent — chunks sized under the proxy threshold), opened and indexed by the backend at the final chunk. `ready` serves the explorer (families → named objects, encrypted flags); the restore action then applies a selection. Uploads are ephemeral staging: discarded by DELETE, by a completed restore, or by the sweeper after 24 hours.
 *
 */
export const zConfigRestoreUploadWritable = z.record(z.string(), z.unknown());

/**
 * The restore PREVIEW (B15/PROPOSAL 15, ratified 2026-08-24): the selection compared per object against the LIVE state, read through the very traversal the pack uses — so the preview and the artefact cannot drift. A pure read: the diff INFORMS, the restore admission DECIDES (#294 — nothing here is consulted by the apply, whose guards re-check everything regardless of what this preview showed).
 *
 */
export const zConfigRestoreDiffWritable = z.record(z.string(), z.unknown());

/**
 * One VM data backup — an append-only observed record created by the `vmBackup.create` task, never POSTed or PATCHed (the Checkpoint and findings precedent). The artefact is a DIRECTORY under the destination's tree, indexed by `manifest`, verifiable artefact by artefact through the sha256 the manifest records. Delete removes the directory and the row (a task — `202`).
 *
 */
export const zVmBackupWritable = z.record(z.string(), z.unknown());

export const zVmBackupListWritable = z.object({
    items: z.array(zVmBackupWritable),
    nextCursor: z.string().optional()
});

/**
 * One CT data backup — an append-only observed record created by the `ctBackup.create` task, never POSTed or PATCHed (the VmBackup precedent). The artefact is a DIRECTORY under the destination's tree, indexed by `manifest`, verifiable artefact by artefact through the sha256 the manifest records. Delete removes the directory and the row (a task — `202`).
 *
 */
export const zCtBackupWritable = z.record(z.string(), z.unknown());

export const zCtBackupListWritable = z.object({
    items: z.array(zCtBackupWritable),
    nextCursor: z.string().optional()
});

/**
 * One App data backup — an append-only observed record created by the `appBackup.create` task, never POSTed or PATCHed. The artefact is a DIRECTORY under the destination's tree, indexed by `manifest`, verifiable artefact by artefact through the sha256 the manifest records. Delete removes the directory and the row (a task — `202`).
 *
 */
export const zAppBackupWritable = z.record(z.string(), z.unknown());

export const zAppBackupListWritable = z.object({
    items: z.array(zAppBackupWritable),
    nextCursor: z.string().optional()
});

export const zTaskListWritable = z.object({
    items: z.array(zTaskWritable),
    nextCursor: z.string().optional()
});

/**
 * Una destinazione di consegna. `kind` e `scope` sono IMMUTABILI dopo la creazione (`422 immutable_field`): cambiare tipo a una destinazione viva significa riconfigurarla da capo, ed e' una destinazione NUOVA — mai una PATCH. `name` e' scrivibile e unico per scope.
 *
 */
export const zNotificationTargetWritable = z.object({
    scope: z.string().max(255).optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: zNotificationTargetKind.optional(),
    enabled: z.boolean().optional().default(true),
    smtp: z.object({
        host: z.string(),
        port: z.int().gte(1).lte(65535).optional().default(25),
        security: z.enum([
            'none',
            'starttls',
            'tls'
        ]).optional().default('starttls'),
        credential: z.string().optional(),
        from: z.string(),
        to: z.array(z.string()).min(1)
    }).optional(),
    webhook: z.object({
        url: z.string(),
        credential: z.string().optional(),
        payload: z.enum(['full', 'minimal']).optional().default('full')
    }).optional(),
    notes: z.string().max(65536).optional()
});

export const zNotificationTargetListWritable = z.object({
    items: z.array(zNotificationTargetWritable),
    nextCursor: z.string().optional()
});

/**
 * La regola di sottoscrizione: quali fatti del giornale escono, e verso quali destinazioni. Le quattro dimensioni sono in AND fra loro e in OR dentro ogni insieme; un insieme VUOTO non filtra (`categories: []` = tutte le categorie). `scope` e' immutabile: e' il recinto della regola, e spostarlo cambierebbe silenziosamente cosa un destinatario riceve.
 *
 */
export const zNotificationRuleWritable = z.object({
    scope: z.string().max(255).optional(),
    recursive: z.boolean().optional().default(true),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    enabled: z.boolean().optional().default(true),
    minSeverity: zEventSeverity.optional().default('warning'),
    categories: z.array(zEventCategory).optional().default([]),
    types: z.array(z.string()).optional().default([]),
    targets: z.array(z.string()).min(1),
    repeatWindowS: z.int().gte(0).optional().default(3600),
    notes: z.string().max(65536).optional()
});

export const zNotificationRuleListWritable = z.object({
    items: z.array(zNotificationRuleWritable),
    nextCursor: z.string().optional()
});

/**
 * Una consegna e il suo esito — interamente scritta dal sistema. Nome della regola e della destinazione sono CONGELATI qui come il nome del soggetto sull'evento: una regola rinominata domani non riscrive cio' che e' stato spedito ieri. Per la stessa ragione la consegna sopravvive alla regola che l'ha generata (nessun vincolo referenziale), esattamente come l'evento sopravvive al suo task.
 *
 */
export const zNotificationDeliveryWritable = z.record(z.string(), z.unknown());

export const zNotificationDeliveryListWritable = z.object({
    items: z.array(zNotificationDeliveryWritable),
    nextCursor: z.string().optional()
});

/**
 * Page size (API spec §8).
 */
export const zLimit = z.int().gte(1).lte(500).default(100);

/**
 * Opaque pagination cursor from a previous response.
 */
export const zCursor = z.string();

/**
 * Uniform, repeatable predicate `<field>:<op>:<value>` (API spec §8). `<field>` is the dotted JSON path of a resource field (`enabled`, `tag.team`). Operators — negation is the `!` prefix — are gated by the field's type: `eq`/`!eq` and `in`/`!in` on any scalar; `gt`/`gte`/`lt`/`lte` on numbers and timestamps; `contains`/`!contains` on free-text strings (substring) and arrays (membership); `startsWith`/`endsWith` and their `!` forms on free-text strings; `exists` only on optional fields such as tag names, with a JSON boolean value (`filter=tag.team:exists:true` / `:exists:false` — no `!` form). Identity-like strings — `id`/UUID references and enum fields such as `phase` — accept only eq/!eq/in/!in (e.g. `phase:in:["running","queued"]`). SET FIELDS (parity R1, Q20 ratified 2026-09-09): a dotted path that reaches a SET of identities through an array — `attachedTo.vm` on a disk attached to N VMs — takes `contains`/`!contains` (exact membership: `attachedTo.vm:contains:"<vm id>"` = one of the attaching VMs is that one) and `in`/`!in` (overlap: one of the members is among the candidates); `exists:true` / `exists:false` tell a non-empty set from an empty one. `eq`/`!eq` and the text operators do NOT apply to a set and are `400 invalid_argument` naming `contains` as the cure. TAG PREDICATES (`tag.<name>:<op>:<value>`, Annex T — they REPLACED `labels.<key>`, no transition alias) match the resource's assignments: `<name>` is the tag's BARE name when it is unambiguous among the tags visible to the caller, and the qualified `/scope/name` form otherwise — an ambiguous bare name is `400 invalid_argument` listing the candidates. `!eq` matches resources that carry a different value AND those that carry the tag not at all, as `labels` did. `<value>` is a JSON value (strings quoted, arrays for in/!in — percent-encode in real URLs). `eq`/`!eq` also accept JSON `null` for absence (round #128 — `parent:eq:null` selects top-level tasks): `eq null` matches where the field is absent, `!eq null` where it is present. Multiple filters AND together. Accepted fields are declared per collection via `x-tvirt-filterable`; violations are `400 invalid_argument` naming the offending filter in `errors[]`.
 *
 */
export const zFilter = z.array(z.string());

/**
 * Sort key on the same field paths as `filter`, `-` prefix for descending: `username`, `-updatedAt`.
 */
export const zSort = z.string();

/**
 * Sudo-mode elevation token from `POST /api/v6/auth/sudo` (API spec §3).
 */
export const zSudoToken = z.string();

/**
 * Full admission (schema, invariants, references, RBAC, quotas) without persisting or scheduling anything; returns what would happen (API spec §13).
 *
 */
export const zDryRun = z.boolean().default(false);

/**
 * The optimistic-locking precondition (RFC 9110 §13.1.1; R6 «Identità e revisioni», ratified 2026-08-06): the `ETag` the client last read, echoed back. The write lands only if the object is STILL in that state — otherwise `412 precondition_failed` naming both validators. `*` is the deliberate opt-out ("whatever the current state is"), which is a decision the client makes ON PURPOSE and leaves in its source, not a header it forgot. PERIMETER (R6 fase 1, ratified 2026-08-24): honored on the update operation of EVERY config resource that serves an `etag` — OPTIONAL everywhere: absent keeps today's behavior (no 428, the existing in-flight conflict guards stay underneath as the floor), present and stale answers `412 precondition_failed` naming both validators. The 428 OBLIGATION exists only where it was ratified on 2026-08-06 — the fabric PATCH, whose body names `logicalNetworks`, `switches` or `laggs`: a document whose arrays REPLACE WHOLE, so a stale body deletes the other operator's work without a single error.
 *
 */
export const zIfMatch = z.string();

/**
 * Scope selector (API spec §8): `/cluster1/vdc1` selects exactly that scope, `/cluster1/vdc1*` selects the scope and every sub-scope. A Scope `id` is accepted in place of the string, with the same optional `*` suffix. SINGLE TREE (cluster-first, 2026-07-15): there is one scope tree per cluster and one authority — the default `*` spans the whole tree everywhere (one-query-one-authority and its `422 scope_ambiguous` died with the node planes).
 *
 */
export const zScopeFilter = z.string().default('/*');

/**
 * Client-generated UUID for action invocations (API spec §2): stored with the resulting Task for 24 h; retries replay the same response instead of re-executing. When task retention has pruned the referenced Task inside that window (#157-T2, ratified 2026-08-04), the replay answers `410` `gone` — the original request DID execute, but its outcome is no longer derivable and it is deliberately NOT re-executed.
 *
 */
export const zIdempotencyKey = z.uuid();

/**
 * The component is alive.
 */
export const zGetSystemHealthResponse = zHealth;

/**
 * Ready to serve.
 */
export const zGetSystemReadyResponse = zHealth;

/**
 * The caller's overview.
 */
export const zGetSystemOverviewResponse = zSystemOverview;

/**
 * The caller's census.
 */
export const zGetSystemExplorerResponse = zSystemExplorer;

export const zGetSystemDiagnoseQuery = z.object({
    area: z.array(z.enum([
        'nodes',
        'tasks',
        'storage',
        'backups',
        'workloads',
        'netsvc',
        'notifications',
        'events',
        'license',
        'ups'
    ])).optional(),
    sinceHours: z.int().gte(1).lte(720).optional().default(24)
});

/**
 * The run, worst first.
 */
export const zGetSystemDiagnoseResponse = zDiagnoseRun;

/**
 * The minted ticket URL.
 */
export const zRequestDiagnoseBundleResponse = z.object({
    downloadUrl: z.string(),
    fileName: z.string()
});

export const zDownloadDiagnoseBundleByTicketPath = z.object({
    ticket: z.string()
});

/**
 * The tar.gz bytes; the ticket is now spent.
 */
export const zDownloadDiagnoseBundleByTicketResponse = z.string();

/**
 * OpenAPI 3.1 document.
 */
export const zGetOpenapiDocumentResponse = z.record(z.string(), z.unknown());

export const zListSessionsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional()
});

/**
 * Session page.
 */
export const zListSessionsResponse = zSessionList;

export const zCreateSessionBody = z.object({
    username: z.string(),
    password: z.string(),
    realm: z.string().optional(),
    otp: z.string().min(6).max(32).optional()
});

/**
 * Session created; the body carries the `tvts_` bearer token (shown exactly once) and the session details.
 *
 */
export const zCreateSessionResponse = zSessionCreated;

/**
 * Logged out.
 */
export const zDeleteCurrentSessionResponse = z.void();

/**
 * The current session.
 */
export const zGetCurrentSessionResponse = zSession;

export const zDeleteSessionPath = z.object({
    session: z.uuid()
});

/**
 * Revoked.
 */
export const zDeleteSessionResponse = z.void();

export const zGetSessionPath = z.object({
    session: z.uuid()
});

/**
 * The session.
 */
export const zGetSessionResponse = zSession;

export const zElevateSudoBody = z.object({
    password: z.string()
});

/**
 * Elevation granted.
 */
export const zElevateSudoResponse = zSudoGrant;

export const zListMyPermissionsQuery = z.object({
    scope: z.string().max(257).optional()
});

/**
 * The held subset of the catalog, sorted by name. Reuses the catalog entry shape of `GET /rbac/permissions`.
 *
 */
export const zListMyPermissionsResponse = zPermissionList;

export const zIdentifyLoginBody = zIdentifyRequest;

/**
 * The next step for this identifier.
 */
export const zIdentifyLoginResponse = zIdentifyResult;

/**
 * The available methods.
 */
export const zListMfaMethodsResponse = zMfaMethodList;

export const zListMfaEnrollmentsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Enrollment page.
 */
export const zListMfaEnrollmentsResponse = zMfaEnrollmentList;

export const zDeleteMfaEnrollmentHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional()
});

export const zDeleteMfaEnrollmentPath = z.object({
    enrollment: z.uuid()
});

/**
 * Reset.
 */
export const zDeleteMfaEnrollmentResponse = z.void();

export const zGetMfaEnrollmentPath = z.object({
    enrollment: z.uuid()
});

/**
 * The enrollment.
 */
export const zGetMfaEnrollmentResponse = zMfaEnrollment;

export const zListAuthRealmsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Realm page.
 */
export const zListAuthRealmsResponse = zAuthRealmList;

export const zCreateAuthRealmBody = zAuthRealmWritable;

export const zCreateAuthRealmHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional()
});

export const zCreateAuthRealmQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateAuthRealmResponse = zAuthRealm;

export const zDeleteAuthRealmHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional()
});

export const zDeleteAuthRealmPath = z.object({
    realm: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteAuthRealmResponse = z.void();

export const zGetAuthRealmPath = z.object({
    realm: z.uuid()
});

/**
 * The realm.
 */
export const zGetAuthRealmResponse = zAuthRealm;

export const zPatchAuthRealmBody = zAuthRealmWritable;

export const zPatchAuthRealmHeaders = z.object({
    'If-Match': z.string().optional(),
    'X-TVirt-Sudo': z.string().optional()
});

export const zPatchAuthRealmPath = z.object({
    realm: z.uuid()
});

export const zPatchAuthRealmQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchAuthRealmResponse = zAuthRealm;

export const zTestAuthRealmBody = z.object({
    username: z.string().max(255).optional()
});

export const zTestAuthRealmHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional()
});

export const zTestAuthRealmPath = z.object({
    realm: z.uuid()
});

/**
 * The probe's outcome — `200` even when the directory refused.
 */
export const zTestAuthRealmResponse = zAuthRealmTestResult;

export const zListUsersQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * User page.
 */
export const zListUsersResponse = zUserList;

export const zCreateUserBody = zUserWritable;

export const zCreateUserQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateUserResponse = zUser;

/**
 * Your account.
 */
export const zGetMyselfResponse = zUser;

export const zPatchMyselfBody = zUserWritable;

export const zPatchMyselfHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchMyselfQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchMyselfResponse = zUser;

export const zReplaceMyPasswordBody = z.object({
    currentPassword: z.string(),
    newPassword: z.string()
});

export const zReplaceMyPasswordHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

/**
 * Password changed.
 */
export const zReplaceMyPasswordResponse = z.void();

/**
 * Your MFA state.
 */
export const zGetMyMfaResponse = zMfaStatus;

export const zDeleteMyTotpEnrollmentHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional()
});

/**
 * Enrollment removed.
 */
export const zDeleteMyTotpEnrollmentResponse = z.void();

/**
 * Enrollment started; the secret is in the body, shown once.
 */
export const zStartMyTotpEnrollmentResponse = zTotpEnrollment;

export const zConfirmMyTotpEnrollmentBody = z.object({
    code: z.string().min(6).max(12)
});

/**
 * Enrollment confirmed; the recovery codes are in the body.
 */
export const zConfirmMyTotpEnrollmentResponse = zRecoveryCodes;

export const zRegenerateMyRecoveryCodesHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional()
});

/**
 * The new codes.
 */
export const zRegenerateMyRecoveryCodesResponse = zRecoveryCodes;

/**
 * Your app tokens.
 */
export const zListMyAppTokensResponse = zAppTokenList;

export const zCreateMyAppTokenBody = zAppTokenWritable;

/**
 * Created — the only response that ever carries the secret.
 */
export const zCreateMyAppTokenResponse = zAppTokenCreated;

export const zDeleteMyAppTokenPath = z.object({
    appToken: z.uuid()
});

/**
 * Revoked.
 */
export const zDeleteMyAppTokenResponse = z.void();

export const zGetMyAppTokenPath = z.object({
    appToken: z.uuid()
});

/**
 * The app token (metadata only).
 */
export const zGetMyAppTokenResponse = zAppToken;

export const zBatchDeleteUsersBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteUsersResponse = zBatchDeleteResult;

export const zDeleteUserPath = z.object({
    user: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteUserResponse = z.void();

export const zGetUserPath = z.object({
    user: z.uuid()
});

/**
 * The user.
 */
export const zGetUserResponse = zUser;

export const zPatchUserBody = zUserWritable;

export const zPatchUserHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchUserPath = z.object({
    user: z.uuid()
});

export const zPatchUserQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchUserResponse = zUser;

export const zReplaceUserPasswordBody = z.object({
    newPassword: z.string(),
    mustChangePassword: z.boolean().optional().default(true)
});

export const zReplaceUserPasswordHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional()
});

export const zReplaceUserPasswordPath = z.object({
    user: z.uuid()
});

/**
 * Password set.
 */
export const zReplaceUserPasswordResponse = z.void();

export const zUnlockUserHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zUnlockUserPath = z.object({
    user: z.uuid()
});

/**
 * Unlocked.
 */
export const zUnlockUserResponse = zUser;

export const zRevokeUserSessionsBody = z.object({
    includeTokens: z.boolean().optional().default(false)
});

export const zRevokeUserSessionsHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRevokeUserSessionsPath = z.object({
    user: z.uuid()
});

/**
 * PROPOSAL: revocation counts; the spec does not define the body.
 */
export const zRevokeUserSessionsResponse = z.object({
    revokedSessions: z.int().optional(),
    revokedTokens: z.int().optional()
});

export const zResetUserMfaHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional()
});

export const zResetUserMfaPath = z.object({
    user: z.uuid()
});

/**
 * Reset.
 */
export const zResetUserMfaResponse = z.void();

export const zGetUserMfaPath = z.object({
    user: z.uuid()
});

/**
 * The user's MFA state.
 */
export const zGetUserMfaResponse = zMfaStatus;

export const zListGroupsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Group page.
 */
export const zListGroupsResponse = zGroupList;

export const zCreateGroupBody = zGroupWritable;

export const zCreateGroupQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateGroupResponse = zGroup;

export const zBatchDeleteGroupsBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteGroupsResponse = zBatchDeleteResult;

export const zDeleteGroupPath = z.object({
    group: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteGroupResponse = z.void();

export const zGetGroupPath = z.object({
    group: z.uuid()
});

/**
 * The group.
 */
export const zGetGroupResponse = zGroup;

export const zPatchGroupBody = zGroupWritable;

export const zPatchGroupHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchGroupPath = z.object({
    group: z.uuid()
});

export const zPatchGroupQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchGroupResponse = zGroup;

export const zAddGroupMemberBody = z.object({
    user: z.string()
});

export const zAddGroupMemberPath = z.object({
    group: z.uuid()
});

/**
 * The group, member included.
 */
export const zAddGroupMemberResponse = zGroup;

export const zRemoveGroupMemberPath = z.object({
    group: z.uuid(),
    member: z.string()
});

/**
 * Removed.
 */
export const zRemoveGroupMemberResponse = z.void();

export const zListScopesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Scope page.
 */
export const zListScopesResponse = zScopeList;

export const zCreateScopeBody = zScopeWritable;

export const zCreateScopeQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateScopeResponse = zScope;

export const zBatchDeleteScopesBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteScopesResponse = zBatchDeleteResult;

export const zDeleteScopePath = z.object({
    scope: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteScopeResponse = z.void();

export const zGetScopePath = z.object({
    scope: z.uuid()
});

/**
 * The scope.
 */
export const zGetScopeResponse = zScope;

export const zPatchScopeBody = zScopeWritable;

export const zPatchScopeHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchScopePath = z.object({
    scope: z.uuid()
});

export const zPatchScopeQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchScopeResponse = zScope;

/**
 * The catalog.
 */
export const zListRbacPermissionsResponse = zPermissionList;

export const zListRbacRolesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Role page.
 */
export const zListRbacRolesResponse = zRoleList;

export const zCreateRbacRoleBody = zRoleWritable;

export const zCreateRbacRoleQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateRbacRoleResponse = zRole;

export const zBatchDeleteRolesBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteRolesResponse = zBatchDeleteResult;

export const zDeleteRbacRolePath = z.object({
    role: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteRbacRoleResponse = z.void();

export const zGetRbacRolePath = z.object({
    role: z.uuid()
});

/**
 * The role.
 */
export const zGetRbacRoleResponse = zRole;

export const zPatchRbacRoleBody = zRoleWritable;

export const zPatchRbacRoleHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchRbacRolePath = z.object({
    role: z.uuid()
});

export const zPatchRbacRoleQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchRbacRoleResponse = zRole;

export const zListRbacGrantsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Grant page.
 */
export const zListRbacGrantsResponse = zGrantList;

export const zCreateRbacGrantBody = zGrantWritable;

export const zCreateRbacGrantQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateRbacGrantResponse = zGrant;

export const zBatchDeleteGrantsBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteGrantsResponse = zBatchDeleteResult;

export const zDeleteRbacGrantPath = z.object({
    grant: z.uuid()
});

/**
 * Revoked.
 */
export const zDeleteRbacGrantResponse = z.void();

export const zGetRbacGrantPath = z.object({
    grant: z.uuid()
});

/**
 * The grant.
 */
export const zGetRbacGrantResponse = zGrant;

export const zListTagsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Tag page.
 */
export const zListTagsResponse = zTagList;

export const zCreateTagBody = zTagWritable;

export const zCreateTagQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateTagResponse = zTag;

export const zBatchDeleteTagsBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteTagsResponse = zBatchDeleteResult;

export const zDeleteTagPath = z.object({
    tag: z.uuid()
});

export const zDeleteTagQuery = z.object({
    cascade: z.boolean().optional().default(false)
});

/**
 * Deleted.
 */
export const zDeleteTagResponse = z.void();

export const zGetTagPath = z.object({
    tag: z.uuid()
});

/**
 * The tag.
 */
export const zGetTagResponse = zTag;

export const zPatchTagBody = zTagWritable;

export const zPatchTagHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchTagPath = z.object({
    tag: z.uuid()
});

export const zPatchTagQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchTagResponse = zTag;

export const zCreateTagValueBody = zTagValueWrite;

export const zCreateTagValuePath = z.object({
    tag: z.uuid()
});

/**
 * Created.
 */
export const zCreateTagValueResponse = zTag;

export const zDeleteTagValuePath = z.object({
    tag: z.uuid(),
    value: z.string()
});

/**
 * Deleted.
 */
export const zDeleteTagValueResponse = z.void();

export const zRenameTagValueBody = zTagValueWrite;

export const zRenameTagValuePath = z.object({
    tag: z.uuid(),
    value: z.string()
});

/**
 * Renamed.
 */
export const zRenameTagValueResponse = zTag;

export const zListTagAssignmentsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Assignment page.
 */
export const zListTagAssignmentsResponse = zTagAssignmentList;

export const zAssignTagBody = zTagAssignmentRequest;

export const zAssignTagQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Attached.
 */
export const zAssignTagResponse = zTagAssignment;

export const zUnassignTagPath = z.object({
    assignment: z.uuid()
});

/**
 * Detached.
 */
export const zUnassignTagResponse = z.void();

export const zRebootNodeBody = z.object({
    force: z.boolean().optional().default(false)
});

export const zRebootNodeHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

/**
 * Reboot scheduled.
 */
export const zRebootNodeResponse = zTask;

export const zShutdownNodeBody = z.object({
    force: z.boolean().optional().default(false)
});

export const zShutdownNodeHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

/**
 * Shutdown scheduled.
 */
export const zShutdownNodeResponse = zTask;

/**
 * Removed.
 */
export const zRemoveLicenseResponse = z.void();

/**
 * The singleton, usage computed at read time — the full `License` for cluster-root authority, the reduced `LicenseStatus` for every other `license.read` holder.
 *
 */
export const zGetLicenseResponse = z.union([
    zLicense,
    zLicenseStatus
]);

export const zRegisterLicenseBody = zLicenseWritable;

/**
 * Registered.
 */
export const zRegisterLicenseResponse = zLicense;

/**
 * The singleton — the full `Cluster` for cluster-root authority, the reduced `ClusterStatus` for every other `cluster.read` holder.
 *
 */
export const zGetClusterResponse = z.union([
    zCluster,
    zClusterStatus
]);

export const zPatchClusterBody = zClusterWritable;

export const zPatchClusterHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchClusterQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchClusterResponse = zCluster;

export const zListClusterNodesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * One page.
 */
export const zListClusterNodesResponse = zClusterNodeList;

export const zAddClusterNodeBody = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    ip: z.string().max(45),
    ttlSeconds: z.int().gte(60).lte(86400).optional().default(1800),
    notes: z.string().max(65536).optional()
});

export const zAddClusterNodeQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Pre-registered; the token rides only this response.
 */
export const zAddClusterNodeResponse = zClusterNodeCreated;

export const zRemoveClusterNodePath = z.object({
    node: z.string()
});

/**
 * Revoked.
 */
export const zRemoveClusterNodeResponse = z.void();

export const zPatchClusterNodeBody = zClusterNodeWritable;

export const zPatchClusterNodeHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchClusterNodePath = z.object({
    node: z.string()
});

export const zPatchClusterNodeQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * The refreshed node; when the placement changed, one `X-TVirt-Task` header per CRUSH-realignment task born of the write.
 *
 */
export const zPatchClusterNodeResponse = zClusterNode;

export const zEnterNodeMaintenancePath = z.object({
    node: z.string()
});

/**
 * Drain parent task accepted.
 */
export const zEnterNodeMaintenanceResponse = z.object({
    phase: z.string().optional()
});

export const zExitNodeMaintenancePath = z.object({
    node: z.string()
});

/**
 * The node, out of maintenance.
 */
export const zExitNodeMaintenanceResponse = zClusterNode;

export const zNodeEvacuationPlanPath = z.object({
    node: z.string()
});

/**
 * The evacuation plan.
 */
export const zNodeEvacuationPlanResponse = zNodeEvacuationPlan;

export const zNodeFirewallPath = z.object({
    node: z.string()
});

/**
 * The node-firewall lens.
 */
export const zNodeFirewallResponse = zNodeFirewall;

export const zGetNodeStatsPath = z.object({
    node: z.string()
});

export const zGetNodeStatsQuery = z.object({
    metric: z.string(),
    range: z.string().regex(/^[0-9]+[smhd]$/).optional().default('1h'),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    step: z.int().gte(30).optional(),
    agg: z.enum(['avg', 'max']).optional().default('avg')
});

/**
 * The series.
 */
export const zGetNodeStatsResponse = zStatsSeries;

export const zPromoteClusterNodePath = z.object({
    node: z.string()
});

/**
 * Promotion parent task accepted.
 */
export const zPromoteClusterNodeResponse = z.object({
    phase: z.string().optional()
});

export const zDemoteClusterNodePath = z.object({
    node: z.string()
});

/**
 * Demotion parent task accepted.
 */
export const zDemoteClusterNodeResponse = z.object({
    phase: z.string().optional()
});

export const zRestartClusterNodeStorePath = z.object({
    node: z.string()
});

/**
 * Restart task accepted.
 */
export const zRestartClusterNodeStoreResponse = z.object({
    phase: z.string().optional()
});

export const zEditClusterNodeNetworkBody = z.object({
    op: z.enum([
        'add-nic',
        'del-nic',
        'set-if',
        'clear-if',
        'edit-if',
        'add-vlan',
        'del-vlan',
        'add-bond',
        'edit-bond',
        'del-bond',
        'add-bridge',
        'edit-bridge',
        'del-bridge',
        'set-hostname',
        'add-static-host-entry',
        'del-static-host-entry',
        'take-ownership'
    ]),
    device: z.string().optional(),
    mac: z.string().regex(/^([0-9a-fA-F]{2}:){5}[0-9a-fA-F]{2}$/).optional(),
    ip4: z.union([
        z.array(z.string()),
        z.enum(['dhcp'])
    ]).optional(),
    gw4: z.string().nullish(),
    dns: z.array(z.string()).optional(),
    routes4: z.array(z.object({
        to: z.string(),
        via: z.string()
    })).optional(),
    ip6: z.union([
        z.array(z.string()),
        z.enum(['dhcp'])
    ]).optional(),
    gw6: z.string().nullish(),
    routes6: z.array(z.object({
        to: z.string(),
        via: z.string()
    })).optional(),
    mtu: z.int().nullish(),
    uplinkMbps: z.int().gte(1).lte(1000000).nullish(),
    vlanId: z.int().gte(1).lte(4094).optional(),
    members: z.array(z.string()).optional(),
    mode: z.enum(['active-backup', 'lacp']).optional(),
    miimon: z.int().optional(),
    primary: z.string().optional(),
    rate: zLacpRate.optional(),
    hash: z.enum([
        'layer2',
        'layer2+3',
        'layer3+4'
    ]).optional(),
    stp: z.boolean().optional(),
    hostname: z.string().optional(),
    ip: z.string().optional(),
    names: z.array(z.string()).optional()
});

export const zEditClusterNodeNetworkPath = z.object({
    node: z.string()
});

/**
 * The refreshed node (networkConfig carries the bumped document).
 */
export const zEditClusterNodeNetworkResponse = zClusterNode;

export const zAttestClusterNodeDownPath = z.object({
    node: z.string()
});

/**
 * Attested — leases released, authorities transferable.
 */
export const zAttestClusterNodeDownResponse = z.void();

export const zGrantClusterNodeReadOnlyAccessBody = z.object({
    reason: z.string().min(1).max(500),
    ttlSeconds: z.int().gte(60).lte(86400).optional().default(28800)
});

export const zGrantClusterNodeReadOnlyAccessPath = z.object({
    node: z.string()
});

/**
 * Grant recorded, task accepted; the password rides ONLY this response.
 */
export const zGrantClusterNodeReadOnlyAccessResponse = zReadOnlyAccessGranted;

export const zRevokeClusterNodeReadOnlyAccessPath = z.object({
    node: z.string()
});

/**
 * Revoke recorded, re-lock task accepted.
 */
export const zRevokeClusterNodeReadOnlyAccessResponse = zReadOnlyAccess;

/**
 * The matrix.
 */
export const zGetClusterLinksResponse = zLinkMatrix;

/**
 * The Proximity panel.
 */
export const zGetProximityResponse = zProximityStatus;

export const zPatchProximityBody = zProximityStatusWritable;

export const zPatchProximityHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchProximityQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * The refreshed panel.
 */
export const zPatchProximityResponse = zProximityStatus;

export const zListProximityRulesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Rule page.
 */
export const zListProximityRulesResponse = zProximityRuleList;

export const zCreateProximityRuleBody = zProximityRuleWritable;

export const zCreateProximityRuleQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateProximityRuleResponse = zProximityRule;

export const zDeleteProximityRulePath = z.object({
    rule: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteProximityRuleResponse = z.void();

export const zGetProximityRulePath = z.object({
    rule: z.uuid()
});

/**
 * The rule.
 */
export const zGetProximityRuleResponse = zProximityRule;

export const zPatchProximityRuleBody = zProximityRuleWritable;

export const zPatchProximityRuleHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchProximityRulePath = z.object({
    rule: z.uuid()
});

export const zPatchProximityRuleQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchProximityRuleResponse = zProximityRule;

export const zListVmsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * VM page.
 */
export const zListVmsResponse = zVirtualMachineList;

export const zCreateVmBody = zVirtualMachineWritable;

export const zCreateVmQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateVmResponse = zVirtualMachine;

export const zBatchDeleteVmsBody = zBatchVmDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteVmsResponse = zBatchDeleteResult;

export const zBatchStartVmsBody = zBatchActionRequest;

/**
 * Per-item outcomes.
 */
export const zBatchStartVmsResponse = zBatchActionResult;

export const zBatchStopVmsBody = zBatchVmStopRequest;

/**
 * Per-item outcomes.
 */
export const zBatchStopVmsResponse = zBatchActionResult;

export const zBatchPauseVmsBody = zBatchActionRequest;

/**
 * Per-item outcomes.
 */
export const zBatchPauseVmsResponse = zBatchActionResult;

export const zBatchResumeVmsBody = zBatchActionRequest;

/**
 * Per-item outcomes.
 */
export const zBatchResumeVmsResponse = zBatchActionResult;

export const zBatchResetVmsBody = zBatchActionRequest;

/**
 * Per-item outcomes.
 */
export const zBatchResetVmsResponse = zBatchActionResult;

export const zDeleteVmPath = z.object({
    vm: z.uuid()
});

export const zDeleteVmQuery = z.object({
    keepDisks: z.boolean().optional().default(false)
});

/**
 * Teardown started.
 */
export const zDeleteVmResponse = zTask;

export const zGetVmPath = z.object({
    vm: z.uuid()
});

/**
 * The VM.
 */
export const zGetVmResponse = zVirtualMachine;

export const zPatchVmBody = zVirtualMachineWritable;

export const zPatchVmHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchVmPath = z.object({
    vm: z.uuid()
});

export const zPatchVmQuery = z.object({
    dryRun: z.boolean().optional().default(false),
    live: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchVmResponse = zVirtualMachine;

export const zGetVmScreenshotPath = z.object({
    vm: z.uuid()
});

/**
 * The capture.
 */
export const zGetVmScreenshotResponse = z.string();

export const zStartVmBody = z.object({
    node: z.union([
        z.string(),
        zNodeSelector
    ]).optional(),
    forceCordoned: z.boolean().optional().default(false)
});

export const zStartVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zStartVmPath = z.object({
    vm: z.uuid()
});

/**
 * Starting.
 */
export const zStartVmResponse = zTask;

export const zVmPlacementPath = z.object({
    vm: z.uuid()
});

/**
 * The placement census.
 */
export const zVmPlacementResponse = zVmPlacement;

export const zVmSpofPath = z.object({
    vm: z.uuid()
});

export const zVmSpofQuery = z.object({
    domain: z.enum(['node']).optional().default('node')
});

/**
 * The independence report.
 */
export const zVmSpofResponse = zVmSpof;

export const zMigrateVmBody = z.object({
    node: z.union([
        z.string(),
        zNodeSelector
    ]).optional()
});

export const zMigrateVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zMigrateVmPath = z.object({
    vm: z.uuid()
});

/**
 * Migrating; the header references the vm.migrate parent task.
 */
export const zMigrateVmResponse = z.object({
    phase: z.enum(['migrating']).optional(),
    warnings: z.array(z.string()).optional()
});

export const zMoveVmStorageBody = z.object({
    storage: z.string()
});

export const zMoveVmStorageHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zMoveVmStoragePath = z.object({
    vm: z.uuid()
});

/**
 * Moving; the body is the vm.moveStorage parent task.
 */
export const zMoveVmStorageResponse = zTask;

export const zMoveVmScopeBody = z.object({
    scope: z.string().max(255).regex(/^(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){0,7}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/)
});

export const zMoveVmScopePath = z.object({
    vm: z.uuid()
});

export const zMoveVmScopeQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Moved — the refreshed VM, new scope (or the no-op echo).
 */
export const zMoveVmScopeResponse = zVirtualMachine;

export const zStopVmBody = z.object({
    mode: z.enum(['acpi', 'hard']).optional().default('acpi'),
    timeoutSeconds: z.int().gte(1).optional().default(60)
});

export const zStopVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zStopVmPath = z.object({
    vm: z.uuid()
});

/**
 * Stopping.
 */
export const zStopVmResponse = zTask;

export const zResetVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zResetVmPath = z.object({
    vm: z.uuid()
});

/**
 * Resetting.
 */
export const zResetVmResponse = zTask;

export const zPauseVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zPauseVmPath = z.object({
    vm: z.uuid()
});

/**
 * Pausing.
 */
export const zPauseVmResponse = zTask;

export const zResumeVmBody = z.object({
    forceCordoned: z.boolean().optional().default(false)
});

export const zResumeVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zResumeVmPath = z.object({
    vm: z.uuid()
});

/**
 * Resuming.
 */
export const zResumeVmResponse = zTask;

export const zSnapshotVmBody = z.object({
    name: z.string().regex(/^[A-Za-z0-9._-]+$/),
    memory: z.boolean().optional().default(false),
    requireQuiesce: z.boolean().optional().default(false)
});

export const zSnapshotVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zSnapshotVmPath = z.object({
    vm: z.uuid()
});

/**
 * Snapshotting.
 */
export const zSnapshotVmResponse = zTask;

export const zRevertVmBody = z.object({
    snapshot: z.string(),
    memory: z.boolean().optional()
});

export const zRevertVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRevertVmPath = z.object({
    vm: z.uuid()
});

/**
 * Reverting.
 */
export const zRevertVmResponse = zTask;

export const zDeleteVmSnapshotBody = z.object({
    name: z.string(),
    acceptDuplication: z.boolean().optional().default(false)
});

export const zDeleteVmSnapshotHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDeleteVmSnapshotPath = z.object({
    vm: z.uuid()
});

/**
 * Deleting the snapshot.
 */
export const zDeleteVmSnapshotResponse = zTask;

export const zCloneVmBody = z.object({
    name: z.string(),
    scope: z.string().optional(),
    storage: z.string().optional(),
    linked: z.boolean().optional().default(true),
    markCloneAs: z.enum(['vm', 'template']).nullish().default(null),
    preferredNode: z.string().nullish()
});

export const zCloneVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCloneVmPath = z.object({
    vm: z.uuid()
});

/**
 * Cloning.
 */
export const zCloneVmResponse = zTask;

export const zQuickDeployVmBody = zQuickDeploySpec;

export const zQuickDeployVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zQuickDeployVmPath = z.object({
    vm: z.uuid()
});

/**
 * Deploying — the parent task.
 */
export const zQuickDeployVmResponse = zTask;

export const zCreateVmReplicaBody = z.object({
    name: z.string(),
    scope: z.string().optional(),
    storage: z.uuid(),
    intervalSeconds: z.int().gte(60).optional().default(900)
});

export const zCreateVmReplicaHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateVmReplicaPath = z.object({
    vm: z.uuid()
});

/**
 * Seeding.
 */
export const zCreateVmReplicaResponse = zTask;

export const zPromoteVmReplicaPath = z.object({
    vm: z.uuid()
});

/**
 * Promossa — la VM aggiornata, ora standalone e spenta.
 */
export const zPromoteVmReplicaResponse = zVirtualMachine;

export const zDetachVmReplicaBody = z.object({
    replica: z.uuid()
});

export const zDetachVmReplicaPath = z.object({
    vm: z.uuid()
});

/**
 * Staccata — la VM sorgente aggiornata.
 */
export const zDetachVmReplicaResponse = zVirtualMachine;

export const zMarkVmTemplatePath = z.object({
    vm: z.uuid()
});

/**
 * Marked — the refreshed VM.
 */
export const zMarkVmTemplateResponse = zVirtualMachine;

export const zUnmarkVmTemplatePath = z.object({
    vm: z.uuid()
});

/**
 * Unmarked — the refreshed VM.
 */
export const zUnmarkVmTemplateResponse = zVirtualMachine;

export const zExportVmToCatalogBody = z.object({
    catalog: z.string(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    storage: z.string().optional()
});

export const zExportVmToCatalogHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zExportVmToCatalogPath = z.object({
    vm: z.uuid()
});

/**
 * Export started; the task's target is the new catalog file.
 */
export const zExportVmToCatalogResponse = zTask;

export const zTransferVmAuthorityBody = z.object({
    node: z.string()
});

export const zTransferVmAuthorityPath = z.object({
    vm: z.uuid()
});

/**
 * Transferred — the refreshed VM.
 */
export const zTransferVmAuthorityResponse = zVirtualMachine;

export const zViewVmPath = z.object({
    vm: z.uuid()
});

export const zViewVmQuery = z.object({
    proto: z.enum(['vnc'])
});

/**
 * Viewer access.
 */
export const zViewVmResponse = zViewerAccess;

export const zConnectVmViewerPath = z.object({
    vm: z.uuid(),
    ticket: z.string().min(1)
});

export const zGetVmViewerFatePath = z.object({
    vm: z.uuid(),
    ticket: z.string().min(1)
});

/**
 * The ticket's fate — always `200`, see the schema.
 */
export const zGetVmViewerFateResponse = zViewerFate;

export const zMountGuestToolsVmPath = z.object({
    vm: z.uuid()
});

/**
 * Mounted — the refreshed VM (plus the live-apply task when the VM is up).
 */
export const zMountGuestToolsVmResponse = zVirtualMachine;

export const zCreateVmDiskBody = z.object({
    storage: z.string(),
    sizeGiB: z.int().gte(1),
    index: z.int().gte(1).lte(4).optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    format: zDiskFormat.optional(),
    encrypted: z.boolean().optional().default(false)
});

export const zCreateVmDiskHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateVmDiskPath = z.object({
    vm: z.uuid()
});

/**
 * Created and attached; the body is the new Disk, `Location` points at it and `X-TVirt-Task` at its `disk.create` task.
 *
 */
export const zCreateVmDiskResponse = zDisk;

export const zDetachVmDiskBody = z.object({
    index: z.int().gte(1).lte(4),
    delete: z.boolean().optional().default(false)
});

export const zDetachVmDiskHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDetachVmDiskPath = z.object({
    vm: z.uuid()
});

export const zDetachVmDiskResponse = z.union([
    zVirtualMachine,
    zTask
]);

export const zGetVmDescriptionPath = z.object({
    vm: z.uuid()
});

/**
 * The description.
 */
export const zGetVmDescriptionResponse = zVmDescription;

export const zGetVmDiskChangeTrackingPath = z.object({
    vm: z.uuid(),
    disk: z.string().regex(/^[1-4]$/)
});

/**
 * The change-tracking state.
 */
export const zGetVmDiskChangeTrackingResponse = zVmDiskChangeTracking;

export const zEnableVmDiskChangeTrackingBody = zEnableChangeTrackingRequest;

export const zEnableVmDiskChangeTrackingHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zEnableVmDiskChangeTrackingPath = z.object({
    vm: z.uuid(),
    disk: z.string().regex(/^[1-4]$/)
});

/**
 * Enabling.
 */
export const zEnableVmDiskChangeTrackingResponse = zTask;

export const zDisableVmDiskChangeTrackingBody = zChangeTrackingRequest;

export const zDisableVmDiskChangeTrackingHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDisableVmDiskChangeTrackingPath = z.object({
    vm: z.uuid(),
    disk: z.string().regex(/^[1-4]$/)
});

/**
 * Disabling.
 */
export const zDisableVmDiskChangeTrackingResponse = zTask;

export const zResetVmDiskChangeTrackingBody = zChangeTrackingRequest;

export const zResetVmDiskChangeTrackingHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zResetVmDiskChangeTrackingPath = z.object({
    vm: z.uuid(),
    disk: z.string().regex(/^[1-4]$/)
});

/**
 * Resetting.
 */
export const zResetVmDiskChangeTrackingResponse = zTask;

export const zQueryVmDiskChangedAreasBody = zQueryChangedAreasRequest;

export const zQueryVmDiskChangedAreasHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zQueryVmDiskChangedAreasPath = z.object({
    vm: z.uuid(),
    disk: z.string().regex(/^[1-4]$/)
});

/**
 * Computing the page; the Task `result` carries a `VmChangedAreasPage`.
 */
export const zQueryVmDiskChangedAreasResponse = zTask;

export const zCreateBackupExportBody = zCreateBackupExportRequest;

export const zCreateBackupExportHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateBackupExportPath = z.object({
    vm: z.uuid()
});

/**
 * Preparing the export.
 */
export const zCreateBackupExportResponse = zTask;

export const zCreateRestoreExportBody = zCreateRestoreExportRequest;

export const zCreateRestoreExportHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateRestoreExportPath = z.object({
    vm: z.uuid()
});

/**
 * Preparing the writable export.
 */
export const zCreateRestoreExportResponse = zTask;

export const zCreateVmFromDescriptionBody = zCreateVmFromDescriptionRequest;

export const zCreateVmFromDescriptionHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

/**
 * Minting the VM; the Task is the provisioning parent, its target the newborn VM.
 */
export const zCreateVmFromDescriptionResponse = zTask;

export const zCreateVmBackupBody = zCreateVmBackupRequest;

export const zCreateVmBackupHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateVmBackupPath = z.object({
    vm: z.uuid()
});

/**
 * Backup started.
 */
export const zCreateVmBackupResponse = zTask;

export const zListBackupExportsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Export page.
 */
export const zListBackupExportsResponse = zBackupExportList;

export const zCloseBackupExportHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCloseBackupExportPath = z.object({
    export: z.uuid()
});

/**
 * Closing.
 */
export const zCloseBackupExportResponse = zTask;

export const zGetBackupExportPath = z.object({
    export: z.uuid()
});

/**
 * The export.
 */
export const zGetBackupExportResponse = zBackupExport;

export const zGetVmStatsPath = z.object({
    vm: z.uuid()
});

export const zGetVmStatsQuery = z.object({
    metric: z.string(),
    range: z.string().regex(/^[0-9]+[smhd]$/).optional().default('1h'),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    step: z.int().gte(30).optional(),
    agg: z.enum(['avg', 'max']).optional().default('avg')
});

/**
 * The series.
 */
export const zGetVmStatsResponse = zStatsSeries;

export const zListFabricsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListFabricsResponse = zFabricList;

export const zCreateFabricBody = zFabricWritable;

export const zCreateFabricQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateFabricResponse = zFabric;

export const zBatchDeleteFabricsBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteFabricsResponse = zBatchDeleteResult;

export const zDeleteFabricPath = z.object({
    fabric: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteFabricResponse = z.void();

export const zGetFabricPath = z.object({
    fabric: z.uuid()
});

/**
 * The fabric.
 */
export const zGetFabricResponse = zFabric;

export const zPatchFabricBody = zFabricWritable;

export const zPatchFabricHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchFabricPath = z.object({
    fabric: z.uuid()
});

export const zPatchFabricQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchFabricResponse = zFabric;

export const zImportFabricNetworkBody = zFabricNetworkImport;

export const zImportFabricNetworkPath = z.object({
    fabric: z.uuid()
});

/**
 * Already imported — the same entry.
 */
export const zImportFabricNetworkResponse = zFabricImportedNetwork;

export const zUnimportFabricNetworkPath = z.object({
    fabric: z.uuid(),
    network: z.string().min(1).max(63)
});

/**
 * Un-imported.
 */
export const zUnimportFabricNetworkResponse = z.void();

export const zPreviewFabricNodeConfigsPath = z.object({
    fabric: z.uuid()
});

/**
 * The plan.
 */
export const zPreviewFabricNodeConfigsResponse = zFabricNodeConfigPlan;

export const zApplyFabricNodeConfigsBody = zFabricNodeConfigApply;

export const zApplyFabricNodeConfigsPath = z.object({
    fabric: z.uuid()
});

/**
 * Per-node outcomes.
 */
export const zApplyFabricNodeConfigsResponse = zFabricNodeConfigApplyResult;

export const zRestoreFabricNodeConfigBody = zFabricNodeConfigRestore;

export const zRestoreFabricNodeConfigPath = z.object({
    fabric: z.uuid()
});

/**
 * The restored document's coordinates.
 */
export const zRestoreFabricNodeConfigResponse = zFabricNodeConfigRestoreResult;

export const zListSubnetsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListSubnetsResponse = zSubnetList;

export const zGetSubnetPath = z.object({
    subnet: z.uuid()
});

/**
 * The subnet.
 */
export const zGetSubnetResponse = zSubnet;

/**
 * The view.
 */
export const zGetIpamViewResponse = zIpamView;

export const zListNetworkServicesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListNetworkServicesResponse = zNetworkServiceList;

export const zCreateNetworkServiceBody = zNetworkServiceWritable;

export const zCreateNetworkServiceQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateNetworkServiceResponse = zNetworkService;

export const zBatchDeleteNetworkServicesBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteNetworkServicesResponse = zBatchDeleteResult;

export const zNetworkServicesPlacementQuery = z.object({
    exclude: z.array(z.string()).optional(),
    scope: z.string().optional()
});

/**
 * One census per visible instance.
 */
export const zNetworkServicesPlacementResponse = zNetsvcPlacementList;

export const zDeleteNetworkServicePath = z.object({
    service: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteNetworkServiceResponse = z.void();

export const zGetNetworkServicePath = z.object({
    service: z.uuid()
});

/**
 * The service.
 */
export const zGetNetworkServiceResponse = zNetworkService;

export const zPatchNetworkServiceBody = zNetworkServiceWritable;

export const zPatchNetworkServiceHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchNetworkServicePath = z.object({
    service: z.uuid()
});

export const zPatchNetworkServiceQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchNetworkServiceResponse = zNetworkService;

export const zNetworkServicePlacementPath = z.object({
    service: z.uuid()
});

export const zNetworkServicePlacementQuery = z.object({
    exclude: z.array(z.string()).optional()
});

/**
 * The placement census of the instance.
 */
export const zNetworkServicePlacementResponse = zNetsvcPlacement;

export const zTransferNetworkServiceAuthorityBody = z.object({
    node: z.string()
});

export const zTransferNetworkServiceAuthorityPath = z.object({
    service: z.uuid()
});

/**
 * Transferred — the refreshed instance.
 */
export const zTransferNetworkServiceAuthorityResponse = zNetworkService;

export const zExportVpnProfileBody = z.object({
    peer: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)
});

export const zExportVpnProfilePath = z.object({
    service: z.uuid()
});

/**
 * The minted ticket URL.
 */
export const zExportVpnProfileResponse = z.object({
    downloadUrl: z.string(),
    fileName: z.string()
});

export const zDownloadVpnProfileByTicketPath = z.object({
    service: z.uuid(),
    ticket: z.string().min(1)
});

/**
 * The profile text; the ticket is now spent.
 */
export const zDownloadVpnProfileByTicketResponse = z.string();

export const zListOverlayNetworksQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListOverlayNetworksResponse = zOverlayNetworkList;

export const zCreateOverlayNetworkBody = zOverlayNetworkWritable;

export const zCreateOverlayNetworkQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateOverlayNetworkResponse = zOverlayNetwork;

export const zBatchDeleteOverlayNetworksBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteOverlayNetworksResponse = zBatchDeleteResult;

export const zDeleteOverlayNetworkPath = z.object({
    network: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteOverlayNetworkResponse = z.void();

export const zGetOverlayNetworkPath = z.object({
    network: z.uuid()
});

/**
 * The overlay.
 */
export const zGetOverlayNetworkResponse = zOverlayNetwork;

export const zPatchOverlayNetworkBody = zOverlayNetworkWritable;

export const zPatchOverlayNetworkHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchOverlayNetworkPath = z.object({
    network: z.uuid()
});

export const zPatchOverlayNetworkQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchOverlayNetworkResponse = zOverlayNetwork;

export const zListFirewallGroupsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Firewall group page.
 */
export const zListFirewallGroupsResponse = zFirewallGroupList;

export const zCreateFirewallGroupBody = zFirewallGroupWritable;

export const zCreateFirewallGroupQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateFirewallGroupResponse = zFirewallGroup;

export const zDeleteFirewallGroupPath = z.object({
    firewallGroup: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteFirewallGroupResponse = z.void();

export const zGetFirewallGroupPath = z.object({
    firewallGroup: z.uuid()
});

/**
 * The group.
 */
export const zGetFirewallGroupResponse = zFirewallGroup;

export const zPatchFirewallGroupBody = zFirewallGroupWritable;

export const zPatchFirewallGroupHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchFirewallGroupPath = z.object({
    firewallGroup: z.uuid()
});

export const zPatchFirewallGroupQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchFirewallGroupResponse = zFirewallGroup;

export const zListStorageQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional(),
    node: z.string().optional()
});

/**
 * Storage page.
 */
export const zListStorageResponse = zStorageList;

export const zCreateStorageBody = zStorageWritable;

export const zCreateStorageQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created; `X-TVirt-Task` points at the `storage.initDir` task initializing the on-disk namespace — the storage turns `ready` when it succeeds (the backend never waits for tasks, decided 2026-07-09).
 *
 */
export const zCreateStorageResponse = zStorage;

export const zBatchDeleteStorageBody = zBatchDeleteRequest;

export const zBatchDeleteStorageQuery = z.object({
    releaseImages: z.boolean().optional()
});

/**
 * Per-item outcomes.
 */
export const zBatchDeleteStorageResponse = zBatchDeleteResult;

export const zDeleteStoragePath = z.object({
    storage: z.uuid()
});

export const zDeleteStorageQuery = z.object({
    releaseImages: z.boolean().optional()
});

/**
 * Deleted.
 */
export const zDeleteStorageResponse = z.void();

export const zGetStoragePath = z.object({
    storage: z.uuid()
});

/**
 * The storage.
 */
export const zGetStorageResponse = zStorage;

export const zPatchStorageBody = zStorageWritable;

export const zPatchStorageHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchStoragePath = z.object({
    storage: z.uuid()
});

export const zPatchStorageQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchStorageResponse = zStorage;

export const zScanStorageHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zScanStoragePath = z.object({
    storage: z.uuid()
});

/**
 * Scan started.
 */
export const zScanStorageResponse = zTask;

export const zInitializeStorageHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zInitializeStoragePath = z.object({
    storage: z.uuid()
});

/**
 * Initialization started.
 */
export const zInitializeStorageResponse = zTask;

export const zAttestStorageLostPath = z.object({
    storage: z.uuid()
});

/**
 * Attested.
 */
export const zAttestStorageLostResponse = z.void();

export const zAdoptStorageVolumeGroupBody = z.object({
    device: z.string(),
    volumeGroup: z.string()
});

export const zAdoptStorageVolumeGroupHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zAdoptStorageVolumeGroupPath = z.object({
    storage: z.uuid()
});

/**
 * Adoption started.
 */
export const zAdoptStorageVolumeGroupResponse = zTask;

export const zListStorageObjectsPath = z.object({
    storage: z.uuid()
});

/**
 * The census, sorted by kind then name.
 */
export const zListStorageObjectsResponse = z.object({
    stale: zStale.optional(),
    stateAsOf: zStateAsOf.optional(),
    items: z.array(zStorageObject)
});

export const zListStorageFindingsPath = z.object({
    storage: z.uuid()
});

/**
 * The findings.
 */
export const zListStorageFindingsResponse = z.object({
    items: z.array(zStorageFinding)
});

export const zActOnStorageFindingBody = z.object({
    action: z.enum([
        'adopt',
        'purge',
        'dismiss'
    ]),
    catalog: z.string().optional()
});

export const zActOnStorageFindingPath = z.object({
    storage: z.uuid(),
    finding: z.uuid()
});

export const zActOnStorageFindingResponse = z.union([
    zStorageFinding,
    zTask
]);

export const zListCredentialsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Credential page — secrets never ride the render.
 */
export const zListCredentialsResponse = zCredentialList;

export const zCreateCredentialBody = zCredentialWritable;

export const zCreateCredentialQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateCredentialResponse = zCredential;

export const zGenerateCaBody = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    scope: z.string().max(255).regex(/^(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){0,7}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/).optional(),
    commonName: z.string().min(1).max(64).regex(/^[A-Za-z0-9][A-Za-z0-9 ._-]{0,63}$/),
    days: z.int().gte(1).lte(7300).optional().default(3650),
    keyType: z.enum([
        'ecP256',
        'rsa2048',
        'rsa4096'
    ]).optional().default('ecP256'),
    notes: z.string().max(65536).optional()
});

/**
 * The CA credential — metadata in `x509`, the key never rides.
 */
export const zGenerateCaResponse = zCredential;

export const zDeleteCredentialPath = z.object({
    credential: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteCredentialResponse = z.void();

export const zGetCredentialPath = z.object({
    credential: z.uuid()
});

/**
 * The credential — the secret never rides the render.
 */
export const zGetCredentialResponse = zCredential;

export const zPatchCredentialBody = zCredentialWritable;

export const zPatchCredentialHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchCredentialPath = z.object({
    credential: z.uuid()
});

export const zPatchCredentialQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated — the secret never rides the render.
 */
export const zPatchCredentialResponse = zCredential;

export const zIssueCertificateBody = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    commonName: z.string().min(1).max(64).regex(/^[A-Za-z0-9][A-Za-z0-9 ._-]{0,63}$/),
    san: z.array(z.string().min(1).max(253).regex(/^[A-Za-z0-9*.:_-]+$/)).max(64).optional(),
    days: z.int().gte(1).lte(3650).optional().default(365),
    usage: z.enum(['server', 'client']).optional().default('client'),
    keyType: z.enum([
        'ecP256',
        'rsa2048',
        'rsa4096'
    ]).optional().default('ecP256'),
    notes: z.string().max(65536).optional()
});

export const zIssueCertificatePath = z.object({
    credential: z.uuid()
});

/**
 * The issued certificate credential.
 */
export const zIssueCertificateResponse = zCredential;

export const zRevokeCertificatePath = z.object({
    credential: z.uuid()
});

/**
 * The revoked credential — `x509.revokedAt` set.
 */
export const zRevokeCertificateResponse = zCredential;

export const zExportCredentialPemPath = z.object({
    credential: z.uuid()
});

export const zExportCredentialPemQuery = z.object({
    chain: z.boolean().optional().default(false)
});

/**
 * The PEM export.
 */
export const zExportCredentialPemResponse = z.object({
    pem: z.string()
});

export const zGetCredentialCrlPath = z.object({
    credential: z.uuid()
});

/**
 * The CRL PEM.
 */
export const zGetCredentialCrlResponse = z.object({
    pem: z.string()
});

export const zListDisksQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Disk page.
 */
export const zListDisksResponse = zDiskList;

export const zCreateDiskBody = zDiskWritable;

export const zCreateDiskQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created; `X-TVirt-Task` points at the `disk.create` task.
 */
export const zCreateDiskResponse = zDisk;

export const zBatchDeleteDisksBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteDisksResponse = zBatchDeleteResult;

export const zDeleteDiskPath = z.object({
    disk: z.uuid()
});

/**
 * Deletion started.
 */
export const zDeleteDiskResponse = zTask;

export const zGetDiskPath = z.object({
    disk: z.uuid()
});

/**
 * The disk.
 */
export const zGetDiskResponse = zDisk;

export const zPatchDiskBody = zDiskWritable;

export const zPatchDiskHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchDiskPath = z.object({
    disk: z.uuid()
});

export const zPatchDiskQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated (`X-TVirt-Task` present when a grow was scheduled).
 */
export const zPatchDiskResponse = zDisk;

export const zMoveDiskBody = z.object({
    storage: z.string()
});

export const zMoveDiskHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zMoveDiskPath = z.object({
    disk: z.uuid()
});

/**
 * Move started; follow the task.
 */
export const zMoveDiskResponse = zTask;

export const zCloneDiskBody = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    storage: z.string().optional(),
    linked: z.boolean().optional().default(false)
});

export const zCloneDiskHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCloneDiskPath = z.object({
    disk: z.uuid()
});

/**
 * Clone started; the task's target is the new disk.
 */
export const zCloneDiskResponse = zTask;

export const zConvertDiskToTemplateBody = z.object({
    catalog: z.string(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional()
});

export const zConvertDiskToTemplateHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zConvertDiskToTemplatePath = z.object({
    disk: z.uuid()
});

/**
 * Conversion started; the task's target is the new catalog file.
 */
export const zConvertDiskToTemplateResponse = zTask;

export const zSnapshotDiskBody = z.object({
    name: z.string().regex(/^[A-Za-z0-9._-]+$/)
});

export const zSnapshotDiskHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zSnapshotDiskPath = z.object({
    disk: z.uuid()
});

/**
 * Snapshot taken started; follow the task.
 */
export const zSnapshotDiskResponse = zTask;

export const zRevertDiskBody = z.object({
    snapshot: z.string()
});

export const zRevertDiskHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRevertDiskPath = z.object({
    disk: z.uuid()
});

/**
 * Reverted started; follow the task.
 */
export const zRevertDiskResponse = zTask;

export const zDeleteDiskSnapshotBody = z.object({
    name: z.string()
});

export const zDeleteDiskSnapshotHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDeleteDiskSnapshotPath = z.object({
    disk: z.uuid()
});

/**
 * Snapshot deleted started; follow the task.
 */
export const zDeleteDiskSnapshotResponse = zTask;

export const zListCatalogsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Catalog page.
 */
export const zListCatalogsResponse = zCatalogList;

export const zCreateCatalogBody = zCatalogWritable;

export const zCreateCatalogQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateCatalogResponse = zCatalog;

export const zBatchDeleteCatalogsBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteCatalogsResponse = zBatchDeleteResult;

export const zDeleteCatalogPath = z.object({
    catalog: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteCatalogResponse = z.void();

export const zGetCatalogPath = z.object({
    catalog: z.uuid()
});

/**
 * The catalog, file counts included.
 */
export const zGetCatalogResponse = zCatalog;

export const zPatchCatalogBody = zCatalogWritable;

export const zPatchCatalogHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchCatalogPath = z.object({
    catalog: z.uuid()
});

export const zPatchCatalogQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchCatalogResponse = zCatalog;

export const zListCatalogFilesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Catalog file page.
 */
export const zListCatalogFilesResponse = zCatalogFileList;

export const zCreateCatalogFileBody = zCatalogFileWritable;

export const zCreateCatalogFileQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created (`X-TVirt-Task` present for URL sources).
 */
export const zCreateCatalogFileResponse = zCatalogFile;

export const zBatchDeleteCatalogFilesBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteCatalogFilesResponse = zBatchDeleteResult;

export const zDeleteCatalogFilePath = z.object({
    file: z.uuid()
});

export const zDeleteCatalogFileResponse = z.union([
    zTask,
    z.void()
]);

export const zGetCatalogFilePath = z.object({
    file: z.uuid()
});

/**
 * The file (metadata; content at `…/content`).
 */
export const zGetCatalogFileResponse = zCatalogFile;

export const zPatchCatalogFileBody = zCatalogFileWritable;

export const zPatchCatalogFileHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchCatalogFilePath = z.object({
    file: z.uuid()
});

export const zPatchCatalogFileQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchCatalogFileResponse = zCatalogFile;

export const zMoveCatalogFileBody = z.object({
    storage: z.string()
});

export const zMoveCatalogFileHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zMoveCatalogFilePath = z.object({
    file: z.uuid()
});

/**
 * Move started; follow the task.
 */
export const zMoveCatalogFileResponse = zTask;

export const zRehomeCatalogFileBody = z.object({
    catalog: z.string()
});

export const zRehomeCatalogFilePath = z.object({
    file: z.uuid()
});

/**
 * The file, now living in the target catalog (same id).
 */
export const zRehomeCatalogFileResponse = zCatalogFile;

export const zCopyCatalogFileBody = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    catalog: z.string().optional(),
    storage: z.string().optional()
});

export const zCopyCatalogFileHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCopyCatalogFilePath = z.object({
    file: z.uuid()
});

/**
 * Copy accepted; the new file is the task's target. Ordinary source: the link row already exists and the `catalog-file.copy` task closes immediately. Embedded source: materialization started (`catalog-file.materialize`).
 *
 */
export const zCopyCatalogFileResponse = zTask;

export const zVerifyCatalogFileHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zVerifyCatalogFilePath = z.object({
    file: z.uuid()
});

/**
 * Verification started; follow the task.
 */
export const zVerifyCatalogFileResponse = zTask;

export const zImportVmTemplateBody = z.object({
    storage: z.string(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    scope: z.string().optional(),
    metadataStorage: z.string().optional()
});

export const zImportVmTemplateHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zImportVmTemplatePath = z.object({
    file: z.uuid()
});

/**
 * Import started; the task's target is the newborn VM Template.
 */
export const zImportVmTemplateResponse = zTask;

export const zDownloadCatalogFileContentHeaders = z.object({
    Range: z.string().optional()
});

export const zDownloadCatalogFileContentPath = z.object({
    file: z.uuid()
});

/**
 * The bytes.
 */
export const zDownloadCatalogFileContentResponse = z.string();

export const zUploadCatalogFileContentBody = z.string();

export const zUploadCatalogFileContentHeaders = z.object({
    'Content-Range': z.string().regex(/^bytes [0-9]+-[0-9]+\/[0-9]+$/).optional()
});

export const zUploadCatalogFileContentPath = z.object({
    file: z.uuid()
});

export const zUploadCatalogFileContentResponse = z.union([
    zCatalogFile,
    z.void()
]);

export const zRequestCatalogFileDownloadPath = z.object({
    file: z.uuid()
});

/**
 * The minted ticket URL.
 */
export const zRequestCatalogFileDownloadResponse = z.object({
    downloadUrl: z.string(),
    fileName: z.string()
});

export const zDownloadCatalogFileByTicketHeaders = z.object({
    Range: z.string().optional()
});

export const zDownloadCatalogFileByTicketPath = z.object({
    file: z.uuid(),
    ticket: z.string().min(1)
});

/**
 * The bytes; the ticket is now spent.
 */
export const zDownloadCatalogFileByTicketResponse = z.string();

export const zListPodsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListPodsResponse = zPodList;

export const zCreatePodBody = zPodWritable;

export const zCreatePodQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreatePodResponse = zPod;

export const zDeletePodPath = z.object({
    pod: z.uuid()
});

export const zDeletePodQuery = z.object({
    purge: z.boolean().optional()
});

/**
 * Delete enqueued; `keptData` lists what stays behind, per instance.
 */
export const zDeletePodResponse = zPodDeleteResult;

export const zGetPodPath = z.object({
    pod: z.uuid()
});

/**
 * The pod.
 */
export const zGetPodResponse = zPod;

export const zPatchPodBody = zPodWritable;

export const zPatchPodHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchPodPath = z.object({
    pod: z.uuid()
});

export const zPatchPodQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchPodResponse = zPod.and(z.object({
    warnings: z.array(z.string()).optional()
}));

export const zStartPodHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zStartPodPath = z.object({
    pod: z.uuid()
});

/**
 * Start enqueued.
 */
export const zStartPodResponse = zTask;

export const zStopPodHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zStopPodPath = z.object({
    pod: z.uuid()
});

/**
 * Stop enqueued.
 */
export const zStopPodResponse = zTask;

export const zRestartPodHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestartPodPath = z.object({
    pod: z.uuid()
});

/**
 * Rolling restart enqueued.
 */
export const zRestartPodResponse = zTask;

export const zRefreshPodHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRefreshPodPath = z.object({
    pod: z.uuid()
});

/**
 * Refresh enqueued.
 */
export const zRefreshPodResponse = zTask;

export const zListAppsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListAppsResponse = zAppList;

export const zGetAppPath = z.object({
    app: z.uuid()
});

/**
 * The app.
 */
export const zGetAppResponse = zApp;

export const zPatchAppBody = zAppWritable;

export const zPatchAppHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchAppPath = z.object({
    app: z.uuid()
});

export const zPatchAppQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchAppResponse = zApp;

export const zStartAppHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zStartAppPath = z.object({
    app: z.uuid()
});

/**
 * Start enqueued.
 */
export const zStartAppResponse = zTask;

export const zStopAppHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zStopAppPath = z.object({
    app: z.uuid()
});

/**
 * Stop enqueued.
 */
export const zStopAppResponse = zTask;

export const zRestartAppBody = zAppRestartRequest;

export const zRestartAppHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestartAppPath = z.object({
    app: z.uuid()
});

/**
 * Restart enqueued.
 */
export const zRestartAppResponse = zTask;

export const zRefreshAppHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRefreshAppPath = z.object({
    app: z.uuid()
});

/**
 * Refresh enqueued.
 */
export const zRefreshAppResponse = zTask;

export const zMoveAppBody = zAppMoveRequest;

export const zMoveAppPath = z.object({
    app: z.uuid()
});

export const zMoveAppQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Moved.
 */
export const zMoveAppResponse = zApp.and(z.object({
    warnings: z.array(z.string()).optional()
}));

export const zLogsAppBody = z.object({
    container: z.string().max(63).optional(),
    tail: z.int().gte(1).lte(10000).optional()
});

export const zLogsAppHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zLogsAppPath = z.object({
    app: z.uuid()
});

/**
 * Log capture enqueued.
 */
export const zLogsAppResponse = zTask;

export const zExecAppBody = zAppExecRequest;

export const zExecAppPath = z.object({
    app: z.uuid()
});

/**
 * Exec session access.
 */
export const zExecAppResponse = zAppExecAccess;

export const zConnectAppExecPath = z.object({
    app: z.uuid(),
    ticket: z.string().min(1)
});

export const zConnectAppExecControlPath = z.object({
    app: z.uuid(),
    ticket: z.string().min(1)
});

export const zCreateAppBackupBody = zCreateAppBackupRequest;

export const zCreateAppBackupHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateAppBackupPath = z.object({
    app: z.uuid()
});

/**
 * Backup started.
 */
export const zCreateAppBackupResponse = zTask;

export const zListOciRegistriesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListOciRegistriesResponse = zOciRegistryList;

export const zCreateOciRegistryBody = zOciRegistryWritable;

export const zCreateOciRegistryQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateOciRegistryResponse = zOciRegistry;

export const zBatchDeleteOciRegistriesBody = zBatchDeleteRequest;

/**
 * Per-item outcomes (`in_use` for registries with repositories).
 */
export const zBatchDeleteOciRegistriesResponse = zBatchDeleteResult;

export const zDeleteOciRegistryPath = z.object({
    registry: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteOciRegistryResponse = z.void();

export const zGetOciRegistryPath = z.object({
    registry: z.uuid()
});

/**
 * The registry.
 */
export const zGetOciRegistryResponse = zOciRegistry;

export const zPatchOciRegistryBody = zOciRegistryWritable;

export const zPatchOciRegistryHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchOciRegistryPath = z.object({
    registry: z.uuid()
});

export const zPatchOciRegistryQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchOciRegistryResponse = zOciRegistry;

export const zVerifyOciRegistryHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zVerifyOciRegistryPath = z.object({
    registry: z.uuid()
});

/**
 * Verify started.
 */
export const zVerifyOciRegistryResponse = zTask;

export const zSearchOciRegistryBody = z.object({
    term: z.string().min(1).max(255),
    pageSize: z.int().gte(1).lte(100).optional().default(25),
    cursor: z.string().min(1).max(1024).optional()
});

export const zSearchOciRegistryHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zSearchOciRegistryPath = z.object({
    registry: z.uuid()
});

/**
 * Search task created; poll it — the structured answer arrives in the task's `result` (`OCISearchResult`) on success.
 *
 */
export const zSearchOciRegistryResponse = zTask;

export const zListOciRemoteTagsBody = z.object({
    path: z.string().min(1).max(255),
    term: z.string().min(1).max(255).optional(),
    pageSize: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().min(1).max(1024).optional()
});

export const zListOciRemoteTagsHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zListOciRemoteTagsPath = z.object({
    registry: z.uuid()
});

/**
 * Tags task created; poll it — `OCIRemoteTags` in the task `result`.
 */
export const zListOciRemoteTagsResponse = zTask;

export const zDescribeOciRemoteRepositoryBody = z.object({
    path: z.string().min(1).max(255)
});

export const zDescribeOciRemoteRepositoryHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDescribeOciRemoteRepositoryPath = z.object({
    registry: z.uuid()
});

/**
 * Description task created; poll it — `OCIRemoteDescription` in the task `result`.
 */
export const zDescribeOciRemoteRepositoryResponse = zTask;

export const zListOciRepositoriesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListOciRepositoriesResponse = zOciRepositoryList;

export const zCreateOciRepositoryBody = zOciRepositoryWritable;

export const zCreateOciRepositoryQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Already registered — branches merged into the existing entry.
 */
export const zCreateOciRepositoryResponse = zOciRepository;

export const zBatchDeleteOciRepositoriesBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteOciRepositoriesResponse = zBatchDeleteResult;

export const zDeleteOciRepositoryPath = z.object({
    repository: z.uuid()
});

export const zDeleteOciRepositoryQuery = z.object({
    purge: z.boolean().optional()
});

/**
 * Deleted; when digests were materialized, the eviction family rides `X-TVirt-Task`.
 *
 */
export const zDeleteOciRepositoryResponse = z.void();

export const zGetOciRepositoryPath = z.object({
    repository: z.uuid()
});

/**
 * The repository.
 */
export const zGetOciRepositoryResponse = zOciRepository;

export const zPatchOciRepositoryBody = zOciRepositoryWritable;

export const zPatchOciRepositoryHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchOciRepositoryPath = z.object({
    repository: z.uuid()
});

export const zPatchOciRepositoryQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchOciRepositoryResponse = zOciRepository;

export const zRefreshOciRepositoryBody = z.object({
    branch: z.string().optional()
});

export const zRefreshOciRepositoryHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRefreshOciRepositoryPath = z.object({
    repository: z.uuid()
});

/**
 * Refresh started.
 */
export const zRefreshOciRepositoryResponse = zTask;

export const zPullOciImageBody = z.object({
    branch: z.string().optional(),
    digest: z.string().regex(/^sha256:[a-f0-9]{64}$/).optional()
});

export const zPullOciImageHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zPullOciImagePath = z.object({
    repository: z.uuid()
});

/**
 * Pull started.
 */
export const zPullOciImageResponse = zTask;

export const zListOciBranchesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListOciBranchesResponse = zOciBranchList;

export const zCreateOciBranchBody = zOciBranchWritable;

export const zCreateOciBranchQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Already observed — the existing branch.
 */
export const zCreateOciBranchResponse = zOciBranch;

export const zDeleteOciBranchPath = z.object({
    branch: z.uuid()
});

export const zDeleteOciBranchQuery = z.object({
    purge: z.boolean().optional()
});

/**
 * Deleted.
 */
export const zDeleteOciBranchResponse = z.void();

export const zGetOciBranchPath = z.object({
    branch: z.uuid()
});

/**
 * The branch.
 */
export const zGetOciBranchResponse = zOciBranch;

export const zPatchOciBranchBody = zOciBranchWritable;

export const zPatchOciBranchHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchOciBranchPath = z.object({
    branch: z.uuid()
});

export const zPatchOciBranchQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchOciBranchResponse = zOciBranch;

export const zListOciImagesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListOciImagesResponse = zOciImageList;

export const zImportOciImageFromCatalogBody = z.object({
    file: z.string()
});

export const zImportOciImageFromCatalogHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

/**
 * Import started.
 */
export const zImportOciImageFromCatalogResponse = zTask;

export const zDeleteOciImageHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDeleteOciImagePath = z.object({
    image: z.uuid()
});

export const zDeleteOciImageQuery = z.object({
    force: z.boolean().optional()
});

export const zDeleteOciImageResponse = z.union([
    zTask,
    z.void()
]);

export const zGetOciImagePath = z.object({
    image: z.uuid()
});

/**
 * The image.
 */
export const zGetOciImageResponse = zOciImage;

export const zListOciViewClaimsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional()
});

/**
 * Page.
 */
export const zListOciViewClaimsResponse = zOciViewClaimList;

export const zDropOciViewClaimPath = z.object({
    digest: z.string().regex(/^sha256:[a-f0-9]{64}$/)
});

/**
 * Dropped.
 */
export const zDropOciViewClaimResponse = z.void();

export const zListCtsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * CT page.
 */
export const zListCtsResponse = zCtList;

export const zCreateCtBody = zCtWritable;

export const zCreateCtQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created; the materialization task rides `X-TVirt-Task`.
 */
export const zCreateCtResponse = zCt;

export const zBatchDeleteCtsBody = zBatchDeleteRequest;

/**
 * Per-item outcomes.
 */
export const zBatchDeleteCtsResponse = zBatchDeleteResult;

export const zDeleteCtPath = z.object({
    ct: z.uuid()
});

/**
 * Deletion started.
 */
export const zDeleteCtResponse = zTask;

export const zGetCtPath = z.object({
    ct: z.uuid()
});

/**
 * The CT.
 */
export const zGetCtResponse = zCt;

export const zPatchCtBody = zCtWritable;

export const zPatchCtHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchCtPath = z.object({
    ct: z.uuid()
});

export const zPatchCtQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchCtResponse = zCt;

export const zExportCtToCatalogBody = z.object({
    catalog: z.string(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
    storage: z.string().optional()
});

export const zExportCtToCatalogHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zExportCtToCatalogPath = z.object({
    ct: z.uuid()
});

/**
 * Export started; the task's target is the new catalog file.
 */
export const zExportCtToCatalogResponse = zTask;

export const zCreateCtBackupBody = zCreateCtBackupRequest;

export const zCreateCtBackupHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateCtBackupPath = z.object({
    ct: z.uuid()
});

/**
 * Backup started.
 */
export const zCreateCtBackupResponse = zTask;

export const zStartCtBody = z.object({
    node: z.union([
        z.string(),
        zNodeSelector
    ]).optional()
});

export const zStartCtHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zStartCtPath = z.object({
    ct: z.uuid()
});

/**
 * Starting.
 */
export const zStartCtResponse = zTask;

export const zStopCtBody = z.object({
    mode: z.enum(['graceful', 'hard']).optional().default('graceful'),
    timeoutSeconds: z.int().gte(1).optional().default(30)
});

export const zStopCtHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zStopCtPath = z.object({
    ct: z.uuid()
});

/**
 * Stopping.
 */
export const zStopCtResponse = zTask;

export const zRestartCtHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestartCtPath = z.object({
    ct: z.uuid()
});

/**
 * Restarting.
 */
export const zRestartCtResponse = zTask;

export const zMarkCtAsTemplatePath = z.object({
    ct: z.uuid()
});

/**
 * Promoted — the refreshed CT.
 */
export const zMarkCtAsTemplateResponse = zCt;

export const zMarkCtAsCtPath = z.object({
    ct: z.uuid()
});

/**
 * Demoted — the refreshed CT.
 */
export const zMarkCtAsCtResponse = zCt;

export const zConsoleCtPath = z.object({
    ct: z.uuid()
});

export const zConsoleCtQuery = z.object({
    proto: z.enum(['tty']),
    rows: zTerminalGeometryAxis.optional(),
    cols: zTerminalGeometryAxis.optional()
});

/**
 * Console access.
 */
export const zConsoleCtResponse = zCtConsoleAccess;

export const zLogsCtBody = z.object({
    tail: z.int().gte(1).lte(10000).optional(),
    source: z.enum(['console', 'supervisor']).optional().default('console')
});

export const zLogsCtHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zLogsCtPath = z.object({
    ct: z.uuid()
});

/**
 * Log capture enqueued.
 */
export const zLogsCtResponse = zTask;

export const zConnectCtConsolePath = z.object({
    ct: z.uuid(),
    ticket: z.string().min(1)
});

export const zConnectCtConsoleControlPath = z.object({
    ct: z.uuid(),
    ticket: z.string().min(1)
});

export const zGetCtConsoleFatePath = z.object({
    ct: z.uuid(),
    ticket: z.string().min(1)
});

/**
 * The ticket's fate, and the endpoint's observed state.
 */
export const zGetCtConsoleFateResponse = zCtConsoleFate;

export const zExecCtBody = zCtExecRequest;

export const zExecCtPath = z.object({
    ct: z.uuid()
});

/**
 * Exec session access.
 */
export const zExecCtResponse = zCtExecAccess;

export const zConnectCtExecPath = z.object({
    ct: z.uuid(),
    ticket: z.string().min(1)
});

export const zConnectCtExecControlPath = z.object({
    ct: z.uuid(),
    ticket: z.string().min(1)
});

export const zListCephClustersQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * CephCluster page.
 */
export const zListCephClustersResponse = zCephClusterList;

export const zCreateCephClusterBody = zCephClusterWritable.and(z.object({
    network: z.record(z.string(), z.unknown())
}));

export const zCreateCephClusterQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created; `X-TVirt-Task` points at the bootstrap parent Task birthing mon + mgr on the `controlNode` — the phase follows the fabric on its own (the backend never waits for tasks, decided 2026-07-09).
 *
 */
export const zCreateCephClusterResponse = zCephCluster;

export const zDeleteCephClusterHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional()
});

export const zDeleteCephClusterPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteCephClusterResponse = z.void();

export const zGetCephClusterPath = z.object({
    cephCluster: z.uuid()
});

/**
 * The cluster.
 */
export const zGetCephClusterResponse = zCephCluster;

export const zPatchCephClusterBody = zCephClusterWritable;

export const zPatchCephClusterHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchCephClusterPath = z.object({
    cephCluster: z.uuid()
});

export const zPatchCephClusterQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchCephClusterResponse = zCephCluster;

export const zDeployCephClusterHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zDeployCephClusterPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Deploy started.
 */
export const zDeployCephClusterResponse = zTask;

export const zAddCephClusterMonBody = z.object({
    node: z.string(),
    address: z.string().optional(),
    clusterAddress: z.string().optional()
});

export const zAddCephClusterMonHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zAddCephClusterMonPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Mon addition started.
 */
export const zAddCephClusterMonResponse = zTask;

export const zRemoveCephClusterMonBody = z.object({
    node: z.string()
});

export const zRemoveCephClusterMonHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zRemoveCephClusterMonPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Mon removal started.
 */
export const zRemoveCephClusterMonResponse = zTask;

export const zAddCephClusterMgrBody = z.object({
    node: z.string(),
    address: z.string().optional()
});

export const zAddCephClusterMgrHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zAddCephClusterMgrPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Mgr addition started.
 */
export const zAddCephClusterMgrResponse = zTask;

export const zRemoveCephClusterMgrBody = z.object({
    node: z.string()
});

export const zRemoveCephClusterMgrHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zRemoveCephClusterMgrPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Mgr removal started.
 */
export const zRemoveCephClusterMgrResponse = zTask;

export const zAddCephClusterMdsBody = z.object({
    node: z.string(),
    address: z.string().optional()
});

export const zAddCephClusterMdsHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zAddCephClusterMdsPath = z.object({
    cephCluster: z.uuid()
});

/**
 * MDS addition started.
 */
export const zAddCephClusterMdsResponse = zTask;

export const zRemoveCephClusterMdsBody = z.object({
    node: z.string()
});

export const zRemoveCephClusterMdsHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zRemoveCephClusterMdsPath = z.object({
    cephCluster: z.uuid()
});

/**
 * MDS removal started.
 */
export const zRemoveCephClusterMdsResponse = zTask;

export const zCreateCephClusterFsBody = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    size: z.int().gte(1).lte(10).optional(),
    minSize: z.int().gte(1).lte(10).optional()
});

export const zCreateCephClusterFsHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateCephClusterFsPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Filesystem creation started.
 */
export const zCreateCephClusterFsResponse = zTask;

export const zRemoveCephClusterFsBody = z.object({
    name: z.string(),
    confirm: z.string()
});

export const zRemoveCephClusterFsHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zRemoveCephClusterFsPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Filesystem removal started.
 */
export const zRemoveCephClusterFsResponse = zTask;

export const zSetCephClusterOsdFlagBody = z.object({
    flag: zCephOsdFlag,
    value: z.boolean()
});

export const zSetCephClusterOsdFlagHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zSetCephClusterOsdFlagPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Flag change started.
 */
export const zSetCephClusterOsdFlagResponse = zTask;

export const zRegisterCephClusterOsdDeviceBody = z.object({
    node: z.string(),
    encrypted: z.boolean().optional(),
    address: z.string().optional(),
    clusterAddress: z.string().optional(),
    device: z.object({
        name: z.string(),
        serial: z.string()
    })
});

export const zRegisterCephClusterOsdDevicePath = z.object({
    cephCluster: z.uuid()
});

export const zRegisterCephClusterOsdDeviceQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Registered; on a live cluster `X-TVirt-Task` points at the automatic prepare task activating the device as a BlueStore OSD.
 *
 */
export const zRegisterCephClusterOsdDeviceResponse = zCephCluster;

export const zUnregisterCephClusterOsdDeviceBody = z.object({
    node: z.string(),
    device: z.object({
        name: z.string(),
        serial: z.string().optional()
    })
});

export const zUnregisterCephClusterOsdDevicePath = z.object({
    cephCluster: z.uuid()
});

export const zUnregisterCephClusterOsdDeviceQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Unregistered.
 */
export const zUnregisterCephClusterOsdDeviceResponse = zCephCluster;

export const zProposeCephClusterStorageBody = z.object({
    driver: z.enum(['rbd', 'cephfs']).optional(),
    pool: z.string().optional(),
    fs: z.string().optional(),
    path: z.string().optional(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)
});

export const zProposeCephClusterStorageHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zProposeCephClusterStoragePath = z.object({
    cephCluster: z.uuid()
});

/**
 * Proposal accepted — the Storage is being materialized.
 */
export const zProposeCephClusterStorageResponse = zTask;

export const zCreateCephClusterPoolBody = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    size: z.int().gte(1).lte(10).optional(),
    minSize: z.int().gte(1).lte(10).optional()
});

export const zCreateCephClusterPoolHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateCephClusterPoolPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Pool creation started.
 */
export const zCreateCephClusterPoolResponse = zTask;

export const zUpdateCephClusterPoolBody = z.object({
    name: z.string(),
    size: z.int().gte(1).lte(10).optional(),
    minSize: z.int().gte(1).lte(10).optional(),
    pgNum: z.int().gte(1).optional(),
    pgAutoscaleMode: z.enum([
        'on',
        'off',
        'warn'
    ]).optional()
});

export const zUpdateCephClusterPoolHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zUpdateCephClusterPoolPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Pool update started.
 */
export const zUpdateCephClusterPoolResponse = zTask;

export const zDeleteCephClusterPoolBody = z.object({
    name: z.string(),
    confirm: z.string()
});

export const zDeleteCephClusterPoolHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zDeleteCephClusterPoolPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Pool deletion started.
 */
export const zDeleteCephClusterPoolResponse = zTask;

export const zCreateCephClusterCrushRuleBody = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    failureDomain: z.enum([
        'osd',
        'host',
        'rack'
    ])
});

export const zCreateCephClusterCrushRuleHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateCephClusterCrushRulePath = z.object({
    cephCluster: z.uuid()
});

/**
 * Rule creation started.
 */
export const zCreateCephClusterCrushRuleResponse = zTask;

export const zDeleteCephClusterCrushRuleBody = z.object({
    name: z.string(),
    confirm: z.string()
});

export const zDeleteCephClusterCrushRuleHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zDeleteCephClusterCrushRulePath = z.object({
    cephCluster: z.uuid()
});

/**
 * Rule deletion started.
 */
export const zDeleteCephClusterCrushRuleResponse = zTask;

export const zSetCephClusterPoolCrushRuleBody = z.object({
    pool: z.string(),
    rule: z.string()
});

export const zSetCephClusterPoolCrushRuleHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zSetCephClusterPoolCrushRulePath = z.object({
    cephCluster: z.uuid()
});

/**
 * Pool re-association started.
 */
export const zSetCephClusterPoolCrushRuleResponse = zTask;

export const zQueryCephClusterCrushmapHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zQueryCephClusterCrushmapPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Query task created; poll it — the decompiled text arrives in the task's `result.text` on success.
 *
 */
export const zQueryCephClusterCrushmapResponse = zTask;

export const zSetCephClusterCrushmapBody = z.object({
    text: z.string().min(1).max(1048576),
    confirm: z.string()
});

export const zSetCephClusterCrushmapHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zSetCephClusterCrushmapPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Crushmap compile + validate + inject started.
 */
export const zSetCephClusterCrushmapResponse = zTask;

export const zQueryCephClusterObjectsBody = z.object({
    pool: z.string().regex(/^[^-]/),
    image: z.string().regex(/^[^-]/).optional(),
    object: z.string().regex(/^[^-]/).optional()
});

export const zQueryCephClusterObjectsHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zQueryCephClusterObjectsPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Query task created; poll it — the structured answer arrives in the task's `result` on success.
 *
 */
export const zQueryCephClusterObjectsResponse = zTask;

export const zMarkCephClusterOsdInBody = z.object({
    osdId: z.int().gte(0)
});

export const zMarkCephClusterOsdInHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zMarkCephClusterOsdInPath = z.object({
    cephCluster: z.uuid()
});

/**
 * OSD mark started.
 */
export const zMarkCephClusterOsdInResponse = zTask;

export const zMarkCephClusterOsdOutBody = z.object({
    osdId: z.int().gte(0)
});

export const zMarkCephClusterOsdOutHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zMarkCephClusterOsdOutPath = z.object({
    cephCluster: z.uuid()
});

/**
 * OSD mark started.
 */
export const zMarkCephClusterOsdOutResponse = zTask;

export const zScrubCephClusterOsdBody = z.object({
    osdId: z.int().gte(0)
});

export const zScrubCephClusterOsdHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zScrubCephClusterOsdPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Scrub scheduling started.
 */
export const zScrubCephClusterOsdResponse = zTask;

export const zDeepScrubCephClusterOsdBody = z.object({
    osdId: z.int().gte(0)
});

export const zDeepScrubCephClusterOsdHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDeepScrubCephClusterOsdPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Deep-scrub scheduling started.
 */
export const zDeepScrubCephClusterOsdResponse = zTask;

export const zRestartCephClusterDaemonBody = z.object({
    daemon: z.string(),
    node: z.string().optional()
});

export const zRestartCephClusterDaemonHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestartCephClusterDaemonPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Daemon restart started.
 */
export const zRestartCephClusterDaemonResponse = zTask;

export const zDestroyCephClusterBody = z.object({
    confirm: z.string(),
    wipeDevices: z.boolean()
});

export const zDestroyCephClusterHeaders = z.object({
    'X-TVirt-Sudo': z.string().optional(),
    'Idempotency-Key': z.uuid().optional()
});

export const zDestroyCephClusterPath = z.object({
    cephCluster: z.uuid()
});

/**
 * Destroy started.
 */
export const zDestroyCephClusterResponse = zTask;

export const zListBackupDestinationsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListBackupDestinationsResponse = zBackupDestinationList;

export const zCreateBackupDestinationBody = zBackupDestinationWritable;

export const zCreateBackupDestinationQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateBackupDestinationResponse = zBackupDestination;

export const zBatchDeleteBackupDestinationsBody = zBatchDeleteRequest;

/**
 * Per-item outcomes (`in_use` for destinations that still hold checkpoints).
 */
export const zBatchDeleteBackupDestinationsResponse = zBatchDeleteResult;

export const zDeleteBackupDestinationPath = z.object({
    destination: z.uuid()
});

/**
 * Deleted.
 */
export const zDeleteBackupDestinationResponse = z.void();

export const zGetBackupDestinationPath = z.object({
    destination: z.uuid()
});

/**
 * The destination.
 */
export const zGetBackupDestinationResponse = zBackupDestination;

export const zPatchBackupDestinationBody = zBackupDestinationWritable;

export const zPatchBackupDestinationHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPatchBackupDestinationPath = z.object({
    destination: z.uuid()
});

export const zPatchBackupDestinationQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zPatchBackupDestinationResponse = zBackupDestination;

export const zCreateCheckpointBody = z.object({
    masterpass: z.string().min(1).optional()
});

export const zCreateCheckpointHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCreateCheckpointPath = z.object({
    destination: z.uuid()
});

/**
 * Checkpoint started.
 */
export const zCreateCheckpointResponse = zTask;

export const zRescanBackupDestinationHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRescanBackupDestinationPath = z.object({
    destination: z.uuid()
});

/**
 * Rescan started.
 */
export const zRescanBackupDestinationResponse = zTask;

export const zListCheckpointsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListCheckpointsResponse = zCheckpointList;

/**
 * Staged.
 */
export const zCreateConfigRestoreUploadResponse = zConfigRestoreUpload;

export const zDeleteConfigRestoreUploadPath = z.object({
    upload: z.uuid()
});

/**
 * Discarded (staged bytes removed).
 */
export const zDeleteConfigRestoreUploadResponse = z.void();

export const zGetConfigRestoreUploadPath = z.object({
    upload: z.uuid()
});

/**
 * The staged upload.
 */
export const zGetConfigRestoreUploadResponse = zConfigRestoreUpload;

export const zUploadConfigRestoreContentBody = z.string();

export const zUploadConfigRestoreContentHeaders = z.object({
    'Content-Range': z.string().regex(/^bytes [0-9]+-[0-9]+\/[0-9]+$/).optional()
});

export const zUploadConfigRestoreContentPath = z.object({
    upload: z.uuid()
});

export const zUploadConfigRestoreContentResponse = z.union([
    zConfigRestoreUpload,
    z.void()
]);

export const zDiffConfigRestoreUploadPath = z.object({
    upload: z.uuid()
});

export const zDiffConfigRestoreUploadQuery = z.object({
    selection: z.string().optional()
});

/**
 * The per-object preview.
 */
export const zDiffConfigRestoreUploadResponse = zConfigRestoreDiff;

export const zCheckConfigRestoreMasterpassBody = z.object({
    masterpass: z.string().min(1)
});

export const zCheckConfigRestoreMasterpassPath = z.object({
    upload: z.uuid()
});

/**
 * The verdict.
 */
export const zCheckConfigRestoreMasterpassResponse = z.object({
    valid: z.boolean()
});

export const zRestoreCheckpointBody = zConfigRestoreRequest;

export const zRestoreCheckpointHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestoreCheckpointPath = z.object({
    upload: z.uuid()
});

/**
 * Restore started.
 */
export const zRestoreCheckpointResponse = zTask;

export const zDeleteCheckpointHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDeleteCheckpointPath = z.object({
    checkpoint: z.uuid()
});

/**
 * Delete started.
 */
export const zDeleteCheckpointResponse = zTask;

export const zGetCheckpointPath = z.object({
    checkpoint: z.uuid()
});

/**
 * The checkpoint record, manifest included.
 */
export const zGetCheckpointResponse = zCheckpoint;

export const zDownloadCheckpointContentPath = z.object({
    checkpoint: z.uuid()
});

/**
 * The artefact bytes.
 */
export const zDownloadCheckpointContentResponse = z.string();

export const zListVmBackupsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListVmBackupsResponse = zVmBackupList;

export const zDeleteVmBackupHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDeleteVmBackupPath = z.object({
    vmBackup: z.uuid()
});

/**
 * Delete started.
 */
export const zDeleteVmBackupResponse = zTask;

export const zGetVmBackupPath = z.object({
    vmBackup: z.uuid()
});

/**
 * The backup record, manifest included.
 */
export const zGetVmBackupResponse = zVmBackup;

export const zRestoreVmBackupToNewVmBody = zRestoreVmBackupToNewVmRequest;

export const zRestoreVmBackupToNewVmHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestoreVmBackupToNewVmPath = z.object({
    vmBackup: z.uuid()
});

/**
 * Restore started. The task targets the newborn VM.
 */
export const zRestoreVmBackupToNewVmResponse = zTask;

export const zRestoreVmBackupOverBody = zRestoreVmBackupOverRequest;

export const zRestoreVmBackupOverHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestoreVmBackupOverPath = z.object({
    vmBackup: z.uuid()
});

/**
 * Restore started. The task targets the VM being restored.
 */
export const zRestoreVmBackupOverResponse = zTask;

export const zListCtBackupsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListCtBackupsResponse = zCtBackupList;

export const zDeleteCtBackupHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDeleteCtBackupPath = z.object({
    ctBackup: z.uuid()
});

/**
 * Delete started.
 */
export const zDeleteCtBackupResponse = zTask;

export const zGetCtBackupPath = z.object({
    ctBackup: z.uuid()
});

/**
 * The backup record, manifest included.
 */
export const zGetCtBackupResponse = zCtBackup;

export const zRestoreCtBackupToNewCtBody = zRestoreCtBackupToNewCtRequest;

export const zRestoreCtBackupToNewCtHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestoreCtBackupToNewCtPath = z.object({
    ctBackup: z.uuid()
});

/**
 * Restore started. The task targets the newborn CT.
 */
export const zRestoreCtBackupToNewCtResponse = zTask;

export const zRestoreCtBackupOverBody = zRestoreCtBackupOverRequest;

export const zRestoreCtBackupOverHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestoreCtBackupOverPath = z.object({
    ctBackup: z.uuid()
});

/**
 * Restore started. The task targets the CT being restored.
 */
export const zRestoreCtBackupOverResponse = zTask;

export const zListAppBackupsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Page.
 */
export const zListAppBackupsResponse = zAppBackupList;

export const zDeleteAppBackupHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zDeleteAppBackupPath = z.object({
    appBackup: z.uuid()
});

/**
 * Delete started.
 */
export const zDeleteAppBackupResponse = zTask;

export const zGetAppBackupPath = z.object({
    appBackup: z.uuid()
});

/**
 * The backup record, manifest included.
 */
export const zGetAppBackupResponse = zAppBackup;

export const zRestoreAppBackupToNewAppBody = zRestoreAppBackupToNewAppRequest;

export const zRestoreAppBackupToNewAppHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestoreAppBackupToNewAppPath = z.object({
    appBackup: z.uuid()
});

/**
 * Restore started. The task targets the newborn App.
 */
export const zRestoreAppBackupToNewAppResponse = zTask;

export const zRestoreAppBackupOverBody = zRestoreAppBackupOverRequest;

export const zRestoreAppBackupOverHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zRestoreAppBackupOverPath = z.object({
    appBackup: z.uuid()
});

/**
 * Restore started. The task targets the App being restored.
 */
export const zRestoreAppBackupOverResponse = zTask;

export const zListEventsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Event page.
 */
export const zListEventsResponse = zEventList;

export const zGetEventPath = z.object({
    event: z.string()
});

/**
 * The event.
 */
export const zGetEventResponse = zEvent;

export const zListAuditRecordsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Audit page.
 */
export const zListAuditRecordsResponse = zAuditRecordList;

export const zGetAuditRecordPath = z.object({
    record: z.string()
});

/**
 * The audit record.
 */
export const zGetAuditRecordResponse = zAuditRecord;

export const zListTasksQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Task page.
 */
export const zListTasksResponse = zTaskList;

export const zGetTaskPath = z.object({
    task: z.string()
});

/**
 * The task.
 */
export const zGetTaskResponse = zTask;

export const zGetTaskLogPath = z.object({
    task: z.string()
});

export const zGetTaskLogQuery = z.object({
    follow: z.boolean().optional().default(false)
});

/**
 * Structured log frames.
 */
export const zGetTaskLogResponse = z.string();

export const zCancelTaskHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zCancelTaskPath = z.object({
    task: z.string()
});

/**
 * Cancellation requested.
 */
export const zCancelTaskResponse = zTask;

export const zResumeTaskHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zResumeTaskPath = z.object({
    task: z.string()
});

/**
 * Resumed.
 */
export const zResumeTaskResponse = zTask;

export const zAbortTaskHeaders = z.object({
    'Idempotency-Key': z.uuid().optional()
});

export const zAbortTaskPath = z.object({
    task: z.string()
});

/**
 * Abort started; the response enumerates what is undone vs merely reported.
 */
export const zAbortTaskResponse = zAbortResult;

export const zListNotificationTargetsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Target page.
 */
export const zListNotificationTargetsResponse = zNotificationTargetList;

export const zCreateNotificationTargetBody = zNotificationTargetWritable;

export const zCreateNotificationTargetQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateNotificationTargetResponse = zNotificationTarget;

export const zDeleteNotificationTargetPath = z.object({
    target: z.string()
});

export const zDeleteNotificationTargetQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Deleted.
 */
export const zDeleteNotificationTargetResponse = z.void();

export const zGetNotificationTargetPath = z.object({
    target: z.string()
});

/**
 * The target.
 */
export const zGetNotificationTargetResponse = zNotificationTarget;

export const zUpdateNotificationTargetBody = zNotificationTargetWritable;

export const zUpdateNotificationTargetPath = z.object({
    target: z.string()
});

export const zUpdateNotificationTargetQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zUpdateNotificationTargetResponse = zNotificationTarget;

export const zTestNotificationTargetPath = z.object({
    target: z.string()
});

/**
 * Test outcome.
 */
export const zTestNotificationTargetResponse = zNotificationTestResult;

export const zListNotificationRulesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Rule page.
 */
export const zListNotificationRulesResponse = zNotificationRuleList;

export const zCreateNotificationRuleBody = zNotificationRuleWritable;

export const zCreateNotificationRuleQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Created.
 */
export const zCreateNotificationRuleResponse = zNotificationRule;

export const zDeleteNotificationRulePath = z.object({
    rule: z.string()
});

export const zDeleteNotificationRuleQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Deleted.
 */
export const zDeleteNotificationRuleResponse = z.void();

export const zGetNotificationRulePath = z.object({
    rule: z.string()
});

/**
 * The rule.
 */
export const zGetNotificationRuleResponse = zNotificationRule;

export const zUpdateNotificationRuleBody = zNotificationRuleWritable;

export const zUpdateNotificationRulePath = z.object({
    rule: z.string()
});

export const zUpdateNotificationRuleQuery = z.object({
    dryRun: z.boolean().optional().default(false)
});

/**
 * Updated.
 */
export const zUpdateNotificationRuleResponse = zNotificationRule;

export const zListNotificationDeliveriesQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    cursor: z.string().optional(),
    scope: z.string().optional().default('/*'),
    filter: z.array(z.string()).optional(),
    sort: z.string().optional()
});

/**
 * Delivery page.
 */
export const zListNotificationDeliveriesResponse = zNotificationDeliveryList;

export const zGetNotificationDeliveryPath = z.object({
    delivery: z.string()
});

/**
 * The delivery.
 */
export const zGetNotificationDeliveryResponse = zNotificationDelivery;

export const zRetryNotificationDeliveryPath = z.object({
    delivery: z.string()
});

/**
 * Requeued.
 */
export const zRetryNotificationDeliveryResponse = zNotificationDelivery;
